/**
 * Controleur d'interface du coffre PWA. Rendu par gabarits de chaines +
 * re-rendu complet de `#app` a chaque mutation d'etat (echelle adaptee a un
 * coffre de mots de passe : quelques centaines d'entrees au plus). Toute la
 * logique zero-knowledge vit dans `../lib/*` ; ce fichier ne fait
 * qu'orchestrer saisie utilisateur <-> `VaultStore`/`sync-client`.
 */
import {
  base64ToBytes,
  bytesToBase64,
  createAccountAsync,
  generatePassword,
  passphraseEntropyBits,
  passwordEntropyBits,
  resetMasterPasswordWithRecoveryKey,
  totp,
  unlockVaultAsync,
  type EntryCustomField,
  type EntrySecrets,
  type SendPayload,
} from '@klavox/crypto-core';
import {
  MAX_PASSPHRASE_WORDS,
  MIN_PASSPHRASE_WORDS,
  PASSPHRASE_SEPARATORS,
  generatePassphraseWith,
  loadWordlist,
  strengthLabel,
  type PassphraseSettings,
  type WordlistLanguage,
} from '../core/generator.js';
import { DEFAULT_URI_MATCH_CHOICES, MAX_ATTACHMENT_BYTES, defaultUriMatchRule, equivalentDomainsOf, isUriMatchRule, mergeVaultRecords, normalizeUriMatch, uriMatchRuleOf, type UriMatchRule, type VaultFolder, type VaultItem, type VaultItemType } from '@klavox/vault-model';
import { syncAttachments } from '../lib/attachment-sync.js';
import { deceptiveHostReason, findDeceptiveUri, type EngagedSites } from '../lib/deceptive-host.js';
import {
  LocalVaultAlreadyExistsError,
  describeRecoveryError,
  recoverAccountOnNewDevice,
  signInOnNewDevice,
} from '../lib/account-recovery.js';
import { ALIAS_PROVIDERS, DEFAULT_ALIAS_PROVIDER_ID, aliasProvider, canDeleteAlias, createEmailAlias, deleteEmailAlias } from '../lib/email-alias.js';
import {
  generatedKindLabel,
  loadGeneratorHistory,
  recordGenerated,
  removeGenerated,
  saveGeneratorHistory,
  type GeneratedKind,
  type GeneratorHistoryEntry,
} from '../lib/generator-history.js';
import {
  DEFAULT_USERNAME_SETTINGS,
  USERNAME_KINDS,
  USERNAME_SUFFIXES,
  generateUsername,
  siteSuffixFromUris,
  usernameKind,
  usernameSuffix,
  type UsernameSettings,
} from '../lib/username-generator.js';
import {
  BreachCheckError,
  DEFAULT_PWNED_PASSWORDS_ENDPOINT,
  checkPwnedPasswords,
  normalizeBreachEndpoint,
  resolveStoredBreachEndpoint,
  type BreachCheckOptions,
} from '../lib/breach-check.js';
import { checkDraftPasswordExposure, draftBreachMessage, type DraftBreachVerdict } from '../lib/draft-breach-check.js';
import {
  CREDENTIALS_AUDIT_CAPABILITY,
  serveCredentialsAudit,
  type CredentialsAuditRequest,
} from '../lib/credentials-audit.js';
import { CREDENTIALS_TRANSFER_CAPABILITY, type CredentialsTransferOffer } from '../lib/credentials-transfer.js';
import { CredentialsTransferInbox } from '../lib/credentials-transfer-inbox.js';
import {
  IMPORT_CLEANUP_ADVICE,
  IMPORT_CLEANUP_ONE_CLICK_ADVICE,
  IMPORT_REMOVED_ADVICE,
  canRemoveImportFile,
  droppedImportFileHandle,
  hasImportFilePicker,
  isPlaintextExport,
  pickImportFile,
  removeImportFile,
  type ImportFileHandle,
  type ImportFileRemoval,
} from '../lib/import-cleanup.js';
import { acceptsKeyFile } from '../lib/kdbx-import.js';
import {
  HELP_ENGINE,
  HELP_VIEWS,
  accountFacts,
  askHelp,
  helpSuggestions,
  type AccountSnapshot,
  type HelpAnswer,
  type HelpViewId,
} from '../lib/contextual-help.js';
import {
  KLAVOX_COMMANDS,
  checkRunnable,
  commandCatalog,
  commandSelector,
  designator,
  findNamed,
  matchCommand,
  type CommandArgs,
  type CommandMatch,
  type NamedMatch,
  type CommandResult,
  type KlavoxCommand,
} from '../lib/assistant-commands.js';
import {
  ImportPasswordInvalidError,
  ImportPasswordRequiredError,
  importedEntryToSecrets,
  normalizeTotpSecret,
  type ImportedEntry,
} from '../lib/csv-import.js';
import { mergeDuplicateSecrets } from '../lib/entry-merge.js';
import { resolveImportedLinks } from '../lib/entry-links.js';
import { seedAttachTarget } from '../lib/seed-attach.js';
import {
  describeSitePasswordRules,
  generateSitePassword,
  sitePasswordEntropyBits,
  sitePasswordRulesFor,
  type SiteRulesMatch,
} from '../lib/site-password-rules.js';
import {
  MIN_MASTER_PASSWORD_LENGTH,
  changeAccountMasterPassword,
  describeMasterPasswordChangeError,
} from '../lib/master-password-change.js';
import { changeAccountEmail, describeEmailChangeError } from '../lib/account-email-change.js';
import {
  ExposedMasterPasswordError,
  assertNewMasterPasswordAccepted,
  assertNewMasterPasswordNotExposed,
  masterPasswordStrength,
} from '../lib/master-password-gate.js';
import { assertExportPasswordAccepted, exportPasswordStrength } from '../lib/export-password-gate.js';
import { parseImportFile } from '../lib/onepux-import.js';
import { IMPORT_FORMATS, isImportFormat, type ImportFormat } from '../lib/headerless-import.js';
import { answerCxpExport, drainCxpInbox, type CxpInboxBridge } from '../lib/cxp-inbox.js';
import { autofillTarget, autofillTargetLabel, entryFillsTarget, fillFromEntry, type AutofillBridge } from '../lib/android-autofill.js';
import {
  GENERATOR_LENGTH_MAX,
  GENERATOR_LENGTH_MIN,
  GENERATOR_MINIMUM_MAX,
  GENERATOR_MODE_KEY,
  GENERATOR_OPTIONS_KEY,
  PASSPHRASE_SETTINGS_KEY,
  generatorPreferencesFromForm,
  generatorProblem,
  passphraseSettingsFromForm,
  readGeneratorMode,
  readGeneratorPreferences,
  readPassphraseSettings,
  serializePassphraseSettings,
  serializeGeneratorPreferences,
  toGeneratorOptions,
  type GeneratorMode,
  type GeneratorPreferences,
} from '../lib/generator-settings.js';
import {
  MAX_PASSKEY_LABEL_LENGTH,
  MAX_PASSKEY_UNLOCKS,
  describePasskeyUnlockError,
  enrollPasskeyUnlock,
  revokePasskeyUnlock,
  unlockVaultWithPasskey,
} from '../lib/passkey-unlock.js';
import { readPasskeys, passkeyLabel, withPasskeys } from '../lib/passkey-credential.js';
import { PASSWORD_EXPIRY_PRESETS, PASSWORD_RENEWAL_PRESETS, passwordExpiryAfterDays, passwordExpiryIn, passwordExpiryState, readPasswordExpiry, readPasswordRenewDays } from '../lib/password-expiry.js';
import { readPasswordHistory } from '../lib/password-history.js';
import { MAX_ITEM_VERSIONS, itemVersionChanges, itemVersionFields, readItemHistory, restoreItemVersion, type ItemVersionField } from '../lib/item-history.js';
import { diffCounts, diffWords, type DiffSegment } from '../lib/text-diff.js';
import { checkboxLabel, customValueRows, isCheckboxField, isMultilineCustomValue, normalizeCustomFields, readCustomFields, spliceCustomValue } from '../lib/custom-fields.js';
import { isRepromptProtected, masterPasswordHashIfValid, verifyMasterPassword } from '../lib/master-password-reprompt.js';
import { isArchived, type ArchiveFilter } from '../lib/entry-archive.js';
import { formatTagList, isWithinTag, normalizeTag, parseTagList, readTags, tagDropRename, tagKey, tagPath, tagTreeNavRows, visibleTagTree, withTags, type TagCount } from '../lib/entry-tags.js';
import { HEALTH_ALERT_LABELS, isHealthExcluded, isIgnorableHealthAlert, readIgnoredHealthAlerts, type IgnorableHealthAlert } from '../lib/health-exclusion.js';
import { hasPasskeyElsewhere } from '../lib/passkey-elsewhere.js';
import { SEARCH_FIELD_LABELS, isAdvancedSearch, parseAdvancedSearch } from '../lib/entry-search.js';
import { TRASH_RETENTION_DAYS, isTrashed, trashDaysLeft } from '../lib/entry-trash.js';
import {
  MAX_SEND_FILE_BYTES,
  MAX_SEND_NOTE_LENGTH,
  MAX_SEND_TTL_MS,
  MAX_SEND_VIEWS,
  SEND_EXPIRY_CHOICES,
  SendPasswordRequiredError,
  createVaultSend,
  describeVaultSend,
  fetchVaultSend,
  listVaultSends,
  openVaultSend,
  parseSendLink,
  parseSendMaxViews,
  resolveCustomSendExpiry,
  revokeVaultSend,
  toLocalDateTimeInput,
  updateVaultSend,
  type CreatedSend,
  type OwnedSend,
} from '../lib/send-client.js';
import { IndexedDbVaultRepository, isIndexedDbAvailable } from '../lib/indexeddb-repository.js';
import {
  IDENVA_CALLBACK_CHANNEL,
  IdenvaLinkError,
  idenvaRedirectUri,
  obtainIdenvaIdToken,
  runIdenvaLink,
  type IdenvaIdToken,
} from '../lib/idenva-link.js';
import { MemoryVaultRepository, type VaultRepository } from '../lib/repository.js';
import {
  KlavoxApiError,
  beginWebAuthnLogin,
  beginWebAuthnRegistration,
  deleteWebAuthnCredential,
  enableServerRecoveryKey,
  getRecoveryKeyStatus,
  fetchIdenvaConfig,
  fetchOidcLinkStatus,
  linkOidcIdentity,
  unlinkOidcIdentity,
  deleteServerAccount,
  revokeAllServerSessions,
  fetchConnectedDevices,
  revokeConnectedDevice,
  fetchSecurityLog,
  fetchTravelMode,
  setTravelMode as apiSetTravelMode,
  finishWebAuthnLogin,
  finishWebAuthnRegistration,
  beginTotpEnrollment as apiBeginTotpEnrollment,
  disableTotp as apiDisableTotp,
  finishTotpEnrollment as apiFinishTotpEnrollment,
  finishTotpLogin,
  getTotpStatus,
  mfaMethods,
  normalizeTotpCode,
  isMfaRequired,
  listWebAuthnCredentials,
  login as apiLogin,
  loginWithIdenva,
  lookupOidcAccount,
  pullSync,
  pushSync,
  registerAccount,
  registerWithIdenva,
  createServerSessionRenewer,
  type AuthenticatedSession,
  type MfaRequired,
  type OidcAccountLookup,
  type SendContent,
  type SendDescription,
  type TotpEnrollment,
  type WebAuthnCredentialSummary,
} from '../lib/sync-client.js';
import {
  DEFAULT_EMERGENCY_WAIT_MS,
  EMERGENCY_WAIT_PRESETS,
  MAX_CONTACT_NAME_LENGTH,
  createEmergencyGrantWithKit,
  describeEmergencyError,
  openEmergencyVault,
  readEmergencyGrants,
  refuseEmergencyGrantById,
  requestEmergencyAccessWithKit,
  revokeEmergencyGrantById,
  type EmergencyGrantView,
  type OpenedEmergencyVault,
} from '../lib/emergency-access.js';
import { createWebAuthnCredential, getWebAuthnAssertion, isWebAuthnSupported } from '../lib/webauthn-browser.js';
import { computeTotpDisplay } from '../lib/totp-live.js';
import {
  SSH_KEY_PROBLEM_MESSAGES,
  checkSshKey,
  completeSshKey,
  isPrivateKeyEncrypted,
  parseSshPublicKey,
  readSshKey,
  withSshKey,
  type SshKeyMaterial,
} from '../lib/ssh-key-item.js';
import { SshKeygenError, generateSshKey } from '../lib/ssh-keygen.js';
import { ensureDeviceId, type TravelPurgePlan } from '../lib/travel-mode.js';
import {
  MAX_FOLDER_INDENT,
  entryDropAllowed,
  folderDropAllowed,
  folderSubtreeIds,
  folderTree,
  folderTreeKey,
  folderTreeTabStop,
  folderTreeTypeAhead,
  folderTypeAheadStep,
  visibleFolderTree,
  type FolderTypeAhead,
} from '../lib/folder-tree.js';
import {
  IDENTITY_DOCUMENTS,
  TYPED_FIELD_GROUPS,
  TYPE_LABELS,
  detectCardBrand,
  fieldSpecs,
  formatCardNumber,
  isCardExpired,
  isDocumentExpired,
  isDocumentExpiryKey,
  isLuhnValid,
  maskCardNumber,
  normalizeDocumentDate,
  normalizeExpiry,
  normalizeTypedFields,
  readTypedFields,
  typedEntrySubtitle,
  typedFieldGroup,
  bankAccountProblem,
  bankAccountType,
  apiKeyProblem,
  apiKeyProvider,
  isApiKeyDateKey,
  isSoftwareLicenseDateKey,
  softwareLicenseProblem,
  isMembershipDateKey,
  isMembershipExpired,
  membershipKind,
  membershipKindLabel,
  membershipProblem,
  medicalProblem,
  serverProblem,
  emailAccountProblem,
  emailDefaultPort,
  emailProtocol,
  emailProtocolLabel,
  emailSecurity,
  emailSecurityLabel,
  databaseConnectionUri,
  databaseDefaultPort,
  databaseEngine,
  databaseEngineLabel,
  databaseProblem,
  bankAccountTypeLabel,
  formatIban,
  isIbanValid,
  maskIban,
  wifiPasswordProblem,
  wifiSecurity,
  wifiSecurityLabel,
  type TypedFieldGroup,
  type TypedFieldSpec,
} from '../lib/typed-fields.js';
import { checkRecoveryPhrase, recoveryPhraseLabel, recoveryPhraseProblem } from '../lib/recovery-phrase.js';
import { entryFieldValue } from '../lib/entry-copy.js';
import { largeTypeCells, largeTypeFieldLabel, largeTypeSummary } from '../lib/large-type.js';
import { modalFocusables, modalKey, modalReturnPlan, modalTabTarget, setModalBackgroundInert, topModal, type ModalReturn } from '../lib/modal-focus.js';
import { encodeQr, qrSvgPath } from '../lib/qr-code.js';
import { looksLikeWifiQr, parseWifiQr, wifiQrContent, type WifiQrContent } from '../lib/wifi-qr.js';
import { totpQrContent, type TotpQrContent } from '../lib/totp-qr.js';
import { attachmentPreview, type AttachmentPreview } from '../lib/attachment-preview.js';
import { buildEntrySharePayload, shareableFields, type ShareableField } from '../lib/entry-share.js';
import { authenticatorUrisFromImages, firstImageFile, readQrFromImageBytes, totpFromQrText } from '../lib/qr-image.js';
import { CAMERA_CANCELLED, cameraScanAvailable, scanQrWithCamera } from '../lib/camera-scan.js';
import type { QrReadResult } from '../lib/qr-decode.js';
import { parseAuthenticatorUriList } from '../lib/authenticator-import.js';
import { DEFAULT_URI_MATCH_KEY, moveEntryUri, normalizeEntryUris, openableUri } from '../lib/entry-uris.js';
import {
  focusSignature,
  LETTER_COMMAND_LABELS,
  LETTER_COMMAND_ORDER,
  neighbourEntryId,
  readShortcutSettings,
  rebindShortcut,
  serializeShortcutSettings,
  SHORTCUT_SETTINGS_KEY,
  shortcutCommand,
  shortcutKeyOf,
  DEFAULT_SHORTCUT_SETTINGS,
  type LetterCommand,
  type ShortcutCommand,
  type ShortcutContext,
  type ShortcutSettings,
} from '../lib/keyboard-shortcuts.js';
import type { AccountEnvelope, PasskeyUnlockRecord, RemoteSession } from '../lib/types.js';
import {
  changePasswordUrl,
  computeVaultHealth,
  healthPasswords,
  vaultSites,
  isInsecureUri,
  type VaultApiKeyIssue,
  type VaultCardIssue,
  type VaultDocumentIssue,
  type VaultDuplicateGroup,
  type SiteBreach,
  type VaultHealthIssue,
  type VaultIgnoredAlerts,
  type VaultLicenseIssue,
  type VaultMembershipIssue,
  type VaultPasswordExpiryIssue,
  type VaultPasskeyIssue,
  type VaultRenamedSiteIssue,
  type VaultSecondaryPasswordIssue,
  type SecondaryPasswordField,
  withRenamedSiteAddresses,
  draftPasswordStrength,
} from '../lib/vault-health.js';
import {
  AttachmentLimitError,
  VaultStore,
  assertAttachmentSize,
  formatFileSize,
  type AttachmentSummary,
} from '../lib/vault-store.js';
import {
  ICON_ALERT,
  ICON_CHECK,
  ICON_CLOCK,
  ICON_CLOSE,
  ICON_EXTERNAL,
  ICON_COPY,
  ICON_DOWNLOAD,
  ICON_EYE,
  ICON_EYE_OFF,
  ICON_FILE,
  ICON_FINGERPRINT,
  ICON_FOLDER,
  ICON_HELP,
  ICON_KEY,
  ICON_LARGE_TYPE,
  ICON_QR,
  ICON_LOCK,
  ICON_LOGO,
  ICON_MOON,
  ICON_PAPERCLIP,
  ICON_PLUS,
  ICON_PRINT,
  ICON_REFRESH,
  ICON_SEARCH,
  ICON_SORT,
  ICON_SEND,
  ICON_TAG,
  ICON_SHIELD,
  ICON_STAR,
  ICON_STAR_FILLED,
  ICON_SUN,
  ICON_TRASH,
  ICON_MOVE_UP,
  ICON_MOVE_DOWN,
  ICON_ARCHIVE,
  ICON_LINK,
  ICON_DUPLICATE,
  ICON_RESTORE,
  ICON_CHEVRON_RIGHT,
  ICON_UPLOAD,
  TYPE_ICONS,
} from './icons.js';
import { entryInitials, escapeHtml, extractDomain, formatTimestamp } from './render-utils.js';
import { bindBusyIndicator, bindNoticeBubbles, busyTaskName, mountStatusBar, mountTitleBar, type ErrorJournal } from '@santvia/ui-socle';
import { TEXT_ZOOM_STEPS } from '@santvia/ui-socle/tokens';
import { CLE_ZOOM_TEXTE, SELECTEUR_BULLES, echecOperation, installSocleTokens, palierZoomTexte, traduireSocle } from './socle.js';
import {
  AUTO_LOCK_MINUTES_KEY,
  AUTO_LOCK_MINUTES_STEPS,
  CLIPBOARD_CLEAR_SECONDS_KEY,
  CLIPBOARD_CLEAR_SECONDS_STEPS,
  LOCK_ON_HIDE_KEY,
  autoLockLabel,
  autoLockMinutes,
  clipboardClearLabel,
  clipboardClearSeconds,
  lockOnHide,
} from '../lib/lock-settings.js';
import { ENTRY_SORT_KEY, ENTRY_SORT_ORDERS, entrySortLabel, entrySortOrder, type EntrySortOrder } from '../lib/entry-sort.js';
import { NOTES_FORMAT_KEY, notesFormat, notesHaveMarkdown, renderNotesMarkdown, renderNotesPreview, type NotesFormat } from '../lib/notes-markdown.js';
import {
  bulkActionLabel,
  bulkActionsFor,
  bulkDoneMessage,
  bulkNeedsReprompt,
  bulkExtendSelection,
  bulkRangeSelection,
  bulkRowKey,
  bulkSelectAll,
  bulkFocusTarget,
  type BulkFocusMove,
  bulkApplies,
  bulkTargets,
  isBulkAction,
  toggleBulkSelection,
  type BulkAction,
  type BulkView,
} from '../lib/entry-bulk.js';
import {
  SECURITY_LOG_ALERT_WINDOW_MS,
  describeUserAgent,
  isSuspiciousEvent,
  securityEventLabel,
  securityResultLabel,
  summarizeSecurityLog,
  type SecurityLogEvent,
} from '../lib/security-log.js';
import type { ConnectedDevice } from '../lib/connected-devices.js';
import { buildVaultExport, mergeVaultExport, parseVaultExport, serializeVaultExport } from '../lib/vault-export.js';
import { WebdavSyncError, normalizeWebdavFolderUrl, syncVaultWithWebdav } from '../lib/webdav-sync.js';
import {
  MIN_CXF_PASSWORD_LENGTH,
  buildCxfHeader,
  encryptCxfExport,
  serializeCxfExport,
} from '../lib/cxf.js';

type Panel = 'vault' | 'generator' | 'settings' | 'health' | 'send';

/** Position du curseur dans le champ qui a le focus, relevee avant un re-rendu (jamais sa valeur). */
interface Caret {
  start: number | null;
  end: number | null;
  direction: 'forward' | 'backward' | 'none' | null;
  scrollTop: number;
}

/**
 * Brouillon d'envoi ponctuel (R15). Le mot de passe eventuel n'est
 * VOLONTAIREMENT pas ici : il est lu du formulaire au moment de sceller, et
 * jamais conserve -- un brouillon ne doit pas devenir un depot de secrets.
 */
interface SendDraft {
  label: string;
  kind: 'text' | 'file';
  text: string;
  /** Texte masque a l'ouverture chez le destinataire (« Hide text by default » de Bitwarden). */
  hidden: boolean;
  /** Note privee de l'emetteur (« Private notes » de Bitwarden Send) : jamais transmise au destinataire. */
  note: string;
  expiresInMs: number;
  /** Echeance personnalisee (`datetime-local`, heure locale) ; `null` = une duree de la liste. */
  expiresAtLocal: string | null;
  /** Telechargements autorises tels que saisis ('' = sans limite), relus par `parseSendMaxViews` a l'envoi. */
  maxViews: string;
}

/** Lecture d'un envoi recu : la seule vue atteignable SANS coffre. */
interface SendReceiveState {
  readonly id: string;
  readonly secret: string;
  phase: 'loading' | 'ready' | 'locked' | 'opened' | 'error';
  description: SendDescription | null;
  /**
   * Ciphertext deja telecharge. Garde en memoire EXPRES : retelecharger pour
   * reessayer un mot de passe consommerait une seconde vue.
   */
  content: SendContent | null;
  payload: SendPayload | null;
  /**
   * Texte masque (`payload.hidden`), ou champs secrets d'une fiche partagee, devoiles par le destinataire ; tant que
   * faux, ils ne sont PAS dans le DOM.
   */
  revealed: boolean;
  error: string | null;
  busy: boolean;
}

const EMPTY_SEND_DRAFT: SendDraft = { label: '', kind: 'text', text: '', hidden: false, note: '', expiresInMs: 7 * 24 * 60 * 60 * 1000, expiresAtLocal: null, maxViews: '' };

/** Point d'acces de verification des fuites (URL, pas un secret) ; absent = desactive. */
/** Activite du compte : lignes affichees, le serveur en rend jusqu'a 200. */
const SECURITY_LOG_SHOWN = 50;
const BREACH_ENDPOINT_KEY = 'klavox:breach-endpoint';

/**
 * Synchro WebDAV/Nextcloud (R26) : dossier et identifiant seulement. Le mot de
 * passe d'application est un SECRET -- il n'est ni memorise ici, ni garde en
 * etat : il est relu du formulaire a chaque synchro.
 */
const WEBDAV_FOLDER_KEY = 'klavox:webdav-folder';
const WEBDAV_USERNAME_KEY = 'klavox:webdav-username';

/** Consentement a la capacite de bus `credentials.audit` (booleen, pas un secret). */
const CREDENTIALS_AUDIT_CONSENT_KEY = 'klavox:credentials-audit-consent';

interface EditorDraft {
  readonly id: string | null;
  type: VaultItemType;
  name: string;
  username: string;
  password: string;
  uri: string;
  /** Adresses au-dela de la premiere (`secrets.uris[1..]`), telles que saisies ; toujours remplace, jamais modifie en place. */
  extraUris: readonly string[];
  /** Regle de detection de chaque adresse, alignee sur `[uri, ...extraUris]` ; '' = regle commune (domaine). */
  uriRules: readonly string[];
  notes: string;
  totpSecret: string;
  /** Champs carte, identite ET Wi-Fi (cles distinctes) : changer de type ne perd rien avant l'enregistrement. */
  fields: Record<string, string>;
  folderId: string | null;
  favorite: boolean;
  /** Faux = l'entree se retire des groupes de domaines equivalents (`secrets.equivalentDomains === false`). */
  equivalentDomains: boolean;
  /** Champs personnalises (`custom-fields.ts`) ; toujours remplace, jamais modifie en place (EMPTY_DRAFT est partage). */
  customFields: readonly EntryCustomField[];
  /** Vrai = le mot de passe maitre est redemande avant de devoiler l'entree (`secrets.reprompt === true`). */
  reprompt: boolean;
  /** Etiquettes (`entry-tags.ts`) telles que saisies, separees par des virgules. */
  tags: string;
  /** Echeance de changement du mot de passe (`password-expiry.ts`), `AAAA-MM-JJ` ou '' ; ecrite pour `PASSWORD_EXPIRY_TYPES`. */
  passwordExpiry: string;
  /** Regle de renouvellement du mot de passe en jours (`password-expiry.ts`), '' = aucune ; ecrite comme l'echeance. */
  passwordRenewDays: string;
  /** Paire d'une cle SSH (`ssh-key-item.ts`), telle que saisie ; ecrite seulement pour le type `ssh-key`. */
  privateKey: string;
  publicKey: string;
}

interface AppState {
  repository: VaultRepository;
  vaultStore: VaultStore | null;
  envelope: AccountEnvelope | null;
  /** Enveloppe locale persistee, connue meme verrouille -- pilote l'ecran d'authentification. */
  localAccountEnvelope: AccountEnvelope | null;
  authMode: 'create' | 'unlock' | 'recover' | 'recover-remote' | 'signin-remote' | 'emergency-contact';
  authBusy: boolean;
  authError: string | null;
  /** Cle de recuperation fraichement generee, affichee UNE SEULE FOIS (`docs/ARCHITECTURE.md` §4). */
  recoveryKeyReveal: string | null;
  entries: VaultItem[];
  /**
   * `entries` est le coffre ENTIER, archives comprises (relu pour la Sante du coffre) : sinon c'est une vue filtree,
   * et la Sante ne se calcule pas dessus -- un rendu intercale (pieces jointes d'une fiche) donnerait un faux rapport.
   */
  entriesForHealth: boolean;
  folders: VaultFolder[];
  selectedFolderId: string | null;
  /** Dossiers replies dans la barre laterale : en memoire seulement, oublies au verrouillage. */
  collapsedFolderIds: Set<string>;
  /** Recherche par frappe en cours dans l'arbre des dossiers (lettres de noms de dossiers) : oubliee au verrouillage. */
  folderTypeAhead: FolderTypeAhead | null;
  /** Etiquettes repliees dans la barre laterale (cles `tagKey`) : en memoire seulement, oubliees au verrouillage. */
  collapsedTagKeys: Set<string>;
  /** Recherche par frappe en cours dans l'arbre des etiquettes : oubliee au verrouillage. */
  tagTypeAhead: FolderTypeAhead | null;
  favoritesOnly: boolean;
  /** Vue d'une etiquette (`entry-tags.ts`) ; `null` hors de cette vue. */
  selectedTag: string | null;
  /** Etiquettes de la barre laterale, comptees ; purgees au verrouillage avec les entrees. */
  tags: TagCount[];
  /** Vue Archives : seules les entrees archivees (`entry-archive.ts`) ; hors de cette vue, elles sont cachees. */
  archivedOnly: boolean;
  /** Vue Corbeille : seules les entrees jetees (`entry-trash.ts`) ; partout ailleurs, elles n'existent plus. */
  trashOnly: boolean;
  searchQuery: string;
  selectedEntryId: string | null;
  /** Elements lies a l'entree ouverte (`entry-links.ts`), relus a chaque ouverture et relecture ; purges au verrouillage. */
  related: { readonly forId: string; readonly items: VaultItem[]; readonly candidates: VaultItem[] } | null;
  /** Entree dont le choix « Lier un element » est ouvert. */
  linkPickerFor: string | null;
  draft: EditorDraft | null;
  revealDraftPassword: boolean;
  revealedEntryId: string | null;
  /**
   * Grand format ouvert : l'entree et le champ (reference `entryFieldValue`), jamais la valeur -- elle est relue dans
   * l'entree a chaque rendu, par `largeTypeValue`, et oubliee en changeant d'entree et au verrouillage.
   */
  largeType: { entryId: string; field: string } | null;
  /**
   * Code QR de connexion Wi-Fi ouvert : l'entree seule, jamais le contenu (il porte la cle du reseau) -- relu dans
   * l'entree a chaque rendu par `wifiQrValue`, oublie en changeant d'entree et au verrouillage.
   */
  wifiQr: { entryId: string } | null;
  /**
   * Code QR de la graine TOTP ouvert : l'entree seule, jamais la graine -- relue dans l'entree a chaque rendu par
   * `totpQrValue`, oubliee en changeant d'entree et au verrouillage.
   */
  totpQr: { entryId: string } | null;
  /**
   * Apercu d'une piece jointe ouvert (`attachment-preview.ts`) : octets dechiffres en memoire seulement, jamais ecrits
   * sur le disque ; l'URL `blob:` d'une image est revoquee a la fermeture, au changement d'entree et au verrouillage.
   */
  attachmentPreview: { entryId: string; attachmentId: string; fileName: string; size: number; preview: AttachmentPreview; url: string | null } | null;
  /** Entree sensible dont le mot de passe maitre vient d'etre retape ; oubliee en changeant d'entree et au verrouillage. */
  repromptConfirmedId: string | null;
  repromptBusy: boolean;
  repromptError: string | null;
  /** Entree dont l'historique des mots de passe est deplie (identifiant, aucun secret). */
  historyOpenEntryId: string | null;
  /** Entree dont les versions precedentes sont depliees (`item-history.ts`). */
  versionsOpenEntryId: string | null;
  /** Version dont le contenu est deplie, dans les versions de `versionsOpenEntryId` (un index, aucun secret). */
  versionShownIndex: number | null;
  /** Version depliee comparee a aujourd'hui plutot qu'au contenu qui l'a remplacee (affichage, aucun secret). */
  versionCompare: boolean;
  /** Pieces jointes (noms dechiffres) de `attachmentsEntryId` ; `null` = a (re)charger. Purge au verrouillage. */
  attachments: AttachmentSummary[];
  attachmentsEntryId: string | null;
  /** Chiffrement de fichiers en cours (entree concernee). */
  attachmentBusyEntryId: string | null;
  /**
   * Export concurrent chiffre (KDBX, Bitwarden protege) en attente de son mot
   * de passe. Les octets restent en memoire le temps de la saisie, jamais sur
   * disque ; purges au verrouillage comme tout le reste.
   */
  importPrompt: { fileName: string; bytes: Uint8Array; format: string; error: string | null } | null;
  /** Import en cours : evite un double envoi pendant le dechiffrement (Argon2 = plusieurs secondes). */
  importBusy: boolean;
  /** Avancement de la derivation de cle KeePass, en pourcent ; `null` = aucune derivation en cours. */
  importProgress: number | null;
  /** Format choisi a l'ecran d'import ; `auto` = reconnu au contenu (`headerless-import.ts`). */
  importFormat: ImportFormat;
  /**
   * Fichier d'export importe EN CLAIR (nom seul) : Klavox dit qu'il contient les mots de passe et propose de le
   * supprimer (`bus/klavox-credentials.v1.md` § 3), jamais d'office.
   */
  importCleanupFile: string | null;
  /**
   * Poignee de ce fichier quand le navigateur sait le supprimer sur demande (selecteur natif ou depot, Chrome / Edge) :
   * « Supprimer le fichier » n'est offert qu'avec elle. Memoire seulement, oubliee au verrouillage.
   */
  importCleanupHandle: ImportFileHandle | null;
  /** Issue du dernier « Supprimer le fichier » ; `null` tant qu'il n'a pas ete demande. */
  importCleanupResult: ImportFileRemoval | null;
  /**
   * Offre `klavox.credentials` en attente du geste de l'utilisateur : qui propose et combien, aucune donnee.
   * `resolve` rend la reponse au recepteur ; un verrouillage la refuse.
   */
  credentialsTransferOffer: { offre: CredentialsTransferOffer; resolve: (accepte: boolean) => void } | null;
  /**
   * Mot de passe maitre redemande pour prouver l'identite au serveur (synchro, revocation d'une cle de securite) :
   * une fenetre du coffre a champ masque, jamais `window.prompt`, qui l'afficherait en clair a l'ecran. `resolve`
   * rend la preuve derivee (ou `null` : Annuler, Echap, verrouillage) ; un mot de passe faux laisse la fenetre ouverte.
   */
  masterPasswordPrompt: { message: string; busy: boolean; error: string | null; resolve: (hash: string | null) => void } | null;
  /**
   * Code de l'application d'authentification demande a une connexion au serveur (second facteur TOTP du compte).
   * `resolve` rend le code tape, `'webauthn'` si l'utilisateur prefere sa cle de securite, ou `null` (Annuler, Echap).
   */
  totpCodePrompt: { error: string | null; allowWebAuthn: boolean; resolve: (answer: string | null) => void } | null;
  /** Export FIDO CXF en cours (une derivation Argon2id) -- evite un double clic. */
  cxfBusy: boolean;
  /** Resultat du dernier export CXF -- jamais un secret, jamais le mot de passe d'export. */
  cxfMessage: string | null;
  /** App Android : adresse a remplir fixee par le service d'autofill (`null` hors demande de remplissage). */
  autofillTarget: string | null;
  panel: Panel;
  theme: 'light' | 'dark';
  /** Palier de taille du texte (`TEXT_ZOOM_STEPS` du socle), pose en `data-text-zoom` sur `<html>`. */
  textZoom: number;
  /** Reglages de verrouillage de cet appareil (`lib/lock-settings.ts`) : des preferences, jamais des secrets. */
  autoLockMinutes: number;
  clipboardClearSeconds: number;
  lockOnHide: boolean;
  /** Raccourcis clavier de cet appareil (`lib/keyboard-shortcuts.ts`) : coupes ou reassignes, jamais un secret. */
  shortcuts: ShortcutSettings;
  /** Lettre refusee au dernier essai de reassignation (non lettre, deja prise). */
  shortcutError: string | null;
  /** Detection par defaut du coffre (`defaultUriMatchRule`) pour les adresses sans regle propre : une preference de cet appareil. */
  defaultUriMatch: UriMatchRule;
  /** Ordre de la liste des entrees (`lib/entry-sort.ts`) : une preference de cet appareil, jamais un secret. */
  entrySort: EntrySortOrder;
  /** Notes mises en forme ou en texte brut (`lib/notes-markdown.ts`) : une preference de cet appareil, jamais un secret. */
  notesFormat: NotesFormat;
  /** Apercu des notes ouvert sous la zone de saisie de l'editeur (le temps de la session, jamais enregistre). */
  notesPreview: boolean;
  /** Selection multiple (`lib/entry-bulk.ts`) : identifiants coches, jamais un secret ; videe en changeant de vue et au verrouillage. */
  bulkMode: boolean;
  bulkSelected: string[];
  /** Derniere ligne cliquee en selection multiple : point de depart d'un Maj+clic (`bulkRangeSelection`). */
  bulkAnchor: string | null;
  /** Dossier cible de « Deplacer » : `undefined` tant qu'aucun n'est choisi, `null` = hors dossier. */
  bulkFolderId: string | null | undefined;
  /** Etiquette saisie pour « Etiqueter » / « Retirer l'etiquette » (le nom d'une etiquette, jamais un secret d'entree). */
  bulkTag: string;
  /** Geste en attente du mot de passe maitre (une entree sensible est cochee). */
  bulkReprompt: { action: BulkAction; folderId: string | null; tag: string } | null;
  bulkBusy: boolean;
  bulkError: string | null;
  generatorMode: GeneratorMode;
  /** Generateur d'identifiants (`lib/username-generator.ts`). L'adresse et le
   * domaine restent EN MEMOIRE : ils ne sont jamais ecrits dans `localStorage`. */
  usernameSettings: UsernameSettings;
  generatedUsername: string;
  usernameError: string | null;
  /** Vrai pendant la generation d'une cle SSH (`lib/ssh-keygen.ts`) : un double clic n'en tire pas deux. */
  sshKeygenBusy: boolean;
  /** Alias e-mail (R19). La cle d'API du fournisseur reste EN MEMOIRE : la
   * poser en clair dans `localStorage` serait un secret stocke non chiffre. */
  aliasProviderId: string;
  aliasEndpoint: string;
  aliasApiKey: string;
  aliasNote: string;
  /** MailKrypt : domaine gere par la cle, et adresse reelle de destination. */
  aliasDomain: string;
  aliasDestination: string;
  aliasBusy: boolean;
  aliasResult: string;
  aliasError: string | null;
  /** Reglages tels que le formulaire les montre, meme impossibles (« Chiffres au minimum » trop haut). */
  generatorOptions: GeneratorPreferences;
  /** Derniers reglages valides : ceux que « Generer » applique partout (generateur, editeur), et les seuls gardes. */
  generatorApplied: GeneratorPreferences;
  passphraseSettings: PassphraseSettings;
  /** Listes de mots chargees a la demande (donnees publiques, aucun secret). */
  wordlists: Partial<Record<WordlistLanguage, readonly string[]>>;
  generatedPassword: string;
  /**
   * Site saisi dans l'ecran Generateur (« Pour le site ») : sa regle de mot de passe connue guide le tirage. En memoire
   * seulement -- jamais dans `localStorage` avec les reglages, oublie au verrouillage.
   */
  generatorSite: string;
  /**
   * Historique du generateur (`generator-history.ts`) : les secrets copies depuis le generateur, dechiffres EN
   * MEMOIRE tant que le coffre est ouvert ; au repos, un blob scelle sous la `vaultKey`. Vide au verrouillage.
   */
  generatorHistory: GeneratorHistoryEntry[];
  generatorHistoryOpen: boolean;
  /** Les valeurs ne sont posees dans le DOM qu'une fois affichees a l'oeil. */
  generatorHistoryRevealed: boolean;
  remoteSession: RemoteSession | null;
  syncBusy: boolean;
  syncMessage: string | null;
  /**
   * Mode voyage (R18) tel que le COMPTE le porte : `null` tant qu'aucune
   * synchro ne l'a lu. Actif = ce qui n'est pas « sur en voyage » a quitte
   * cet appareil, et seul un AUTRE appareil peut desactiver.
   */
  travelMode: boolean | null;
  travelBusy: boolean;
  travelMessage: string | null;
  /** Changement du mot de passe maitre en cours (deux derivations Argon2id). */
  masterPasswordBusy: boolean;
  /** Resultat du dernier changement de mot de passe maitre -- jamais un secret. */
  masterPasswordMessage: string | null;
  /** Changement d'adresse e-mail (sel de derivation) : en cours, et ce qu'il faut en dire -- jamais un secret. */
  emailChangeBusy: boolean;
  emailChangeMessage: string | null;
  /** Le dernier nouveau mot de passe maitre soumis figure dans des fuites connues : la case de consentement parait. */
  masterPasswordExposed: boolean;
  /**
   * « Verifier dans les fuites connues » de la fiche (`draft-breach-check.ts`) : le mot de passe verifie et son verdict
   * (`null` = verification en cours). Montre seulement tant que la fiche porte CE mot de passe ; part avec la fiche.
   */
  draftBreach: { password: string; verdict: DraftBreachVerdict | null } | null;
  /**
   * Passkeys autorisees a deverrouiller ce coffre sur CET appareil (R12).
   * Connues MEME verrouille -- sans cela l'ecran de deverrouillage ne pourrait
   * pas proposer le raccourci. Ne contiennent aucun secret en clair.
   */
  passkeyUnlocks: PasskeyUnlockRecord[];
  passkeyBusy: boolean;
  /** Dernier resultat d'enrolement/revocation de passkey -- jamais un secret. */
  passkeyMessage: string | null;
  /** Authentificateurs 2FA du compte serveur (donnees publiques) ; `null` = non charges. Purges au verrouillage. */
  webauthnCredentials: WebAuthnCredentialSummary[] | null;
  webauthnBusy: boolean;
  webauthnMessage: string | null;
  /** Second facteur TOTP du compte serveur : actif ou non ; `null` = inconnu (non charge, ou serveur sans TOTP). */
  accountTotpEnabled: boolean | null;
  /**
   * Cle de recuperation activee sur le compte serveur AVANT la preuve de possession : le serveur la refuse
   * desormais, le titulaire doit la regenerer. `false` aussi quand l'etat est inconnu : on ne crie pas sans savoir.
   */
  recoveryKeyStale: boolean;
  /** Graine proposee a l'activation, montree une fois puis oubliee (confirmee, annulee ou verrouillage). */
  accountTotpEnrollment: TotpEnrollment | null;
  accountTotpBusy: boolean;
  accountTotpMessage: string | null;
  /** Idenva du serveur connecte : `null` = pas encore lu, `'absent'` = instance autonome. */
  idenvaConfig: { readonly issuer: string; readonly clientId: string } | 'absent' | null;
  /** Compte lie a Idenva selon le serveur ; `null` = pas lu (les deux actions restent offertes). */
  idenvaLinked: boolean | null;
  idenvaBusy: boolean;
  idenvaMessage: string | null;
  /**
   * Idenva du serveur qui SERT cette PWA (`MountOptions.homeServer`) : l'accueil commence alors par Idenva, sans
   * e-mail. `null` = pas encore lu, `'absent'` = pas de serveur d'origine ou serveur sans Idenva (accueil local).
   */
  idenvaHome: { readonly baseUrl: string; readonly issuer: string; readonly clientId: string } | 'absent' | null;
  /**
   * Connexion Idenva de l'accueil : l'ID token (le SERVEUR le verifie) et ce que le serveur sait de ce `sub`.
   * En memoire seulement, oubliee au verrouillage et des que le coffre est ouvert.
   */
  idenvaSignIn: { readonly token: IdenvaIdToken; readonly lookup: OidcAccountLookup } | null;
  /** « Deconnecter tous mes appareils » : en cours, et ce qu'il faut en dire. */
  sessionsRevokeBusy: boolean;
  sessionsRevokeMessage: string | null;
  /** Appareils connectes (`GET /v1/sessions`) : `null` tant qu'on ne les a pas demandes. */
  connectedDevices: ConnectedDevice[] | null;
  connectedDevicesBusy: boolean;
  connectedDevicesMessage: string | null;
  /** Activite du compte (journal de securite serveur) : `null` tant qu'on ne l'a pas demandee. */
  securityLog: SecurityLogEvent[] | null;
  securityLogBusy: boolean;
  securityLogMessage: string | null;
  /** Suppression du compte serveur (droit a l'effacement) : en cours, et ce qu'il faut en dire. */
  accountDeletionBusy: boolean;
  accountDeletionMessage: string | null;
  /** Delegations d'acces d'urgence du TITULAIRE (R32) ; `null` tant que non chargees. */
  emergencyGrants: EmergencyGrantView[] | null;
  emergencyBusy: boolean;
  emergencyMessage: string | null;
  /** Kit affiche UNE fois apres creation : jamais persiste, efface des que confirme. */
  emergencyKitReveal: { readonly contactName: string; readonly kitDisplay: string } | null;
  /** Vue du CONTACT (aucune session, lecture seule) : etat de sa demande puis coffre ouvert. */
  emergencyContactState: { readonly status: string; readonly availableAt: number | null } | null;
  emergencyContactVault: OpenedEmergencyVault | null;
  /**
   * Ce que le contact a saisi. Conserve entre deux rendus : sans cela, la
   * reponse a « Demander l'acces » effacerait le kit qu'il vient de taper et
   * il devrait le resaisir pour ouvrir le coffre. Efface en quittant la vue.
   */
  emergencyContactDraft: { readonly baseUrl: string; readonly kit: string };
  /** Service Pwned Passwords (API ou miroir UE) ; `null` = verification des fuites desactivee. */
  breachEndpoint: string | null;
  /** Mot de passe -> occurrences dans les fuites : memoire uniquement, purge au verrouillage. `null` = non verifie. */
  breachCounts: Map<string, number> | null;
  breachBusy: boolean;
  breachError: string | null;
  /** Horodatage du dernier resultat de fuites ; `null` = jamais verifie (regle 1 du contrat de bus). */
  breachCheckedAt: number | null;
  /** Synchro sans serveur Klavox (R26) : dossier WebDAV ; `null` = non configuree. */
  webdavFolderUrl: string | null;
  webdavUsername: string;
  webdavBusy: boolean;
  /** Resultat de la derniere synchro WebDAV -- jamais un secret, jamais le mot de passe. */
  webdavMessage: string | null;
  /** Capacite `credentials.audit` : verdicts par origine exposes aux produits freres (NAVKRYPT). */
  credentialsAuditConsent: boolean;
  /** Envoi ponctuel (R15) : brouillon en cours de composition. */
  sendDraft: SendDraft;
  /** Fichier choisi pour l'envoi -- memoire seule, purge au verrouillage comme tout le reste. */
  sendFile: { name: string; mimeType: string; bytes: Uint8Array } | null;
  /** Envois deposes par ce compte ; `null` = pas encore charges. */
  sends: OwnedSend[] | null;
  sendBusy: boolean;
  /** Dernier resultat d'envoi ou de revocation -- jamais un secret. */
  sendMessage: string | null;
  /** Envoi tout juste cree : son lien s'affiche une fois, a copier. */
  sendCreated: CreatedSend | null;
  /** Envoi en cours de modification (« Edit Send » de Bitwarden) : son identifiant seul, jamais un secret. */
  sendEditingId: string | null;
  /**
   * Fiche partagee par lien (« Share item » de 1Password) : l'entree, son nom, les libelles de ses champs
   * partageables et les references DECOCHEES -- jamais une valeur ; les valeurs sont relues dans l'entree a la
   * creation du lien (`buildEntrySharePayload`). Purgee au verrouillage.
   */
  sendShare: { entryId: string; name: string; fields: ShareableField[]; excluded: string[] } | null;
  /** Lecture d'un envoi recu (`#/send/…`) -- prend toute la fenetre, coffre ou pas. */
  sendReceive: SendReceiveState | null;
  /** Aide contextuelle (Regle IHM) : panneau ouvert ? Rendu seulement si ouvert. */
  helpOpen: boolean;
  /** Question en cours de frappe -- donnee personnelle, memoire seule, purgee au verrouillage. */
  helpQuestion: string;
  helpAnswer: HelpAnswer | null;
  /** Commande reconnue dans la question (§ 8.1) : proposee avec « Faire » / « Annuler », jamais executee sans le clic. */
  helpCommand: CommandMatch | null;
  /** L'element que la demande NOMME (§ 8.6 a bis), resolu sur l'ecran : son nom affiche, jamais une valeur. */
  helpTarget: NamedMatch;
  /** « Faire » en cours : le bouton porte `aria-busy`, le socle en fait la bulle « Travail en cours ». */
  helpCommandBusy: boolean;
  flash: string | null;
}

const EMPTY_DRAFT: EditorDraft = {
  id: null,
  type: 'login',
  name: '',
  username: '',
  password: '',
  uri: '',
  extraUris: [],
  uriRules: [],
  notes: '',
  totpSecret: '',
  fields: {},
  folderId: null,
  favorite: false,
  equivalentDomains: true,
  customFields: [],
  reprompt: false,
  tags: '',
  passwordExpiry: '',
  passwordRenewDays: '',
  privateKey: '',
  publicKey: '',
};

function newState(repository: VaultRepository): AppState {
  return {
    repository,
    vaultStore: null,
    envelope: null,
    localAccountEnvelope: null,
    authMode: 'create',
    authBusy: false,
    authError: null,
    recoveryKeyReveal: null,
    entries: [],
    entriesForHealth: false,
    folders: [],
    selectedFolderId: null,
    collapsedFolderIds: new Set(),
    folderTypeAhead: null,
    collapsedTagKeys: new Set(),
    tagTypeAhead: null,
    favoritesOnly: false,
    selectedTag: null,
    tags: [],
    archivedOnly: false,
    trashOnly: false,
    searchQuery: '',
    selectedEntryId: null,
    related: null,
    linkPickerFor: null,
    draft: null,
    revealDraftPassword: false,
    revealedEntryId: null,
    largeType: null,
    wifiQr: null,
    totpQr: null,
    attachmentPreview: null,
    repromptConfirmedId: null,
    repromptBusy: false,
    repromptError: null,
    historyOpenEntryId: null,
    versionsOpenEntryId: null,
    versionShownIndex: null,
    versionCompare: false,
    attachments: [],
    attachmentsEntryId: null,
    attachmentBusyEntryId: null,
    importPrompt: null,
    importFormat: 'auto',
    importCleanupFile: null,
    importCleanupHandle: null,
    importCleanupResult: null,
    credentialsTransferOffer: null,
    masterPasswordPrompt: null,
    totpCodePrompt: null,
    importBusy: false,
    importProgress: null,
    cxfBusy: false,
    cxfMessage: null,
    autofillTarget: null,
    panel: 'vault',
    theme: (localStorage.getItem('klavox:theme') as 'light' | 'dark' | null) ?? preferredTheme(),
    textZoom: palierZoomTexte(localStorage.getItem(CLE_ZOOM_TEXTE)),
    autoLockMinutes: autoLockMinutes(localStorage.getItem(AUTO_LOCK_MINUTES_KEY)),
    clipboardClearSeconds: clipboardClearSeconds(localStorage.getItem(CLIPBOARD_CLEAR_SECONDS_KEY)),
    lockOnHide: lockOnHide(localStorage.getItem(LOCK_ON_HIDE_KEY)),
    shortcuts: readShortcutSettings(localStorage.getItem(SHORTCUT_SETTINGS_KEY)),
    shortcutError: null,
    defaultUriMatch: defaultUriMatchRule(localStorage.getItem(DEFAULT_URI_MATCH_KEY)),
    entrySort: entrySortOrder(localStorage.getItem(ENTRY_SORT_KEY)),
    notesFormat: notesFormat(localStorage.getItem(NOTES_FORMAT_KEY)),
    notesPreview: false,
    bulkMode: false,
    bulkSelected: [],
    bulkAnchor: null,
    bulkFolderId: undefined,
    bulkTag: '',
    bulkReprompt: null,
    bulkBusy: false,
    bulkError: null,
    generatorMode: readGeneratorMode(localStorage.getItem(GENERATOR_MODE_KEY)),
    usernameSettings: DEFAULT_USERNAME_SETTINGS,
    generatedUsername: '',
    usernameError: null,
    sshKeygenBusy: false,
    aliasProviderId: DEFAULT_ALIAS_PROVIDER_ID,
    aliasEndpoint: aliasProvider(DEFAULT_ALIAS_PROVIDER_ID).defaultEndpoint,
    aliasApiKey: '',
    aliasNote: '',
    aliasDomain: '',
    aliasDestination: '',
    aliasBusy: false,
    aliasResult: '',
    aliasError: null,
    generatorOptions: readGeneratorPreferences(localStorage.getItem(GENERATOR_OPTIONS_KEY)),
    generatorApplied: readGeneratorPreferences(localStorage.getItem(GENERATOR_OPTIONS_KEY)),
    passphraseSettings: readPassphraseSettings(localStorage.getItem(PASSPHRASE_SETTINGS_KEY)),
    wordlists: {},
    generatedPassword: '',
    generatorSite: '',
    generatorHistory: [],
    generatorHistoryOpen: false,
    generatorHistoryRevealed: false,
    remoteSession: null,
    syncBusy: false,
    syncMessage: null,
    travelMode: null,
    travelBusy: false,
    travelMessage: null,
    masterPasswordBusy: false,
    masterPasswordMessage: null,
    emailChangeBusy: false,
    emailChangeMessage: null,
    masterPasswordExposed: false,
    draftBreach: null,
    passkeyUnlocks: [],
    passkeyBusy: false,
    passkeyMessage: null,
    webauthnCredentials: null,
    webauthnBusy: false,
    webauthnMessage: null,
    accountTotpEnabled: null,
    recoveryKeyStale: false,
    accountTotpEnrollment: null,
    accountTotpBusy: false,
    accountTotpMessage: null,
    idenvaConfig: null,
    idenvaLinked: null,
    idenvaBusy: false,
    idenvaMessage: null,
    idenvaHome: 'absent',
    idenvaSignIn: null,
    sessionsRevokeBusy: false,
    sessionsRevokeMessage: null,
    connectedDevices: null,
    connectedDevicesBusy: false,
    connectedDevicesMessage: null,
    securityLog: null,
    securityLogBusy: false,
    securityLogMessage: null,
    accountDeletionBusy: false,
    accountDeletionMessage: null,
    emergencyGrants: null,
    emergencyBusy: false,
    emergencyMessage: null,
    emergencyKitReveal: null,
    emergencyContactState: null,
    emergencyContactVault: null,
    emergencyContactDraft: { baseUrl: '', kit: '' },
    breachEndpoint: storedBreachEndpoint(),
    breachCounts: null,
    breachBusy: false,
    breachError: null,
    breachCheckedAt: null,
    webdavFolderUrl: storedWebdavFolder(),
    webdavUsername: localStorage.getItem(WEBDAV_USERNAME_KEY) ?? '',
    webdavBusy: false,
    webdavMessage: null,
    credentialsAuditConsent: localStorage.getItem(CREDENTIALS_AUDIT_CONSENT_KEY) === 'oui',
    sendDraft: { ...EMPTY_SEND_DRAFT },
    sendFile: null,
    sends: null,
    sendBusy: false,
    sendMessage: null,
    sendCreated: null,
    sendEditingId: null,
    sendShare: null,
    sendReceive: null,
    helpOpen: false,
    helpQuestion: '',
    helpAnswer: null,
    helpCommand: null,
    helpTarget: { kind: 'aucun' },
    helpCommandBusy: false,
    flash: null,
  };
}

function storedWebdavFolder(): string | null {
  const stored = localStorage.getItem(WEBDAV_FOLDER_KEY);
  try {
    return stored === null ? null : normalizeWebdavFolderUrl(stored);
  } catch {
    return null;
  }
}

function storedBreachEndpoint(): string | null {
  const { endpoint, replaced } = resolveStoredBreachEndpoint(localStorage.getItem(BREACH_ENDPOINT_KEY));
  if (replaced && endpoint !== null) localStorage.setItem(BREACH_ENDPOINT_KEY, endpoint);
  return endpoint;
}

function preferredTheme(): 'light' | 'dark' {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Titre de la section d'un groupe de champs types, dans la fiche et dans l'editeur. */
const TYPED_GROUP_TITLES: Record<TypedFieldGroup, string> = {
  card: 'Carte',
  identity: 'Identite',
  wifi: 'Reseau Wi-Fi',
  bank: 'Compte bancaire',
  wallet: 'Portefeuille crypto',
  database: 'Base de donnees',
  'api-key': 'Cle d’API',
  'software-license': 'Licence logicielle',
  server: 'Serveur',
  email: 'Compte de messagerie',
  membership: 'Carte de membre',
  medical: 'Dossier medical',
};

function draftFromEntry(entry: VaultItem): EditorDraft {
  return {
    id: entry.id,
    type: entry.type,
    name: entry.secrets.name ?? '',
    username: entry.secrets.username ?? '',
    password: entry.secrets.password ?? '',
    uri: entry.secrets.uris?.[0] ?? '',
    extraUris: (entry.secrets.uris ?? []).slice(1),
    uriRules: (entry.secrets.uris ?? []).map((uri) => uriMatchRuleOf(entry.secrets, uri) ?? ''),
    notes: entry.secrets.notes ?? '',
    totpSecret: entry.secrets.totpSecret ?? '',
    fields: Object.assign({}, ...TYPED_FIELD_GROUPS.map((group) => readTypedFields(entry.secrets, group))) as Record<string, string>,
    folderId: entry.folderId,
    favorite: entry.favorite,
    equivalentDomains: entry.secrets.equivalentDomains !== false,
    customFields: readCustomFields(entry.secrets),
    reprompt: isRepromptProtected(entry.secrets),
    tags: formatTagList(readTags(entry.secrets)),
    passwordExpiry: readPasswordExpiry(entry.secrets) ?? '',
    passwordRenewDays: String(readPasswordRenewDays(entry.secrets) ?? ''),
    ...readSshKey(entry.secrets),
  };
}

/**
 * Secrets enregistres depuis le brouillon. On part des secrets EXISTANTS (`previous`) : ce que l'editeur ne gere pas
 * (passkeys, champs d'import) survit a l'enregistrement. Les cles que l'editeur gere, y
 * compris celles qu'il peut retirer (TOTP vide, groupe carte/identite, opt-out), sont reecrites depuis le brouillon.
 */
/** Types dont l'editeur propose une echeance de changement du mot de passe (les autres ont leur propre date ou pas de mot de passe). */
/** Compte d'un mot de passe secondaire, tel que la Sante du coffre le nomme. */
const SECONDARY_PASSWORD_LABELS: Readonly<Record<SecondaryPasswordField, string>> = {
  adminPassword: 'Console',
  smtpPassword: 'SMTP',
};

const PASSWORD_EXPIRY_TYPES: ReadonlySet<VaultItemType> = new Set(['login', 'wifi', 'wallet', 'database', 'server', 'email']);

function draftToSecrets(draft: EditorDraft, previous: EntrySecrets = {}): EntrySecrets {
  const card = normalizeTypedFields('card', draft.fields);
  const identity = normalizeTypedFields('identity', draft.fields);
  const wifi = normalizeTypedFields('wifi', draft.fields);
  const bank = normalizeTypedFields('bank', draft.fields);
  const wallet = normalizeTypedFields('wallet', draft.fields);
  const database = normalizeTypedFields('database', draft.fields);
  const apiKey = normalizeTypedFields('api-key', draft.fields);
  const license = normalizeTypedFields('software-license', draft.fields);
  const server = normalizeTypedFields('server', draft.fields);
  const email = normalizeTypedFields('email', draft.fields);
  const membership = normalizeTypedFields('membership', draft.fields);
  const medical = normalizeTypedFields('medical', draft.fields);
  const { card: _card, identity: _identity, wifi: _wifi, bank: _bank, wallet: _wallet, database: _database, 'api-key': _apiKey, 'software-license': _license, server: _server, email: _email, membership: _membership, medical: _medical, totpSecret: _totp, equivalentDomains: _equivalents, uriMatch: _uriMatch, customFields: _custom, reprompt: _reprompt, tags: _tags, passwordExpiry: _expiry, passwordRenewDays: _renewal, ...kept } = previous;
  const customFields = normalizeCustomFields(draft.customFields);
  const uris = normalizeEntryUris([draft.uri, ...draft.extraUris]);
  // Regle de chaque adresse gardee, celle de sa premiere ligne si elle est saisie deux fois ; « par defaut du coffre » n'est pas ecrit.
  const rules = new Map<string, UriMatchRule | undefined>();
  [draft.uri, ...draft.extraUris].forEach((uri, index) => {
    const rule = draft.uriRules[index];
    if (!rules.has(uri.trim())) rules.set(uri.trim(), isUriMatchRule(rule) ? rule : undefined);
  });
  const uriMatch = normalizeUriMatch(uris, rules);
  // Une echeance sans mot de passe, ou sur un type qui ne la propose pas, n'a rien a faire changer.
  const passwordExpiry = PASSWORD_EXPIRY_TYPES.has(draft.type) && draft.password.length > 0 ? normalizeDocumentDate(draft.passwordExpiry) : null;
  // La regle suit l'echeance ; une valeur hors bornes est relue nulle et n'est pas ecrite (le magasin arme la date).
  const passwordRenewDays = PASSWORD_EXPIRY_TYPES.has(draft.type) && draft.password.length > 0 ? readPasswordRenewDays({ passwordRenewDays: Number(draft.passwordRenewDays) }) : null;
  const secrets: EntrySecrets = {
    ...kept,
    ...(Object.keys(card).length > 0 ? { card } : {}),
    ...(Object.keys(identity).length > 0 ? { identity } : {}),
    ...(Object.keys(wifi).length > 0 ? { wifi } : {}),
    ...(Object.keys(bank).length > 0 ? { bank } : {}),
    ...(Object.keys(wallet).length > 0 ? { wallet } : {}),
    ...(Object.keys(database).length > 0 ? { database } : {}),
    ...(Object.keys(apiKey).length > 0 ? { 'api-key': apiKey } : {}),
    ...(Object.keys(license).length > 0 ? { 'software-license': license } : {}),
    ...(Object.keys(server).length > 0 ? { server } : {}),
    ...(Object.keys(email).length > 0 ? { email } : {}),
    ...(Object.keys(membership).length > 0 ? { membership } : {}),
    ...(Object.keys(medical).length > 0 ? { medical } : {}),
    name: draft.name.trim() || 'Sans nom',
    username: draft.username.trim(),
    password: draft.password,
    // Toutes les adresses viennent du brouillon : une adresse retiree dans l'editeur l'est aussi de l'entree.
    uris,
    ...(uriMatch === undefined ? {} : { uriMatch }),
    notes: draft.notes,
    ...(draft.totpSecret.trim().length > 0 ? { totpSecret: normalizeTotpSecret(draft.totpSecret).secret } : {}),
    // Seul l'opt-out est ecrit : l'absence vaut « reconnaitre aussi les equivalents » (entryMatchesSite).
    ...(draft.equivalentDomains ? {} : { equivalentDomains: false }),
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(draft.reprompt ? { reprompt: true } : {}),
    ...(passwordExpiry !== null ? { passwordExpiry } : {}),
    ...(passwordRenewDays !== null ? { passwordRenewDays } : {}),
    ...withTags({}, parseTagList(draft.tags)),
  };
  // La paire n'est reecrite que pour une cle SSH : un autre type garde ce qu'il avait (ex. un import a reclasser).
  return draft.type === 'ssh-key' ? withSshKey(secrets, draft) : secrets;
}

/** Libelles des regles de detection d'une adresse (`uri-match.ts`), dans l'ordre du selecteur. */
const URI_MATCH_LABELS: readonly (readonly [UriMatchRule, string])[] = [
  ['domain', 'Domaine'],
  ['host', 'Hote et port'],
  ['starts-with', 'Commence par'],
  ['exact', 'Adresse exacte'],
  ['regex', 'Expression reguliere (avance)'],
  ['never', 'Jamais'],
];

/** Pastille de la fiche : une regle effective autre que le domaine, ou un « Domaine » choisi contre un defaut plus strict. */
const URI_MATCH_SHORT: Readonly<Record<UriMatchRule, string>> = {
  domain: 'Domaine',
  host: 'Hote et port',
  'starts-with': 'Commence par',
  exact: 'Adresse exacte',
  regex: 'Expression reguliere',
  never: 'Jamais proposee',
};

/** Indication en direct sous la case des domaines equivalents : ce qu'elle retire pour CE site. */
function equivalentDomainsHint(uri: string): string {
  if (uri.trim() === '') return 'Indiquez le site pour voir ses domaines equivalents. Decochez pour limiter l’entree a son propre site.';
  const others = equivalentDomainsOf(uri.trim());
  return others.length === 0
    ? 'Aucun domaine equivalent connu pour ce site : seuls ses sous-domaines sont reconnus.'
    : `Aussi reconnu sur : ${others.join(', ')}. Decochez pour limiter l’entree a son propre site.`;
}

/** Regle de mot de passe du site du brouillon : la premiere de ses adresses qu'une regle connue designe. */
function draftSitePasswordRules(draft: Pick<EditorDraft, 'uri' | 'extraUris'>): SiteRulesMatch | null {
  for (const uri of [draft.uri, ...draft.extraUris]) {
    const site = sitePasswordRulesFor(uri);
    if (site !== null) return site;
  }
  return null;
}

/** Indication en direct sous le mot de passe : la regle du site que « Generer » suivra. Vide sans regle connue. */
function sitePasswordRulesHint(uris: readonly string[]): string {
  const site = draftSitePasswordRules({ uri: uris[0] ?? '', extraUris: uris.slice(1) });
  return site === null ? '' : `« Generer » suit les regles de ${site.domain} : ${describeSitePasswordRules(site.rules)}.`;
}

/** Indication sous « Pour le site » de l'ecran Generateur : la regle suivie, ou son absence. Vide sans site saisi. */
function generatorSiteRulesHint(site: string): string {
  if (site.trim() === '') return '';
  const match = sitePasswordRulesFor(site);
  return match === null
    ? 'Aucune regle connue pour ce site : vos reglages ci-dessous s’appliquent.'
    : `Suit les regles de ${match.domain} : ${describeSitePasswordRules(match.rules)}. La longueur est ramenee dans ces bornes ; les autres reglages ne s’appliquent pas a ce site.`;
}

/**
 * Alerte en direct sous les adresses de l'editeur : la premiere adresse trompeuse (homographe IDN, ou nom qui enchasse
 * un domaine tres visite ou un site du coffre) saisie, le site qu'elle imite et sa forme reelle (punycode, ou le domaine
 * qui la possede). Vide si aucune : l'alerte est alors masquee. On previent, sans bloquer -- un nom en `.рф` ou
 * `bücher.de` est legitime, et seul l'utilisateur sait ou il a cree son compte.
 */
function deceptiveUriHint(uris: readonly string[], sites: EngagedSites): string {
  const found = findDeceptiveUri(uris, sites);
  if (found === null) return '';
  return `Adresse trompeuse : ${deceptiveHostReason(found, ', ')}. Verifiez le site avant d’y saisir ce mot de passe.`;
}

/** Indication en direct sous le numero de carte : reseau detecte et controle de Luhn (la valeur n'est jamais reprise). */
function cardNumberHint(value: string): string {
  const digits = value.replace(/[\s-]+/gu, '');
  if (digits.length === 0) return 'Chiffres uniquement, espaces toleres';
  if (!/^\d+$/u.test(digits)) return 'Chiffres uniquement';
  const brand = detectCardBrand(digits);
  if (digits.length < 12) return brand || 'Numero incomplet';
  return `${brand ? `${brand} · ` : ''}${isLuhnValid(digits) ? 'numero valide' : 'numero non conforme (controle de Luhn)'}`;
}

/**
 * Attributs d'un bouton dont l'action tourne : desactive, et `aria-busy` -- c'est ce que lit le socle pour la bulle
 * « Travail en cours » et la barre d'etat.
 */
/** Valeur du choix « Aucun dossier » de « Deplacer vers » (un identifiant de dossier n'est jamais vide). */
/** Champs du generateur qui se tapent : appliques a la validation, jamais a chaque frappe. */
const GENERATOR_TYPED_FIELDS: ReadonlySet<string> = new Set(['minDigits', 'minSymbols', 'symbolCharacters', 'excludeCharacters']);

const BULK_NO_FOLDER = '-';

/** Retrait d'un sous-dossier dans une liste de choix (une option n'accepte pas de CSS de marge). */
function folderOptionIndent(depth: number): string {
  return '&nbsp;&nbsp;&nbsp;'.repeat(Math.min(depth, MAX_FOLDER_INDENT));
}

const BULK_ICONS: Readonly<Record<Exclude<BulkAction, 'move'>, string>> = {
  favorite: ICON_STAR_FILLED,
  unfavorite: ICON_STAR,
  tag: ICON_TAG,
  untag: ICON_TAG,
  archive: ICON_ARCHIVE,
  unarchive: ICON_ARCHIVE,
  trash: ICON_TRASH,
  restore: ICON_RESTORE,
  purge: ICON_TRASH,
};

function occupe(busy: boolean): string {
  return busy ? 'disabled aria-busy="true"' : '';
}

export interface MountOptions {
  /**
   * Journal d'erreurs du socle, branche par `main.ts` (`installerJournalErreurs`). Lisible SUR DEMANDE seulement :
   * Parametres → « Copier le journal d'erreurs » le pose dans le presse-papiers, rien ne part sur le reseau.
   */
  readonly journalErreurs?: ErrorJournal;
  /**
   * Serveur Klavox qui sert cette PWA (`main.ts` : son origine). S'il a Idenva et que ce navigateur n'a pas de
   * coffre, l'accueil commence par Idenva : la PWA demande au serveur si un coffre existe pour ce compte et
   * propose « Ouvrir » ou « Creer » -- jamais un e-mail. Absent : accueil local, comme avant.
   */
  readonly homeServer?: string;
}

export function mountApp(root: HTMLElement, options: MountOptions = {}): void {
  const repository: VaultRepository = isIndexedDbAvailable() ? new IndexedDbVaultRepository() : new MemoryVaultRepository();
  const state = newState(repository);
  if (options.homeServer !== undefined) state.idenvaHome = null;
  const renewServerSession = createServerSessionRenewer<RemoteSession>({
    load: () => state.remoteSession ?? undefined,
    save: (fresh) => {
      state.remoteSession = fresh;
    },
  });
  /**
   * Session serveur a jour avant un geste : renouvelee par le socle si elle expire, sinon rendue telle quelle. Un
   * renouvellement refuse (jeton expire, serveur injoignable) laisse la session rangee : la requete du geste echoue
   * alors avec SON message (« Session serveur expiree : reconnectez-vous »), comme avant.
   */
  async function liveRemoteSession(): Promise<RemoteSession | null> {
    if (state.remoteSession === null) return null;
    try {
      await renewServerSession();
    } catch {
      // Message porte par le geste lui-meme, voir ci-dessus.
    }
    return state.remoteSession;
  }
  let totpTimer: ReturnType<typeof setInterval> | null = null;
  /** Fenetres modales ouvertes et l'endroit d'ou chacune l'a ete : le focus y revient a leur fermeture. */
  let modalReturns: ModalReturn<string>[] = [];
  /**
   * Brouillon dont l'editeur affiche est le rendu. Tant qu'aucun geste ne l'a remplace, c'est le formulaire qui fait
   * foi : la frappe n'ecrit pas dans le brouillon, et le rendu le relit d'abord (voir `render`).
   */
  let renderedDraft: EditorDraft | null = null;
  /**
   * Brouillon issu de la derniere lecture du formulaire, et l'etat du formulaire a cette lecture. Un geste asynchrone
   * (cle SSH, identifiant) remplace le brouillon au clic puis attend : ce qui est saisi pendant l'attente n'est que dans
   * le formulaire -- frappe, ou valeur posee sans evenement `input` (script, certains remplisseurs) -- et le rendu le
   * reconnait a un formulaire qui n'est plus celui qu'on a lu (voir `formChangedSinceRead`). Oublie des que l'editeur
   * se ferme.
   */
  let readDraft: EditorDraft | null = null;
  let readForm: string[] | null = null;
  /**
   * Brouillon en cours d'enregistrement (suivi a travers les relectures du formulaire) : l'editeur ne se ferme qu'une
   * fois la liste relue, et une seconde validation (double Entree) n'enregistre pas une seconde entree.
   */
  let savingDraft: EditorDraft | null = null;
  /** Le clic en cours de traitement : `runCommand` l'attend, pour que « Faire » reste occupe tant que le geste tourne. */
  let pendingClick: Promise<void> = Promise.resolve();
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  let flashTimer: ReturnType<typeof setTimeout> | null = null;
  let clipboardTimer: ReturnType<typeof setTimeout> | null = null;
  /** Entree dont les pieces jointes sont en cours de dechiffrement (un seul chargement a la fois). */
  let attachmentsLoadingFor: string | null = null;
  /** Numero du dernier chargement des pieces jointes : un chargement depasse (oubli ou autre chargement) n'ecrit rien. */
  let attachmentsGeneration = 0;
  /** Numero de la derniere relecture du coffre lancee : une relecture depassee n'ecrit rien (voir `refreshVaultData`). */
  let refreshGeneration = 0;
  // Import d'un export chiffre en cours de derivation : « Annuler » et le verrouillage l'arretent, rien n'est verse au coffre.
  let importRun: AbortController | null = null;
  /** Lecture a la camera en cours (une seule a la fois) ; annulee au verrouillage. */
  let cameraScan: AbortController | null = null;
  /** Liaison Idenva en cours : l'interrompre rend la main sans rien lier. */
  let idenvaRun: AbortController | null = null;
  /**
   * Recepteur `klavox.credentials` v1 (`bus/klavox-credentials.v1.md`) : un pair (NAVKRYPT) verse des identifiants,
   * scelles HPKE vers une cle ephemere de cet appareil, apres le geste de l'utilisateur. Le versement passe par
   * l'import existant (rechiffre sous la vaultKey) ; le retour ne porte que les verdicts des sites transferes.
   */
  const credentialsInbox = new CredentialsTransferInbox({
    deverrouille: () => state.vaultStore !== null,
    consentir: (offre) =>
      new Promise<boolean>((resolve) => {
        state.credentialsTransferOffer = { offre, resolve };
        render();
      }),
    importer: async (entrees) => {
      const { imported } = await importParsedEntries(entrees);
      await refreshVaultData();
      scheduleFlash(`${imported} identifiant(s) verse(s) dans le coffre`);
      render();
      return imported;
    },
    // Les mots de passe qui arrivent n'ont pas ete confrontes a la source de fuites : `verifie_le: null`
    // (« non verifie »), jamais un « aucune fuite » emprunte a un audit anterieur du reste du coffre.
    verdicts: (origines) => {
      const detectionParDefaut = state.defaultUriMatch;
      const reponse = serveCredentialsAudit(
        { origines },
        {
          deverrouille: state.vaultStore !== null,
          consenti: true,
          entrees: state.entries,
          breachCounts: null,
          breachVerifieLe: null,
          detectionParDefaut,
          emailCompte: vaultAccountEmail(),
        },
      );
      return reponse.etat === 'ok' ? reponse.verdicts : [];
    },
  });

  /** Reponse de l'utilisateur a la fenetre de consentement ; la fenetre se ferme dans les deux cas. */
  function answerCredentialsTransfer(accepte: boolean): void {
    const pending = state.credentialsTransferOffer;
    if (pending === null) return;
    state.credentialsTransferOffer = null;
    pending.resolve(accepte);
    render();
  }

  function scheduleFlash(message: string): void {
    state.flash = message;
    if (flashTimer !== null) clearTimeout(flashTimer);
    flashTimer = setTimeout(() => {
      state.flash = null;
      // Le bandeau seul s'en va : un rendu complet reconstruirait l'editeur ou une fenetre depuis l'etat et effacerait
      // la saisie en cours (nom tape dans les 3 s qui suivent « Entree enregistree », mot de passe maitre redemande).
      view.querySelector('[data-role="flash"]')?.remove();
    }, 3200);
  }

  /**
   * Relit le formulaire du generateur. Des reglages impossibles restent affiches avec la phrase qui dit quoi
   * changer, mais ne s'appliquent pas : le dernier mot de passe valide reste, et seuls des reglages valides sont gardes.
   */
  function applyGeneratorForm(form: HTMLFormElement): void {
    state.generatorOptions = generatorPreferencesFromForm(new FormData(form), state.generatorOptions);
    if (generatorProblem(state.generatorOptions) === null) {
      state.generatorApplied = state.generatorOptions;
      localStorage.setItem(GENERATOR_OPTIONS_KEY, serializeGeneratorPreferences(state.generatorApplied));
      state.generatedPassword = generateForGenerator();
    }
    render();
  }

  /** Mot de passe ou phrase de passe selon le mode du generateur (aussi utilise par l'editeur et la sante du coffre). */
  function generateSecret(): string {
    const wordlist = state.generatorMode === 'passphrase' ? state.wordlists[state.passphraseSettings.language] : undefined;
    return wordlist === undefined
      ? generatePassword(toGeneratorOptions(state.generatorApplied))
      : generatePassphraseWith(wordlist, state.passphraseSettings).password;
  }

  /**
   * Secret d'une entree : si son site a une regle de mot de passe connue (table d'Apple, celle de Safari), un mot de
   * passe que le site accepte, a la longueur du generateur ramenee dans ses bornes ; sinon le generateur habituel.
   */
  function generateSecretForDraft(draft: EditorDraft): string {
    return generateSecretForSite(draftSitePasswordRules(draft));
  }

  /** Tirage de l'ecran Generateur : en mode mot de passe, la regle du site saisi dans « Pour le site » s'il en a une. */
  function generateForGenerator(): string {
    return generateSecretForSite(state.generatorMode === 'password' ? sitePasswordRulesFor(state.generatorSite) : null);
  }

  function generateSecretForSite(site: SiteRulesMatch | null): string {
    if (site === null) return generateSecret();
    const password = generateSitePassword(site.rules, state.generatorApplied.length);
    if (password !== null) return password;
    scheduleFlash(`Les regles connues de ${site.domain} ne laissent aucun mot de passe possible : generateur habituel utilise.`);
    return generateSecret();
  }

  async function ensureWordlist(): Promise<void> {
    const language = state.passphraseSettings.language;
    if (state.wordlists[language] !== undefined) return;
    try {
      state.wordlists[language] = await loadWordlist(language);
    } catch {
      state.generatorMode = 'password';
      scheduleFlash('Liste de mots indisponible (hors ligne ?) : generateur de mots de passe conserve.');
    }
  }

  /** Le suffixe « nom du site » ne se tire que depuis une fiche : le Generateur seul n'a pas de site. */
  function usernameNeedsSite(): boolean {
    return state.usernameSettings.kind !== 'word' && state.usernameSettings.suffix === 'site';
  }

  /**
   * Identifiant selon les reglages du generateur ; `null` + message si la saisie ne le permet pas.
   * `site` : nom du site de la fiche (`siteSuffixFromUris`), `null` hors d'une fiche.
   */
  async function drawUsername(site: string | null = null): Promise<string | null> {
    const language = state.passphraseSettings.language;
    if (state.usernameSettings.kind === 'word' && state.wordlists[language] === undefined) {
      try {
        state.wordlists[language] = await loadWordlist(language);
      } catch {
        // `generateUsername` signale la liste absente.
      }
    }
    try {
      state.usernameError = null;
      return generateUsername(state.usernameSettings, state.wordlists[language], site);
    } catch (cause) {
      state.usernameError = cause instanceof Error ? cause.message : 'generation impossible';
      return null;
    }
  }

  async function regenerateUsername(): Promise<void> {
    if (usernameNeedsSite()) {
      // Pas d'exemple tire sur un faux site : il serait copie tel quel.
      state.usernameError = null;
      state.generatedUsername = '';
      render();
      return;
    }
    const username = await drawUsername();
    // Une saisie invalide n'affiche pas l'ancien identifiant : il ne correspondrait plus aux reglages.
    state.generatedUsername = username ?? '';
    render();
  }

  function resetIdleTimer(): void {
    if (idleTimer !== null) clearTimeout(idleTimer);
    if (state.vaultStore === null) return;
    idleTimer = setTimeout(() => {
      void lock();
    }, state.autoLockMinutes * 60 * 1000);
  }

  /** L'etiquette a-t-elle des sous-etiquettes listees (« Travail/Clients » sous « Travail ») ? Renommer ou supprimer les emporte. */
  function hasSubTags(parent: string): boolean {
    return state.tags.some(({ tag }) => tagKey(tag) !== tagKey(parent) && isWithinTag(tag, parent));
  }

  /**
   * Relit dossiers, etiquettes et entrees de la vue. Rend `false` si une relecture plus recente (ou le verrouillage)
   * l'a depassee : elle n'a rien ecrit, et c'est la plus recente qui rendra.
   */
  async function refreshVaultData(): Promise<boolean> {
    if (state.vaultStore === null) return true;
    const store = state.vaultStore;
    // Deux relectures peuvent se croiser (Archives puis Tous les elements, frappe dans la recherche, verrouillage) :
    // seule la plus recente ecrit l'etat, sinon une reponse en retard repeint la vue precedente sous la nouvelle.
    const generation = ++refreshGeneration;
    // La sante juge le coffre ENTIER, archives comprises : un mot de passe archive peut encore ouvrir un compte vivant.
    const archived: ArchiveFilter = state.panel === 'health' ? 'include' : state.archivedOnly ? 'only' : 'exclude';
    const query = state.searchQuery;
    const trash = state.trashOnly;
    const folders = await store.listFolders();
    const tags = await store.listTags();
    // L'etiquette de la vue n'est plus portee par aucune entree (retiree, archivee, jetee) : retour a « Tous les elements ».
    const tagKept = (wanted: string | null): wanted is string => wanted !== null && tags.some(({ tag }) => tagKey(tag) === tagKey(wanted));
    const tag = tagKept(state.selectedTag) ? state.selectedTag : null;
    let entries: VaultItem[];
    if (trash) {
      entries =
        query.trim().length > 0
          ? await store.searchEntries(query, { trash: 'only', sort: state.entrySort })
          : await store.listEntries({ trash: 'only', sort: state.entrySort });
    } else {
      entries =
        query.trim().length > 0
          ? await store.searchEntries(query, { archived, sort: state.entrySort })
          : await store.listEntries({
              // `null` = aucune vue de dossier, pas « hors dossier » : « Tous les elements », les Favoris et la vue
              // d'une etiquette montrent aussi les entrees rangees dans un dossier.
              ...(state.selectedFolderId !== null ? { folderId: state.selectedFolderId } : {}),
              favoritesOnly: state.favoritesOnly,
              ...(tag !== null ? { tag } : {}),
              archived,
              sort: state.entrySort,
            });
    }
    if (generation !== refreshGeneration) return false;
    state.folders = folders;
    state.tags = tags;
    if (!tagKept(state.selectedTag)) state.selectedTag = null;
    state.entries = entries;
    state.entriesForHealth = !trash && archived === 'include';
    await refreshRelated();
    return generation === refreshGeneration;
  }

  /**
   * Relit la vue puis la rend -- sauf si la relecture a ete depassee : rendre l'etat d'un geste plus recent avec la
   * liste du precedent montrerait une vue fausse le temps d'une lecture, et reconstruirait l'ecran pour rien a
   * chaque frappe dans la recherche.
   */
  async function refreshVaultAndRender(): Promise<void> {
    if (await refreshVaultData()) render();
  }

  /** Relit les elements lies de l'entree ouverte (`VaultStore.relatedEntries`) ; aucun hors d'une fiche. */
  async function refreshRelated(): Promise<void> {
    const id = state.selectedEntryId;
    if (id === null || state.vaultStore === null) {
      state.related = null;
      return;
    }
    const { related, candidates } = await state.vaultStore.relatedEntries(id);
    if (state.selectedEntryId === id) state.related = { forId: id, items: related, candidates };
  }

  /**
   * Quitte la Sante du coffre (qui voit aussi les archives) pour la fiche d'une entree : la liste reprend la vue ou
   * l'entree se trouve -- Archives si elle est archivee, Tous les elements sinon.
   */
  async function leaveHealthFor(entry: VaultItem): Promise<void> {
    if (state.panel !== 'health') return;
    state.panel = 'vault';
    state.archivedOnly = isArchived(entry.secrets);
    await refreshVaultData();
  }

  async function unlockWith(vaultStore: VaultStore, envelope: AccountEnvelope): Promise<void> {
    state.vaultStore = vaultStore;
    state.envelope = envelope;
    state.selectedEntryId = null;
    state.draft = null;
    await vaultStore.upgradeLegacyRecords();
    // Les entrees restees a la corbeille au-dela du delai sont supprimees pour de bon, a chaque ouverture.
    await vaultStore.purgeExpiredTrash();
    // App Android : les transferts CXP recus coffre ferme attendent, scelles, d'etre verses ici.
    const androidInbox = (globalThis as { KlavoxAndroid?: CxpInboxBridge }).KlavoxAndroid;
    if (androidInbox !== undefined) {
      const { imported, failed } = await drainCxpInbox(androidInbox, importParsedEntries);
      const notes: string[] = [];
      if (imported + failed > 0) {
        const kept = failed > 0 ? ` -- ${failed} transfert(s) illisible(s), garde(s) en attente` : '';
        notes.push(`${imported} entree(s) recue(s) d'un autre gestionnaire${kept}`);
      }
      // Role source : un autre gestionnaire a demande ce coffre (consentement donne cote Android).
      const sent = await answerCxpExport(androidInbox, async () =>
        buildCxfHeader({
          email: envelope.email,
          items: await vaultStore.listEntries({ archived: 'include' }),
          folders: await vaultStore.listFolders(),
        }),
      );
      if (sent) notes.push('Coffre transmis a l’autre gestionnaire par le systeme.');
      if (notes.length > 0) scheduleFlash(notes.join(' -- '));
    }
    await refreshVaultData();
    // Autofill Android : l'OS a ouvert le coffre pour remplir une app ou un site ; la premiere entree qui le
    // reconnait est affichee, avec son bouton « Remplir ».
    state.autofillTarget = autofillTarget(androidInbox as AutofillBridge | undefined);
    if (state.autofillTarget !== null) {
      const target = state.autofillTarget;
      const match = state.entries.find((entry) => entryFillsTarget(entry.secrets, target, state.defaultUriMatch));
      if (match !== undefined) state.selectedEntryId = match.id;
      scheduleFlash(match !== undefined
        ? `Choisissez l’identifiant a remplir dans ${autofillTargetLabel(target)}`
        : `Aucune entree ne correspond a ${autofillTargetLabel(target)}`);
    }
    state.generatorHistory = loadGeneratorHistory(localStorage, vaultStore, Date.now());
    // Dernier type garde = phrase de passe : la liste est chargee des l'ouverture, pour que « Generer » dans
    // l'editeur tire une phrase de passe et pas, faute de liste, un mot de passe.
    if (state.generatorMode === 'passphrase') await ensureWordlist();
    resetIdleTimer();
  }

  /**
   * Un secret copie depuis le generateur entre dans son historique, scelle aussitot : c'est celui qui a pu etre
   * colle dans un formulaire d'inscription sans que l'entree soit jamais enregistree.
   */
  function rememberGenerated(value: string, kind: GeneratedKind): void {
    if (state.vaultStore === null) return;
    state.generatorHistory = recordGenerated(state.generatorHistory, value, kind, Date.now());
    persistGeneratorHistory();
  }

  function persistGeneratorHistory(): void {
    if (state.vaultStore === null) return;
    try {
      saveGeneratorHistory(localStorage, state.vaultStore, state.generatorHistory);
    } catch {
      // Stockage plein ou refuse : la copie a eu lieu, l'historique reste en memoire pour cette session.
      scheduleFlash('Historique du generateur non enregistre sur cet appareil');
    }
  }

  /** Quitte la selection multiple : rien de coche ne survit a un changement de vue ni au verrouillage. */
  function exitBulk(): void {
    state.bulkMode = false;
    state.bulkSelected = [];
    state.bulkAnchor = null;
    state.bulkFolderId = undefined;
    state.bulkTag = '';
    state.bulkReprompt = null;
    state.bulkError = null;
  }

  /**
   * Coche une ligne de la selection multiple : bascule seule, ou (`range`) toute la plage affichee depuis
   * l'ancre (`bulkRangeSelection`). Chemin UNIQUE du clic, du Maj+clic et de Maj+Espace / Maj+Entree.
   */
  /** Tout cocher : chemin UNIQUE du bouton « Tout cocher » et de Ctrl+A / Cmd+A -- la liste affichee, jamais un masque. */
  function selectAllBulk(): void {
    state.bulkSelected = bulkSelectAll(state.entries.map((item) => item.id), state.bulkSelected);
    state.bulkReprompt = null;
    state.bulkError = null;
    render();
  }

  function toggleBulkRow(id: string, range: boolean): void {
    state.bulkSelected = range
      ? bulkRangeSelection(state.entries.map((item) => item.id), state.bulkSelected, state.bulkAnchor, id)
      : toggleBulkSelection(state.bulkSelected, id);
    state.bulkAnchor = id;
    state.bulkReprompt = null;
    state.bulkError = null;
    render();
  }

  /**
   * Fleche bas / haut, Origine / Fin sur une ligne cochable : le focus passe a la ligne voisine, premiere ou derniere
   * de la liste affichee (`bulkFocusTarget`) ; avec Maj, la plage depuis l'ancre prend l'etat de l'ancre
   * (`bulkExtendSelection`). `false` : pas de ligne atteinte, la touche reste au navigateur.
   */
  function moveBulkFocus(from: string, move: BulkFocusMove, extend: boolean): boolean {
    const ids = state.entries.map((item) => item.id);
    const next = bulkFocusTarget(ids, from, move);
    if (next === null) return false;
    if (extend) {
      // Sans ancre affichee, la ligne de depart devient l'ancre : Maj+bas depuis une ligne coche les deux.
      if (state.bulkAnchor === null || !ids.includes(state.bulkAnchor)) {
        state.bulkAnchor = from;
        if (!state.bulkSelected.includes(from)) state.bulkSelected = [...state.bulkSelected, from];
      }
      state.bulkSelected = bulkExtendSelection(ids, state.bulkSelected, state.bulkAnchor, next);
      state.bulkReprompt = null;
      state.bulkError = null;
    }
    // Focus pose AVANT le rendu : `render` le retrouve sur le jumeau de la ligne (`focusSignature`).
    const rowOf = (): HTMLElement | undefined => Array.from(view.querySelectorAll<HTMLElement>('[data-action="bulk-toggle"]')).find((row) => row.dataset.id === next);
    rowOf()?.focus();
    if (extend) render();
    rowOf()?.scrollIntoView?.({ block: 'nearest' });
    return true;
  }

  async function lock(): Promise<void> {
    cameraScan?.abort();
    state.vaultStore?.lock();
    state.vaultStore = null;
    // Une relecture en vol au verrouillage ne remet pas d'entrees dechiffrees dans l'etat.
    refreshGeneration++;
    state.entries = [];
    state.entriesForHealth = false;
    state.folders = [];
    state.collapsedFolderIds = new Set();
    state.folderTypeAhead = null;
    // Les etiquettes sont des secrets de l'entree : la barre laterale les oublie avec le reste.
    state.collapsedTagKeys = new Set();
    state.tagTypeAhead = null;
    state.tags = [];
    state.selectedTag = null;
    state.draft = null;
    state.selectedEntryId = null;
    state.largeType = null;
    state.wifiQr = null;
    state.totpQr = null;
    state.repromptConfirmedId = null;
    state.repromptError = null;
    closeAttachmentPreview();
    // Les elements lies sont des noms d'entrees dechiffres : oublies avec le reste.
    state.related = null;
    state.linkPickerFor = null;
    exitBulk();
    state.attachments = [];
    forgetAttachments();
    state.importPrompt = null;
    importRun?.abort();
    importRun = null;
    state.importBusy = false;
    state.importProgress = null;
    state.importCleanupFile = null;
    state.importCleanupHandle = null;
    state.importCleanupResult = null;
    // Coffre ferme : l'offre en attente est refusee et toute cle ephemere oubliee.
    answerCredentialsTransfer(false);
    answerMasterPasswordPrompt(null);
    answerTotpCodePrompt(null);
    credentialsInbox.forget();
    state.cxfBusy = false;
    state.cxfMessage = null;
    state.remoteSession = null;
    state.webauthnCredentials = null;
    state.webauthnMessage = null;
    state.accountTotpEnabled = null;
    state.recoveryKeyStale = false;
    state.accountTotpEnrollment = null;
    state.accountTotpMessage = null;
    // Une liaison en cours meurt avec le coffre : son verifier PKCE est oublie.
    idenvaRun?.abort();
    idenvaRun = null;
    state.idenvaConfig = null;
    state.idenvaLinked = null;
    state.idenvaBusy = false;
    state.idenvaMessage = null;
    state.idenvaSignIn = null;
    state.sessionsRevokeBusy = false;
    state.sessionsRevokeMessage = null;
    state.connectedDevices = null;
    state.connectedDevicesBusy = false;
    state.connectedDevicesMessage = null;
    state.securityLog = null;
    state.securityLogBusy = false;
    state.securityLogMessage = null;
    state.accountDeletionBusy = false;
    state.accountDeletionMessage = null;
    state.emergencyGrants = null;
    state.emergencyMessage = null;
    // Le kit n'a aucune raison de survivre a un verrouillage : il ne se
    // reaffiche jamais, c'est le sens de l'avertissement montre a sa creation.
    state.emergencyKitReveal = null;
    state.emergencyContactState = null;
    state.emergencyContactVault = null;
    state.emergencyContactDraft = { baseUrl: '', kit: '' };
    state.masterPasswordMessage = null;
    state.emailChangeBusy = false;
    state.emailChangeMessage = null;
    state.passkeyMessage = null;
    state.breachCounts = null;
    state.breachError = null;
    state.breachCheckedAt = null;
    state.draftBreach = null;
    state.sendDraft = { ...EMPTY_SEND_DRAFT };
    state.sendFile = null;
    state.sends = null;
    state.sendMessage = null;
    state.sendCreated = null;
    state.sendEditingId = null;
    state.sendShare = null;
    state.generatedPassword = '';
    state.generatorSite = '';
    // L'historique du generateur ne vit en clair qu'en memoire : il repart avec la vaultKey.
    state.generatorHistory = [];
    state.generatorHistoryOpen = false;
    state.generatorHistoryRevealed = false;
    // L'adresse et le domaine du generateur d'identifiants sont des donnees personnelles : ils suivent le coffre.
    state.usernameSettings = DEFAULT_USERNAME_SETTINGS;
    state.generatedUsername = '';
    state.usernameError = null;
    state.authError = null;
    // Ce que l'utilisateur a tape dans l'aide est une donnee personnelle : elle
    // suit le coffre et disparait avec lui.
    state.helpOpen = false;
    state.helpQuestion = '';
    state.helpAnswer = null;
    state.helpCommand = null;
    // Un coffre verrouille se rouvre sur le coffre, jamais sur l'ecran ou on l'a ferme
    // (rouvrir dans Parametres ou le Generateur n'a aucun sens).
    state.panel = 'vault';
    // Un coffre cree pendant cette session doit se rouvrir par « Deverrouiller », pas par le formulaire de creation.
    state.authMode = state.localAccountEnvelope === null ? 'create' : 'unlock';
    if (idleTimer !== null) clearTimeout(idleTimer);
    render();
  }

  // Les jetons du socle d'IHM avant le premier rendu : sans eux, la palette de la feuille est vide.
  installSocleTokens(root.ownerDocument);
  document.documentElement.dataset.theme = state.theme;
  // La vue est re-rendue en entier ; la barre d'etat et les bulles du socle vivent a cote, dans la meme racine.
  const view = root.ownerDocument.createElement('div');
  view.className = 'app-view';
  const titleHost = root.ownerDocument.createElement('header');
  const statusHost = root.ownerDocument.createElement('footer');
  root.replaceChildren(titleHost, view, statusHost);
  const titleBar = mountTitleBar(titleHost, { translate: traduireSocle });
  const statusBar = mountStatusBar(statusHost, { translate: traduireSocle });
  bindNoticeBubbles(root, { selector: SELECTEUR_BULLES, translate: traduireSocle });
  bindBusyIndicator(root, traduireSocle);

  function render(): void {
    // Rendu venu d'ailleurs (fin d'une synchro ou d'une verification de fuites, transfert recu d'un pair) : sans cette
    // relecture, l'editeur serait reconstruit depuis le brouillon d'avant la frappe et la saisie effacee. Un brouillon
    // remplace par un geste (generation, type, edition d'une autre entree) n'est pas relu : le geste prime.
    // Meme relecture pour une saisie faite pendant un geste asynchrone : le brouillon lu au clic n'est pas encore affiche.
    if (state.draft !== null && (state.draft === renderedDraft || formChangedSinceRead())) syncDraftFromForm();
    // Le verdict « fuites » de la fiche part avec elle : il ne survit pas a l'editeur ferme (il garde le mot de passe).
    if (state.draft === null) state.draftBreach = null;
    document.documentElement.dataset.theme = state.theme;
    document.documentElement.dataset.textZoom = String(state.textZoom);
    // Un lien d'envoi prime sur tout : le destinataire n'a peut-etre aucun
    // coffre, et on ne lui demande surtout pas d'en creer un pour lire.
    const main = state.sendReceive !== null ? renderSendReceive() : state.vaultStore === null ? renderAuth() : renderShell();
    const focused = root.ownerDocument.activeElement;
    const focus = focused !== null && view.contains(focused) ? focusSignature(focused) : null;
    // Zone de texte comprise (notes, champ multiligne) : sans cela, un rendu venu d'ailleurs pendant la frappe des notes
    // ramenait le curseur en fin de zone et la suite tapee atterrissait en bas, la zone defilee en haut.
    const selection = focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement ? caretOf(focused) : null;
    const modalsBefore = openModalKeys();
    // Origine d'une fenetre qui s'ouvre, retrouvee par sa signature : la vue et la barre de titre recreent leurs boutons.
    const origin = focused !== null && root.contains(focused) ? focusSignature(focused) : null;
    view.innerHTML = `${main}${renderLargeType()}${renderWifiQr()}${renderTotpQr()}${renderAttachmentPreview()}${renderHelp()}${renderCredentialsTransferConsent()}${renderMasterPasswordPrompt()}${renderTotpCodePrompt()}`;
    renderedDraft = state.draft;
    if (state.draft === null) {
      readDraft = null;
      readForm = null;
    }
    if (state.draft !== null) {
      // Le parseur HTML retire un retour a la ligne place en tete d'un <textarea> : une valeur multiligne de champ
      // personnalise qui commence par une ligne vide la perdrait a chaque rendu. Reposee depuis le brouillon.
      for (const area of view.querySelectorAll<HTMLTextAreaElement>('textarea[data-custom-index]')) {
        const field = state.draft.customFields[Number(area.dataset.customIndex)];
        if (field !== undefined) area.value = field.value;
      }
    }
    // Fenetre ouverte : tout le reste (coffre, fenetres dessous, barres de titre et d'etat) devient inerte.
    setModalBackgroundInert(view, [titleHost, statusHost]);
    if (focus !== null) restoreFocus(focus, selection);
    renderTitleBar();
    const plan = modalReturnPlan(modalReturns, modalsBefore, openModalKeys(), origin);
    modalReturns = plan.stack;
    // Fenetre fermee : le focus revient a l'element qui l'avait ouverte (ARIA APG), pas au debut de la page.
    if (plan.returnTo !== null) returnFocus(plan.returnTo);
    statusBar.setActivities(
      Array.from(view.querySelectorAll<HTMLElement>('[aria-busy="true"]'), (element, index) => ({
        id: String(index),
        label: busyTaskName(element),
        state: 'running' as const,
      })),
    );
    if (state.largeType !== null && !state.helpOpen) {
      // Le dialogue prend le focus : Echap et Tab y agissent, pas sur la fiche cachee dessous.
      const dialog = view.querySelector<HTMLElement>('.large-type-panel');
      if (dialog !== null && !dialog.contains(root.ownerDocument.activeElement)) dialog.querySelector<HTMLElement>('[data-action="close-large-type"]')?.focus();
    }
    if (state.wifiQr !== null && !state.helpOpen) {
      const dialog = view.querySelector<HTMLElement>('.wifi-qr-panel');
      if (dialog !== null && !dialog.contains(root.ownerDocument.activeElement)) dialog.querySelector<HTMLElement>('[data-action="close-wifi-qr"]')?.focus();
    }
    if (state.totpQr !== null && !state.helpOpen) {
      const dialog = view.querySelector<HTMLElement>('.totp-qr-panel');
      if (dialog !== null && !dialog.contains(root.ownerDocument.activeElement)) dialog.querySelector<HTMLElement>('[data-action="close-totp-qr"]')?.focus();
    }
    if (state.attachmentPreview !== null && !state.helpOpen) {
      const dialog = view.querySelector<HTMLElement>('.attachment-preview-panel');
      if (dialog !== null && !dialog.contains(root.ownerDocument.activeElement)) dialog.querySelector<HTMLElement>('[data-action="close-attachment-preview"]')?.focus();
    }
    if (state.credentialsTransferOffer !== null) {
      // Le focus va sur « Refuser » : Entree par reflexe ne verse rien dans le coffre.
      const dialog = view.querySelector<HTMLElement>('.credentials-transfer-panel');
      if (dialog !== null && !dialog.contains(root.ownerDocument.activeElement)) dialog.querySelector<HTMLElement>('[data-action="credentials-transfer-refuse"]')?.focus();
    }
    if (state.totpCodePrompt !== null) {
      const dialog = view.querySelector<HTMLElement>('.totp-code-prompt-panel');
      if (dialog !== null && !dialog.contains(root.ownerDocument.activeElement)) dialog.querySelector<HTMLElement>('input[name="code"]')?.focus();
    }
    if (state.masterPasswordPrompt !== null) {
      // Le focus va dans le champ : la fenetre attend une saisie, pas un clic.
      const dialog = view.querySelector<HTMLElement>('.master-password-prompt-panel');
      if (dialog !== null && !dialog.contains(root.ownerDocument.activeElement)) dialog.querySelector<HTMLElement>('input[name="masterPassword"]')?.focus();
    }
    if (state.helpOpen) {
      // Le re-rendu recree le champ : sans cela, l'aide perdrait le focus a
      // chaque reponse et deviendrait inatteignable au clavier.
      // Sous une autre fenetre, l'aide est inerte : le focus lui est refuse et reste a celle du dessus.
      const champ = root.querySelector<HTMLInputElement>('[data-input="help-question"]');
      champ?.focus();
      champ?.setSelectionRange(champ.value.length, champ.value.length);
    }
    if (totpTimer !== null) clearInterval(totpTimer);
    if (state.vaultStore !== null) {
      totpTimer = setInterval(() => {
        const hasTotp = state.entries.some((entry) => Boolean(entry.secrets.totpSecret));
        if (hasTotp && !inputInProgress()) render();
      }, 1000);
    }
  }

  /**
   * Une saisie est en cours (editeur, mot de passe maitre redemande) : un rendu venu d'ailleurs -- minuteur, lecture
   * en arriere-plan -- reconstruirait le formulaire depuis l'etat et effacerait ce qui vient d'etre tape.
   */
  function inputInProgress(): boolean {
    return state.draft !== null || state.masterPasswordPrompt !== null || state.totpCodePrompt !== null;
  }

  function openModalKeys(): string[] {
    return Array.from(view.querySelectorAll('[aria-modal="true"]'), modalKey);
  }

  /** Jumeau, apres le rendu, de l'element de signature `signature` dans `scope`. */
  function focusTwin(scope: HTMLElement, signature: string): HTMLElement | undefined {
    return Array.from(scope.querySelectorAll<HTMLElement>('input, textarea, select, button, a[href], [role="treeitem"]')).find(
      (element) => focusSignature(element) === signature,
    );
  }

  /** Rend le focus a l'origine d'une fenetre fermee, dans la vue ou la barre de titre, si elle existe encore. */
  function returnFocus(signature: string): void {
    focusTwin(root, signature)?.focus({ preventScroll: true });
  }

  /** Curseur, sens de la selection et defilement d'un champ, pour les rendre a son jumeau apres le re-rendu. */
  function caretOf(field: HTMLInputElement | HTMLTextAreaElement): Caret {
    return { start: field.selectionStart, end: field.selectionEnd, direction: field.selectionDirection, scrollTop: field.scrollTop };
  }

  /**
   * Rend le focus au jumeau de l'element qui l'avait avant le re-rendu (`focusSignature`), curseur compris :
   * la recherche se tape d'une traite et un bouton atteint au clavier ne se perd pas a chaque seconde de code 2FA.
   */
  function restoreFocus(signature: string, selection: Caret | null): void {
    const twin = focusTwin(view, signature);
    if (twin === undefined) return;
    twin.focus({ preventScroll: true });
    if ((twin instanceof HTMLInputElement || twin instanceof HTMLTextAreaElement) && selection !== null) {
      if (selection.start !== null && selection.end !== null) {
        // Types sans curseur (case a cocher, nombre) : setSelectionRange leve, le focus suffit.
        try {
          twin.setSelectionRange(selection.start, selection.end, selection.direction ?? undefined);
        } catch {
          /* pas de curseur pour ce type de champ */
        }
      }
      // Apres le curseur : une zone recreee repart en haut, la ligne tapee resterait hors de vue.
      twin.scrollTop = selection.scrollTop;
    }
  }

  function renderTitleBar(): void {
    // La barre de titre du socle est le seul chrome commun a toutes les vues : le bouton d'aide y vit, et nulle
    // part ailleurs, donc l'aide est atteignable depuis chacune d'elles (Regle IHM galaxie, « une aide dans CHAQUE vue »).
    // Le retour au coffre n'a de sens que coffre ouvert : verrouille, l'ecran de connexion porte deja la marque.
    titleBar.setItem(
      'brand',
      state.vaultStore === null ? undefined : { side: 'left', label: 'Klavox', action: 'select-all' },
    );
    titleBar.setItem('help', { side: 'right', label: 'Ouvrir l’aide', action: 'open-help', icon: ICON_HELP, pressed: state.helpOpen });
    titleBar.setItem('theme', {
      side: 'right',
      label: 'Changer de theme',
      action: 'toggle-theme',
      icon: state.theme === 'dark' ? ICON_SUN : ICON_MOON,
    });
  }

  // --- Aide contextuelle (Regle IHM galaxie) ----------------------------

  /** La vue courante, au sens de l'aide. */
  function currentHelpView(): HelpViewId {
    if (state.sendReceive !== null) return 'send-receive';
    if (state.vaultStore === null) {
      if (state.authMode === 'emergency-contact') return 'emergency-contact';
      if (state.authMode === 'recover') return 'auth-recover';
      if (state.authMode === 'recover-remote' || state.authMode === 'signin-remote') return 'auth-recover-remote';
      return state.authMode === 'create' ? 'auth-create' : 'auth-unlock';
    }
    if (state.draft !== null) return 'entry-editor';
    if (state.panel === 'generator') return 'generator';
    if (state.panel === 'settings') return 'settings';
    if (state.panel === 'health') return 'health';
    if (state.panel === 'send') return 'send';
    return state.selectedEntryId === null ? 'vault-list' : 'entry-detail';
  }

  /**
   * L'etat du compte tel que l'aide a le droit de le voir : des compteurs et
   * des indicateurs, jamais un nom d'entree ni un secret.
   */
  function helpSnapshot(): AccountSnapshot {
    const report = state.vaultStore === null ? null : vaultHealthReport();
    return {
      locked: state.vaultStore === null,
      hasLocalVault: state.localAccountEnvelope !== null,
      entryCount: state.entries.length,
      folderCount: state.folders.length,
      recoveryKeyEnabled: Boolean((state.envelope ?? state.localAccountEnvelope)?.recoveryProtectedSymmetricKey),
      passkeyUnlockCount: state.passkeyUnlocks.length,
      syncServer: state.remoteSession?.baseUrl ?? null,
      twoFactorCount:
        state.webauthnCredentials === null || state.accountTotpEnabled === null
          ? null
          : state.webauthnCredentials.length + (state.accountTotpEnabled ? 1 : 0),
      breachCheckEnabled: state.breachEndpoint !== null,
      health:
        report === null || report.score === null
          ? null
          : {
              score: report.score,
              weak: report.weak,
              reused: report.reused,
              old: report.old,
              breached: report.breached,
              insecureUri: report.insecureUri,
              cardsToRenew: report.cardIssues.length,
              documentsToRenew: report.documentIssues.length,
            },
    };
  }

  /**
   * La valeur montree en grand format, relue dans l'entree : l'UNIQUE garde du grand format, a l'ouverture comme a
   * chaque rendu. Une entree sensible pas encore confirmee ne livre rien, comme sa fiche et ses copies.
   */
  function largeTypeValue(): { entry: VaultItem; label: string; value: string } | null {
    if (state.largeType === null || state.vaultStore === null) return null;
    const { entryId, field } = state.largeType;
    const entry = state.entries.find((item) => item.id === entryId);
    if (entry === undefined || repromptPending(entry)) return null;
    const label = largeTypeFieldLabel(entry.secrets, field);
    const value = entryFieldValue(entry.secrets, field);
    return label === null || value === null ? null : { entry, label, value };
  }

  /**
   * Bouton « Grand format » d'un champ de la fiche : il ne porte que la reference, la valeur est relue a l'ouverture.
   * Absent pour un champ que la garde refuserait (multiligne, affiche en clair, vide).
   */
  function largeTypeButton(entry: VaultItem, ref: string): string {
    const label = largeTypeFieldLabel(entry.secrets, ref);
    if (label === null) return '';
    // Le raccourci du grand format clique ce bouton-la : il n'existe que sur le mot de passe courant.
    const letter = ref === 'password' ? shortcutKeyOf(state.shortcuts, 'large-type') : null;
    return `<button class="icon-btn" data-action="open-large-type" data-id="${entry.id}" data-field="${escapeHtml(ref)}"${shortcutAttr(letter)} title="${letter === null ? 'Grand format' : `Grand format (${letter})`}" aria-label="Afficher ${escapeHtml(label)} en grand format, caractere par caractere">${ICON_LARGE_TYPE}</button>`;
  }

  /**
   * Fenetre de consentement `klavox.credentials` : l'offre ne porte que le nom du pair (deja nettoye et tronque par
   * `parseCredentialsTransfer`) et un nombre. Pas de fermeture au clic exterieur : seul un bouton repond.
   */
  function renderCredentialsTransferConsent(): string {
    const pending = state.credentialsTransferOffer;
    if (pending === null) return '';
    const nom = escapeHtml(pending.offre.source.name);
    const count = pending.offre.count;
    const quantite = count === 1 ? '<strong>1</strong> identifiant' : `<strong>${count}</strong> identifiants`;
    return `<div class="modal-backdrop credentials-transfer-backdrop">
      <section class="modal glass credentials-transfer-panel" role="alertdialog" aria-modal="true" aria-labelledby="credentials-transfer-title" aria-describedby="credentials-transfer-summary">
        <div class="modal-head">
          <h2 id="credentials-transfer-title">${ICON_SHIELD}<span>Verser des identifiants dans le coffre</span></h2>
        </div>
        <p id="credentials-transfer-summary"><strong>${nom}</strong> veut verser ${quantite} dans votre coffre. Klavox vous dira ensuite lesquels sont faibles, reutilises ou fuites.</p>
        <ul class="credentials-transfer-facts">
          <li>${ICON_LOCK}<span>Ils arrivent chiffres pour cet appareil seul, puis sont rechiffres sous la cle de votre coffre.</span></li>
          <li>${ICON_KEY}<span>${nom} ne voit jamais votre coffre : il n'apprend en retour que le verdict des sites qu'il vous remet.</span></li>
          <li>${ICON_CLOCK}<span>Cette autorisation vaut pour un seul envoi, pendant 5 minutes.</span></li>
        </ul>
        <div class="credentials-transfer-actions">
          <button type="button" class="secondary" data-action="credentials-transfer-refuse">Refuser</button>
          <button type="button" class="primary" data-action="credentials-transfer-accept">${ICON_CHECK}<span>Verser dans le coffre</span></button>
        </div>
      </section>
    </div>`;
  }

  /** Fenetre du mot de passe maitre : le champ est recree vide a chaque rendu, la saisie ne reste jamais dans le DOM. */
  function renderMasterPasswordPrompt(): string {
    const pending = state.masterPasswordPrompt;
    if (pending === null) return '';
    return `<div class="modal-backdrop master-password-prompt-backdrop">
      <section class="modal glass master-password-prompt-panel" role="dialog" aria-modal="true" aria-labelledby="master-password-prompt-title" aria-describedby="master-password-prompt-summary">
        <div class="modal-head">
          <h2 id="master-password-prompt-title">${ICON_LOCK}<span>Mot de passe maitre</span></h2>
        </div>
        <p id="master-password-prompt-summary">${escapeHtml(pending.message)}</p>
        <form data-form="master-password-prompt" class="stack">
          <label>Mot de passe maitre<input name="masterPassword" type="password" autocomplete="current-password" required aria-describedby="hint-master-password-prompt" ${pending.error !== null ? 'aria-invalid="true"' : ''} /></label>
          <span class="field-hint" id="hint-master-password-prompt">${escapeHtml(pending.error ?? 'Verifie sur cet appareil : seule une preuve derivee part au serveur, jamais le mot de passe.')}</span>
          <div class="credentials-transfer-actions">
            <button type="button" class="secondary" data-action="master-password-prompt-cancel">Annuler</button>
            <button type="submit" class="primary" ${occupe(pending.busy)}>Confirmer</button>
          </div>
        </form>
      </section>
    </div>`;
  }

  /** Code de l'application d'authentification a la connexion : champ recree vide a chaque rendu, comme le mot de passe maitre. */
  function renderTotpCodePrompt(): string {
    const pending = state.totpCodePrompt;
    if (pending === null) return '';
    return `<div class="modal-backdrop master-password-prompt-backdrop">
      <section class="modal glass master-password-prompt-panel totp-code-prompt-panel" role="dialog" aria-modal="true" aria-labelledby="totp-code-prompt-title" aria-describedby="totp-code-prompt-summary">
        <div class="modal-head">
          <h2 id="totp-code-prompt-title">${ICON_SHIELD}<span>Double authentification</span></h2>
        </div>
        <p id="totp-code-prompt-summary">Tapez le code a 6 chiffres affiche par votre application d’authentification pour Klavox.</p>
        <form data-form="totp-code-prompt" class="stack">
          <label>Code<input name="code" inputmode="numeric" autocomplete="one-time-code" maxlength="9" required aria-describedby="hint-totp-code-prompt" ${pending.error !== null ? 'aria-invalid="true"' : ''} /></label>
          <span class="field-hint" id="hint-totp-code-prompt">${escapeHtml(pending.error ?? 'Un code ne sert qu’une fois ; cinq codes faux obligent a redonner le mot de passe maitre.')}</span>
          <div class="credentials-transfer-actions">
            <button type="button" class="secondary" data-action="totp-code-prompt-cancel">Annuler</button>
            ${pending.allowWebAuthn ? `<button type="button" class="secondary" data-action="totp-code-prompt-webauthn">Utiliser ma cle de securite</button>` : ''}
            <button type="submit" class="primary">Valider</button>
          </div>
        </form>
      </section>
    </div>`;
  }

  /** Grand format : n'existe dans le DOM que s'il est ouvert, et la valeur n'y figure jamais dans un attribut. */
  function renderLargeType(): string {
    const shown = largeTypeValue();
    if (shown === null) return '';
    const cells = largeTypeCells(shown.value);
    const name = shown.entry.secrets.name ?? 'Sans nom';
    const items = cells
      .map(
        (cell) => `<li class="large-type-cell ${cell.kind}" title="${escapeHtml(cell.spoken)}">
          <span class="large-type-glyph" aria-hidden="true">${escapeHtml(cell.glyph)}</span>
          <span class="large-type-position" aria-hidden="true">${cell.position}</span>
          <span class="visually-hidden">${cell.position} : ${escapeHtml(cell.spoken)}</span>
        </li>`,
      )
      .join('');
    return `<div class="modal-backdrop large-type-backdrop" data-action="close-large-type">
      <section class="modal glass large-type-panel" role="dialog" aria-modal="true" aria-labelledby="large-type-title" aria-describedby="large-type-summary">
        <div class="modal-head">
          <h2 id="large-type-title">${ICON_LARGE_TYPE}<span>${escapeHtml(name)}</span><span class="large-type-field">${escapeHtml(shown.label)}</span></h2>
          <button class="icon-btn" data-action="close-large-type" title="Fermer (Echap)" aria-label="Fermer le grand format">${ICON_CLOSE}</button>
        </div>
        <p class="muted large-type-summary" id="large-type-summary">${escapeHtml(largeTypeSummary(cells))}</p>
        <ol class="large-type-cells">${items}</ol>
        <p class="large-type-legend" aria-hidden="true">
          <span class="upper">Majuscule</span><span class="lower">minuscule</span><span class="digit">chiffre</span><span class="symbol">symbole</span>
        </p>
      </section>
    </div>`;
  }

  /**
   * Contenu du code de connexion, relu dans l'entree : l'UNIQUE garde du code QR, a l'ouverture comme a chaque rendu.
   * Une entree sensible pas encore confirmee ne livre rien, comme sa fiche, ses copies et son grand format.
   */
  function wifiQrValue(): { entry: VaultItem; content: WifiQrContent } | null {
    if (state.wifiQr === null || state.vaultStore === null) return null;
    const { entryId } = state.wifiQr;
    const entry = state.entries.find((item) => item.id === entryId);
    if (entry === undefined || entry.type !== 'wifi' || repromptPending(entry)) return null;
    return { entry, content: wifiQrContent(entry.secrets) };
  }

  /** Bouton « Code QR » d'un reseau : il ne porte que l'entree, le contenu est relu a l'ouverture. */
  function wifiQrButton(entry: VaultItem): string {
    return `<button class="secondary small icon-label" data-action="open-wifi-qr" data-id="${entry.id}" title="Code QR de connexion" aria-label="Afficher le code QR de connexion au reseau">${ICON_QR}<span>Code QR</span></button>`;
  }

  /**
   * Code QR de connexion : n'existe dans le DOM que s'il est ouvert. Le dessin ne porte que de la geometrie ; ni le
   * contenu ni la cle ne figurent dans un attribut ou un texte de la page.
   */
  function renderWifiQr(): string {
    const shown = wifiQrValue();
    if (shown === null) return '';
    const { entry, content } = shown;
    const name = entry.secrets.name ?? 'Sans nom';
    let body: string;
    if (!content.ok) {
      body = `<p class="wifi-qr-problem" data-wifi-qr="problem">${escapeHtml(content.problem)}</p>`;
    } else {
      const code = encodeQr(content.payload);
      if (code === null) {
        body = '<p class="wifi-qr-problem" data-wifi-qr="problem">Nom du reseau et mot de passe trop longs pour un code QR de connexion.</p>';
      } else {
        const extent = code.size + 8;
        const details = [content.securityLabel, ...(content.hidden ? ['reseau masque'] : [])].join(' · ');
        // Fiche invite (FRITZ!Box « fiche d'acces invite ») : le panneau seul s'imprime (`@media print`), sans le nom
        // de l'entree ni les consignes de l'ecran ; le titre et la phrase destines a l'invite n'existent qu'a l'impression.
        body = `<p class="wifi-qr-print-only wifi-qr-print-title" data-wifi-qr="print-title">Reseau Wi-Fi</p>
        <figure class="wifi-qr-figure">
          <svg class="wifi-qr-code" data-wifi-qr="code" viewBox="0 0 ${extent} ${extent}" shape-rendering="crispEdges" role="img" aria-label="Code QR de connexion au reseau ${escapeHtml(content.ssid)}">
            <rect width="${extent}" height="${extent}" fill="#fff"/><path fill="#000" d="${qrSvgPath(code)}"/>
          </svg>
          <figcaption>
            <span class="wifi-qr-ssid">${escapeHtml(content.ssid)}</span>
            <span class="muted" data-wifi-qr="details">${escapeHtml(details)}</span>
          </figcaption>
        </figure>
        <p class="muted wifi-qr-hint" id="wifi-qr-hint">Visez ce code avec l’appareil photo d’un telephone : il propose de rejoindre le reseau, sans saisir la cle.${content.assumed ? ' Securite non renseignee dans l’entree : WPA est suppose.' : ''}</p>
        <p class="wifi-qr-print-only wifi-qr-print-hint" data-wifi-qr="print-hint">Pour rejoindre ce reseau, visez ce code avec l’appareil photo de votre telephone, puis touchez la proposition de connexion.</p>
        <p class="wifi-qr-warning">Ce code contient le mot de passe du reseau : ne le montrez qu’a qui peut le recevoir, et n’affichez la fiche imprimee que la ou ce reseau peut etre partage.</p>
        <div class="wifi-qr-actions">
          <button class="secondary small icon-label" data-action="print-wifi-qr" title="Imprimer le code et le nom du reseau, pour vos invites">${ICON_PRINT}<span>Imprimer la fiche invite</span></button>
        </div>`;
      }
    }
    return `<div class="modal-backdrop wifi-qr-backdrop" data-action="close-wifi-qr">
      <section class="modal glass wifi-qr-panel" role="dialog" aria-modal="true" aria-labelledby="wifi-qr-title"${content.ok ? ' aria-describedby="wifi-qr-hint"' : ''}>
        <div class="modal-head">
          <h2 id="wifi-qr-title">${ICON_QR}<span>${escapeHtml(name)}</span></h2>
          <button class="icon-btn" data-action="close-wifi-qr" title="Fermer (Echap)" aria-label="Fermer le code QR">${ICON_CLOSE}</button>
        </div>
        ${body}
      </section>
    </div>`;
  }

  /**
   * Contenu du code QR de la graine, relu dans l'entree : l'UNIQUE garde de ce code, a l'ouverture comme a chaque rendu.
   * Une entree sensible pas encore confirmee ne livre rien, comme sa fiche, ses copies et son grand format.
   */
  function totpQrValue(): { entry: VaultItem; content: TotpQrContent } | null {
    if (state.totpQr === null || state.vaultStore === null) return null;
    const { entryId } = state.totpQr;
    const entry = state.entries.find((item) => item.id === entryId);
    if (entry === undefined || !entry.secrets.totpSecret || repromptPending(entry)) return null;
    return { entry, content: totpQrContent(entry.secrets) };
  }

  /** Bouton « Code QR » de la graine : il ne porte que l'entree, la graine est relue a l'ouverture. */
  function totpQrButton(entry: VaultItem): string {
    return `<button class="icon-btn" data-action="open-totp-qr" data-id="${entry.id}" title="Code QR de la graine, pour une application d’authentification" aria-label="Afficher le code QR de la graine de double authentification">${ICON_QR}</button>`;
  }

  /**
   * Code QR de la graine (« Afficher le code QR » de KeePassXC) : n'existe dans le DOM que s'il est ouvert. Le dessin
   * ne porte que de la geometrie ; ni la graine ni l'URI ne figurent dans un attribut ou un texte de la page. Le code
   * courant est montre a cote : l'application d'en face doit afficher le meme, c'est la verification du transfert.
   */
  function renderTotpQr(): string {
    const shown = totpQrValue();
    if (shown === null) return '';
    const { entry, content } = shown;
    const name = entry.secrets.name ?? 'Sans nom';
    let body: string;
    if (!content.ok) {
      body = `<p class="wifi-qr-problem" data-totp-qr="problem">${escapeHtml(content.problem)}</p>`;
    } else {
      const code = encodeQr(content.payload);
      if (code === null) {
        body = '<p class="wifi-qr-problem" data-totp-qr="problem">Graine, emetteur et identifiant trop longs pour un code QR.</p>';
      } else {
        const extent = code.size + 8; // zone de silence de 4 modules de chaque cote
        let current = '';
        try {
          current = computeTotpDisplay({ secret: entry.secrets.totpSecret ?? '' }).formattedCode;
        } catch {
          current = '';
        }
        const caption = [content.issuer, content.account].filter((part) => part.length > 0);
        body = `<figure class="wifi-qr-figure">
          <svg class="wifi-qr-code" data-totp-qr="code" viewBox="0 0 ${extent} ${extent}" shape-rendering="crispEdges" role="img" aria-label="Code QR de la graine de double authentification de ${escapeHtml(name)}">
            <rect width="${extent}" height="${extent}" fill="#fff"/><path fill="#000" d="${qrSvgPath(code)}"/>
          </svg>
          <figcaption>
            <span class="wifi-qr-ssid" data-totp-qr="label">${escapeHtml(caption.join(' · ') || name)}</span>
            ${current === '' ? '' : `<span class="muted" data-totp-qr="current">Code actuel : <span class="mono">${escapeHtml(current)}</span></span>`}
          </figcaption>
        </figure>
        <p class="muted wifi-qr-hint" id="totp-qr-hint">Scannez ce code avec une application d’authentification (Aegis, 2FAS, Google ou Microsoft Authenticator…) : elle doit afficher le meme code que Klavox ci-dessus.</p>
        ${content.nonStandard.length === 0 ? '' : `<p class="muted wifi-qr-hint" data-totp-qr="non-standard">Parametres non standard (${escapeHtml(content.nonStandard.join(' · '))}) : certaines applications les ignorent. Verifiez que le code affiche est bien le meme.</p>`}
        <p class="wifi-qr-warning">Ce code contient la graine : qui le photographie peut produire vos codes, sans limite de duree. Ne l’affichez qu’a l’abri des regards et des cameras.</p>`;
      }
    }
    return `<div class="modal-backdrop totp-qr-backdrop" data-action="close-totp-qr">
      <section class="modal glass totp-qr-panel" role="dialog" aria-modal="true" aria-labelledby="totp-qr-title"${content.ok ? ' aria-describedby="totp-qr-hint"' : ''}>
        <div class="modal-head">
          <h2 id="totp-qr-title">${ICON_QR}<span>${escapeHtml(name)}</span></h2>
          <button class="icon-btn" data-action="close-totp-qr" title="Fermer (Echap)" aria-label="Fermer le code QR">${ICON_CLOSE}</button>
        </div>
        ${body}
      </section>
    </div>`;
  }

  function renderHelpAnswer(answer: HelpAnswer): string {
    const sources =
      answer.sources.length === 0
        ? ''
        : `<p class="help-sources">Source : ${answer.sources.map((source) => `<code>${escapeHtml(source)}</code>`).join(' · ')}</p>`;
    return `<div class="help-answer ${answer.kind === 'inconnu' ? 'unknown' : ''}">
      <h4>${escapeHtml(answer.title)}</h4>
      <p>${escapeHtml(answer.text)}</p>
      ${sources}
    </div>`;
  }

  /** Controles ACTIFS de l'ecran pour une commande (le panneau d'aide n'en fait pas partie). */
  function commandControls(selector: string): HTMLElement[] {
    return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter((element) => element.closest('.help-panel') === null && !element.hasAttribute('disabled'));
  }

  /** Les controles d'une commande ; nommee et `target` donne : seulement celui qui porte CE nom affiche. */
  function controlsFor(command: KlavoxCommand, target?: string): HTMLElement[] {
    const controls = commandControls(commandSelector(command));
    return command.named === undefined || target === undefined ? controls : controls.filter((element) => element.dataset.name === target);
  }

  /** Les noms affiches que designe une commande (`data-name` des controles de son `designator`) : la seule source. */
  function namesFor(command: KlavoxCommand): string[] {
    const via = designator(command);
    return via === undefined ? [] : controlsFor(via).map((element) => element.dataset.name ?? '');
  }

  /** La commande reconnue, proposee dans la discussion : « Faire » / « Annuler », ou pourquoi elle ne se peut pas. */
  function renderHelpCommand(match: CommandMatch): string {
    if (match.kind === 'aucune') return '';
    if (match.kind === 'ambigue') {
      const titres = match.commands.map((command) => `<strong>${escapeHtml(command.title)}</strong>`).join(' ou ');
      return `<div class="help-command"><p>Votre demande peut vouloir dire : ${titres}. Precisez laquelle : je ne devine pas.</p></div>`;
    }
    const { command } = match;
    const nomme = state.helpTarget;
    if (nomme.kind === 'plusieurs') {
      const noms = nomme.names.map((name) => `<strong>${escapeHtml(name)}</strong>`).join(' ou ');
      return `<div class="help-command"><p>Votre demande nomme : ${noms}. Precisez lequel : je ne devine pas.</p></div>`;
    }
    const target = nomme.kind === 'un' ? nomme.name : undefined;
    const via = target === undefined ? undefined : designator(command);
    const present = controlsFor(command, target).length;
    const opens = KLAVOX_COMMANDS.find((candidate) => candidate.id === command.opens);
    const possible =
      present === 1 ||
      (via !== undefined && via !== command && controlsFor(via, target).length === 1) ||
      (command.nature === 'preparer' && present === 0 && opens !== undefined && commandControls(commandSelector(opens)).length === 1);
    if (!possible) {
      const raison =
        present > 1
          ? command.named !== undefined
            ? `dites quel(le) ${command.named}, par son nom`
            : 'plusieurs elements correspondent a l’ecran : ouvrez celui qui est vise'
          : command.unavailable;
      return `<div class="help-command"><p>« ${escapeHtml(command.title)} » est indisponible maintenant : ${escapeHtml(raison)}.</p></div>`;
    }
    const note = command.nature === 'preparer' ? ' Ce geste ne se defait pas, ou attend votre saisie : Klavox vous y amene, c’est vous qui confirmez.' : '';
    const cible = target === undefined ? '' : ` — « ${escapeHtml(target)} »`;
    const dataTarget = target === undefined ? '' : ` data-target="${escapeHtml(target)}"`;
    return `<div class="help-command">
      <p>Je peux le faire : <strong>${escapeHtml(command.title)}</strong>${cible}.${note}</p>
      <div class="detail-actions">
        <button type="button" class="primary" data-action="help-command-run" data-id="${escapeHtml(command.id)}"${dataTarget} ${occupe(state.helpCommandBusy)}>Faire</button>
        <button type="button" class="secondary" data-action="help-command-cancel">Annuler</button>
      </div>
    </div>`;
  }

  /** Le panneau n'existe dans le DOM que s'il est ouvert : ferme, il ne coute ni un mot lu, ni un geste. */
  function renderHelp(): string {
    if (!state.helpOpen) return '';
    const view = currentHelpView();
    const label = HELP_VIEWS.find((candidate) => candidate.id === view)?.label ?? '';
    const facts = accountFacts(helpSnapshot())
      .map(
        (fact) => `<li class="help-fact ${fact.level}">
          <span class="help-fact-label">${escapeHtml(fact.label)}</span>
          <span class="help-fact-value">${escapeHtml(fact.value)}</span>
        </li>`,
      )
      .join('');
    const suggestions = (state.helpAnswer?.related ?? helpSuggestions(view))
      .map((question) => `<button type="button" class="help-suggestion" data-action="ask-help-suggestion" data-question="${escapeHtml(question)}">${escapeHtml(question)}</button>`)
      .join('');
    return `<div class="modal-backdrop help-backdrop" data-action="close-help">
      <section class="modal glass help-panel" role="dialog" aria-modal="true" aria-label="Aide Klavox">
        <div class="modal-head">
          <h2>${ICON_HELP}<span>Aide</span></h2>
          <button class="icon-btn" data-action="close-help" title="Fermer" aria-label="Fermer l’aide">${ICON_CLOSE}</button>
        </div>
        <p class="muted help-context">Vous etes sur : <strong>${escapeHtml(label)}</strong></p>
        <form data-form="help" class="help-ask">
          <label class="visually-hidden" for="help-question">Votre question</label>
          <input id="help-question" name="question" data-input="help-question" type="text" autocomplete="off"
            placeholder="Posez votre question avec vos mots" value="${escapeHtml(state.helpQuestion)}" />
          <button type="submit" class="primary">Demander</button>
        </form>
        ${state.helpAnswer !== null ? renderHelpAnswer(state.helpAnswer) : ''}
        ${state.helpCommand !== null ? renderHelpCommand(state.helpCommand) : ''}
        ${suggestions === '' ? '' : `<div class="help-suggestions">${suggestions}</div>`}
        <h3 class="help-section">Votre compte, maintenant</h3>
        <ul class="help-facts">${facts}</ul>
        <p class="help-notice" data-sk-notice="info">${escapeHtml(HELP_ENGINE.notice)}</p>
      </section>
    </div>`;
  }

  function renderAuth(): string {
    if (state.authMode === 'recover') return renderRecoverForm();
    if (state.authMode === 'recover-remote') return renderRemoteRecoverForm();
    if (state.authMode === 'signin-remote') return renderRemoteSignInForm();
    if (state.authMode === 'emergency-contact') return renderEmergencyContactForm();
    // Serveur d'origine avec Idenva et aucun coffre dans ce navigateur : l'accueil commence par Idenva. Le serveur
    // dit si un coffre existe pour ce compte -- l'utilisateur n'a ni e-mail a saisir, ni choix a deviner.
    const viaIdenva = state.authMode === 'create' && state.localAccountEnvelope === null && state.idenvaHome !== 'absent';
    if (viaIdenva && state.idenvaSignIn === null) return renderIdenvaStart();
    if (viaIdenva && state.idenvaSignIn?.lookup.exists === true) return renderIdenvaOpen();
    const canRecover = Boolean(state.localAccountEnvelope?.recoveryProtectedSymmetricKey);
    const creating = state.authMode === 'create';
    // Coffre cree sans e-mail (sel aleatoire) : le seul mot de passe maitre l'ouvre. Depuis Idenva : jamais d'e-mail.
    const askEmail = !viaIdenva && (creating || state.localAccountEnvelope?.kdfParams.saltBase64 === undefined);
    return `<div class="auth-screen">
      <main class="auth-card glass">
        <div class="auth-logo">${ICON_LOGO}</div>
        <h2>${state.authMode === 'create' ? 'Creer votre coffre' : 'Deverrouiller votre coffre'}</h2>
        ${viaIdenva ? renderIdenvaConnected() : ''}
        <p class="muted">Le mot de passe maitre ne quitte jamais cet appareil.</p>
        ${state.authError !== null ? `<p class="error">${escapeHtml(state.authError)}</p>` : ''}
        <form data-form="auth" class="stack">
          ${
            !askEmail
              ? ''
              : creating
                ? `<label>E-mail de synchronisation <span class="muted">(facultatif)</span>
                    <input name="email" type="email" autocomplete="username" aria-describedby="auth-email-hint" />
                    <small class="muted" id="auth-email-hint" data-testid="auth-email-hint">Seulement pour synchroniser ce coffre avec un serveur Klavox. Laissez vide : votre coffre s’ouvrira avec votre seul mot de passe maitre.</small>
                  </label>`
                : `<label>E-mail
                    <input name="email" type="email" required autocomplete="username" />
                  </label>`
          }
          <label>Mot de passe maitre
            <input name="masterPassword" type="password" required autocomplete="${creating ? 'new' : 'current'}-password" minlength="8"${creating ? ' aria-describedby="auth-master-hint"' : ''} />
            ${
              creating
                ? `<small class="muted" id="auth-master-hint" data-testid="auth-master-hint">Choisissez un mot de passe maitre DIFFERENT de votre mot de passe Idenva. Il chiffre votre coffre ; Klavox ne le connait pas et ne peut pas le retrouver.</small>${renderMasterPasswordStrengthHint()}`
                : ''
            }
          </label>
          ${
            state.authMode === 'create'
              ? `<label>Confirmer le mot de passe maitre<input name="confirm" type="password" required autocomplete="new-password" minlength="8" /></label>${renderWeakMasterConsent()}${renderMasterBreachCheck()}`
              : ''
          }
          <button type="submit" class="primary" ${occupe(state.authBusy)}>
            ${state.authBusy ? 'Derivation en cours…' : state.authMode === 'create' ? 'Creer le coffre' : 'Deverrouiller'}
          </button>
        </form>
        ${
          state.authMode === 'unlock' && state.passkeyUnlocks.length > 0 && isWebAuthnSupported()
            ? `<button class="secondary" data-action="unlock-with-passkey" ${occupe(state.authBusy)}>
                ${ICON_SHIELD} Deverrouiller avec une passkey
              </button>`
            : ''
        }
        ${
          // « J'ai deja un coffre local » n'a de sens que si CE navigateur en contient un.
          creating && state.localAccountEnvelope === null
            ? ''
            : `<button class="link-btn" data-action="toggle-auth-mode">
          ${creating ? 'J’ai deja un coffre local' : 'Creer un nouveau coffre'}
        </button>`
        }
        ${
          state.authMode === 'unlock' && canRecover
            ? '<button class="link-btn" data-action="toggle-recover-mode">Mot de passe maitre oublie ?</button>'
            : ''
        }
        ${
          // Depuis Idenva, le serveur a deja repondu « aucun coffre pour ce compte » : rien a ouvrir ni a recuperer.
          viaIdenva
            ? `<button class="link-btn" data-action="idenva-signout">Changer de compte Idenva</button>`
            : state.authMode === 'create'
              ? `<button class="link-btn" data-action="open-remote-signin">J’ai deja un compte : l’ouvrir sur cet appareil</button>
               <button class="link-btn" data-action="open-remote-recover">Mot de passe maitre oublie ?</button>`
              : ''
        }
        <button class="link-btn" data-action="open-emergency-contact">J’ai recu un kit d’acces d’urgence</button>
      </main>
    </div>`;
  }

  /**
   * Session Idenva ouverte. Idenva ne donne pas encore l'adresse aux applications (portee `email`, carte Idenva
   * `idenva-a23a3e0871ab`) : on dit « Connecte a Idenva », sans inventer d'adresse.
   */
  function renderIdenvaConnected(): string {
    return `<p class="muted" role="status" data-testid="idenva-connected">${ICON_SHIELD} Connecte a Idenva</p>`;
  }

  /** Accueil d'un serveur Idenva, sans coffre dans ce navigateur : une seule porte, Idenva. */
  function renderIdenvaStart(): string {
    const loading = state.idenvaHome === null;
    return `<div class="auth-screen">
      <main class="auth-card glass">
        <div class="auth-logo">${ICON_LOGO}</div>
        <h2>Bienvenue dans Klavox</h2>
        <p class="muted">Connectez-vous avec votre compte Idenva : Klavox ouvre votre coffre s’il existe, sinon vous le creez. Aucun e-mail a saisir.</p>
        ${state.authError !== null ? `<p class="error">${escapeHtml(state.authError)}</p>` : ''}
        <button class="primary" data-action="idenva-signin" ${occupe(loading || state.authBusy)}>
          ${loading ? 'Chargement…' : state.authBusy ? 'Connexion a Idenva…' : 'Continuer avec Idenva'}
        </button>
        <button class="link-btn" data-action="open-remote-recover">Mot de passe maitre oublie ?</button>
        <button class="link-btn" data-action="open-emergency-contact">J’ai recu un kit d’acces d’urgence</button>
      </main>
    </div>`;
  }

  /** Le serveur connait un coffre pour ce compte Idenva : on l'ouvre ici, avec le seul mot de passe maitre. */
  function renderIdenvaOpen(): string {
    return `<div class="auth-screen">
      <main class="auth-card glass">
        <div class="auth-logo">${ICON_KEY}</div>
        <h2>Ouvrir votre coffre</h2>
        ${renderIdenvaConnected()}
        <p class="muted">Votre coffre existe deja : saisissez votre mot de passe maitre pour l’ouvrir sur cet appareil. Il ne quitte jamais cet appareil : seule une preuve derivee part au serveur.</p>
        ${state.authError !== null ? `<p class="error">${escapeHtml(state.authError)}</p>` : ''}
        <form data-form="idenva-open" class="stack">
          <label>Mot de passe maitre
            <input name="masterPassword" type="password" required autocomplete="current-password" minlength="8" />
          </label>
          <button type="submit" class="primary" ${occupe(state.authBusy)}>
            ${state.authBusy ? 'Ouverture…' : 'Ouvrir le coffre'}
          </button>
        </form>
        <button class="link-btn" data-action="idenva-signout">Changer de compte Idenva</button>
        <button class="link-btn" data-action="open-remote-recover">Mot de passe maitre oublie ?</button>
        <button class="link-btn" data-action="open-emergency-contact">J’ai recu un kit d’acces d’urgence</button>
      </main>
    </div>`;
  }

  /**
   * Vue du CONTACT DE CONFIANCE (R32). Il n'a pas de compte Klavox : son kit
   * est son seul titre. Deux temps volontairement separes -- declencher
   * l'attente (le titulaire est notifie et peut refuser), puis, le delai
   * ecoule, ouvrir le coffre EN LECTURE SEULE. Aucune session n'est emise :
   * il n'y a rien a ecrire, pas meme par erreur.
   */
  function renderEmergencyContactForm(): string {
    const ouvert = state.emergencyContactVault;
    if (ouvert !== null) return renderEmergencyContactVault(ouvert);
    const etat = state.emergencyContactState;
    const attente =
      etat === null
        ? ''
        : etat.availableAt === null
          ? `<p class="muted" role="status" data-testid="emergency-contact-state">Aucune attente en cours.</p>`
          : `<p class="muted" role="status" data-testid="emergency-contact-state">Demande enregistree. Le titulaire en a ete prevenu et peut la refuser. Acces possible a partir du ${formatTimestamp(etat.availableAt)}.</p>`;
    return `<div class="auth-screen">
      <main class="auth-card glass">
        <div class="auth-logo">${ICON_KEY}</div>
        <h2>Acces d’urgence</h2>
        <p class="muted">Saisissez le kit qui vous a ete remis en main propre. Il ne quitte JAMAIS cet appareil : seule une preuve derivee, qui n’ouvre rien, part vers le serveur.</p>
        <p class="muted">Le titulaire est prevenu de votre demande et peut la refuser pendant le delai d’attente qu’il a choisi.</p>
        ${state.authError !== null ? `<p class="error">${escapeHtml(state.authError)}</p>` : ''}
        ${attente}
        <form data-form="emergency-contact" class="stack">
          <label>URL du serveur
            <input name="baseUrl" required placeholder="https://api.klavox.santvia.eu" autocomplete="off" value="${escapeHtml(state.emergencyContactDraft.baseUrl)}" />
          </label>
          <label>Kit d’acces d’urgence
            <input name="kit" required autocomplete="off" spellcheck="false" placeholder="XXXX-XXXX-XXXX-…" data-testid="emergency-kit-input" value="${escapeHtml(state.emergencyContactDraft.kit)}" />
          </label>
          <button type="submit" class="primary" ${occupe(state.emergencyBusy)}>
            ${state.emergencyBusy ? 'En cours…' : 'Demander l’acces'}
          </button>
          <button type="button" class="secondary" data-action="open-emergency-vault" ${occupe(state.emergencyBusy)}>
            Ouvrir le coffre (delai ecoule)
          </button>
        </form>
        <button class="link-btn" data-action="cancel-emergency-contact">Retour</button>
      </main>
    </div>`;
  }

  /** Coffre du titulaire, ouvert par le contact : lecture seule, aucune action d'ecriture. */
  function renderEmergencyContactVault(vault: OpenedEmergencyVault): string {
    const lignes = vault.items
      .map((item, index) => {
        const nom = typeof item.secrets.name === 'string' ? item.secrets.name : '(sans nom)';
        const identifiant = typeof item.secrets.username === 'string' ? item.secrets.username : '';
        const motDePasse = typeof item.secrets.password === 'string' ? item.secrets.password : '';
        return `<li class="attachment-row">
          <div class="attachment-meta">
            <span class="attachment-name">${escapeHtml(nom)}</span>
            <span class="muted">${escapeHtml(identifiant)}</span>
          </div>
          ${motDePasse === '' ? '' : `<button class="icon-btn" data-action="copy-emergency-password" data-index="${index}" data-name="${escapeHtml(nom)}" title="Copier le mot de passe" aria-label="Copier le mot de passe de ${escapeHtml(nom)}">${ICON_COPY}</button>`}
        </li>`;
      })
      .join('');
    return `<div class="auth-screen">
      <main class="auth-card glass">
        <div class="auth-logo">${ICON_KEY}</div>
        <h2>Coffre ouvert en lecture seule</h2>
        <p class="muted" data-testid="emergency-vault-open">${vault.items.length} entree(s) et ${vault.folders.length} dossier(s). Rien ne peut etre modifie : ce mode ne dispose d’aucune session d’ecriture.</p>
        ${vault.rejected > 0 ? `<p data-sk-notice="alert" role="alert">${vault.rejected} enregistrement(s) non authentiques ont ete ecartes.</p>` : ''}
        ${lignes === '' ? '<p class="muted">Ce coffre est vide.</p>' : `<ul class="attachment-list">${lignes}</ul>`}
        <button class="link-btn" data-action="cancel-emergency-contact">Fermer</button>
      </main>
    </div>`;
  }

  function renderRecoverForm(): string {
    return `<div class="auth-screen">
      <main class="auth-card glass">
        <div class="auth-logo">${ICON_KEY}</div>
        <h2>Reinitialiser via la cle de recuperation</h2>
        <p class="muted">Saisissez la cle de recuperation imprimee lors de son activation. Le coffre existant reste intact -- seul le mot de passe maitre change.</p>
        ${state.authError !== null ? `<p class="error">${escapeHtml(state.authError)}</p>` : ''}
        <form data-form="recover" class="stack">
          <label>Cle de recuperation
            <input name="recoveryKey" required autocomplete="off" spellcheck="false" placeholder="XXXX-XXXX-XXXX-…" />
          </label>
          <label>Nouveau mot de passe maitre
            <input name="newMasterPassword" type="password" required autocomplete="new-password" minlength="8" />
            ${renderMasterPasswordStrengthHint()}
          </label>
          <label>Confirmer
            <input name="confirm" type="password" required autocomplete="new-password" minlength="8" />
          </label>
          ${renderWeakMasterConsent()}${renderMasterBreachCheck()}
          <button type="submit" class="primary" ${occupe(state.authBusy)}>
            ${state.authBusy ? 'Reinitialisation…' : 'Reinitialiser le mot de passe maitre'}
          </button>
        </form>
        <button class="link-btn" data-action="cancel-recover">Retour au deverrouillage</button>
      </main>
    </div>`;
  }

  function renderRemoteRecoverForm(): string {
    return `<div class="auth-screen">
      <main class="auth-card glass">
        <div class="auth-logo">${ICON_KEY}</div>
        <h2>Recuperer votre compte</h2>
        <p class="muted">Nouvel appareil : la cle de recuperation ouvre votre coffre synchronise et definit un nouveau mot de passe maitre. La cle et le mot de passe ne quittent jamais cet appareil.</p>
        <p data-sk-notice="warning"><strong>Le nouveau mot de passe remplace l’ancien sur tous vos appareils</strong>, qui seront deconnectes.</p>
        ${state.authError !== null ? `<p class="error">${escapeHtml(state.authError)}</p>` : ''}
        <form data-form="recover-remote" class="stack">
          <label>URL du serveur
            <input name="baseUrl" type="url" required placeholder="https://api.klavox.santvia.eu" autocomplete="url" />
          </label>
          <label>E-mail
            <input name="email" type="email" required autocomplete="username" />
          </label>
          <label>Cle de recuperation
            <input name="recoveryKey" required autocomplete="off" spellcheck="false" placeholder="XXXX-XXXX-XXXX-…" />
          </label>
          <label>Nouveau mot de passe maitre
            <input name="newMasterPassword" type="password" required autocomplete="new-password" minlength="8" />
            ${renderMasterPasswordStrengthHint()}
          </label>
          <label>Confirmer
            <input name="confirm" type="password" required autocomplete="new-password" minlength="8" />
          </label>
          ${renderWeakMasterConsent()}${renderMasterBreachCheck()}
          <button type="submit" class="primary" ${occupe(state.authBusy)}>
            ${state.authBusy ? 'Recuperation…' : 'Recuperer le coffre'}
          </button>
        </form>
        <button class="link-btn" data-action="cancel-remote-recover">Retour</button>
      </main>
    </div>`;
  }

  /** Changement d'appareil : e-mail + mot de passe maitre, le coffre synchronise (graines TOTP comprises) revient. */
  function renderRemoteSignInForm(): string {
    return `<div class="auth-screen">
      <main class="auth-card glass">
        <div class="auth-logo">${ICON_KEY}</div>
        <h2>Ouvrir votre compte sur cet appareil</h2>
        <p class="muted">Votre coffre synchronise revient ici, codes de double authentification compris. Le mot de passe maitre ne quitte jamais cet appareil : seule une preuve derivee part au serveur.</p>
        ${state.authError !== null ? `<p class="error">${escapeHtml(state.authError)}</p>` : ''}
        <form data-form="signin-remote" class="stack">
          <label>URL du serveur
            <input name="baseUrl" type="url" required placeholder="https://api.klavox.santvia.eu" autocomplete="url" />
          </label>
          <label>E-mail
            <input name="email" type="email" required autocomplete="username" />
          </label>
          <label>Mot de passe maitre
            <input name="masterPassword" type="password" required autocomplete="current-password" minlength="8" />
          </label>
          <button type="submit" class="primary" ${occupe(state.authBusy)}>
            ${state.authBusy ? 'Ouverture…' : 'Ouvrir le coffre'}
          </button>
        </form>
        <button class="link-btn" data-action="cancel-remote-recover">Retour</button>
      </main>
    </div>`;
  }

  function renderSidebar(): string {
    const allActive = state.selectedFolderId === null && state.selectedTag === null && !state.favoritesOnly && !state.archivedOnly && !state.trashOnly;
    const favActive = state.favoritesOnly;
    // Arborescence : un sous-dossier suit son parent, un cran plus loin (deux « Clients » ne se confondent plus).
    // Un dossier a sous-dossiers porte un chevron replier / deplier ; replie au-dessus de la vue, il le signale.
    // Motif « tree » de l'ARIA APG : chaque ligne est un `treeitem` (niveau, rang, deplie, ouvert) et l'arbre n'est
    // qu'UN arret de Tabulation -- les fleches font le reste. Le chevron, doublon souris de `aria-expanded`, en sort.
    const visibleFolders = visibleFolderTree(folderTree(state.folders), state.collapsedFolderIds, state.selectedFolderId);
    const folderTabStop = folderTreeTabStop(visibleFolders, state.selectedFolderId);
    const folderRows = visibleFolders
      .map(({ folder, depth, hasChildren, collapsed, holdsActive, position, setSize }) => {
        const name = escapeHtml(folder.secrets.name);
        const active = state.selectedFolderId === folder.id;
        const twisty = hasChildren
          ? `<button class="nav-twisty" data-action="toggle-folder-collapse" data-id="${folder.id}" tabindex="-1" aria-hidden="true" title="${collapsed ? 'Deplier' : 'Replier'} « ${name} »">${ICON_CHEVRON_RIGHT}</button>`
          : '';
        return `<div class="nav-folder-row ${active ? 'active' : ''} ${collapsed ? 'collapsed' : ''}" role="none" data-depth="${Math.min(depth, MAX_FOLDER_INDENT)}">
          ${twisty}<div class="nav-row nav-folder ${active ? 'active' : ''} ${holdsActive ? 'holds-active' : ''}" role="treeitem" aria-level="${depth + 1}" aria-posinset="${position}" aria-setsize="${setSize}" aria-selected="${active}"${hasChildren ? ` aria-expanded="${!collapsed}"` : ''} tabindex="${folder.id === folderTabStop ? 0 : -1}" draggable="true" data-action="select-folder" data-id="${folder.id}" data-name="${name}" data-depth="${Math.min(depth, MAX_FOLDER_INDENT)}"${holdsActive ? ' title="Le dossier ouvert est replie dessous"' : ''}>
          ${ICON_FOLDER}<span>${name}</span>
        </div></div>`;
      })
      .join('');
    const folderTreeHtml = folderRows === '' ? '' : `<div class="nav-folder-tree" role="tree" aria-label="Dossiers">${folderRows}</div>`;
    const activeTag = state.selectedTag === null ? null : tagKey(state.selectedTag);
    // Meme motif « tree » que les dossiers : une ligne `treeitem` par etiquette, un seul arret de Tabulation, les fleches,
    // `*` et la recherche par frappe font le reste ; le chevron, doublon souris de `aria-expanded`, sort de l'arbre accessible.
    const visibleTags = visibleTagTree(state.tags, state.collapsedTagKeys, state.selectedTag);
    const tagTabStop = folderTreeTabStop(tagTreeNavRows(visibleTags), visibleTags.find((row) => tagKey(row.tag) === activeTag)?.tag ?? null);
    const tagRows = visibleTags
      .map(({ tag, count, label, depth, hasChildren, collapsed, holdsActive, position, setSize }) => {
        // Etiquette imbriquee (« Travail/Clients ») : son dernier niveau, en retrait sous son parent ; le chemin complet en bulle.
        const active = tagKey(tag) === activeTag;
        const name = escapeHtml(tag);
        const twisty = hasChildren
          ? `<button class="nav-twisty" data-action="toggle-tag-collapse" data-id="${name}" tabindex="-1" aria-hidden="true" title="${collapsed ? 'Deplier' : 'Replier'} « ${name} »">${ICON_CHEVRON_RIGHT}</button>`
          : '';
        const title = holdsActive ? "L'etiquette ouverte est repliee dessous" : depth > 0 ? tag : null;
        return `<div class="nav-tag-row ${active ? 'active' : ''} ${collapsed ? 'collapsed' : ''}" role="none" data-depth="${Math.min(depth, MAX_FOLDER_INDENT)}">${twisty}<div class="nav-row nav-tag ${active ? 'active' : ''} ${holdsActive ? 'holds-active' : ''}" role="treeitem" aria-level="${depth + 1}" aria-posinset="${position}" aria-setsize="${setSize}" aria-selected="${active}"${hasChildren ? ` aria-expanded="${!collapsed}"` : ''} tabindex="${tag === tagTabStop ? 0 : -1}"${state.draft === null ? ' draggable="true"' : ''} data-action="select-tag" data-id="${name}" data-name="${name}" data-depth="${Math.min(depth, MAX_FOLDER_INDENT)}"${title !== null ? ` title="${escapeHtml(title)}"` : ''}>
          ${ICON_TAG}<span class="nav-label">${escapeHtml(label)}</span><span class="nav-count">${count}</span>
        </div></div>`;
      })
      .join('');
    const tagSection =
      tagRows.length > 0 ? `<div class="nav-section" data-tag-drop="root" title="Deposer ici remet une etiquette a la racine"><span class="nav-section-title">Etiquettes</span></div><div class="nav-tag-tree" role="tree" aria-label="Etiquettes">${tagRows}</div>` : '';
    return `<nav class="sidebar glass">
      <button class="nav-row ${allActive ? 'active' : ''}" data-action="select-all">${ICON_LOCK}<span>Tous les elements</span></button>
      <button class="nav-row ${favActive ? 'active' : ''}" data-action="select-favorites">${ICON_STAR}<span>Favoris</span></button>
      <button class="nav-row ${state.archivedOnly ? 'active' : ''}" data-action="select-archives">${ICON_ARCHIVE}<span>Archives</span></button>
      <button class="nav-row ${state.trashOnly ? 'active' : ''}" data-action="select-trash">${ICON_TRASH}<span>Corbeille</span></button>
      <div class="nav-section" data-folder-drop="root" title="Deposer ici remet un dossier a la racine, ou sort une entree de son dossier">
        <span class="nav-section-title">Dossiers</span>
        <button class="icon-btn small" data-action="new-folder" title="Nouveau dossier">${ICON_PLUS}</button>
      </div>
      ${folderTreeHtml}
      ${tagSection}
      <div class="nav-spacer"></div>
      <button class="nav-row ${state.panel === 'health' ? 'active' : ''}" data-action="open-health">${ICON_SHIELD}<span>Sante du coffre</span></button>
      <button class="nav-row ${state.panel === 'generator' ? 'active' : ''}" data-action="open-generator">${ICON_REFRESH}<span>Generateur</span></button>
      <button class="nav-row ${state.panel === 'send' ? 'active' : ''}" data-action="open-send">${ICON_SEND}<span>Envoi ponctuel</span></button>
      <button class="nav-row ${state.panel === 'settings' ? 'active' : ''}" data-action="open-settings">${ICON_FOLDER}<span>Parametres</span></button>
      <button class="nav-row" data-action="lock"${shortcutAttr(shortcutKeyOf(state.shortcuts, 'lock'))}>${ICON_LOCK}<span>Verrouiller</span></button>
    </nav>`;
  }

  function renderEntryRow(entry: VaultItem): string {
    const domain = entry.secrets.uris?.[0] ? extractDomain(entry.secrets.uris[0]) : '';
    const selected = state.selectedEntryId === entry.id;
    // En selection multiple, la ligne coche au lieu d'ouvrir la fiche : rien de secret ne s'affiche en cochant.
    const checked = state.bulkSelected.includes(entry.id);
    const opener = state.bulkMode
      ? `<button class="entry-row bulk-row ${checked ? 'checked' : ''}"${bulkRowDraggable(entry.id) ? ' draggable="true"' : ''} data-action="bulk-toggle" data-id="${entry.id}" role="checkbox" aria-checked="${checked}">
      <span class="bulk-check" aria-hidden="true">${checked ? ICON_CHECK : ''}</span>`
      : `<button class="entry-row ${selected ? 'active' : ''}"${entryDraggable(entry) ? ' draggable="true"' : ''} data-action="select-entry" data-id="${entry.id}" data-name="${escapeHtml(entry.secrets.name ?? 'Sans nom')}">`;
    return `${opener}
      <span class="entry-avatar">${TYPE_ICONS[entry.type] ?? entryInitials(entry.secrets.name ?? '')}</span>
      <span class="entry-meta">
        <span class="entry-name">${escapeHtml(entry.secrets.name ?? 'Sans nom')}</span>
        <span class="entry-sub muted">${escapeHtml(typedEntrySubtitle(entry.type, entry.secrets) || entry.secrets.username || domain)}</span>
      </span>
      ${entry.favorite ? `<span class="entry-fav">${ICON_STAR_FILLED}</span>` : ''}
    </button>`;
  }

  /** Choix de l'ordre de la liste (utile des qu'il y a deux entrees a ordonner) et entree en selection multiple. */
  function renderSortBar(): string {
    const options = ENTRY_SORT_ORDERS.map(
      ({ order, label }) => `<option value="${order}" ${order === state.entrySort ? 'selected' : ''}>${escapeHtml(label)}</option>`,
    ).join('');
    const sort =
      state.entries.length > 1
        ? `<label class="list-sort-label">${ICON_SORT}<span>Trier</span>
        <select data-input="entry-sort" aria-label="Trier les entrees">${options}</select>
      </label>`
        : '';
    const select = state.bulkMode ? '' : '<button class="link-btn" data-action="bulk-start">Selectionner</button>';
    return `<div class="list-toolbar list-sort">${select}${sort}</div>`;
  }

  function bulkView(): BulkView {
    return state.trashOnly ? 'trash' : state.archivedOnly ? 'archives' : 'vault';
  }

  /** Barre de la selection multiple : compte, tout cocher, gestes de la vue, et le mot de passe maitre si une entree sensible est cochee. */
  function renderBulkBar(): string {
    if (!state.bulkMode) return '';
    const targets = bulkTargets(state.entries, state.bulkSelected);
    const all = targets.length === state.entries.length && state.entries.length > 0;
    const none = targets.length === 0;
    const off = none || state.bulkBusy ? 'disabled' : '';
    const buttons = bulkActionsFor(bulkView())
      .map((action) => {
        if (action === 'move') {
          const folderOptions = folderTree(state.folders)
            .map(({ folder, depth }) => `<option value="${folder.id}" ${state.bulkFolderId === folder.id ? 'selected' : ''}>${folderOptionIndent(depth)}${escapeHtml(folder.secrets.name)}</option>`)
            .join('');
          return `<span class="bulk-move">
            <select data-input="bulk-folder" aria-label="Dossier de destination">
              <option value="" disabled ${state.bulkFolderId === undefined ? 'selected' : ''}>Deplacer vers…</option>
              <option value="${BULK_NO_FOLDER}" ${state.bulkFolderId === null ? 'selected' : ''}>Aucun dossier</option>
              ${folderOptions}
            </select>
            <button class="secondary small" data-action="bulk-apply" data-bulk="move" data-name="${escapeHtml(bulkActionLabel('move'))}" ${state.bulkFolderId === undefined ? 'disabled' : off}>${ICON_FOLDER} ${bulkActionLabel('move')}</button>
          </span>`;
        }
        if (action === 'tag') {
          const suggestions = state.tags.map(({ tag }) => `<option value="${escapeHtml(tag)}"></option>`).join('');
          return `<span class="bulk-move bulk-tag">
            <input type="text" data-input="bulk-tag" list="bulk-tag-suggestions" maxlength="64" placeholder="Etiquette" aria-label="Etiquette a ajouter ou retirer" value="${escapeHtml(state.bulkTag)}" />
            <datalist id="bulk-tag-suggestions">${suggestions}</datalist>
            <button class="secondary small" data-action="bulk-apply" data-bulk="tag" data-name="${escapeHtml(bulkActionLabel('tag'))}" ${off}>${ICON_TAG} ${bulkActionLabel('tag')}</button>
            <button class="secondary small" data-action="bulk-apply" data-bulk="untag" data-name="${escapeHtml(bulkActionLabel('untag'))}" ${off}>${bulkActionLabel('untag')}</button>
          </span>`;
        }
        // « Retirer l'etiquette » partage le champ de « Etiqueter », rendu ci-dessus.
        if (action === 'untag') return '';
        const icon = BULK_ICONS[action as Exclude<BulkAction, 'move'>];
        const kind = action === 'trash' || action === 'purge' ? 'danger' : 'secondary';
        return `<button class="${kind} small" data-action="bulk-apply" data-bulk="${action}" data-name="${escapeHtml(bulkActionLabel(action))}" ${off}>${icon} ${bulkActionLabel(action)}</button>`;
      })
      .join('');
    const reprompt =
      state.bulkReprompt === null
        ? ''
        : `<form data-form="bulk-reprompt" class="bulk-reprompt stack">
        <label>Une entree sensible est cochee : mot de passe maitre<input name="masterPassword" type="password" autocomplete="current-password" required aria-describedby="hint-bulk-reprompt" ${state.bulkError !== null ? 'aria-invalid="true"' : ''} /></label>
        <span class="field-hint" id="hint-bulk-reprompt">${escapeHtml(state.bulkError ?? `Pour « ${bulkActionLabel(state.bulkReprompt.action)}${state.bulkReprompt.tag.length > 0 ? ` : ${state.bulkReprompt.tag}` : ''} ». Verifie sur cet appareil, rien n’est transmis.`)}</span>
        <div class="row">
          <button type="submit" class="primary small" ${occupe(state.bulkBusy)}>Confirmer</button>
          <button type="button" class="link-btn" data-action="bulk-cancel-reprompt">Annuler</button>
        </div>
      </form>`;
    return `<div class="bulk-bar" role="toolbar" aria-label="Selection multiple">
      <div class="list-toolbar">
        <span class="bulk-count" aria-live="polite">${targets.length} selectionnee(s)</span>
        <span class="row">
          <button class="link-btn" data-action="bulk-select-all">${all ? 'Tout decocher' : 'Tout cocher'}</button>
          <button class="link-btn" data-action="bulk-exit">Terminer</button>
        </span>
      </div>
      <div class="bulk-actions">${buttons}</div>
      ${state.bulkReprompt === null && state.bulkError !== null ? `<p class="field-hint" role="alert">${escapeHtml(state.bulkError)}</p>` : ''}
      ${reprompt}
    </div>`;
  }

  function renderList(): string {
    const empty = state.trashOnly
      ? `<p class="muted empty">Corbeille vide. Une entree supprimee y reste ${TRASH_RETENTION_DAYS} jours, restaurable.</p>`
      : state.archivedOnly
        ? '<p class="muted empty">Aucune entree archivee. Archivez un ancien compte pour le sortir des listes sans le supprimer.</p>'
        : state.selectedTag !== null
          ? `<p class="muted empty">Aucune entree avec l’etiquette « ${escapeHtml(state.selectedTag)} ».</p>`
          : '<p class="muted empty">Aucune entree.</p>';
    const rows = state.entries.length > 0 ? state.entries.map(renderEntryRow).join('') : empty;
    const trashBar =
      state.trashOnly && state.entries.length > 0 && state.searchQuery.trim().length === 0
        ? `<div class="list-toolbar"><span class="muted">Supprimees definitivement apres ${TRASH_RETENTION_DAYS} jours</span>
          <button class="link-btn link-danger" data-action="empty-trash">Vider la corbeille</button></div>`
        : '';
    // Gestion de l'etiquette de la vue : renommer ou retirer partout. Pas pendant une edition (le brouillon
    // garderait l'ancien nom et le reecrirait a l'enregistrement), ni en selection multiple.
    const tagBar =
      state.selectedTag !== null && state.draft === null && !state.bulkMode
        ? `<div class="list-toolbar tag-bar"><span class="muted tag-bar-title">${ICON_TAG}<span>${escapeHtml(state.selectedTag)}</span></span>
          <span class="row">
            <button class="link-btn" data-action="rename-tag">Renommer</button>
            <button class="link-btn link-danger" data-action="delete-tag">Supprimer l’etiquette</button>
          </span></div>`
        : '';
    // Meme regle pour le dossier de la vue (« Edit folder » de Bitwarden) : pas pendant une edition, dont le
    // brouillon porte le dossier, ni en selection multiple.
    const viewFolder = state.selectedFolderId === null ? undefined : state.folders.find((folder) => folder.id === state.selectedFolderId);
    // « Deplacer sous » : le dossier lui-meme et ses descendants ne sont pas proposes (cycle).
    const parentOptions =
      viewFolder === undefined
        ? ''
        : (() => {
            const subtree = folderSubtreeIds(state.folders, viewFolder.id);
            return folderTree(state.folders)
              .filter(({ folder }) => !subtree.has(folder.id))
              .map(({ folder, depth }) => `<option value="${folder.id}" ${viewFolder.parentId === folder.id ? 'selected' : ''}>${folderOptionIndent(depth)}${escapeHtml(folder.secrets.name)}</option>`)
              .join('');
          })();
    const folderBar =
      viewFolder !== undefined && state.draft === null && !state.bulkMode
        ? `<div class="list-toolbar tag-bar folder-bar"><span class="muted tag-bar-title">${ICON_FOLDER}<span>${escapeHtml(viewFolder.secrets.name)}</span></span>
          <span class="row">
            <select class="folder-parent" data-input="folder-parent" aria-label="Deplacer le dossier sous" title="Deplacer le dossier sous">
              <option value="${BULK_NO_FOLDER}" ${viewFolder.parentId === null ? 'selected' : ''}>Racine</option>
              ${parentOptions}
            </select>
            <button class="link-btn" data-action="new-subfolder">Nouveau sous-dossier</button>
            <button class="link-btn" data-action="rename-folder">Renommer</button>
            <button class="link-btn link-danger" data-action="delete-folder">Supprimer le dossier</button>
          </span></div>`
        : '';
    return `<section class="list-pane glass">
      <div class="search-bar">
        ${ICON_SEARCH}
        <input type="search" placeholder="Rechercher" aria-label="Rechercher"${isAdvancedSearch(state.searchQuery) ? ' aria-describedby="search-syntax"' : ''}${shortcutAttr(shortcutKeyOf(state.shortcuts, 'focus-search'))} value="${escapeHtml(state.searchQuery)}" data-input="search" />
        ${state.shortcuts.singleKey ? '<kbd class="kbd-hint" aria-hidden="true">/</kbd>' : ''}
      </div>
      ${renderSearchSyntax()}
      ${state.entries.length > 0 ? renderSortBar() : ''}
      ${tagBar}
      ${folderBar}
      ${renderBulkBar()}
      ${trashBar}
      <div class="list-scroll">${rows}</div>
      ${state.archivedOnly || state.trashOnly || state.bulkMode ? '' : `<button class="fab" data-action="new-entry" title="${shortcutTitle('Nouvelle entree', 'new-entry')}" aria-label="Nouvelle entree"${shortcutAttr(shortcutKeyOf(state.shortcuts, 'new-entry'))}>${ICON_PLUS}</button>`}
    </section>`;
  }

  // Recherche avancee (« > ») : ce que la demande a compris, terme par terme, et la syntaxe sous les yeux -- un
  // prefixe inconnu (« ste:sg ») est cherche comme texte, on le dit plutot que de laisser la liste vide sans raison.
  function renderSearchSyntax(): string {
    if (!isAdvancedSearch(state.searchQuery)) return '';
    const terms = parseAdvancedSearch(state.searchQuery);
    const chips = terms
      .map((term) => {
        const scope = term.field === null ? '' : SEARCH_FIELD_LABELS[term.field];
        const label = term.exclude ? (scope === '' ? 'sans' : `sans · ${scope}`) : scope;
        return `<span class="search-term${term.exclude ? ' is-excluded' : ''}">${label === '' ? '' : `<span class="search-term-field">${escapeHtml(label)}</span>`}${escapeHtml(term.text)}</span>`;
      })
      .join('');
    const unknown = [...new Set(terms.flatMap((term) => (term.unknownField === undefined ? [] : [term.unknownField])))];
    const fields = Object.values(SEARCH_FIELD_LABELS)
      .map((name) => `<code>${name}:</code>`)
      .join(' ');
    return `<div class="search-syntax" id="search-syntax" data-role="search-syntax">
      ${chips === '' ? '' : `<div class="search-terms">${chips}</div>`}
      ${unknown.length === 0 ? '' : `<p class="search-syntax-warn" data-role="search-unknown-field">${unknown.map((name) => `« ${escapeHtml(name)}: »`).join(', ')} n’est pas un champ : cherche comme texte.</p>`}
      <p class="muted">Recherche avancee : ${fields} · <code>-mot</code> exclut · <code>"mots exacts"</code></p>
    </div>`;
  }

  function renderDetail(): string {
    if (state.draft !== null) return renderEditor(state.draft);
    if (state.selectedEntryId === null) {
      return '<section class="detail-pane glass empty-state"><p class="muted">Selectionnez une entree, ou creez-en une nouvelle.</p></section>';
    }
    const entry = state.entries.find((item) => item.id === state.selectedEntryId);
    if (entry === undefined) return '<section class="detail-pane glass empty-state"><p class="muted">Entree introuvable.</p></section>';
    if (repromptPending(entry)) return renderRepromptGate(entry);
    const revealed = state.revealedEntryId === entry.id;
    const totp = entry.secrets.totpSecret ? computeTotpDisplay({ secret: entry.secrets.totpSecret }) : null;
    const trashed = isTrashed(entry.secrets);
    return `<section class="detail-pane glass">
      <div class="detail-header">
        <span class="entry-avatar large">${TYPE_ICONS[entry.type] ?? entryInitials(entry.secrets.name ?? '')}</span>
        <h2>${escapeHtml(entry.secrets.name ?? 'Sans nom')}</h2>
        ${trashed ? '' : `<button class="icon-btn" data-action="toggle-favorite" data-id="${entry.id}" title="Favori">
          ${entry.favorite ? ICON_STAR_FILLED : ICON_STAR}
        </button>`}
      </div>
      ${entry.secrets.username ? `<div class="field">
        <label>${entry.type === 'api-key' ? 'Identifiant (ID de cle, compte)' : entry.type === 'server' ? 'Identifiant du serveur' : entry.type === 'email' ? 'Adresse ou identifiant' : 'Identifiant'}</label>
        <div class="field-row"><span>${escapeHtml(entry.secrets.username)}</span>
          <button class="icon-btn" data-action="copy-entry-field" data-id="${entry.id}" data-field="username" title="Copier" aria-label="Copier l’identifiant">${ICON_COPY}</button>
        </div></div>` : ''}
      ${entry.secrets.password ? `<div class="field">
        <label>${entry.type === 'wifi' ? 'Mot de passe du reseau' : entry.type === 'wallet' ? 'Mot de passe du portefeuille' : entry.type === 'database' ? 'Mot de passe de la base' : entry.type === 'api-key' ? 'Cle / jeton' : entry.type === 'server' ? 'Mot de passe du serveur' : entry.type === 'email' ? 'Mot de passe de la messagerie' : 'Mot de passe'}</label>
        <div class="field-row"><span class="mono">${revealed ? escapeHtml(entry.secrets.password) : '•'.repeat(10)}</span>${wifiPasswordBadge(entry)}${apiKeyProviderBadge(entry)}
          <button class="icon-btn" data-action="toggle-reveal" data-id="${entry.id}" title="Afficher">${revealed ? ICON_EYE_OFF : ICON_EYE}</button>
          ${largeTypeButton(entry, 'password')}
          <button class="icon-btn" data-action="copy-entry-field" data-id="${entry.id}" data-field="password" title="Copier" aria-label="Copier le mot de passe">${ICON_COPY}</button>
        </div></div>` : ''}
      ${renderPasswordExpiryDetail(entry)}
      ${totp !== null ? `<div class="field">
        <label>Code TOTP</label>
        <div class="field-row"><span class="mono totp-code">${totp.formattedCode}</span>
          <span class="muted">${totp.secondsRemaining}s</span>
          <button class="icon-btn" data-action="copy" data-name="code" data-value="${totp.formattedCode.replace(/\s+/gu, '')}" title="Copier">${ICON_COPY}</button>
          ${totpQrButton(entry)}
        </div></div>` : ''}
      ${renderPasskeys(entry)}
      ${renderPasswordHistory(entry, revealed)}
      ${renderTypedDetail(entry, 'card', revealed)}
      ${renderTypedDetail(entry, 'identity', revealed)}
      ${renderTypedDetail(entry, 'wifi', revealed)}
      ${renderTypedDetail(entry, 'bank', revealed)}
      ${renderTypedDetail(entry, 'wallet', revealed)}
      ${renderTypedDetail(entry, 'database', revealed)}
      ${renderTypedDetail(entry, 'api-key', revealed)}
      ${renderTypedDetail(entry, 'software-license', revealed)}
      ${renderTypedDetail(entry, 'server', revealed)}
      ${renderTypedDetail(entry, 'email', revealed)}
      ${renderTypedDetail(entry, 'membership', revealed)}
      ${renderTypedDetail(entry, 'medical', revealed)}
      ${renderSshKeyDetail(entry, revealed)}
      ${renderCustomFieldsDetail(entry, revealed)}
      ${renderUrisDetail(entry)}
      ${renderNotesDetail(entry)}
      ${renderTagsDetail(entry)}
      ${trashed ? '' : renderRelatedDetail(entry)}
      ${renderAttachments(entry)}
      ${trashed ? '' : renderItemHistory(entry, revealed)}
      ${isArchived(entry.secrets) ? `<p class="field-hint" data-section="archived">${ICON_ARCHIVE} Entree archivee : absente des listes et de la recherche, toujours synchronisee, exportee et analysee par la Sante du coffre.</p>` : ''}
      ${hasPasskeyElsewhere(entry.secrets) && !trashed ? `<p class="field-hint" data-section="passkey-elsewhere">${ICON_FINGERPRINT} Passkey rangee ailleurs (cle de securite, trousseau du systeme) : la Sante du coffre ne propose plus d’en creer une. <button class="link-btn" data-action="passkey-suggest" data-id="${entry.id}">Proposer a nouveau</button></p>` : ''}
      ${isHealthExcluded(entry.secrets) ? `<p class="field-hint" data-section="health-excluded">${ICON_SHIELD} Exclue de la Sante du coffre : ni alerte ni score pour cette entree. Les autres entrees qui reutiliseraient son mot de passe restent signalees.</p>` : ''}
      ${!trashed && !isHealthExcluded(entry.secrets) ? renderIgnoredAlertsHint(entry) : ''}
      ${trashed ? `<p class="field-hint" data-section="trashed">${ICON_TRASH} Dans la corbeille : supprimee definitivement dans ${trashDaysLeft(entry.secrets, Date.now())} jour(s), sauf restauration.</p>` : ''}
      <div class="detail-footer muted">Modifie le ${formatTimestamp(entry.updatedAt)}</div>
      ${trashed ? renderTrashActions(entry) : `<div class="detail-actions">
        ${state.autofillTarget !== null && entryFillsTarget(entry.secrets, state.autofillTarget, state.defaultUriMatch)
          ? `<button class="primary" data-action="autofill-entry" data-id="${entry.id}">${ICON_KEY} Remplir dans ${escapeHtml(autofillTargetLabel(state.autofillTarget))}</button>`
          : ''}
        <button class="secondary" data-action="edit-entry" data-id="${entry.id}"${shortcutAttr(shortcutKeyOf(state.shortcuts, 'edit-entry'))}>Modifier</button>
        <button class="secondary" data-action="duplicate-entry" data-id="${entry.id}">${ICON_DUPLICATE} Dupliquer</button>
        ${shareableFields(entry.secrets).length > 0 ? `<button class="secondary" data-action="share-entry" data-id="${entry.id}" title="Lien chiffre, a usage limite, pour quelqu’un qui n’a pas Klavox">${ICON_SEND} Partager par lien</button>` : ''}
        ${isArchived(entry.secrets)
          ? `<button class="secondary" data-action="unarchive-entry" data-id="${entry.id}">${ICON_ARCHIVE} Desarchiver</button>`
          : `<button class="secondary" data-action="archive-entry" data-id="${entry.id}">${ICON_ARCHIVE} Archiver</button>`}
        ${isHealthExcluded(entry.secrets)
          ? `<button class="secondary" data-action="include-health" data-id="${entry.id}">${ICON_SHIELD} Surveiller a nouveau</button>`
          : `<button class="secondary" data-action="exclude-health" data-id="${entry.id}" title="Ne plus signaler cette entree dans la Sante du coffre">${ICON_SHIELD} Exclure de la Sante</button>`}
        <button class="danger" data-action="delete-entry" data-id="${entry.id}">${ICON_TRASH} Supprimer</button>
      </div>`}
    </section>`;
  }

  /**
   * Notes de la fiche, mises en forme (`renderNotesMarkdown` : tout est echappe, seuls les liens http(s) s'ouvrent,
   * aucune image chargee) ou en texte brut, selon la preference de cet appareil. La bascule n'apparait que si la note
   * porte une mise en forme : sur une note sans Markdown, les deux rendus sont identiques.
   */
  function renderNotesDetail(entry: VaultItem): string {
    const notes = entry.secrets.notes;
    if (typeof notes !== 'string' || notes.length === 0) return '';
    const formatted = notesHaveMarkdown(notes);
    const plain = !formatted || state.notesFormat === 'plain';
    const toggle = formatted
      ? `<button class="link-btn notes-format-toggle" data-action="toggle-notes-format" aria-pressed="${plain}">Texte brut</button>`
      : '';
    return `<div class="field" data-section="notes">
      <div class="notes-head"><label>Notes</label>${toggle}</div>
      ${plain ? `<p class="notes">${escapeHtml(notes)}</p>` : `<div class="notes notes-md">${renderNotesMarkdown(notes)}</div>`}
    </div>`;
  }

  /**
   * Elements lies de la fiche (`entry-links.ts`) : chacun s'ouvre ou qu'il soit range, et se delie ; « Lier un element »
   * propose les autres entrees vivantes du coffre. Seuls les noms s'affichent, jamais un secret de l'autre entree.
   */
  function renderRelatedDetail(entry: VaultItem): string {
    const related = state.related?.forId === entry.id ? state.related : null;
    if (related === null || (related.items.length === 0 && related.candidates.length === 0)) return '';
    const nameOf = (item: VaultItem): string => item.secrets.name?.trim() || 'Sans nom';
    const rows = related.items.map((other) => `<div class="field-row" data-related="${other.id}">
        <button class="link-btn" data-action="open-related" data-id="${other.id}" data-name="${escapeHtml(nameOf(other))}" title="Ouvrir">${ICON_LINK}<span>${escapeHtml(nameOf(other))}</span></button>
        ${isArchived(other.secrets) ? '<span class="muted">Archivee</span>' : ''}
        <button class="icon-btn" data-action="unlink-entry" data-id="${entry.id}" data-other="${other.id}" data-name="${escapeHtml(nameOf(other))}" title="Retirer le lien" aria-label="Retirer le lien avec ${escapeHtml(nameOf(other))}">${ICON_CLOSE}</button>
      </div>`);
    const picker =
      related.candidates.length === 0
        ? ''
        : state.linkPickerFor === entry.id
          ? `<div class="field-row" data-section="link-picker">
        <select data-input="link-target" aria-label="Element a lier">${related.candidates.map((item) => `<option value="${item.id}">${escapeHtml(nameOf(item))}</option>`).join('')}</select>
        <button class="secondary" data-action="link-entry" data-id="${entry.id}">Lier</button>
        <button class="link-btn" data-action="cancel-link">Annuler</button>
      </div>`
          : `<button class="link-btn" data-action="start-link" data-id="${entry.id}">${ICON_LINK} Lier un element</button>`;
    const label = rows.length === 1 ? 'Element lie' : rows.length > 1 ? `Elements lies (${rows.length})` : 'Elements lies';
    return `<div class="field" data-section="related"><label>${label}</label>${rows.join('')}${picker}</div>`;
  }

  /**
   * Adresses de l'entree dans la fiche : TOUTES, pas seulement la premiere. Chacune se copie ; seule une adresse web
   * (`openableUri` : http ou https) s'ouvre, dans un nouvel onglet sans `opener` ni referent -- une adresse
   * `javascript:` ou d'application importee reste du texte.
   */
  function renderUrisDetail(entry: VaultItem): string {
    const uris = Array.isArray(entry.secrets.uris) ? entry.secrets.uris : [];
    const rows = uris
      .map((uri, index) => ({ uri, index }))
      .filter(({ uri }) => typeof uri === 'string' && uri.trim().length > 0)
      .map(({ uri, index }) => {
        const href = openableUri(uri);
        const own = uriMatchRuleOf(entry.secrets, uri);
        const rule = own ?? state.defaultUriMatch;
        const shown = rule !== 'domain' || (own !== undefined && state.defaultUriMatch !== 'domain');
        const deceptive = deceptiveUriHint([uri], vaultSites(state.entries));
        return `<div class="field-row" data-uri="${index}"><span>${escapeHtml(uri)}</span>
          ${deceptive === '' ? '' : `<span class="health-badge danger" data-deceptive-uri="${index}" title="${escapeHtml(deceptive)}">Adresse trompeuse</span>`}
          ${shown ? `<span class="uri-match-badge" data-uri-match="${rule}" data-uri-match-source="${own === undefined ? 'coffre' : 'adresse'}" title="${own === undefined ? 'Detection par defaut du coffre' : 'Detection'}">${escapeHtml(URI_MATCH_SHORT[rule])}</span>` : ''}
          ${href === null ? '' : `<a class="icon-btn" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" title="Ouvrir" aria-label="Ouvrir ${escapeHtml(extractDomain(uri))}">${ICON_EXTERNAL}</a>`}
          <button class="icon-btn" data-action="copy-entry-field" data-id="${entry.id}" data-field="uri:${index}" title="Copier" aria-label="Copier l’adresse">${ICON_COPY}</button>
        </div>`;
      });
    if (rows.length === 0) return '';
    return `<div class="field" data-section="uris"><label>${rows.length > 1 ? `Sites (${rows.length})` : 'Site'}</label>${rows.join('')}</div>`;
  }

  /** Fiche d'une entree jetee : ni modifier, ni archiver -- la restaurer ou la supprimer pour de bon. */
  /** Alertes ignorees de l'entree ouverte, chacune retablie en un clic depuis la fiche. */
  function renderIgnoredAlertsHint(entry: VaultItem): string {
    const alerts = readIgnoredHealthAlerts(entry.secrets);
    if (alerts.length === 0) return '';
    const name = escapeHtml(entry.secrets.name ?? 'Sans nom');
    const buttons = alerts.map((alert) => `<button class="link-btn" data-action="unignore-health-alert" data-id="${entry.id}" data-alert="${alert}" data-name="${name}">Signaler « ${HEALTH_ALERT_LABELS[alert]} »</button>`).join(' ');
    return `<p class="field-hint" data-section="health-ignored">${ICON_SHIELD} Alertes ignorees par la Sante du coffre : ${alerts.map((alert) => HEALTH_ALERT_LABELS[alert]).join(', ')}. Une fuite, un site compromis ou une adresse sosie restent signales. ${buttons}</p>`;
  }

  function renderTrashActions(entry: VaultItem): string {
    return `<div class="detail-actions">
        <button class="secondary" data-action="restore-entry" data-id="${entry.id}">${ICON_RESTORE} Restaurer</button>
        <button class="danger" data-action="purge-entry" data-id="${entry.id}">${ICON_TRASH} Supprimer definitivement</button>
      </div>`;
  }

  /** L'entree est sensible et le mot de passe maitre n'a pas encore ete retape pour elle. */
  /** Echeance de changement du mot de passe, sous le mot de passe de la fiche ; rien sans mot de passe ni echeance. */
  function renderPasswordExpiryDetail(entry: VaultItem): string {
    const expiry = readPasswordExpiry(entry.secrets);
    if (expiry === null || !entry.secrets.password) return '';
    const status = passwordExpiryState(expiry, Date.now());
    const badge =
      status === 'expired'
        ? '<span class="health-badge danger" data-badge="password-expiry">Echeance passee</span>'
        : status === 'soon'
          ? '<span class="health-badge warn" data-badge="password-expiry">Bientot</span>'
          : '';
    const renewDays = readPasswordRenewDays(entry.secrets);
    const renewal = renewDays === null ? '' : `<span class="field-hint" data-field="password-renewal">Reportee de ${renewDays} jours a chaque changement du mot de passe</span>`;
    return `<div class="field" data-field="password-expiry">
        <label>Changer le mot de passe avant le</label>
        <div class="field-row"><span>${escapeHtml(expiry)}</span>${badge}</div>${renewal}</div>`;
  }

  /** Echeance de changement du mot de passe dans l'editeur : une date, ou un delai d'un clic. */
  function renderPasswordExpiryEditor(draft: EditorDraft): string {
    const expiry = normalizeDocumentDate(draft.passwordExpiry);
    const status = expiry === null ? null : passwordExpiryState(expiry, Date.now());
    const hint =
      status === 'expired'
        ? 'Echeance passee : changez le mot de passe (l’echeance sera acquittee a l’enregistrement), ou choisissez une nouvelle date.'
        : draft.passwordRenewDays.length > 0
          ? 'Changer le mot de passe reporte l’echeance selon la regle ; sans date, elle est posee a l’enregistrement. La Sante du coffre previent 14 jours avant.'
          : 'Facultatif. La Sante du coffre previent 14 jours avant ; changer le mot de passe acquitte une echeance passee ou proche.';
    return `<label>Changer le mot de passe avant le
          <input name="passwordExpiry" type="date" value="${escapeHtml(draft.passwordExpiry)}" aria-describedby="hint-password-expiry" />
        </label>
        <div class="expiry-presets" role="group" aria-label="Echeance du mot de passe">
          ${PASSWORD_EXPIRY_PRESETS.map(({ months, label }) => `<button type="button" class="link-btn" data-action="password-expiry-preset" data-name="${escapeHtml(label)}" data-months="${months}">Dans ${label}</button>`).join('')}
          ${draft.passwordExpiry.trim().length > 0 || draft.passwordRenewDays.length > 0 ? '<button type="button" class="link-btn" data-action="password-expiry-preset" data-name="aucune echeance" data-months="0">Aucune echeance</button>' : ''}
        </div>
        <label>Renouveler
          <select name="passwordRenewDays" aria-describedby="hint-password-expiry">
            <option value="">Jamais (date unique)</option>
            ${PASSWORD_RENEWAL_PRESETS.map(({ days, label }) => `<option value="${days}"${draft.passwordRenewDays === String(days) ? ' selected' : ''}>Tous les ${label}</option>`).join('')}
            ${draft.passwordRenewDays.length > 0 && !PASSWORD_RENEWAL_PRESETS.some(({ days }) => String(days) === draft.passwordRenewDays) ? `<option value="${escapeHtml(draft.passwordRenewDays)}" selected>Tous les ${escapeHtml(draft.passwordRenewDays)} jours</option>` : ''}
          </select>
        </label>
        <span class="field-hint" id="hint-password-expiry">${escapeHtml(hint)}</span>`;
  }

  function repromptPending(entry: VaultItem): boolean {
    return isRepromptProtected(entry.secrets) && state.repromptConfirmedId !== entry.id;
  }

  /**
   * Fiche d'une entree sensible avant confirmation : son nom et son site seulement. Aucun secret n'entre dans le DOM
   * -- pas meme dans un `data-value` de bouton Copier --, et ni Modifier ni l'oeil ne sont proposes.
   */
  function renderRepromptGate(entry: VaultItem): string {
    const domain = entry.secrets.uris?.[0] ? extractDomain(entry.secrets.uris[0]) : '';
    return `<section class="detail-pane glass" data-section="reprompt">
      <div class="detail-header">
        <span class="entry-avatar large">${TYPE_ICONS[entry.type] ?? entryInitials(entry.secrets.name ?? '')}</span>
        <h2>${escapeHtml(entry.secrets.name ?? 'Sans nom')}</h2>
      </div>
      ${domain ? `<p class="muted">${escapeHtml(domain)}</p>` : ''}
      <form data-form="reprompt" class="stack">
        <p>${ICON_LOCK} Entree protegee : retapez votre mot de passe maitre pour l’afficher ou la modifier.</p>
        <label>Mot de passe maitre<input name="masterPassword" type="password" autocomplete="current-password" required aria-describedby="hint-reprompt" ${state.repromptError !== null ? 'aria-invalid="true"' : ''} /></label>
        <span class="field-hint" id="hint-reprompt">${escapeHtml(state.repromptError ?? 'Verifie sur cet appareil, rien n’est transmis. Demande a nouveau quand vous changez d’entree ou verrouillez le coffre.')}</span>
        <div class="detail-actions">
          <button type="submit" class="primary" ${occupe(state.repromptBusy)}>Afficher l’entree</button>
        </div>
      </form>
    </section>`;
  }

  /**
   * Applique un geste a la selection : seulement ce qui est coche ET visible (`bulkTargets`). Ce qui a quitte la vue
   * n'est plus coche ; le reste le demeure pour un second geste (favori puis deplacer, par exemple).
   */
  /**
   * Geste groupe sur la selection : le bouton de la barre ET le depot d'une selection glissee sur la barre laterale
   * passent par ici (gestes de la vue, mot de passe maitre si une entree sensible est cochee).
   */
  async function requestBulk(bulk: string | undefined): Promise<void> {
    // Seuls les gestes de la vue courante : la corbeille ne s'archive pas, meme par un bouton forge.
    if (!state.bulkMode || !isBulkAction(bulk) || !bulkActionsFor(bulkView()).includes(bulk)) return;
    const folderId = bulk === 'move' ? state.bulkFolderId : null;
    if (folderId === undefined) return;
    const tag = bulk === 'tag' || bulk === 'untag' ? normalizeTag(state.bulkTag) : '';
    if ((bulk === 'tag' || bulk === 'untag') && tag.length === 0) {
      state.bulkError = 'Saisissez une etiquette.';
      render();
      return;
    }
    const targets = bulkTargets(state.entries, state.bulkSelected);
    if (targets.length === 0) return;
    if (bulk === 'purge' && !window.confirm(`Supprimer definitivement ${targets.length} entree(s) ? Ce geste est irreversible.`)) return;
    if (bulkNeedsReprompt(targets)) {
      state.bulkReprompt = { action: bulk, folderId, tag };
      state.bulkError = null;
      render();
      root.querySelector<HTMLInputElement>('form[data-form="bulk-reprompt"] input[name="masterPassword"]')?.focus();
      return;
    }
    await runBulk(bulk, folderId, tag);
  }

  async function runBulk(action: BulkAction, folderId: string | null, tag = ''): Promise<void> {
    if (state.vaultStore === null || state.bulkBusy) return;
    const targets = bulkTargets(state.entries, state.bulkSelected);
    if (targets.length === 0) return;
    state.bulkBusy = true;
    state.bulkError = null;
    try {
      const count = await state.vaultStore.applyBulk(
        targets.map((item) => item.id),
        action,
        { folderId, tag },
      );
      state.bulkReprompt = null;
      await refreshVaultData();
      const visible = new Set(state.entries.map((item) => item.id));
      const acted = new Set(targets.map((item) => item.id));
      state.bulkSelected = state.bulkSelected.filter((id) => !acted.has(id) || visible.has(id));
      // La fiche ouverte se ferme si son entree a quitte la vue (archivee, jetee, restauree, purgee, deplacee).
      if (state.selectedEntryId !== null && !visible.has(state.selectedEntryId)) {
        state.selectedEntryId = null;
        forgetAttachments();
      }
      scheduleFlash(bulkDoneMessage(action, count, TRASH_RETENTION_DAYS, tag));
    } catch (cause) {
      state.bulkError = cause instanceof Error ? cause.message : 'Action impossible';
    } finally {
      state.bulkBusy = false;
      render();
    }
  }

  async function handleBulkRepromptSubmit(form: HTMLFormElement): Promise<void> {
    const pending = state.bulkReprompt;
    if (state.envelope === null || pending === null || state.bulkBusy) return;
    const masterPassword = String(new FormData(form).get('masterPassword') ?? '');
    state.bulkBusy = true;
    state.bulkError = null;
    render();
    let confirmed = false;
    try {
      confirmed = await verifyMasterPassword(state.envelope, masterPassword);
      if (!confirmed) state.bulkError = 'Mot de passe maitre incorrect.';
    } catch (cause) {
      state.bulkError = cause instanceof Error ? cause.message : 'Verification impossible';
    } finally {
      state.bulkBusy = false;
    }
    // Seulement si la selection n'a pas change pendant la derivation (cocher, decocher ou quitter annule le geste).
    if (confirmed && state.bulkReprompt === pending) {
      await runBulk(pending.action, pending.folderId, pending.tag);
      return;
    }
    // Re-rendu complet : le mot de passe tape ne reste pas dans le DOM.
    render();
  }

  async function handleRepromptSubmit(form: HTMLFormElement): Promise<void> {
    const entryId = state.selectedEntryId;
    if (state.envelope === null || entryId === null || state.repromptBusy) return;
    const masterPassword = String(new FormData(form).get('masterPassword') ?? '');
    state.repromptBusy = true;
    state.repromptError = null;
    render();
    try {
      if (await verifyMasterPassword(state.envelope, masterPassword)) {
        // Seulement si l'utilisateur est reste sur la meme entree pendant la derivation.
        if (state.selectedEntryId === entryId) state.repromptConfirmedId = entryId;
      } else {
        state.repromptError = 'Mot de passe maitre incorrect.';
      }
    } catch (cause) {
      state.repromptError = cause instanceof Error ? cause.message : 'Verification impossible';
    } finally {
      state.repromptBusy = false;
      // Re-rendu complet : le mot de passe tape ne reste pas dans le DOM.
      render();
    }
  }

  /** La liste des pieces jointes est a relire (ajout, suppression, synchro, verrouillage) : un chargement en vol est jete. */
  function forgetAttachments(): void {
    state.attachmentsEntryId = null;
    attachmentsLoadingFor = null;
    attachmentsGeneration++;
  }

  /** Ferme l'apercu d'une piece jointe : l'URL `blob:` de l'image est revoquee, le texte dechiffre oublie. */
  function closeAttachmentPreview(): void {
    if (state.attachmentPreview?.url) URL.revokeObjectURL(state.attachmentPreview.url);
    state.attachmentPreview = null;
  }

  /** Dialogue d'apercu : image par son URL `blob:` au type de sa signature, texte echappe, ou la raison de l'absence d'apercu. */
  function renderAttachmentPreview(): string {
    const shown = state.attachmentPreview;
    if (shown === null) return '';
    const { preview } = shown;
    const body =
      preview.kind === 'image' && shown.url !== null
        ? `<div class="attachment-preview-image"><img src="${escapeHtml(shown.url)}" alt="Apercu de ${escapeHtml(shown.fileName)}" data-attachment-preview="image" /></div>`
        : preview.kind === 'text'
          ? `<pre class="attachment-preview-text" data-attachment-preview="text" tabindex="0">${escapeHtml(preview.text)}</pre>`
          : `<p class="wifi-qr-problem" data-attachment-preview="none">${escapeHtml(preview.kind === 'none' ? preview.reason : 'Apercu indisponible.')}</p>`;
    return `<div class="modal-backdrop attachment-preview-backdrop" data-action="close-attachment-preview">
      <section class="modal glass attachment-preview-panel" role="dialog" aria-modal="true" aria-labelledby="attachment-preview-title" aria-describedby="attachment-preview-hint">
        <div class="modal-head">
          <h2 id="attachment-preview-title">${ICON_FILE}<span title="${escapeHtml(shown.fileName)}">${escapeHtml(shown.fileName)}</span></h2>
          <button class="icon-btn" data-action="close-attachment-preview" title="Fermer (Echap)" aria-label="Fermer l’apercu">${ICON_CLOSE}</button>
        </div>
        ${body}
        <p class="muted wifi-qr-hint" id="attachment-preview-hint">${formatFileSize(shown.size)} · apercu dechiffre en memoire sur cet appareil : rien n’est ecrit sur le disque, et il est oublie a la fermeture.</p>
        <div class="wifi-qr-actions">
          <button class="secondary" data-action="download-attachment" data-name="${escapeHtml(shown.fileName)}" data-id="${escapeHtml(shown.entryId)}" data-attachment="${escapeHtml(shown.attachmentId)}">${ICON_DOWNLOAD} Telecharger</button>
        </div>
      </section>
    </div>`;
  }

  /** Charge (dechiffre) les noms des pieces jointes de l'entree affichee, puis re-rend si elle l'est toujours. */
  async function loadAttachments(entryId: string): Promise<void> {
    const vaultStore = state.vaultStore;
    if (vaultStore === null) return;
    const generation = ++attachmentsGeneration;
    let attachments: AttachmentSummary[] = [];
    try {
      attachments = await vaultStore.listAttachments(entryId);
    } catch {
      scheduleFlash('Piece jointe illisible (alteree ?) : ignoree');
    }
    // Un ajout, une suppression ou une synchro a oublie la liste pendant la lecture : celle-ci est perimee.
    if (generation !== attachmentsGeneration) return;
    attachmentsLoadingFor = null;
    if (state.vaultStore !== vaultStore || state.selectedEntryId !== entryId) return;
    state.attachments = attachments;
    state.attachmentsEntryId = entryId;
    // Editeur ouvert entre-temps (« Nouvelle entree » juste apres un enregistrement) : la fiche est cachee, la liste
    // attend le prochain rendu -- en rendre un maintenant effacerait la saisie.
    if (!inputInProgress()) render();
  }

  /** Pieces jointes chiffrees de l'entree : liste, ajout (bouton ou glisser-deposer), telechargement, suppression. */
  /** Etiquettes de la fiche : chacune ouvre la vue de son etiquette. */
  function renderTagsDetail(entry: VaultItem): string {
    const tags = readTags(entry.secrets);
    if (tags.length === 0) return '';
    const chips = tags
      .map((tag) => `<button class="tag-chip" data-action="select-tag" data-id="${escapeHtml(tag)}" data-name="${escapeHtml(tag)}" title="Voir les entrees « ${escapeHtml(tag)} »">${ICON_TAG}<span>${escapeHtml(tag)}</span></button>`)
      .join('');
    return `<div class="field" data-section="tags"><label>Etiquettes</label><div class="tag-chips">${chips}</div></div>`;
  }

  function renderAttachments(entry: VaultItem): string {
    const loaded = state.attachmentsEntryId === entry.id;
    if (!loaded && attachmentsLoadingFor !== entry.id) {
      attachmentsLoadingFor = entry.id;
      queueMicrotask(() => void loadAttachments(entry.id));
    }
    const attachments = loaded ? state.attachments : [];
    const busy = state.attachmentBusyEntryId === entry.id;
    const rows = attachments
      .map((attachment) => `<li class="attachment-row">
        <span class="attachment-icon">${ICON_FILE}</span>
        <div class="attachment-meta">
          <span class="attachment-name" title="${escapeHtml(attachment.fileName)}">${escapeHtml(attachment.fileName)}</span>
          <span class="muted">${formatFileSize(attachment.size)} · ajoute le ${formatTimestamp(attachment.createdAt)}</span>
        </div>
        <button class="icon-btn" data-action="preview-attachment" data-name="${escapeHtml(attachment.fileName)}" data-id="${entry.id}" data-attachment="${escapeHtml(attachment.id)}" title="Apercu, sans rien ecrire sur le disque" aria-label="Apercu de ${escapeHtml(attachment.fileName)}">${ICON_EYE}</button>
        <button class="icon-btn" data-action="download-attachment" data-name="${escapeHtml(attachment.fileName)}" data-id="${entry.id}" data-attachment="${escapeHtml(attachment.id)}" title="Telecharger" aria-label="Telecharger ${escapeHtml(attachment.fileName)}">${ICON_DOWNLOAD}</button>
        <button class="icon-btn" data-action="delete-attachment" data-name="${escapeHtml(attachment.fileName)}" data-id="${entry.id}" data-attachment="${escapeHtml(attachment.id)}" title="Supprimer" aria-label="Supprimer ${escapeHtml(attachment.fileName)}">${ICON_TRASH}</button>
      </li>`)
      .join('');
    const status = busy
      ? '<p class="muted attachment-hint">Chiffrement en cours…</p>'
      : !loaded
        ? '<p class="muted attachment-hint">Dechiffrement…</p>'
        : attachments.length === 0
          ? `<p class="muted attachment-hint">Glissez des fichiers ici ou ajoutez-en. Chiffres sur cet appareil avant tout stockage ou synchro, ${formatFileSize(MAX_ATTACHMENT_BYTES)} maximum par fichier.</p>`
          : '';
    return `<div class="typed-section attachments-section" data-dropzone="attachments" data-id="${entry.id}">
      <div class="typed-section-head">
        <h3 class="attachments-title">${ICON_PAPERCLIP}<span>Pieces jointes${attachments.length > 0 ? ` (${attachments.length})` : ''}</span></h3>
        <label class="link-btn file-btn ${occupe(busy)}">${ICON_PLUS} Ajouter
          <input type="file" multiple data-input="attach-files" data-id="${entry.id}" hidden ${occupe(busy)} />
        </label>
      </div>
      ${rows.length > 0 ? `<ul class="attachment-list">${rows}</ul>` : ''}
      ${status}
    </div>`;
  }

  /**
   * Passkeys du compte, gardees dans les secrets chiffres de l'entree (R13).
   * La cle privee n'est NI affichee, NI copiable : rien ici ne l'expose. Tant
   * que Klavox ne sait pas la presenter a un site (extension / Credential
   * Provider, Phase 3/4), la section le dit plutot que de le laisser croire.
   */
  function renderPasskeys(entry: VaultItem): string {
    const passkeys = readPasskeys(entry.secrets);
    if (passkeys.length === 0) return '';
    const rows = passkeys
      .map((passkey, index) => `<li class="history-row">
        <div class="history-value">
          <span>${escapeHtml(passkeyLabel(passkey))}</span>
          <span class="muted">${escapeHtml(passkey.rpId || 'domaine inconnu')}${passkey.createdAt > 0 ? ` — creee le ${formatTimestamp(passkey.createdAt)}` : ''}</span>
        </div>
        <span class="health-badge info">Cle privee scellee</span>
        <button class="icon-btn" data-action="remove-passkey" data-name="${escapeHtml(passkeyLabel(passkey))}" data-id="${entry.id}" data-index="${index}" title="Retirer" aria-label="Retirer la passkey ${escapeHtml(passkeyLabel(passkey))}">${ICON_TRASH}</button>
      </li>`)
      .join('');
    return `<div class="typed-section">
      <div class="typed-section-head">
        <h3 class="attachments-title">${ICON_FINGERPRINT}<span>Passkeys (${passkeys.length})</span></h3>
      </div>
      <ul class="history-list">${rows}</ul>
      <p class="muted">Conservees et re-exportables (FIDO CXF). Les presenter a un site demandera l'extension navigateur ou l'application mobile.</p>
    </div>`;
  }

  /** Anciens mots de passe (secrets chiffres de l'entree), replies par defaut, masques tant que l'entree l'est. */
  function renderPasswordHistory(entry: VaultItem, revealed: boolean): string {
    const history = readPasswordHistory(entry.secrets);
    if (history.length === 0) return '';
    const open = state.historyOpenEntryId === entry.id;
    // Sans mot de passe courant, la ligne « Mot de passe » (et son bouton d'affichage) est absente.
    const reveal = open && !entry.secrets.password
      ? `<button class="icon-btn" data-action="toggle-reveal" data-id="${entry.id}" title="${revealed ? 'Masquer' : 'Afficher'} les anciens mots de passe">${revealed ? ICON_EYE_OFF : ICON_EYE}</button>`
      : '';
    const rows = history
      .map((item, index) => `<li class="history-row">
        <div class="history-value">
          <span class="mono">${revealed ? escapeHtml(item.password) : '•'.repeat(10)}</span>
          <span class="muted">Remplace le ${formatTimestamp(item.changedAt)}</span>
        </div>
        ${largeTypeButton(entry, `history:${index}`)}
        <button class="icon-btn" data-action="copy-entry-field" data-id="${entry.id}" data-field="history:${index}" title="Copier" aria-label="Copier l'ancien mot de passe">${ICON_COPY}</button>
        <button class="icon-btn" data-action="restore-password" data-id="${entry.id}" data-index="${index}" title="Restaurer" aria-label="Restaurer ce mot de passe">${ICON_REFRESH}</button>
      </li>`)
      .join('');
    return `<div class="typed-section history-section">
      <div class="typed-section-head">
        <button class="history-toggle" data-action="toggle-history" data-id="${entry.id}" aria-expanded="${open}">
          ${ICON_CLOCK}<span class="history-title">Historique des mots de passe (${history.length})</span>
        </button>${reveal}
      </div>
      ${open ? `<ul class="history-list">${rows}</ul>
      <button class="link-btn" data-action="clear-history" data-id="${entry.id}">Effacer l'historique</button>` : ''}
    </div>`;
  }

  /**
   * Versions precedentes de l'entree (`item-history.ts`), repliees par defaut. Chaque ligne dit QUAND la version a ete
   * remplacee et QUELS champs ont change depuis -- des libelles, jamais une valeur. Le contenu d'une version n'entre
   * dans le DOM que deplie a la demande (`renderVersionFields`), une version a la fois, secrets masques tant que
   * l'entree l'est. Restaurer verse le contenu courant dans les versions : la restauration se defait.
   */
  function renderItemHistory(entry: VaultItem, revealed: boolean): string {
    const versions = readItemHistory(entry.secrets);
    if (versions.length === 0) return '';
    const open = state.versionsOpenEntryId === entry.id;
    const rows = versions
      .map((version, index) => {
        const replacedBy = index === 0 ? entry.secrets : versions[index - 1]!.secrets;
        const changes = itemVersionChanges(version.secrets, replacedBy);
        const shown = state.versionShownIndex === index;
        return `<li class="history-row version-row${shown ? ' version-row-open' : ''}">
        <div class="history-value">
          <span>Version remplacee le ${formatTimestamp(version.replacedAt)}</span>
          <span class="muted">${changes.length > 0 ? `Modifie depuis : ${escapeHtml(changes.join(', '))}` : 'Contenu identique'}</span>
        </div>
        <button class="icon-btn" data-action="show-version" data-id="${entry.id}" data-index="${index}" aria-expanded="${shown}" aria-controls="version-fields-${index}" title="${shown ? 'Replier' : 'Voir'} le contenu" aria-label="${shown ? 'Replier' : 'Voir'} le contenu de la version du ${formatTimestamp(version.replacedAt)}"><span class="version-chevron">${ICON_CHEVRON_RIGHT}</span></button>
        <button class="icon-btn" data-action="restore-version" data-id="${entry.id}" data-index="${index}" title="Restaurer cette version" aria-label="Restaurer la version du ${formatTimestamp(version.replacedAt)}">${ICON_RESTORE}</button>
        ${shown ? renderVersionFields(entry, index, itemVersionFields(version.secrets, state.versionCompare ? entry.secrets : replacedBy), revealed, state.versionCompare) : ''}
      </li>`;
      })
      .join('');
    return `<div class="typed-section history-section" data-section="item-history">
      <div class="typed-section-head">
        <button class="history-toggle" data-action="toggle-versions" data-id="${entry.id}" aria-expanded="${open}">
          ${ICON_CLOCK}<span class="history-title">Versions precedentes (${versions.length})</span>
        </button>
      </div>
      ${open ? `<ul class="history-list">${rows}</ul>
      <p class="muted">Gardees chiffrees avec l'entree, les ${MAX_ITEM_VERSIONS} plus recentes. Restaurer reprend le contenu de la version ; passkeys, archive et re-demande du mot de passe maitre restent ceux d'aujourd'hui.</p>
      <button class="link-btn" data-action="clear-versions" data-id="${entry.id}">Effacer les versions</button>` : ''}
    </div>`;
  }

  /**
   * Un cote d'une difference mot a mot (`diffWords`) : la version montre le commun et le retire (`<del>`), aujourd'hui
   * le commun et l'ajoute (`<ins>`). Tout est echappe ; un lecteur d'ecran entend « retire » / « ajoute » (balises
   * muettes par defaut).
   */
  function diffSideHtml(segments: readonly DiffSegment[], side: 'before' | 'after'): string {
    return segments
      .map((segment) => {
        if (segment.kind === 'same') return escapeHtml(segment.text);
        if (side === 'before' && segment.kind === 'removed') {
          return `<del class="version-diff-removed"><span class="visually-hidden">[retire : </span>${escapeHtml(segment.text)}<span class="visually-hidden">]</span></del>`;
        }
        if (side === 'after' && segment.kind === 'added') {
          return `<ins class="version-diff-added"><span class="visually-hidden">[ajoute : </span>${escapeHtml(segment.text)}<span class="visually-hidden">]</span></ins>`;
        }
        return '';
      })
      .join('');
  }

  /**
   * Resume d'une difference mot a mot sous le champ. Comparee a aujourd'hui : combien de caracteres la restauration
   * remettrait et retirerait. Vue ordinaire : ce que la modification suivante a retire et ajoute.
   */
  function diffCountsHtml(segments: readonly DiffSegment[], compare: boolean): string {
    const { removed, added } = diffCounts(segments);
    const part = (count: number, word: string): string => `${count} caractere${count > 1 ? 's' : ''} ${word}${count > 1 ? 's' : ''}`;
    return `<p class="muted version-diff-counts" data-role="version-diff-counts">${compare ? 'Depuis cette version' : 'A la modification suivante'} : ${part(removed, 'retire')}, ${part(added, 'ajoute')}.</p>`;
  }

  /**
   * Contenu d'une version depliee (`itemVersionFields`) : chaque champ, marque s'il a change depuis. Un secret reste
   * masque tant que l'entree l'est (meme oeil que la fiche) ; Copier et Grand format ne portent qu'une reference
   * `version:<i>:<champ>`, relue dans le coffre au clic -- jamais la valeur dans un attribut.
   * « Comparer a aujourd'hui » (`compare`) : chaque champ cote a cote avec sa valeur d'aujourd'hui, ce qui differe
   * marque -- exactement ce qu'une restauration changerait. La colonne d'aujourd'hui ne se copie pas d'ici : la fiche
   * le fait deja.
   * Vue ordinaire : un champ « Modifie depuis » porte, dans sa propre valeur, ce que la modification suivante a retire
   * (`<del>`), et le compte -- la version reste lue seule, rien de la suivante n'y entre.
   */
  function renderVersionFields(entry: VaultItem, index: number, fields: readonly ItemVersionField[], revealed: boolean, compare: boolean): string {
    const reveal = fields.some((field) => field.sensitive && (field.value.length > 0 || (compare && field.after.length > 0)))
      ? `<button class="link-btn" data-action="toggle-reveal" data-id="${entry.id}">${revealed ? 'Masquer' : 'Afficher'} les secrets</button>`
      : '';
    const shownValue = (field: ItemVersionField, value: string, empty: string, marked: string | null = null): string =>
      value.length === 0
        ? `<span class="muted">${empty}</span>`
        : field.sensitive && !revealed
          ? `<span class="mono">${'•'.repeat(10)}</span>`
          : field.multiline
            ? `<pre class="version-multiline${field.sensitive ? ' mono' : ''}">${marked ?? escapeHtml(value)}</pre>`
            : `<span class="${field.sensitive ? 'mono' : ''}">${marked ?? escapeHtml(value)}</span>`;
    const badge = compare ? 'Differe d’aujourd’hui' : 'Modifie depuis';
    const rows = fields
      .map((field) => {
        // Valeur presente des deux cotes, differente et visible -- note, identifiant, adresse, mot de passe oeil ouvert :
        // ce qui differe, mot a mot, d'aujourd'hui (comparee) ou de la version suivante (ordinaire). Un champ seulement
        // renomme (meme valeur) n'a rien a marquer.
        const diff = field.value !== field.after && field.value.length > 0 && field.after.length > 0 && (!field.sensitive || revealed)
          ? diffWords(field.value, field.after)
          : null;
        const value = shownValue(field, field.value, 'Vide dans cette version', diff === null ? null : diffSideHtml(diff, 'before'));
        const copy = field.ref === null
          ? ''
          : `<button class="icon-btn" data-action="copy-entry-field" data-id="${entry.id}" data-field="version:${index}:${escapeHtml(field.ref)}" title="Copier" aria-label="Copier ${escapeHtml(field.label)} de cette version">${ICON_COPY}</button>`;
        const own = `<div class="field-row">${value}${field.ref === null ? '' : largeTypeButton(entry, `version:${index}:${field.ref}`)}${copy}</div>`;
        const content = compare
          ? `<div class="version-compare">
            <div class="version-side"><span class="version-side-label">Cette version</span>${own}</div>
            <div class="version-side version-side-today"><span class="version-side-label">Aujourd’hui</span><div class="field-row">${shownValue(field, field.after, 'Vide aujourd’hui', diff === null ? null : diffSideHtml(diff, 'after'))}</div></div>
          </div>${diff === null ? '' : diffCountsHtml(diff, true)}`
          : `${own}${diff === null ? '' : diffCountsHtml(diff, false)}`;
        return `<div class="version-field${field.changed ? ' version-field-changed' : ''}">
          <dt>${escapeHtml(field.label)}${field.changed ? `<span class="health-badge info">${badge}</span>` : ''}</dt>
          <dd>${content}</dd>
        </div>`;
      })
      .join('');
    const differing = fields.filter((field) => field.changed).length;
    const summary = compare
      ? `<p class="muted version-compare-summary" data-role="version-compare-summary">${differing === 0
        ? 'Identique a aujourd’hui : la restaurer ne changerait aucun champ.'
        : `${differing} champ${differing > 1 ? 's' : ''} differe${differing > 1 ? 'nt' : ''} d’aujourd’hui : la restauration ${differing > 1 ? 'les' : 'le'} reprendrait.`}</p>`
      : '';
    return `<div class="version-fields${compare ? ' version-fields-compare' : ''}" id="version-fields-${index}" data-section="version-fields">
      <div class="version-fields-head">
        <button class="link-btn" data-action="compare-version" aria-pressed="${compare}" aria-controls="version-fields-${index}">Comparer a aujourd’hui</button>
      </div>
      ${summary}
      <dl>${rows}</dl>
      ${reveal}
    </div>`;
  }

  /**
   * Service d'une cle d'API reconnu a son prefixe publie (`ghp_`, `sk_live_`…) : nomme a cote de la cle masquee, il dit
   * ou la revoquer sans jamais l'afficher. Le badge ne porte que le nom du service, rien de la cle.
   */
  function apiKeyProviderBadge(entry: VaultItem): string {
    if (entry.type !== 'api-key') return '';
    const provider = apiKeyProvider(entry.secrets.password ?? '');
    return provider === null ? '' : `<span class="health-badge info" data-badge="api-key-provider" title="${escapeHtml(provider.kind)}">${escapeHtml(provider.service)}</span>`;
  }

  /** Cle d'un reseau Wi-Fi qu'aucun appareil n'accepterait pour la securite declaree (`wifiPasswordProblem`). */
  function wifiPasswordBadge(entry: VaultItem): string {
    if (entry.type !== 'wifi') return '';
    const problem = wifiPasswordProblem(readTypedFields(entry.secrets, 'wifi')['security'] ?? '', entry.secrets.password ?? '');
    return problem === null ? '' : `<span class="health-badge warn" data-badge="wifi-password" title="${escapeHtml(problem)}">Cle non conforme</span>`;
  }

  function renderTypedDetail(entry: VaultItem, group: TypedFieldGroup, revealed: boolean): string {
    const fields = readTypedFields(entry.secrets, group);
    const specs = fieldSpecs(group).filter((spec) => fields[spec.key] !== undefined);
    if (specs.length === 0) return '';
    const rows = specs
      .map((spec) => {
        const value = fields[spec.key]!;
        let shown = spec.sensitive && !revealed ? '•'.repeat(8) : value;
        let extra = '';
        if (group === 'card' && spec.key === 'number') {
          shown = revealed ? formatCardNumber(value) : maskCardNumber(value);
          const brand = detectCardBrand(value);
          extra = [
            brand ? `<span class="health-badge info">${escapeHtml(brand)}</span>` : '',
            isLuhnValid(value) ? '' : '<span class="health-badge warn">Numero non conforme</span>',
          ].join('');
        }
        if (group === 'card' && spec.key === 'expiry' && isCardExpired(value)) extra = '<span class="health-badge danger">Expiree</span>';
        if (group === 'identity' && isDocumentExpiryKey(spec.key) && isDocumentExpired(value)) extra = '<span class="health-badge danger">Expiree</span>';
        if (group === 'api-key' && spec.key === 'apiExpiry' && isDocumentExpired(value)) extra = '<span class="health-badge danger" data-badge="api-key-expiry">Expiree</span>';
        // La veille du premier jour de validite, la cle n'est pas encore acceptee par le service.
        if (group === 'api-key' && spec.key === 'apiValidFrom' && !isDocumentExpired(value, Date.now() + 24 * 60 * 60 * 1000)) {
          extra = '<span class="health-badge warn" data-badge="api-key-valid-from">Pas encore valable</span>';
        }
        if (group === 'software-license' && spec.key === 'softwareExpiry' && isDocumentExpired(value)) {
          extra = '<span class="health-badge danger" data-badge="license-expiry">Expiree</span>';
        }
        // Serveur ou console en `http://` : le mot de passe qu'on y tape circule en clair. On le DIT, sans bloquer.
        if (group === 'server' && (spec.key === 'serverUrl' || spec.key === 'adminConsoleUrl') && isInsecureUri(value)) {
          extra = '<span class="health-badge warn" data-badge="server-insecure" title="Adresse en http:// : le mot de passe y circule en clair">Non chiffree</span>';
        }
        if (group === 'membership' && spec.key === 'membershipKind') {
          const kind = membershipKind(value);
          shown = kind === null ? value : membershipKindLabel(kind);
        }
        if (group === 'membership' && spec.key === 'membershipExpiry' && isMembershipExpired(value)) {
          extra = '<span class="health-badge danger" data-badge="membership-expiry">Expiree</span>';
        }
        if (group === 'email' && spec.key === 'incomingProtocol') {
          const protocol = emailProtocol(value);
          shown = protocol === null ? value : emailProtocolLabel(protocol);
        }
        if (group === 'email' && (spec.key === 'incomingSecurity' || spec.key === 'smtpSecurity')) {
          const security = emailSecurity(value);
          shown = security === null ? value : emailSecurityLabel(security);
          // Sans TLS, identifiant et mot de passe partent en clair a chaque releve ou envoi : on le DIT, sans bloquer.
          if (security === 'none') {
            extra = '<span class="health-badge warn" data-badge="email-insecure" title="Connexion sans TLS : le mot de passe y circule en clair">Non chiffree</span>';
          }
        }
        if (group === 'wifi' && spec.key === 'security') {
          const security = wifiSecurity(value);
          shown = security === null ? value : wifiSecurityLabel(security);
          // WEP se casse en minutes, un reseau ouvert circule en clair : on le DIT, sans bloquer.
          if (security === 'wep') extra = '<span class="health-badge warn" data-badge="wifi-security">Chiffrement obsolete</span>';
          if (security === 'unsecured') extra = '<span class="health-badge warn" data-badge="wifi-security">Trafic non chiffre</span>';
        }
        if (group === 'wifi' && spec.key === 'hidden') shown = 'Oui';
        if (group === 'bank' && spec.key === 'iban') {
          shown = revealed ? formatIban(value) : maskIban(value);
          // Un IBAN importe peut etre faux : on le DIT, l'editeur refusera de l'enregistrer tel quel.
          if (!isIbanValid(value)) extra = '<span class="health-badge warn" data-badge="bank-iban">IBAN non conforme</span>';
        }
        if (group === 'bank' && spec.key === 'accountType') {
          const accountType = bankAccountType(value);
          shown = accountType === null ? value : bankAccountTypeLabel(accountType);
        }
        if (group === 'wallet' && spec.sensitive) return renderWalletSecret(entry, spec, value, revealed);
        if (group === 'database' && spec.key === 'engine') {
          const engine = databaseEngine(value);
          shown = engine === null ? value : databaseEngineLabel(engine);
        }
        const mono =
          (spec.sensitive && group !== 'medical') || (group === 'medical' && spec.key === 'recordDate') || spec.key === 'expiry' || isDocumentExpiryKey(spec.key) || (group === 'api-key' && isApiKeyDateKey(spec.key)) || (group === 'software-license' && isSoftwareLicenseDateKey(spec.key)) ||
          (group === 'membership' && (isMembershipDateKey(spec.key) || spec.key === 'membershipNumber' || spec.key === 'additionalNumber'))
            ? 'mono'
            : '';
        // Un bloc de licence revele garde ses lignes et passe a la ligne n'importe ou plutot que deborder.
        const block = group === 'software-license' && spec.key === 'licenseKey' && revealed ? ' license-key' : '';
        // Un motif ou des remarques reveles gardent leurs paragraphes et coupent aux mots, comme un texte.
        const prose = group === 'medical' && spec.multiline === true && revealed ? ' medical-text' : '';
        // Une liste fermee n'a rien d'utile a copier (un code, pas une valeur a coller). La valeur n'est pas dans le
        // bouton : elle est lue dans l'entree au clic (`entryFieldValue`), un numero de carte masque reste hors du DOM.
        const copy = spec.options !== undefined ? '' : `
          <button class="icon-btn" data-action="copy-entry-field" data-id="${entry.id}" data-field="typed:${group}:${spec.key}" title="Copier" aria-label="Copier ${escapeHtml(spec.label)}">${ICON_COPY}</button>`;
        return `<div class="field">
        <label>${escapeHtml(spec.label)}</label>
        <div class="field-row"><span class="${mono}${block}${prose}" data-typed-field="${spec.key}">${escapeHtml(shown)}</span>${extra}${largeTypeButton(entry, `typed:${group}:${spec.key}`)}${copy}
        </div></div>`;
      })
      .join('');
    // Le bouton de la ligne « Mot de passe » pilote deja l'affichage : pas de doublon.
    const reveal = specs.some((spec) => spec.sensitive) && !entry.secrets.password
      ? `<button class="icon-btn" data-action="toggle-reveal" data-id="${entry.id}" title="${revealed ? 'Masquer' : 'Afficher'} les champs masques">${revealed ? ICON_EYE_OFF : ICON_EYE}</button>`
      : '';
    return `<div class="typed-section">
      <div class="typed-section-head"><h3>${TYPED_GROUP_TITLES[group]}</h3>${group === 'wifi' ? wifiQrButton(entry) : ''}${reveal}</div>
      ${rows}${group === 'database' ? renderDatabaseUri(entry, fields) : ''}
    </div>`;
  }

  /**
   * Adresse de connexion d'une base, SANS mot de passe : affichee en clair, elle peut aller dans le bouton (rien de
   * secret). Le client a qui on la colle demandera le mot de passe, copie a part depuis sa ligne.
   */
  function renderDatabaseUri(entry: VaultItem, fields: Readonly<Record<string, string>>): string {
    const uri = databaseConnectionUri(fields, entry.secrets.username ?? '');
    if (uri === null) return '';
    return `<div class="field">
        <label>Adresse de connexion (sans mot de passe)</label>
        <div class="field-row"><span class="mono database-uri" data-database-uri>${escapeHtml(uri)}</span>
          <button class="icon-btn" data-action="copy" data-name="adresse de connexion" data-value="${escapeHtml(uri)}" title="Copier" aria-label="Copier l’adresse de connexion">${ICON_COPY}</button>
        </div></div>`;
  }

  /**
   * Phrase de recuperation et phrase de passe BIP39 dans la fiche : qui les lit vide le portefeuille. Masquees, elles ne
   * sont PAS dans le DOM (ni texte ni attribut) ; « Copier » les lit dans l'entree au moment du clic, comme une cle
   * privee SSH. Revelee, la phrase s'affiche mot par mot, numerotee, comme sur la feuille de sauvegarde.
   */
  function renderWalletSecret(entry: VaultItem, spec: TypedFieldSpec, value: string, revealed: boolean): string {
    const phrase = spec.key === 'recoveryPhrase';
    const check = phrase ? checkRecoveryPhrase(value) : null;
    const label = check === null ? null : recoveryPhraseLabel(check);
    const problem = phrase ? recoveryPhraseProblem(value) : null;
    // Une phrase importee peut etre fausse : on le DIT, l'editeur refusera de l'enregistrer telle quelle.
    const badge = problem !== null
      ? `<span class="health-badge warn" data-badge="wallet-phrase" title="${escapeHtml(problem)}">Phrase non conforme</span>`
      : label !== null ? `<span class="health-badge info" data-badge="wallet-phrase-format">${escapeHtml(label)}</span>` : '';
    const words = value.split(' ');
    const shown = !revealed
      ? `<span class="mono" data-typed-field="${spec.key}">${'•'.repeat(10)}</span>`
      : phrase && words.length > 1
        ? `<ol class="seed-words mono" data-typed-field="${spec.key}">${words.map((word) => `<li>${escapeHtml(word)}</li>`).join('')}</ol>`
        : `<span class="mono" data-typed-field="${spec.key}">${escapeHtml(value)}</span>`;
    return `<div class="field">
        <label>${escapeHtml(spec.label)}</label>
        <div class="field-row">${shown}${badge}${largeTypeButton(entry, `typed:wallet:${spec.key}`)}
          <button class="icon-btn" data-action="copy-wallet-secret" data-name="${escapeHtml(spec.label)}" data-id="${entry.id}" data-key="${spec.key}" title="Copier" aria-label="Copier ${escapeHtml(spec.label)}">${ICON_COPY}</button>
        </div></div>`;
  }

  /**
   * Cle SSH dans la fiche : type, empreinte et cle publique en clair (rien de secret), cle privee masquee jusqu'a
   * l'oeil. Masquee, elle n'est PAS dans le DOM ; « Copier » la lit dans l'entree au moment du clic.
   */
  function renderSshKeyDetail(entry: VaultItem, revealed: boolean): string {
    const { privateKey, publicKey } = readSshKey(entry.secrets);
    if (privateKey.length === 0 && publicKey.length === 0) return '';
    const info = parseSshPublicKey(publicKey);
    const encrypted = privateKey.length > 0 ? isPrivateKeyEncrypted(privateKey) : null;
    const reveal = privateKey.length > 0
      ? `<button class="icon-btn" data-action="toggle-reveal" data-id="${entry.id}" title="${revealed ? 'Masquer' : 'Afficher'} la cle privee">${revealed ? ICON_EYE_OFF : ICON_EYE}</button>`
      : '';
    return `<div class="typed-section" data-section="ssh-key">
      <div class="typed-section-head"><h3>Cle SSH</h3>${reveal}</div>
      ${info !== null ? `<div class="field"><label>Type</label><div class="field-row"><span data-ssh="algorithm">${escapeHtml(info.label)}</span>${encrypted === true ? '<span class="health-badge info">Protegee par phrase de passe</span>' : ''}</div></div>
      <div class="field"><label>Empreinte</label><div class="field-row"><span class="mono" data-ssh="fingerprint">${escapeHtml(info.fingerprint)}</span>
        <button class="icon-btn" data-action="copy" data-name="empreinte" data-value="${escapeHtml(info.fingerprint)}" title="Copier" aria-label="Copier l’empreinte">${ICON_COPY}</button></div></div>` : ''}
      ${publicKey.length > 0 ? `<div class="field"><label>Cle publique</label><div class="field-row"><span class="mono ssh-public" data-ssh="public">${escapeHtml(publicKey)}</span>
        <button class="icon-btn" data-action="copy" data-name="cle publique" data-value="${escapeHtml(publicKey)}" title="Copier" aria-label="Copier la cle publique">${ICON_COPY}</button></div>
        ${info === null ? '<span class="health-badge warn">Cle publique illisible</span>' : ''}</div>` : ''}
      ${privateKey.length > 0 ? `<div class="field"><label>Cle privee</label><div class="field-row">
        ${revealed ? `<pre class="mono ssh-private" data-ssh="private">${escapeHtml(privateKey)}</pre>` : `<span class="mono" data-ssh="private">${'•'.repeat(10)}</span>`}
        <button class="icon-btn" data-action="copy-ssh-private" data-id="${entry.id}" title="Copier" aria-label="Copier la cle privee">${ICON_COPY}</button></div></div>` : ''}
    </div>`;
  }

  /**
   * Cle SSH dans l'editeur. La cle privee deja enregistree reste masquee : la zone n'est rendue qu'a l'oeil (ou vide,
   * pour en coller une) ; masquee, le brouillon garde sa valeur. La cle publique se deduit d'une cle OpenSSH collee.
   */
  function renderSshKeyEditor(draft: EditorDraft): string {
    const showPrivate = state.revealDraftPassword || draft.privateKey.length === 0;
    const derived = completeSshKey(draft).publicKey;
    const info = parseSshPublicKey(derived);
    return `<fieldset class="typed-fields" data-section="ssh-key-editor">
          <legend>Cle SSH
            <button type="button" class="icon-btn" data-action="toggle-draft-reveal" title="${state.revealDraftPassword ? 'Masquer' : 'Afficher'} la cle privee">${state.revealDraftPassword ? ICON_EYE_OFF : ICON_EYE}</button>
          </legend>
          <label class="wide">Cle privee
            ${showPrivate
              ? `<textarea name="privateKey" rows="6" class="mono" spellcheck="false" autocomplete="off" placeholder="-----BEGIN OPENSSH PRIVATE KEY-----">${escapeHtml(draft.privateKey)}</textarea>`
              : `<span class="mono" data-ssh="private-masked">${'•'.repeat(10)}</span>`}
          </label>
          <label class="wide">Cle publique
            <input name="publicKey" class="mono" value="${escapeHtml(draft.publicKey)}" spellcheck="false" autocomplete="off" placeholder="${escapeHtml(draft.publicKey.length === 0 && derived.length > 0 ? derived : 'ssh-ed25519 AAAA… commentaire')}" aria-describedby="hint-ssh-key" />
          </label>
          <div class="wide ssh-keygen-row">
            <button type="button" class="secondary" data-action="generate-ssh-key-into-draft"${state.sshKeygenBusy ? ' disabled' : ''}>${ICON_REFRESH}<span>Generer une cle Ed25519</span></button>
          </div>
          <span class="field-hint wide" id="hint-ssh-key" data-hint="ssh-key">${escapeHtml(
            info !== null
              ? `${info.label} · ${info.fingerprint}${draft.publicKey.trim().length === 0 ? ' (deduite de la cle privee)' : ''}`
              : 'Collez la cle privee (fichier id_ed25519) : la cle publique d’une cle OpenSSH s’en deduit. Une cle protegee par phrase de passe le reste.',
          )}</span>
        </fieldset>`;
  }

  /**
   * Champs personnalises dans la vue de l'entree : un champ masque l'est jusqu'a l'oeil, chacun se copie. Une case a
   * cocher montre son etat (case et mot, jamais la valeur stockee `true`) : rien a copier ni a agrandir.
   */
  function renderCustomFieldsDetail(entry: VaultItem, revealed: boolean): string {
    const fields = readCustomFields(entry.secrets);
    if (fields.length === 0) return '';
    const rows = fields
      .map((field, index) => {
        if (isCheckboxField(field)) {
          const checked = field.value === 'true';
          return `<div class="field" data-custom-kind="boolean">
        <label>${escapeHtml(field.name)}</label>
        <div class="field-row"><span class="custom-checkbox-state${checked ? ' checked' : ''}" data-custom-value data-checked="${checked}"><span class="custom-checkbox-box" aria-hidden="true">${checked ? ICON_CHECK : ''}</span>${checkboxLabel(field.value)}</span></div></div>`;
        }
        const masked = field.hidden === true && !revealed;
        // Multiligne : les retours a la ligne se voient (sinon une cle ou des codes de secours s'affichent en une ligne).
        const classes = [field.hidden === true ? 'mono' : '', !masked && isMultilineCustomValue(field.value) ? 'custom-value-multiline' : ''].filter(Boolean).join(' ');
        return `<div class="field">
        <label>${escapeHtml(field.name)}</label>
        <div class="field-row"><span class="${classes}" data-custom-value>${masked ? '•'.repeat(8) : escapeHtml(field.value)}</span>${largeTypeButton(entry, `custom:${index}`)}
          <button class="icon-btn" data-action="copy-entry-field" data-id="${entry.id}" data-field="custom:${index}" title="Copier" aria-label="Copier ${escapeHtml(field.name)}">${ICON_COPY}</button>
        </div></div>`;
      })
      .join('');
    // Le bouton de la ligne « Mot de passe » pilote deja l'affichage : pas de doublon.
    const reveal = fields.some((field) => field.hidden === true) && !entry.secrets.password
      ? `<button class="icon-btn" data-action="toggle-reveal" data-id="${entry.id}" title="${revealed ? 'Masquer' : 'Afficher'} les champs masques">${revealed ? ICON_EYE_OFF : ICON_EYE}</button>`
      : '';
    return `<div class="typed-section" data-section="custom-fields">
      <div class="typed-section-head"><h3>Champs personnalises</h3>${reveal}</div>
      ${rows}
    </div>`;
  }

  function renderTypedInput(spec: TypedFieldSpec, draft: EditorDraft): string {
    const value = draft.fields[spec.key] ?? '';
    if (spec.options !== undefined) {
      return `<label class="${spec.wide ? 'wide' : ''}">${escapeHtml(spec.label)}
          <select name="field:${spec.key}">
            ${spec.options.map((option) => `<option value="${escapeHtml(option.value)}" ${option.value === value ? 'selected' : ''}>${escapeHtml(option.label)}</option>`).join('')}
          </select>
        </label>`;
    }
    if (spec.multiline) {
      // Comme la cle privee SSH : la valeur DEJA ENREGISTREE reste masquee (absente du formulaire, le brouillon la garde)
      // jusqu'a l'oeil. Une saisie en cours -- entree neuve, valeur changee, refusee a l'enregistrement -- reste ouverte :
      // on ne corrige pas une phrase qu'on ne voit plus.
      const saved = draft.id === null ? undefined : state.entries.find((entry) => entry.id === draft.id);
      const savedValue = saved === undefined ? '' : (readTypedFields(saved.secrets, typedFieldGroup(saved.type) ?? 'wallet')[spec.key] ?? '');
      const show = state.revealDraftPassword || !spec.sensitive || value.length === 0 || value !== savedValue;
      return `<label class="${spec.wide ? 'wide' : ''}">${escapeHtml(spec.label)}
          ${show
            ? `<textarea name="field:${spec.key}" rows="3" class="mono" spellcheck="false" autocomplete="off" autocapitalize="off" ${spec.placeholder ? `placeholder="${escapeHtml(spec.placeholder)}"` : ''}>${escapeHtml(value)}</textarea>`
            : `<span class="mono" data-masked-field="${spec.key}">${'•'.repeat(10)}</span>`}
        </label>`;
    }
    const cardNumber = spec.key === 'number' && draft.type === 'card';
    // Port : le port par defaut du moteur choisi en indication (laisse vide, le client prend ce meme port).
    const defaultPort =
      spec.key === 'port' && draft.type === 'database'
        ? databaseDefaultPort(draft.fields['engine'] ?? '')
        : spec.key === 'incomingPort' && draft.type === 'email'
          ? emailDefaultPort(emailProtocol(draft.fields['incomingProtocol'] ?? ''), emailSecurity(draft.fields['incomingSecurity'] ?? ''))
          : spec.key === 'smtpPort' && draft.type === 'email'
            ? emailDefaultPort('smtp', emailSecurity(draft.fields['smtpSecurity'] ?? ''))
            : null;
    const placeholder = defaultPort !== null ? String(defaultPort) : spec.placeholder;
    return `<label class="${spec.wide ? 'wide' : ''}">${escapeHtml(spec.label)}
          <input name="field:${spec.key}" type="${spec.sensitive && !state.revealDraftPassword ? 'password' : 'text'}" value="${escapeHtml(value)}" autocomplete="off"
            ${spec.inputMode ? `inputmode="${spec.inputMode}"` : ''} ${placeholder ? `placeholder="${escapeHtml(placeholder)}"` : ''}
            ${cardNumber ? 'data-input="card-number"' : ''} />
          ${cardNumber ? `<span class="field-hint" data-hint="card-number">${escapeHtml(cardNumberHint(value))}</span>` : ''}
          ${spec.key === 'ssid' && draft.type === 'wifi' ? '<span class="field-hint" data-hint="wifi-qr-paste">Collez ici le texte d’un code QR Wi-Fi (WIFI:…, lu sur l’etiquette de la box) ou une capture du code : nom, cle, securite et reseau masque se remplissent.</span>' : ''}
        </label>`;
  }

  /** Detection d'une adresse de l'editeur (rang 0 = « Site (URL) ») : defaut du coffre (nomme), sinon une regle de `uri-match.ts`. */
  function renderUriMatchSelect(draft: EditorDraft, index: number): string {
    const current = draft.uriRules[index] ?? '';
    const options = [['', `Par defaut (${URI_MATCH_SHORT[state.defaultUriMatch]})`], ...URI_MATCH_LABELS]
      .map(([value, label]) => `<option value="${value}" ${value === current ? 'selected' : ''}>${escapeHtml(label!)}</option>`)
      .join('');
    return `<select class="uri-match" name="uri-match:${index}" title="Detection" aria-label="Detection de l’adresse ${index + 1}" aria-describedby="hint-uri-match">${options}</select>`;
  }

  /**
   * Monter / descendre l'adresse de rang `position` (0 = « Site (URL) ») : seulement s'il y a une ligne de ce cote.
   * Monter la deuxieme adresse en fait le « Site (URL) » ; sa regle de detection la suit.
   */
  function renderUriMoveButtons(draft: EditorDraft, position: number): string {
    const count = 1 + draft.extraUris.length;
    const up = position > 0
      ? `<button type="button" class="icon-btn" data-action="move-uri" data-index="${position}" data-direction="-1" title="${position === 1 ? 'Faire le site principal' : 'Monter'}" aria-label="${position === 1 ? 'Faire de l’adresse 2 le site principal' : `Monter l’adresse ${position + 1}`}">${ICON_MOVE_UP}</button>`
      : '';
    const down = position < count - 1
      ? `<button type="button" class="icon-btn" data-action="move-uri" data-index="${position}" data-direction="1" title="Descendre" aria-label="Descendre l’adresse ${position + 1}">${ICON_MOVE_DOWN}</button>`
      : '';
    return up + down;
  }

  /**
   * Adresses supplementaires dans l'editeur (domaine de connexion distinct, application, ancien nom du site) : une
   * ligne par adresse avec son retrait, « Ajouter une adresse » en dernier. La premiere reste « Site (URL) ».
   */
  function renderExtraUrisEditor(draft: EditorDraft): string {
    const rows = draft.extraUris
      .map((uri, index) => `<div class="field-row" data-extra-uri="${index}">
          <input name="uri-extra:${index}" value="${escapeHtml(uri)}" placeholder="https://" aria-label="Adresse ${index + 2}" autocomplete="off" />
          ${renderUriMatchSelect(draft, index + 1)}
          ${renderUriMoveButtons(draft, index + 1)}
          <button type="button" class="icon-btn" data-action="remove-uri" data-index="${index}" title="Retirer" aria-label="Retirer l’adresse ${index + 2}">${ICON_TRASH}</button>
        </div>`)
      .join('');
    return `<div class="stack" data-section="extra-uris">
          ${rows}
          <button type="button" class="secondary" data-action="add-uri">${ICON_PLUS} Ajouter une adresse</button>
        </div>`;
  }

  /** Alerte « Adresse trompeuse » de l'editeur : toujours presente (la frappe la remplit), masquee tant qu'elle est vide. */
  function renderDeceptiveUriAlert(uris: readonly string[]): string {
    const message = deceptiveUriHint(uris, vaultSites(state.entries));
    return `<span class="field-hint field-alert" data-hint="deceptive-uri" role="status" aria-live="polite"${message === '' ? ' hidden' : ''}>${escapeHtml(message)}</span>`;
  }

  /**
   * Jauge sous le NOUVEAU mot de passe maitre (creation, recuperation, changement) : la meme estimation que la Sante du
   * coffre, l'e-mail du compte en guise d'identifiant. Vide au rendu (le champ l'est aussi), la frappe la remplit.
   */
  function renderMasterPasswordStrengthHint(): string {
    return '<span class="field-hint strength-hint" data-hint="master-password-strength" role="status" aria-live="polite" hidden></span>';
  }

  /**
   * Consentement a un mot de passe maitre FAIBLE (`master-password-gate.ts`) : la case ne parait qu'avec la jauge
   * « faible » ; sans elle, la porte refuse le mot de passe.
   */
  function renderWeakMasterConsent(): string {
    return '<label class="checkbox" data-hint="master-weak-consent" hidden><input type="checkbox" name="acceptWeakMaster" /> Utiliser ce mot de passe faible quand meme</label>';
  }

  /**
   * Verification du NOUVEAU mot de passe maitre dans les fuites connues (`assertNewMasterPasswordNotExposed`) : cochee
   * d'office seulement si l'utilisateur a deja active la verification des fuites (rien ne part sans son accord), avec le
   * service qui sera interroge. La case de consentement « expose » ne parait qu'apres un refus pour fuite.
   */
  function renderMasterBreachCheck(): string {
    const host = new URL(state.breachEndpoint ?? DEFAULT_PWNED_PASSWORDS_ENDPOINT).host;
    return `<label class="checkbox"><input type="checkbox" name="checkMasterBreach" ${state.breachEndpoint !== null ? 'checked' : ''} /> Verifier ce mot de passe dans les fuites connues</label>
          <span class="field-hint" data-hint="master-breach-check">k-anonymat : seuls les 5 premiers caracteres de son empreinte SHA-1 partent vers ${escapeHtml(host)}, jamais le mot de passe.</span>
          <label class="checkbox" data-hint="master-exposed-consent"${state.masterPasswordExposed ? '' : ' hidden'}><input type="checkbox" name="acceptExposedMaster" /> Utiliser ce mot de passe expose quand meme</label>`;
  }

  /** Verification des fuites demandee dans le formulaire du nouveau mot de passe maitre ; `null` = non demandee. */
  function masterBreachCheck(data: FormData): BreachCheckOptions | null {
    return data.get('checkMasterBreach') === 'on' ? { endpoint: state.breachEndpoint ?? DEFAULT_PWNED_PASSWORDS_ENDPOINT } : null;
  }

  /** Jauge de robustesse sous le mot de passe de l'editeur : toujours presente (la frappe la remplit), masquee tant qu'il est vide. */
  function renderPasswordStrengthHint(draft: EditorDraft): string {
    const strength = draftPasswordStrength(draft.password, draft.username, [draft.uri, ...draft.extraUris], draft.name, vaultAccountEmail());
    return `<span class="field-hint strength-hint" data-hint="password-strength" data-strength="${strength?.label ?? ''}" role="status" aria-live="polite"${strength === null ? ' hidden' : ''}>${escapeHtml(strength?.message ?? '')}</span>
            ${renderDraftBreachHint(draft)}`;
  }

  /** Service interroge par « Verifier dans les fuites connues » : celui des reglages, le service par defaut sinon. */
  function draftBreachEndpoint(): string {
    return state.breachEndpoint ?? DEFAULT_PWNED_PASSWORDS_ENDPOINT;
  }

  /** Bouton « Verifier dans les fuites connues » a cote du mot de passe de la fiche : rien ne part sans ce clic. */
  function renderDraftBreachButton(): string {
    const host = new URL(draftBreachEndpoint()).host;
    const busy = state.draftBreach !== null && state.draftBreach.verdict === null;
    return `<button type="button" class="icon-btn" data-action="check-draft-breach" title="Verifier dans les fuites connues (k-anonymat : seuls 5 caracteres de son empreinte partent vers ${escapeHtml(host)})" aria-label="Verifier ce mot de passe dans les fuites connues"${busy ? ' disabled aria-busy="true"' : ''}>${ICON_SHIELD}</button>`;
  }

  /** Verdict « fuites connues » sous la jauge, seulement s'il porte sur le mot de passe affiche ; jamais le mot de passe. */
  function renderDraftBreachHint(draft: EditorDraft): string {
    const result = state.draftBreach;
    const current = result !== null && result.password === draft.password && draft.password !== '';
    const message = !current ? '' : result.verdict === null ? 'Verification dans les fuites connues…' : draftBreachMessage(result.verdict, draftBreachEndpoint());
    const kind = current && result.verdict !== null ? result.verdict.kind : '';
    const hidden = message === '' ? ' hidden' : '';
    return `<span class="field-hint" data-hint="password-breach" data-breach="${kind}" role="status" aria-live="polite"${hidden}>${escapeHtml(message)}</span>`;
  }

  function renderSitePasswordRulesHint(uris: readonly string[]): string {
    const message = sitePasswordRulesHint(uris);
    const hidden = message === '' ? ' hidden' : '';
    return `<span class="field-hint" data-hint="site-password-rules" role="status" aria-live="polite"${hidden}>${escapeHtml(message)}</span>`;
  }

  /**
   * Champs personnalises dans la fiche : nom, valeur, « Masque », retrait ; une case a cocher : nom, case, retrait.
   * « Ajouter un champ » et « Ajouter une case a cocher » en dernier.
   */
  function renderCustomFieldsEditor(draft: EditorDraft): string {
    const rows = draft.customFields
      .map((field, index) => {
        const label = field.name.trim() || `champ ${index + 1}`;
        if (isCheckboxField(field)) {
          return `<div class="custom-field-row custom-field-checkbox" data-custom-field="${index}" data-custom-kind="boolean">
          <input name="custom-name:${index}" value="${escapeHtml(field.name)}" placeholder="Nom (ex. Compte professionnel)" aria-label="Nom du ${escapeHtml(label)}" autocomplete="off" />
          <label class="checkbox"><input type="checkbox" name="custom-checked:${index}" ${field.value === 'true' ? 'checked' : ''} aria-label="${escapeHtml(label)} : cochee" /> Cochee</label>
          <button type="button" class="icon-btn" data-action="remove-custom-field" data-name="${escapeHtml(field.name)}" data-index="${index}" title="Retirer" aria-label="Retirer ${escapeHtml(label)}">${ICON_TRASH}</button>
        </div>`;
        }
        const masked = field.hidden === true && !state.revealDraftPassword;
        // Une valeur multiligne n'entre JAMAIS dans un `<input>` (il en retire les retours a la ligne) : zone de texte,
        // ou, masquee, hors du formulaire -- le brouillon la garde jusqu'a l'oeil, comme la cle privee SSH.
        const value = !isMultilineCustomValue(field.value)
          ? `<input name="custom-value:${index}" type="${masked ? 'password' : 'text'}" value="${escapeHtml(field.value)}" placeholder="Valeur" aria-label="Valeur de ${escapeHtml(label)}" title="Maj+Entree : nouvelle ligne" data-input="custom-value" data-index="${index}" autocomplete="off" />`
          : masked
            ? `<span class="mono custom-value-masked" data-masked-custom="${index}" title="Valeur masquee sur plusieurs lignes : l’oeil l’affiche">${'•'.repeat(10)}</span>`
            : `<textarea name="custom-value:${index}" data-custom-index="${index}" rows="${customValueRows(field.value)}" class="mono" spellcheck="false" aria-label="Valeur de ${escapeHtml(label)}" autocomplete="off" autocapitalize="off">${escapeHtml(field.value)}</textarea>`;
        return `<div class="custom-field-row" data-custom-field="${index}">
          <input name="custom-name:${index}" value="${escapeHtml(field.name)}" placeholder="Nom (ex. Numero client)" aria-label="Nom du ${escapeHtml(label)}" autocomplete="off" />
          ${value}
          <label class="checkbox"><input type="checkbox" name="custom-hidden:${index}" ${field.hidden === true ? 'checked' : ''} /> Masque</label>
          <button type="button" class="icon-btn" data-action="remove-custom-field" data-name="${escapeHtml(field.name)}" data-index="${index}" title="Retirer" aria-label="Retirer ${escapeHtml(label)}">${ICON_TRASH}</button>
        </div>`;
      })
      .join('');
    const reveal = draft.customFields.some((field) => field.hidden === true)
      ? `<button type="button" class="icon-btn" data-action="toggle-draft-reveal" title="${state.revealDraftPassword ? 'Masquer' : 'Afficher'} les champs masques">${state.revealDraftPassword ? ICON_EYE_OFF : ICON_EYE}</button>`
      : '';
    return `<fieldset class="custom-fields">
          <legend>Champs personnalises ${reveal}</legend>
          ${rows}
          <div class="custom-field-add">
            <button type="button" class="secondary" data-action="add-custom-field">${ICON_PLUS} Ajouter un champ</button>
            <button type="button" class="secondary" data-action="add-custom-checkbox">${ICON_PLUS} Ajouter une case a cocher</button>
          </div>
        </fieldset>`;
  }

  function renderEditor(draft: EditorDraft): string {
    const folderOptions = folderTree(state.folders)
      .map(({ folder, depth }) => `<option value="${folder.id}" ${draft.folderId === folder.id ? 'selected' : ''}>${folderOptionIndent(depth)}${escapeHtml(folder.secrets.name)}</option>`)
      .join('');
    const group = typedFieldGroup(draft.type);
    return `<section class="detail-pane glass">
      <h2>${draft.id === null ? 'Nouvelle entree' : 'Modifier l’entree'}</h2>
      <form data-form="entry" class="stack">
        <label>Nom<input name="name" value="${escapeHtml(draft.name)}" required /></label>
        <label>Type
          <select name="type">
            ${(['login', 'card', 'identity', 'note', 'ssh-key', 'wifi', 'bank', 'wallet', 'database', 'api-key', 'software-license', 'server', 'email', 'membership', 'medical'] as const).map((t) => `<option value="${t}" ${draft.type === t ? 'selected' : ''}>${TYPE_LABELS[t]}</option>`).join('')}
          </select>
        </label>
        ${group !== null ? `<fieldset class="typed-fields">
          <legend>${TYPED_GROUP_TITLES[group]}
            ${group === 'wifi' || group === 'wallet' || group === 'database' || group === 'api-key' || group === 'server' || group === 'email' ? '' : `<button type="button" class="icon-btn" data-action="toggle-draft-reveal" title="${state.revealDraftPassword ? 'Masquer' : 'Afficher'} les champs masques">${state.revealDraftPassword ? ICON_EYE_OFF : ICON_EYE}</button>`}
          </legend>
          ${fieldSpecs(group).map((spec) => renderTypedInput(spec, draft)).join('')}
          ${group === 'wifi' ? `<label class="wide">Mot de passe du reseau
            <div class="field-row">
              <input name="password" type="${state.revealDraftPassword ? 'text' : 'password'}" value="${escapeHtml(draft.password)}" autocomplete="off" />
              <button type="button" class="icon-btn" data-action="toggle-draft-reveal" title="${state.revealDraftPassword ? 'Masquer' : 'Afficher'}">${state.revealDraftPassword ? ICON_EYE_OFF : ICON_EYE}</button>
              <button type="button" class="icon-btn" data-action="generate-into-draft" title="Generer">${ICON_REFRESH}</button>
              ${renderDraftBreachButton()}
            </div>
            ${renderPasswordStrengthHint(draft)}
            <span class="field-hint">WPA : 8 a 63 caracteres. Laissez vide pour un reseau ouvert.</span>
          </label>` : ''}
          ${group === 'wallet' ? `<label class="wide">Mot de passe du portefeuille
            <div class="field-row">
              <input name="password" type="${state.revealDraftPassword ? 'text' : 'password'}" value="${escapeHtml(draft.password)}" autocomplete="off" />
              <button type="button" class="icon-btn" data-action="toggle-draft-reveal" title="${state.revealDraftPassword ? 'Masquer' : 'Afficher'} les champs masques">${state.revealDraftPassword ? ICON_EYE_OFF : ICON_EYE}</button>
              <button type="button" class="icon-btn" data-action="generate-into-draft" title="Generer">${ICON_REFRESH}</button>
              ${renderDraftBreachButton()}
            </div>
            ${renderPasswordStrengthHint(draft)}
            <span class="field-hint">Celui qui deverrouille l’application ou le fichier du portefeuille. Une phrase BIP39 mal recopiee (mot hors liste, somme de controle fausse) est refusee.</span>
          </label>` : ''}
          ${group === 'database' ? `<label>Identifiant de connexion
            <input name="username" value="${escapeHtml(draft.username)}" autocomplete="off" spellcheck="false" autocapitalize="off" />
          </label>
          <label>Mot de passe de la base
            <div class="field-row">
              <input name="password" type="${state.revealDraftPassword ? 'text' : 'password'}" value="${escapeHtml(draft.password)}" autocomplete="off" />
              <button type="button" class="icon-btn" data-action="toggle-draft-reveal" title="${state.revealDraftPassword ? 'Masquer' : 'Afficher'}">${state.revealDraftPassword ? ICON_EYE_OFF : ICON_EYE}</button>
              <button type="button" class="icon-btn" data-action="generate-into-draft" title="Generer">${ICON_REFRESH}</button>
              ${renderDraftBreachButton()}
            </div>
            ${renderPasswordStrengthHint(draft)}
          </label>
          <span class="field-hint wide">L’hote seul dans « Serveur » : une adresse de connexion collee entiere y est refusee, son mot de passe a sa place ici (historique, Sante du coffre).</span>` : ''}
          ${group === 'api-key' ? `<label>Identifiant (ID de cle, compte)
            <input name="username" value="${escapeHtml(draft.username)}" autocomplete="off" spellcheck="false" autocapitalize="off" />
          </label>
          <label>Cle / jeton
            <div class="field-row">
              <input name="password" type="${state.revealDraftPassword ? 'text' : 'password'}" value="${escapeHtml(draft.password)}" autocomplete="off" spellcheck="false" autocapitalize="off" />
              <button type="button" class="icon-btn" data-action="toggle-draft-reveal" title="${state.revealDraftPassword ? 'Masquer' : 'Afficher'}">${state.revealDraftPassword ? ICON_EYE_OFF : ICON_EYE}</button>
              <button type="button" class="icon-btn" data-action="generate-into-draft" title="Generer un secret partage">${ICON_REFRESH}</button>
            </div>
          </label>
          <span class="field-hint wide">La cle a sa ligne : a chaque rotation, l’ancienne rejoint l’historique. Renseignez l’expiration, la Sante du coffre previent 30 jours avant. Une adresse qui porte la cle (« ?token=… ») est refusee.</span>` : ''}
          ${group === 'server' ? `<label>Identifiant du serveur
            <input name="username" value="${escapeHtml(draft.username)}" autocomplete="off" spellcheck="false" autocapitalize="off" />
          </label>
          <label>Mot de passe du serveur
            <div class="field-row">
              <input name="password" type="${state.revealDraftPassword ? 'text' : 'password'}" value="${escapeHtml(draft.password)}" autocomplete="off" />
              <button type="button" class="icon-btn" data-action="toggle-draft-reveal" title="${state.revealDraftPassword ? 'Masquer' : 'Afficher'} les champs masques">${state.revealDraftPassword ? ICON_EYE_OFF : ICON_EYE}</button>
              <button type="button" class="icon-btn" data-action="generate-into-draft" title="Generer">${ICON_REFRESH}</button>
              ${renderDraftBreachButton()}
            </div>
            ${renderPasswordStrengthHint(draft)}
          </label>
          <span class="field-hint wide">Le compte du serveur a son historique et passe par la Sante du coffre ; celui de la console reste masque et n’est jamais cherche. Une adresse qui porte un mot de passe ou une cle est refusee.</span>` : ''}
          ${group === 'email' ? `<label>Adresse ou identifiant
            <input name="username" value="${escapeHtml(draft.username)}" autocomplete="off" spellcheck="false" autocapitalize="off" inputmode="email" />
          </label>
          <label>Mot de passe de la messagerie
            <div class="field-row">
              <input name="password" type="${state.revealDraftPassword ? 'text' : 'password'}" value="${escapeHtml(draft.password)}" autocomplete="off" />
              <button type="button" class="icon-btn" data-action="toggle-draft-reveal" title="${state.revealDraftPassword ? 'Masquer' : 'Afficher'} les champs masques">${state.revealDraftPassword ? ICON_EYE_OFF : ICON_EYE}</button>
              <button type="button" class="icon-btn" data-action="generate-into-draft" title="Generer">${ICON_REFRESH}</button>
              ${renderDraftBreachButton()}
            </div>
            ${renderPasswordStrengthHint(draft)}
          </label>
          <span class="field-hint wide">Le mot de passe a son historique et passe par la Sante du coffre. Laissez le compte SMTP vide s’il est le meme. Port vide : celui de la securite choisie. Sans securite, le mot de passe circule en clair, c’est signale.</span>` : ''}
          ${group === 'software-license' ? `<span class="field-hint wide">La cle reste masquee et n’est jamais cherchee. Pour un abonnement ou une maintenance, renseignez l’expiration : la Sante du coffre previent 30 jours avant.</span>` : ''}
          ${group === 'medical' ? `<span class="field-hint wide">Donnees de sante : le motif, le medicament, la posologie et les remarques restent masques et ne sont jamais cherches. Retrouvez le dossier par le praticien, le lieu ou le patient.</span>` : ''}
          ${group === 'membership' ? `<span class="field-hint wide">Le numero de membre reste lisible, comme sur la carte ; le PIN est masque et n’est jamais cherche. Dates au mois (03/2027) ou au jour : la Sante du coffre previent 30 jours avant l’expiration.</span>` : ''}
        </fieldset>` : ''}
        ${draft.type === 'login' ? `<label>Identifiant
          <div class="field-row">
            <input name="username" value="${escapeHtml(draft.username)}" autocomplete="off" />
            <button type="button" class="icon-btn" data-action="generate-username-into-draft" title="Generer un identifiant">${ICON_REFRESH}</button>
          </div>
        </label>
        <label>Mot de passe
          <div class="field-row">
            <input name="password" type="${state.revealDraftPassword ? 'text' : 'password'}" value="${escapeHtml(draft.password)}" autocomplete="off" />
            <button type="button" class="icon-btn" data-action="toggle-draft-reveal">${state.revealDraftPassword ? ICON_EYE_OFF : ICON_EYE}</button>
            <button type="button" class="icon-btn" data-action="generate-into-draft" title="Generer">${ICON_REFRESH}</button>
            ${renderDraftBreachButton()}
          </div>
          ${renderPasswordStrengthHint(draft)}
          ${renderSitePasswordRulesHint([draft.uri, ...draft.extraUris])}
        </label>
        <label>Site (URL)
          <div class="field-row">
            <input name="uri" data-input="entry-uri" value="${escapeHtml(draft.uri)}" autocomplete="off" />
            ${renderUriMatchSelect(draft, 0)}
            ${renderUriMoveButtons(draft, 0)}
          </div>
        </label>
        ${renderExtraUrisEditor(draft)}
        ${renderDeceptiveUriAlert([draft.uri, ...draft.extraUris])}
        <label class="checkbox"><input type="checkbox" name="equivalentDomains" aria-describedby="hint-equivalent-domains" ${draft.equivalentDomains ? 'checked' : ''} /> Reconnaitre aussi sur les domaines equivalents</label>
        <span class="field-hint" id="hint-equivalent-domains" data-hint="equivalent-domains">${escapeHtml(equivalentDomainsHint(draft.uri))}</span>
        <span class="field-hint" id="hint-uri-match">Detection : « Par defaut » suit le reglage du coffre (Parametres, aujourd’hui ${escapeHtml(URI_MATCH_SHORT[state.defaultUriMatch])}) ; « Domaine » reconnait le site et ses sous-domaines ; « Hote et port » une seule application ; « Commence par » et « Adresse exacte » une page precise ; « Jamais » garde l’adresse sans la proposer.</span>
        <div class="totp-field" data-dropzone="totp-qr">
          <label>Graine TOTP (base32, URI otpauth:// ou steam://, optionnel)<input name="totpSecret" value="${escapeHtml(draft.totpSecret)}" autocomplete="off" aria-describedby="hint-totp-qr" /></label>
          <div class="totp-qr-row">
            <label class="link-btn file-btn" title="Lire le code QR d’activation sur une capture d’ecran">${ICON_QR}<span>Lire un code QR</span>
              <input type="file" accept="image/*" data-input="totp-qr-image" hidden />
            </label>
            ${cameraScanAvailable(window) ? `<button type="button" class="link-btn" data-action="scan-totp-qr-camera" title="Lire le code QR d’activation a la camera">${ICON_QR}<span>Camera</span></button>` : ''}
            <span class="field-hint" id="hint-totp-qr" data-hint="totp-qr">ou collez (Ctrl+V) / deposez ici une capture d’ecran du code : elle est lue sur cet appareil, jamais envoyee ni gardee.</span>
          </div>
        </div>` : ''}
        ${draft.type === 'ssh-key' ? renderSshKeyEditor(draft) : ''}
        ${PASSWORD_EXPIRY_TYPES.has(draft.type) ? renderPasswordExpiryEditor(draft) : ''}
        <label>Dossier
          <select name="folderId">
            <option value="">Racine</option>
            ${folderOptions}
          </select>
        </label>
        <label>Notes<textarea name="notes" rows="3" data-input="entry-notes" aria-describedby="notes-format-hint">${escapeHtml(draft.notes)}</textarea></label>
        <div class="notes-head">
          <p class="field-hint" id="notes-format-hint">Mise en forme : **gras**, *italique*, # titre, - liste, - [ ] case, [lien](https://…), \`code\`, | tableau |.</p>
          <button type="button" class="link-btn notes-format-toggle" data-action="toggle-notes-preview" aria-pressed="${state.notesPreview}" aria-controls="notes-preview">Apercu</button>
        </div>
        ${state.notesPreview ? `<div class="notes notes-md notes-preview" id="notes-preview" data-preview="notes" role="region" aria-label="Apercu des notes">${renderNotesPreview(draft.notes)}</div>` : ''}
        <label>Etiquettes<input name="tags" value="${escapeHtml(draft.tags)}" autocomplete="off" list="entry-tag-suggestions" aria-describedby="hint-entry-tags" /></label>
        <datalist id="entry-tag-suggestions">${state.tags.map(({ tag }) => `<option value="${escapeHtml(tag)}"></option>`).join('')}</datalist>
        <span class="field-hint" id="hint-entry-tags">Separees par des virgules (travail, banque, a changer). Une entree peut en porter plusieurs, en plus de son dossier.</span>
        ${renderCustomFieldsEditor(draft)}
        <label class="checkbox"><input type="checkbox" name="favorite" ${draft.favorite ? 'checked' : ''} /> Favori</label>
        <label class="checkbox"><input type="checkbox" name="reprompt" aria-describedby="hint-reprompt-option" ${draft.reprompt ? 'checked' : ''} /> Redemander le mot de passe maitre</label>
        <span class="field-hint" id="hint-reprompt-option">Pour une entree sensible (banque, messagerie principale) : rien ne s’affiche ni ne se copie sans retaper le mot de passe maitre, meme coffre ouvert.</span>
        <div class="detail-actions">
          <button type="submit" class="primary">Enregistrer</button>
          <button type="button" class="secondary" data-action="cancel-edit">Annuler</button>
        </div>
      </form>
    </section>`;
  }

  /**
   * Alias e-mail (R19) : un alias par site. L'appel part du navigateur vers le
   * fournisseur de l'utilisateur -- le serveur Klavox n'est pas sur le chemin.
   * La cle d'API n'est ni pre-remplie dans le HTML, ni conservee au-dela de la
   * session : le champ est vide a chaque rendu.
   */
  function generatorSegment(mode: AppState['generatorMode'], label: string): string {
    return `<button type="button" class="${state.generatorMode === mode ? 'active' : ''}" aria-pressed="${state.generatorMode === mode}" data-action="generator-mode" data-mode="${mode}" data-name="${escapeHtml(label)}">${label}</button>`;
  }

  function generatorSegments(): string {
    return `<div class="segmented" role="group" aria-label="Type de secret">
      ${generatorSegment('password', 'Mot de passe')}${generatorSegment('passphrase', 'Phrase de passe')}${generatorSegment('username', 'Identifiant')}${generatorSegment('alias', 'Alias e-mail')}
    </div>`;
  }

  function renderUsernameGenerator(): string {
    const settings = state.usernameSettings;
    const suffixField = `<label>Suffixe
          <select name="suffix">
            ${USERNAME_SUFFIXES.map((option) => `<option value="${option.value}" ${option.value === settings.suffix ? 'selected' : ''}>${escapeHtml(option.label)}</option>`).join('')}
          </select>
        </label>
        ${settings.suffix === 'site' ? `<p class="muted">Le suffixe est le site de la fiche (moi+github.com@...) : pratique pour trier le courrier, mais devinable d'un site a l'autre. Il se tire avec le bouton a cote du champ Identifiant d'une fiche, depuis son Site (URL).</p>` : ''}`;
    const fields =
      settings.kind === 'plus'
        ? `<label>Votre adresse e-mail<input name="email" type="email" autocomplete="off" value="${escapeHtml(settings.email)}" placeholder="moi@exemple.eu" /></label>
        <p class="muted">Chaque site recoit moi+suffixe@... : le courrier arrive dans votre boite habituelle, et un suffixe qui recoit du spam designe le site qui a fuite. Votre fournisseur doit accepter le « + » (Proton, Gmail, Fastmail, Outlook, la plupart des serveurs).</p>
        ${suffixField}`
        : settings.kind === 'catchall'
          ? `<label>Votre domaine<input name="domain" autocomplete="off" value="${escapeHtml(settings.domain)}" placeholder="exemple.eu" /></label>
        <p class="muted">Une adresse par site sur votre domaine : il doit etre configure pour recevoir sur n'importe quelle adresse (« attrape-tout »).</p>
        ${suffixField}`
          : `<label class="checkbox"><input type="checkbox" name="capitalize" ${settings.capitalize ? 'checked' : ''} /> Majuscule initiale</label>
        <label class="checkbox"><input type="checkbox" name="includeNumber" ${settings.includeNumber ? 'checked' : ''} /> Ajouter 4 chiffres</label>`;
    return `<section class="detail-pane glass generator-pane">
      <h2>Generateur</h2>
      ${generatorSegments()}
      <div class="generated-display mono" data-role="generated-username">${escapeHtml(state.generatedUsername || ' ')}</div>
      ${usernameNeedsSite() ? `<p class="muted" data-role="username-site-hint">Suffixe au nom du site : utilisez le bouton a cote du champ Identifiant d'une fiche.</p>` : ''}
      <div class="detail-actions">
        <button class="secondary" data-action="regenerate">${ICON_REFRESH} Regenerer</button>
        <button class="secondary" data-action="copy-generated" ${state.generatedUsername.length === 0 ? 'disabled' : ''}>${ICON_COPY} Copier</button>
      </div>
      ${state.usernameError !== null ? echecOperation(state.usernameError) : ''}
      <form data-form="username" class="stack">
        <label>Forme
          <select name="kind">
            ${USERNAME_KINDS.map((option) => `<option value="${option.value}" ${option.value === settings.kind ? 'selected' : ''}>${escapeHtml(option.label)}</option>`).join('')}
          </select>
        </label>
        ${fields}
        <p class="muted">Tire par le generateur cryptographique de l'appareil ; rien n'est envoye, et votre adresse n'est pas memorisee apres le verrouillage. Le bouton a cote du champ Identifiant d'une fiche utilise ces reglages.</p>
      </form>
    </section>`;
  }

  function renderAliasForm(): string {
    const provider = ALIAS_PROVIDERS.find((candidate) => candidate.id === state.aliasProviderId);
    const mailkrypt = provider?.requires !== undefined;
    return `<form data-form="alias" class="stack">
      <label>Fournisseur
        <select name="provider" data-input="alias-provider">
          ${ALIAS_PROVIDERS.map((provider) => `<option value="${provider.id}" ${provider.id === state.aliasProviderId ? 'selected' : ''}>${escapeHtml(provider.label)}</option>`).join('')}
        </select>
      </label>
      <label>Service (compte du fournisseur ou instance auto-hebergee)
        <input name="endpoint" value="${escapeHtml(state.aliasEndpoint)}" ${mailkrypt ? 'placeholder="https://mail.votre-organisation.eu"' : ''} />
      </label>
      ${mailkrypt ? `<label>Domaine de l'alias (gere par la cle)
        <input name="domain" value="${escapeHtml(state.aliasDomain)}" placeholder="alias.votre-organisation.eu" />
      </label>
      <label>Faire suivre vers (votre adresse reelle)
        <input name="destination" type="email" value="${escapeHtml(state.aliasDestination)}" />
      </label>` : ''}
      <label>Cle d'API (reste sur l'appareil, le temps de la session)
        <input name="apiKey" type="password" autocomplete="off" value="${escapeHtml(state.aliasApiKey)}" />
      </label>
      <label>Note chez le fournisseur (le site vise)
        <input name="note" value="${escapeHtml(state.aliasNote)}" placeholder="exemple.eu" />
      </label>
      <button type="submit" ${occupe(state.aliasBusy)}>${state.aliasBusy ? 'Creation...' : "Creer l'alias"}</button>
      ${state.aliasError !== null ? echecOperation(state.aliasError) : ''}
      <p class="muted">Un alias par site : le site n'apprend jamais votre adresse reelle, et un alias qui recoit du spam DESIGNE le site qui a fuite. Klavox n'envoie rien a son serveur ; enregistrez la cle d'API dans une entree du coffre pour la retrouver.</p>
      ${mailkrypt ? `<p class="muted">MailKrypt : cle d'administrateur du domaine (<code>mkk_...</code>, pas en lecture seule) ; point d'acces = l'adresse qui sert <code>/v1</code> derriere le mandataire de la console. Un alias expire garde sa place dans le plafond du domaine : supprimez ceux qui ne servent plus.</p>` : ''}
    </form>`;
  }

  function renderGenerator(): string {
    if (state.generatorMode === 'username') return renderUsernameGenerator();
    if (state.generatorMode === 'alias') {
      return `<section class="detail-pane glass generator-pane">
        <h2>Generateur</h2>
        ${generatorSegments()}
        <div class="generated-display mono">${escapeHtml(state.aliasResult || ' ')}</div>
        <div class="detail-actions">
          <button class="secondary" data-action="copy" data-value="${escapeHtml(state.aliasResult)}">${ICON_COPY} Copier</button>
          ${state.aliasResult !== '' && canDeleteAlias(state.aliasProviderId) ? `<button class="secondary" data-action="delete-alias" ${occupe(state.aliasBusy)}>Supprimer cet alias</button>` : ''}
        </div>
        ${renderAliasForm()}
      </section>`;
    }
    const options = state.generatorOptions;
    const passphrase = state.generatorMode === 'passphrase';
    const settings = state.passphraseSettings;
    const wordlist = state.wordlists[settings.language];
    const problem = passphrase ? null : generatorProblem(options);
    // Regle du site saisi : c'est elle qui a tire le mot de passe affiche, son entropie est celle de la regle.
    const site = passphrase ? null : sitePasswordRulesFor(state.generatorSite);
    let entropy = 0;
    if (state.generatedPassword.length > 0) {
      entropy = Math.round(
        passphrase && wordlist !== undefined
          ? passphraseEntropyBits({ wordlist, words: settings.words })
          : site !== null
            ? sitePasswordEntropyBits(site.rules, state.generatorApplied.length)
            : passwordEntropyBits(toGeneratorOptions(state.generatorApplied)),
      );
    }
    const siteHint = generatorSiteRulesHint(state.generatorSite);
    const form = passphrase
      ? `<form data-form="passphrase" class="stack">
        <label>Nombre de mots : <span data-role="words-value">${settings.words}</span>
          <input type="range" name="words" min="${MIN_PASSPHRASE_WORDS}" max="${MAX_PASSPHRASE_WORDS}" value="${settings.words}" />
        </label>
        <label>Langue de la liste
          <select name="language">
            <option value="fr" ${settings.language === 'fr' ? 'selected' : ''}>Francais (7776 mots)</option>
            <option value="en" ${settings.language === 'en' ? 'selected' : ''}>Anglais -- EFF (7776 mots)</option>
          </select>
        </label>
        <label>Separateur
          <select name="separator">
            ${PASSPHRASE_SEPARATORS.map((option) => `<option value="${escapeHtml(option.value)}" ${option.value === settings.separator ? 'selected' : ''}>${escapeHtml(option.label)}</option>`).join('')}
          </select>
        </label>
        <label class="checkbox"><input type="checkbox" name="capitalize" ${settings.capitalize ? 'checked' : ''} /> Majuscule en debut de mot</label>
        <label class="checkbox"><input type="checkbox" name="includeNumber" ${settings.includeNumber ? 'checked' : ''} /> Ajouter un chiffre</label>
        <p class="muted">Mots tires au hasard par le generateur cryptographique de l'appareil, sur une liste embarquee (rien n'est envoye). Listes : EFF (CC BY 3.0), diceware-fr-alt d'Arthur Pons (MIT).</p>
      </form>`
      : `<form data-form="generator" class="stack">
        <label>Pour le site <span class="muted">(facultatif)</span>
          <input type="text" name="site" value="${escapeHtml(state.generatorSite)}" placeholder="exemple.fr" autocomplete="off" spellcheck="false" inputmode="url" />
        </label>
        <span class="field-hint" data-hint="generator-site-rules" role="status" aria-live="polite"${siteHint === '' ? ' hidden' : ''}>${escapeHtml(siteHint)}</span>
        <label>Longueur : <span data-role="length-value">${options.length}</span>
          <input type="range" name="length" min="${GENERATOR_LENGTH_MIN}" max="${GENERATOR_LENGTH_MAX}" value="${options.length}" />
        </label>
        <label class="checkbox"><input type="checkbox" name="lowercase" ${options.lowercase ? 'checked' : ''} /> Minuscules</label>
        <label class="checkbox"><input type="checkbox" name="uppercase" ${options.uppercase ? 'checked' : ''} /> Majuscules</label>
        <label class="checkbox"><input type="checkbox" name="digits" ${options.digits ? 'checked' : ''} /> Chiffres</label>
        <label class="checkbox"><input type="checkbox" name="symbols" ${options.symbols ? 'checked' : ''} /> Symboles</label>
        <label class="checkbox"><input type="checkbox" name="excludeAmbiguous" ${options.excludeAmbiguous ? 'checked' : ''} /> Exclure les caracteres ambigus</label>
        <div class="generator-advanced">
          <label>Chiffres au minimum
            <input type="number" name="minDigits" min="0" max="${GENERATOR_MINIMUM_MAX}" value="${options.minDigits}" ${options.digits ? '' : 'disabled'} />
          </label>
          <label>Symboles au minimum
            <input type="number" name="minSymbols" min="0" max="${GENERATOR_MINIMUM_MAX}" value="${options.minSymbols}" ${options.symbols ? '' : 'disabled'} />
          </label>
          <label>Symboles autorises
            <input type="text" name="symbolCharacters" class="mono" value="${escapeHtml(options.symbolCharacters)}" placeholder="Tous (!#$%&amp;*...)" autocomplete="off" spellcheck="false" ${options.symbols ? '' : 'disabled'} />
          </label>
          <label>Caracteres a exclure
            <input type="text" name="excludeCharacters" class="mono" value="${escapeHtml(options.excludeCharacters)}" placeholder="Aucun" autocomplete="off" spellcheck="false" />
          </label>
        </div>
        ${problem === null ? '' : `<p class="error" role="alert" data-role="generator-problem">${escapeHtml(problem)}</p>`}
        <p class="muted">Pour un site qui n'accepte que certains symboles, ou en refuse : ces reglages sont gardes sur cet appareil. Le site saisi ci-dessus ne l'est pas, et n'est envoye nulle part.</p>
      </form>`;
    return `<section class="detail-pane glass generator-pane">
      <h2>Generateur</h2>
      ${generatorSegments()}
      <div class="generated-display mono${passphrase ? ' passphrase' : ''}">${escapeHtml(state.generatedPassword || ' ')}</div>
      <div class="detail-actions">
        <button class="secondary" data-action="regenerate">${ICON_REFRESH} Regenerer</button>
        <button class="secondary" data-action="copy-generated">${ICON_COPY} Copier</button>
      </div>
      <p class="muted">Entropie ${passphrase ? 'minimale' : 'estimee'} : ${entropy} bits -- ${strengthLabel(entropy)}</p>
      ${form}
      ${renderGeneratorHistory()}
    </section>`;
  }

  /**
   * Historique du generateur, replie par defaut ; valeurs masquees tant que l'oeil n'est pas ouvert -- masquees,
   * elles ne sont pas dans le DOM, et la copie relit l'historique en memoire, jamais un attribut.
   */
  function renderGeneratorHistory(): string {
    const history = state.generatorHistory;
    if (history.length === 0) {
      return `<div class="typed-section history-section generator-history" data-role="generator-history">
      <p class="muted">Historique : vide. Chaque secret copie ici y est garde 30 jours, chiffre, sur cet appareil.</p>
    </div>`;
    }
    const open = state.generatorHistoryOpen;
    const revealed = state.generatorHistoryRevealed;
    const rows = history
      .map((item, index) => `<li class="history-row">
        <div class="history-value">
          <span class="mono">${revealed ? escapeHtml(item.value) : '•'.repeat(10)}</span>
          <span class="muted">${generatedKindLabel(item.kind)} -- copie le ${formatTimestamp(item.copiedAt)}</span>
        </div>
        <button class="icon-btn" data-action="copy-generator-history" data-index="${index}" title="Copier" aria-label="Copier ce secret genere">${ICON_COPY}</button>
        <button class="icon-btn" data-action="remove-generator-history" data-index="${index}" title="Retirer" aria-label="Retirer de l'historique">${ICON_TRASH}</button>
      </li>`)
      .join('');
    const reveal = open
      ? `<button class="icon-btn" data-action="toggle-generator-history-reveal" title="${revealed ? 'Masquer' : 'Afficher'} les secrets generes">${revealed ? ICON_EYE_OFF : ICON_EYE}</button>`
      : '';
    return `<div class="typed-section history-section generator-history" data-role="generator-history">
      <div class="typed-section-head">
        <button class="history-toggle" data-action="toggle-generator-history" aria-expanded="${open}">
          ${ICON_CLOCK}<span class="history-title">Historique du generateur (${history.length})</span>
        </button>${reveal}
      </div>
      ${open ? `<ul class="history-list">${rows}</ul>
      <p class="muted">Secrets copies depuis le generateur, chiffres sur cet appareil seulement, gardes 30 jours (100 au plus).</p>
      <button class="link-btn" data-action="clear-generator-history">Effacer l'historique du generateur</button>` : ''}
    </div>`;
  }

  /**
   * Un export chiffre (KDBX, Bitwarden protege) demande son mot de passe, ici
   * meme : il sert UNIQUEMENT a dechiffrer le fichier sur cet appareil, n'est
   * jamais conserve et n'a rien a voir avec le mot de passe maitre.
   */
  /** Format de l'export : reconnu au contenu par defaut, CHOISI pour un export sans en-tete, consigne d'export sous le choix. */
  function renderImportFormatPicker(): string {
    const chosen = IMPORT_FORMATS.find((format) => format.id === state.importFormat) ?? IMPORT_FORMATS[0];
    return `<label class="import-format">Format de l’export
          <select data-input="import-format" aria-describedby="hint-import-format">
            ${IMPORT_FORMATS.map((format) => `<option value="${format.id}"${format.id === chosen.id ? ' selected' : ''}>${escapeHtml(format.label)}</option>`).join('')}
          </select>
        </label>
        <span class="field-hint" id="hint-import-format">${escapeHtml(chosen.hint)}</span>`;
  }

  /**
   * Apres un import en clair : le fichier est nomme et sa suppression proposee. « Supprimer le fichier » n'apparait que
   * la ou le navigateur sait le faire (poignee) ; ailleurs, la consigne. Jamais d'effacement d'office.
   */
  function renderImportCleanup(fileName: string): string {
    const name = escapeHtml(fileName);
    const result = state.importCleanupResult;
    if (result === 'supprime' || result === 'deja-absent') {
      const done = result === 'supprime' ? `« ${name} » est supprime de cet appareil.` : `« ${name} » n’est deja plus a son emplacement.`;
      return `<p data-role="import-cleanup-result"><strong>${done}</strong> ${escapeHtml(IMPORT_REMOVED_ADVICE)}</p>
          <button type="button" class="secondary small" data-action="dismiss-import-cleanup">Fermer</button>`;
    }
    const removable = state.importCleanupHandle !== null;
    const problem =
      result === 'refuse'
        ? 'Le navigateur n’a pas accorde le droit de le supprimer.'
        : result === 'impossible'
          ? 'Klavox n’a pas pu le supprimer.'
          : null;
    return `<p><strong>« ${name} » contient vos mots de passe en clair.</strong> ${escapeHtml(removable ? IMPORT_CLEANUP_ONE_CLICK_ADVICE : IMPORT_CLEANUP_ADVICE)}</p>
          ${problem === null ? '' : `<p class="field-hint" role="alert" data-role="import-cleanup-problem">${problem} ${removable ? 'Reessayez, ou supprimez-le vous-meme.' : 'Supprimez-le vous-meme.'}</p>`}
          <div class="import-cleanup-actions">
            ${removable ? `<button type="button" class="primary small" data-action="remove-import-file">${ICON_TRASH} Supprimer le fichier</button>` : ''}
            <button type="button" class="secondary small" data-action="dismiss-import-cleanup">C’est fait</button>
          </div>`;
  }

  /** « Choisir un fichier d'export » par le selecteur natif : sa poignee permet la suppression en un clic apres import. */
  async function handleImportPick(accept: string): Promise<void> {
    if (state.vaultStore === null || state.importBusy) return;
    try {
      const picked = await pickImportFile(window, accept);
      if (picked === null || picked === undefined) return;
      const bytes = new Uint8Array(await picked.file.arrayBuffer());
      await handleImportCsvFile(picked.file.name, bytes, undefined, null, picked.handle);
    } catch (cause) {
      scheduleFlash(cause instanceof Error ? cause.message : "fichier d'import illisible");
      render();
    }
  }

  function renderImportPasswordForm(): string {
    if (state.importPrompt === null) return '';
    // Base KeePass : le fichier-cle s'ajoute au mot de passe, ou le remplace (mot de passe alors laisse vide).
    const keePass = acceptsKeyFile(state.importPrompt.format);
    return `<form data-form="import-password" class="stack">
      <p class="muted" role="status">${escapeHtml(state.importPrompt.fileName)} est chiffre (${escapeHtml(state.importPrompt.format)}). Saisissez le mot de passe de CET export : il sert uniquement a le lire ici, il n’est ni conserve ni envoye.</p>
      <label>Mot de passe de l’export
        <input name="password" type="password" ${keePass ? '' : 'required '}autocomplete="off" data-testid="import-password"${keePass ? ' aria-describedby="hint-import-key-file"' : ''} />
      </label>
      ${
        keePass
          ? `<label>Fichier-cle (facultatif)
        <input name="keyFile" type="file" data-input="import-key-file" data-testid="import-key-file" aria-describedby="hint-import-key-file" />
      </label>
      <span class="field-hint" id="hint-import-key-file">Si la base s’ouvre aussi avec un fichier-cle (.keyx, .key ou tout autre fichier), choisissez-le : il est lu sur cet appareil, ni conserve ni envoye. Base protegee par le fichier-cle seul : laissez le mot de passe vide.</span>`
          : ''
      }
      ${state.importPrompt.error === null ? '' : `<p class="error">${escapeHtml(state.importPrompt.error)}</p>`}
      <div class="detail-actions">
        <button type="submit" class="secondary" ${occupe(state.importBusy)}>${ICON_UPLOAD} ${state.importBusy ? `Dechiffrement…${state.importProgress === null ? '' : ` ${state.importProgress} %`}` : 'Dechiffrer et importer'}</button>
        <button type="button" class="secondary" data-action="cancel-import">Annuler</button>
      </div>
    </form>`;
  }

  /** `aria-keyshortcuts` d'un bouton : absent quand sa touche ne fait rien (raccourcis d'une seule touche coupes). */
  function shortcutAttr(key: string | null): string {
    return key === null ? '' : ` aria-keyshortcuts="${escapeHtml(key)}"`;
  }

  function shortcutTitle(label: string, command: LetterCommand): string {
    const key = shortcutKeyOf(state.shortcuts, command);
    return key === null ? label : `${label} (${escapeHtml(key)})`;
  }

  /** Parametres › Raccourcis clavier : couper les raccourcis d'une seule touche (WCAG 2.1.4), reassigner chaque lettre. */
  function renderShortcutSettings(): string {
    const { singleKey, keys } = state.shortcuts;
    const rows = LETTER_COMMAND_ORDER.map(
      (command) => `<label class="shortcut-row"><span>${escapeHtml(LETTER_COMMAND_LABELS[command])}</span>
          <input class="shortcut-key" type="text" maxlength="1" autocomplete="off" autocapitalize="off" spellcheck="false" data-input="shortcut-letter" data-field="${command}" value="${escapeHtml(keys[command])}" aria-label="Touche : ${escapeHtml(LETTER_COMMAND_LABELS[command])}" />
        </label>`,
    ).join('');
    return `<div class="settings-block" data-testid="shortcut-settings">
        <h3>Raccourcis clavier</h3>
        <p class="muted">Lettres seules, « / » et « ? », hors de tout champ de saisie. Coupez-les si vous dictez a la voix ou si une frappe accidentelle vous gene ; Ctrl+C et Ctrl+B restent actifs. Retenu sur cet appareil.</p>
        <label class="check-row"><input type="checkbox" data-input="single-key-shortcuts" ${singleKey ? 'checked' : ''} /> Raccourcis d'une seule touche</label>
        ${
          singleKey
            ? `<div class="shortcut-grid">${rows}</div>
        ${state.shortcutError === null ? '' : `<p class="error" role="alert" data-testid="shortcut-error">${escapeHtml(state.shortcutError)}</p>`}
        <div class="detail-actions"><button type="button" class="secondary" data-action="reset-shortcuts" ${serializeShortcutSettings(state.shortcuts) === null ? 'disabled' : ''}>Retablir les touches par defaut</button></div>`
            : ''
        }
      </div>`;
  }

  function renderSettings(): string {
    return `<section class="detail-pane glass settings-pane">
      <h2>Parametres</h2>
      <div class="settings-block">
        <h3>Affichage</h3>
        <p class="muted">Agrandit ou reduit tout le texte de Klavox, ecran de deverrouillage compris. Retenu sur cet appareil.</p>
        <label>Taille du texte
          <select data-input="text-zoom">
            ${TEXT_ZOOM_STEPS.map((palier) => `<option value="${palier}" ${palier === state.textZoom ? 'selected' : ''}>${palier} %</option>`).join('')}
          </select>
        </label>
      </div>
      <div class="settings-block" data-testid="lock-settings">
        <h3>Verrouillage et presse-papiers</h3>
        <p class="muted">Le verrouillage efface de la memoire la cle du coffre et tout ce qui a ete dechiffre : il faut ensuite le mot de passe maitre. Retenu sur cet appareil.</p>
        <label>Verrouiller apres une inactivite de
          <select data-input="auto-lock-minutes">
            ${AUTO_LOCK_MINUTES_STEPS.map((minutes) => `<option value="${minutes}" ${minutes === state.autoLockMinutes ? 'selected' : ''}>${autoLockLabel(minutes)}</option>`).join('')}
          </select>
        </label>
        <label class="check-row"><input type="checkbox" data-input="lock-on-hide" ${state.lockOnHide ? 'checked' : ''} /> Verrouiller des que je quitte l'onglet ou reduis la fenetre</label>
        <label>Effacer le presse-papiers apres
          <select data-input="clipboard-clear-seconds">
            ${CLIPBOARD_CLEAR_SECONDS_STEPS.map((seconds) => `<option value="${seconds}" ${seconds === state.clipboardClearSeconds ? 'selected' : ''}>${clipboardClearLabel(seconds)}</option>`).join('')}
          </select>
        </label>
      </div>
      ${renderShortcutSettings()}
      <div class="settings-block" data-testid="uri-match-settings">
        <h3>Detection des sites</h3>
        <p class="muted">Regle suivie par chaque adresse qui n'a pas la sienne. « Domaine » reconnait le site et ses sous-domaines ; « Hote et port » une seule application ; « Commence par » et « Adresse exacte » une page precise. Une adresse peut toujours choisir sa propre regle dans l'editeur. Retenu sur cet appareil.</p>
        <label>Detection par defaut
          <select data-input="default-uri-match">
            ${DEFAULT_URI_MATCH_CHOICES.map((rule) => `<option value="${rule}" ${rule === state.defaultUriMatch ? 'selected' : ''}>${escapeHtml(URI_MATCH_SHORT[rule])}</option>`).join('')}
          </select>
        </label>
      </div>
      <div class="settings-block">
        <h3>Export / import chiffre</h3>
        <p class="muted">Le fichier exporte ne contient que du ciphertext (comme le serveur) : il ne s'ouvre qu'avec votre mot de passe maitre.</p>
        <div class="detail-actions">
          <button class="secondary" data-action="export-vault">${ICON_DOWNLOAD} Exporter le coffre</button>
          <label class="secondary file-btn">${ICON_UPLOAD} Importer un export Klavox
            <input type="file" accept="application/json" data-input="import-vault" hidden />
          </label>
        </div>
      </div>
      <div class="settings-block">
        <h3>Export interoperable (FIDO CXF)</h3>
        <p class="muted">Format d'echange standard de la FIDO Alliance (CXF v1.0), relu par Apple Passwords, Google, 1Password, Bitwarden et Dashlane : vous repartez avec vos donnees, sans enfermement. Ce fichier-la contient vos secrets -- Klavox ne l'ecrit donc JAMAIS en clair : il est scelle par un mot de passe d'export que vous choisissez ici (${MIN_CXF_PASSWORD_LENGTH} caracteres minimum). Ce n'est pas votre mot de passe maitre, et Klavox ne le conserve pas : perdu, le fichier est perdu.</p>
        ${state.cxfMessage === null ? '' : `<p class="muted" role="status" data-testid="cxf-status">${escapeHtml(state.cxfMessage)}</p>`}
        <form data-form="cxf-export" class="stack">
          <label>Mot de passe de l'export
            <input name="password" type="password" required autocomplete="new-password" minlength="${MIN_CXF_PASSWORD_LENGTH}" data-testid="cxf-password" />
            <span class="field-hint strength-hint" data-hint="export-password-strength" role="status" aria-live="polite" hidden></span>
          </label>
          <label>Confirmation
            <input name="confirmPassword" type="password" required autocomplete="new-password" minlength="${MIN_CXF_PASSWORD_LENGTH}" />
          </label>
          <label class="checkbox" data-hint="export-weak-consent" hidden><input type="checkbox" name="acceptWeakExport" /> Utiliser ce mot de passe faible quand meme</label>
          <button type="submit" class="secondary" ${occupe(state.cxfBusy)}>${ICON_DOWNLOAD} ${state.cxfBusy ? 'Chiffrement…' : 'Exporter au format CXF'}</button>
        </form>
      </div>
      <div class="settings-block import-block" data-dropzone="import-qr">
        <h3>Importer depuis un autre gestionnaire</h3>
        <p class="muted">KeePass / KeePassXC / KeeWeb / Strongbox (.kdbx, XML ou CSV), KeePass 1.x / KeePassX (.kdb ou CSV), Passbolt (CSV KeePass), Buttercup (CSV), Padloc (CSV), Bitwarden (JSON, chiffre compris), Proton Pass (JSON ou .zip), FIDO CXF (chiffre ou nu), 1Password (.1pux, .1pif ou CSV), LastPass, Dashlane (CSV, .zip ou JSON), NordPass (CSV), Keeper (CSV ou JSON), RoboForm (CSV), Enpass (JSON), Zoho Vault (CSV), Kaspersky (.txt), Avast Passwords (JSON ou CSV), Psono (JSON), Myki / JumpCloud (CSV), Avira (CSV), Password Boss (JSON), Passky (JSON), Passman / Nextcloud (JSON), F-Secure ID PROTECTION / KEY (.fsk), Sticky Password (XML), Password Depot (XML), SafeInCloud (XML), Password Safe / pwSafe (XML), Delinea Secret Server / Thycotic (XML ou CSV), Password Dragon (XML), Password XP (CSV), GNOME Passwords / Seahorse (JSON), Clipperz (HTML), mSecure (CSV), Codebook (CSV), Netwrix Password Secure (CSV), Encryptr (CSV), RememBear (CSV), Passpack (CSV), Blur / Abine (CSV), Meldium (CSV), Yoti (CSV), LogMeOnce (CSV), PassKeep (CSV), SaferPass (CSV), Universal Password Manager, Password Agent, PasswordWallet, SplashID Safe, Ascendo DataVault et BlackBerry Password Keeper (format a choisir ci-dessous), McAfee True Key (CSV), Chrome, Edge, Firefox ou CSV generique. Codes de verification 2FA : Aegis (JSON, chiffre compris, ou liste d'URI), 2FAS (.2fas, chiffre compris), Google Authenticator (captures d’ecran des QR codes « Transferer des comptes », toutes ensemble, ou leur texte otpauth-migration://), Proton Authenticator (JSON, chiffre compris), Ente Auth (epingles, etiquettes et notes compris), Bitwarden Authenticator ou toute liste d'URI otpauth://. Dossiers, historique des mots de passe et pieces jointes sont conserves quand le format les porte. Le fichier est lu et chiffre sur cet appareil, il n'est jamais envoye. Supprimez-le ensuite : il contient vos mots de passe en clair.</p>
        ${renderImportFormatPicker()}
        <label class="secondary file-btn">${ICON_UPLOAD} Choisir un fichier d'export
          <input type="file" accept=".csv,text/csv,.txt,text/plain,.1pux,.1pif,.kdbx,.kdb,.json,application/json,.2fas,.fsk,.xml,text/xml,application/xml,.html,.htm,text/html,.zip,application/zip" data-input="import-csv" hidden />
        </label>
        <label class="secondary file-btn" title="Captures d’ecran des codes QR « Transferer des comptes » de Google Authenticator, toutes ensemble">${ICON_QR} Lire des captures de codes QR
          <input type="file" accept="image/*" multiple data-input="import-qr-images" hidden />
        </label>
        <p class="field-hint" data-hint="import-qr-drop">Ou deposez ici le fichier d’export, ou les captures des codes QR toutes ensemble, ou collez-en une (Ctrl+V) : tout est lu sur cet appareil.</p>
        ${renderImportPasswordForm()}
        ${
          state.importCleanupFile === null
            ? ''
            : `<div class="import-cleanup" role="status">
          ${ICON_TRASH}
          <div class="import-cleanup-body">
          ${renderImportCleanup(state.importCleanupFile)}
          </div>
        </div>`
        }
      </div>
      ${renderEmailSettings()}
      ${renderMasterPasswordSettings()}
      ${renderPasskeyUnlockSettings()}
      <div class="settings-block">
        <h3>Cle de recuperation</h3>
        <p class="muted">Seul moyen de retrouver votre coffre si vous oubliez votre mot de passe maitre -- sinon la perte est definitive (zero-knowledge). Klavox ne la conserve jamais en clair.</p>
        ${
          state.recoveryKeyStale
            ? '<p data-sk-notice="warning">Activee avant la preuve de possession : le serveur la refuse, regenerez-la.</p>'
            : state.envelope?.recoveryProtectedSymmetricKey
              ? '<p class="muted">Activee sur cet appareil.</p>'
              : ''
        }
        <div class="detail-actions">
          <button class="secondary" data-action="enable-recovery-key">${ICON_KEY} ${state.envelope?.recoveryProtectedSymmetricKey ? 'Regenerer la cle' : 'Activer la cle de recuperation'}</button>
        </div>
        ${
          state.recoveryKeyReveal !== null
            ? `<div class="recovery-reveal glass">
                <p data-sk-notice="warning"><strong>Notez et conservez cette cle en lieu sur.</strong> Elle ne sera plus jamais affichee.</p>
                <div class="generated-display mono">${escapeHtml(state.recoveryKeyReveal)}</div>
                <div class="detail-actions">
                  <button class="secondary" data-action="print-recovery-key">${ICON_PRINT} Imprimer</button>
                  <button class="secondary" data-action="copy" data-value="${escapeHtml(state.recoveryKeyReveal)}">${ICON_COPY} Copier</button>
                  <button class="primary" data-action="confirm-recovery-key">J’ai enregistre ma cle</button>
                </div>
              </div>`
            : ''
        }
      </div>
      <div class="settings-block">
        <h3>Mots de passe fuites</h3>
        <p class="muted">Compare vos mots de passe aux fuites connues en k-anonymat : seuls les 5 premiers caracteres de leur empreinte SHA-1 sont envoyes, jamais le mot de passe ni l’empreinte complete. La verification se fait chez nous, en Europe, sur notre miroir (fuites.santvia.eu, sans journal) : aucun service tiers n’est interroge. Donnees : Have I Been Pwned, licence CC BY 4.0. Un miroir auto-heberge du meme jeu de donnees est aussi accepte.</p>
        <form data-form="breach" class="stack">
          <label class="checkbox"><input type="checkbox" name="enabled" ${state.breachEndpoint !== null ? 'checked' : ''} /> Activer la verification des fuites</label>
          <label>Service (notre miroir europeen, ou le votre)<input name="endpoint" value="${escapeHtml(state.breachEndpoint ?? DEFAULT_PWNED_PASSWORDS_ENDPOINT)}" /></label>
          <button type="submit" class="secondary">Enregistrer</button>
        </form>
      </div>
      <div class="settings-block">
        <h3>Partager le verdict avec vos autres outils SANTVIA</h3>
        <p class="muted">Capacite <code>${CREDENTIALS_AUDIT_CAPABILITY}</code> : un produit frere installe sur cet appareil (NAVKRYPT) peut demander a AFFICHER, pour un site, si le mot de passe que vous y utilisez est compromis, faible ou reutilise. Seul ce verdict sort : jamais un mot de passe, jamais un identifiant, jamais une empreinte. Sans votre accord ci-dessous, rien n'est rendu. Le coffre fonctionne sans, et le declenchement de l'audit reste le votre.</p>
        <label class="checkbox"><input type="checkbox" data-input="credentials-audit-consent" ${state.credentialsAuditConsent ? 'checked' : ''} /> Autoriser un produit frere a afficher le verdict par site</label>
      </div>
      <div class="settings-block">
        <h3>Synchro serveur souverain</h3>
        <p class="muted">${state.remoteSession !== null ? `Connecte a ${escapeHtml(state.remoteSession.baseUrl)}.` : 'Non connecte -- le coffre reste local a cet appareil.'}</p>
        ${state.syncMessage !== null ? `<p class="muted">${escapeHtml(state.syncMessage)}</p>` : ''}
        <form data-form="sync" class="stack">
          <label>URL du serveur<input name="baseUrl" placeholder="https://api.klavox.santvia.eu" value="${escapeHtml(state.remoteSession?.baseUrl ?? '')}" /></label>
          <button type="submit" class="primary" ${occupe(state.syncBusy)}>${state.syncBusy ? 'Synchro en cours…' : 'Se connecter et synchroniser'}</button>
        </form>
      </div>
      ${renderWebdavSettings()}
      ${renderTravelModeSettings()}
      ${renderTwoFactorSettings()}
      ${renderSessionsSettings()}
      ${renderSecurityLogSettings()}
      ${renderIdenvaSettings()}
      ${renderEmergencyAccessSettings()}
      ${renderErrorJournalSettings()}
      ${renderAccountDeletionSettings()}
    </section>`;
  }

  /** Journal d'erreurs lisible sur demande (CDC, canal release : « le journal reste local, lisible sur demande »). */
  function renderErrorJournalSettings(): string {
    if (options.journalErreurs === undefined) return '';
    return `<div class="settings-block" data-testid="error-journal-settings">
        <h3>Journal d'erreurs</h3>
        <p class="muted">Les erreurs techniques de Klavox sont notees sur cet appareil, sans le contenu de votre coffre, et ne sont jamais envoyees. Pour un signalement, copiez-les puis collez-les vous-meme dans votre message.</p>
        <div class="detail-actions"><button type="button" class="secondary" data-action="copy-error-journal">${ICON_COPY} Copier le journal d'erreurs</button></div>
      </div>`;
  }

  /**
   * Synchro SANS serveur Klavox (R26 / G34) : le coffre chiffre est depose sur
   * le WebDAV/Nextcloud de l'utilisateur. Le mot de passe d'application n'est
   * PAS pre-rempli : Klavox ne le conserve nulle part.
   */
  function renderWebdavSettings(): string {
    return `<div class="settings-block">
      <h3>Synchro sans serveur Klavox (WebDAV / Nextcloud)</h3>
      <p class="muted">Vos appareils se synchronisent par VOTRE espace de stockage : aucun serveur Klavox, aucun compte a creer. Ce qui est depose la-bas est un export chiffre -- exactement ce que verrait le serveur souverain, jamais un secret en clair. Votre hebergeur ne peut donc rien lire. Indiquez un dossier WebDAV (Nextcloud : Fichiers &rsaquo; &laquo; Copier le lien WebDAV &raquo;) et un <strong>mot de passe d'application</strong>, pas celui de votre compte : Klavox ne le memorise pas, il est a resaisir a chaque synchro.</p>
      ${state.webdavMessage === null ? '' : `<p class="muted" role="status" data-testid="webdav-status">${escapeHtml(state.webdavMessage)}</p>`}
      <form data-form="webdav" class="stack">
        <label>Dossier WebDAV<input name="folderUrl" placeholder="https://cloud.example.eu/remote.php/dav/files/vous/Klavox" value="${escapeHtml(state.webdavFolderUrl ?? '')}" data-testid="webdav-folder" /></label>
        <label>Identifiant<input name="username" autocomplete="username" value="${escapeHtml(state.webdavUsername)}" data-testid="webdav-username" /></label>
        <label>Mot de passe d'application<input name="password" type="password" autocomplete="off" data-testid="webdav-password" /></label>
        <button type="submit" class="primary" ${occupe(state.webdavBusy)} data-testid="webdav-submit">${state.webdavBusy ? 'Synchro en cours…' : 'Synchroniser avec mon stockage'}</button>
      </form>
    </div>`;
  }

  /** Mode voyage (R18) : marquage des dossiers surs, armement, etat. */
  function renderTravelModeSettings(): string {
    const rows =
      state.folders.length === 0
        ? '<p class="muted">Aucun dossier : creez-en un et marquez-le « sur en voyage ».</p>'
        : state.folders
            .map(
              (folder) => `<label class="checkbox"><input type="checkbox" data-action="toggle-travel-safe" data-id="${folder.id}" data-name="${escapeHtml(folder.secrets.name)}" ${
                folder.secrets.travelSafe === true ? 'checked' : ''
              } /> ${escapeHtml(folder.secrets.name)}</label>`,
            )
            .join('');
    return `<div class="settings-block">
      <h3>Mode voyage</h3>
      <p class="muted">A la frontiere, un ecran verrouille se fait deverrouiller. Le mode voyage RETIRE de cet appareil tout ce qui n'est pas dans un dossier « sur en voyage » : ce n'est pas masque, c'est absent. Rien n'est supprime du coffre -- le serveur et vos autres appareils gardent tout, et la synchro rapatriera l'ensemble.${
        state.travelMode === true ? '' : ' Marquez ci-dessous les dossiers a emporter, puis armez le mode voyage depuis l’appareil qui part.'
      }</p>
      ${
        state.travelMode === true
          ? '<p data-sk-notice="warning" data-testid="travel-active"><strong>Mode voyage ACTIF.</strong> La desactivation doit venir d’un AUTRE appareil : c’est ce qui fait tenir la protection.</p>'
          : ''
      }
      ${state.travelMessage === null ? '' : `<p class="muted" role="status" data-testid="travel-status">${escapeHtml(state.travelMessage)}</p>`}
      <div class="stack">${rows}</div>
      <div class="detail-actions">
        <button class="secondary" data-action="enable-travel-mode" ${occupe(state.travelBusy)}>Armer le mode voyage sur cet appareil</button>
        <button class="secondary" data-action="disable-travel-mode" ${occupe(state.travelBusy)}>Desactiver (depuis un autre appareil)</button>
      </div>
    </div>`;
  }

  /**
   * Adresse e-mail du compte (« Change email » de Bitwarden, Proton Pass, 1Password). C'est le sel de la
   * derivation : la changer re-scelle la cle du coffre comme un changement de mot de passe maitre. Dit AVANT le
   * geste ce qui change (l'adresse de connexion, les autres appareils deconnectes) et ce qui ne change pas.
   */
  function renderEmailSettings(): string {
    if (state.envelope === null) return '';
    const consequence =
      state.remoteSession !== null
        ? 'Elle devient votre identifiant de connexion au serveur ; vos autres appareils seront deconnectes et redemanderont la nouvelle adresse avec votre mot de passe maitre.'
        : 'Ce coffre est local a cet appareil : l’adresse ne sert qu’a deriver sa cle, le changement n’est applique qu’ici.';
    return `<div class="settings-block">
      <h3>Adresse e-mail du compte</h3>
      <p class="muted">Adresse actuelle : <strong data-testid="account-email-current">${escapeHtml(state.envelope.email)}</strong>. ${consequence} Votre mot de passe maitre ne change pas, aucune entree n’est rechiffree${state.envelope.recoveryProtectedSymmetricKey ? ' et votre cle de recuperation reste valable' : ''}.</p>
      ${state.emailChangeMessage !== null ? `<p class="muted" role="status" data-testid="account-email-status">${escapeHtml(state.emailChangeMessage)}</p>` : ''}
      <form data-form="account-email" class="stack">
        <label>Nouvelle adresse e-mail
          <input name="newEmail" type="email" required autocomplete="email" maxlength="254" spellcheck="false" />
        </label>
        <label>Mot de passe maitre
          <input name="masterPassword" type="password" required autocomplete="current-password" />
        </label>
        <button type="submit" class="secondary" ${occupe(state.emailChangeBusy)} data-testid="account-email-submit">${ICON_LOCK} ${state.emailChangeBusy ? 'Derivation en cours…' : 'Changer l’adresse e-mail'}</button>
      </form>
    </div>`;
  }

  /**
   * Racine de tout le chiffrement : le changer re-scelle la cle du coffre, il
   * ne rechiffre AUCUNE entree (`docs/ARCHITECTURE.md` §1).
   */
  function renderMasterPasswordSettings(): string {
    const consequence =
      state.remoteSession !== null
        ? 'Vos autres appareils seront deconnectes du serveur : ils redemanderont le nouveau mot de passe a la prochaine synchro.'
        : 'Ce coffre est local a cet appareil : le changement n’est applique qu’ici. Un autre appareil garde son propre mot de passe tant qu’il n’est pas synchronise.';
    return `<div class="settings-block">
      <h3>Mot de passe maitre</h3>
      <p class="muted">Il n’est ni stocke, ni transmis : le changer re-scelle la cle de votre coffre, aucune entree n’est rechiffree et rien n’est perdu. ${consequence}${state.envelope?.recoveryProtectedSymmetricKey ? ' Votre cle de recuperation reste valable, inutile d’en generer une nouvelle.' : ''}</p>
      ${state.masterPasswordMessage !== null ? `<p class="muted" role="status" data-testid="master-password-status">${escapeHtml(state.masterPasswordMessage)}</p>` : ''}
      <form data-form="master-password" class="stack">
        <label>Mot de passe maitre actuel
          <input name="currentMasterPassword" type="password" required autocomplete="current-password" minlength="${MIN_MASTER_PASSWORD_LENGTH}" />
        </label>
        <label>Nouveau mot de passe maitre
          <input name="newMasterPassword" type="password" required autocomplete="new-password" minlength="${MIN_MASTER_PASSWORD_LENGTH}" />
          ${renderMasterPasswordStrengthHint()}
        </label>
        <label>Confirmer le nouveau mot de passe maitre
          <input name="confirm" type="password" required autocomplete="new-password" minlength="${MIN_MASTER_PASSWORD_LENGTH}" />
        </label>
        ${renderWeakMasterConsent()}${renderMasterBreachCheck()}
        <button type="submit" class="secondary" ${occupe(state.masterPasswordBusy)}>${ICON_LOCK} ${state.masterPasswordBusy ? 'Derivation en cours…' : 'Changer le mot de passe maitre'}</button>
      </form>
    </div>`;
  }

  /**
   * RACCOURCI de deverrouillage local (R12), a ne pas confondre avec le bloc
   * « Double authentification » ci-dessous : celui-ci ouvre le COFFRE sur cet
   * appareil, celui-la protege la CONNEXION au serveur. Le mot de passe maitre
   * reste la racine et ouvre toujours, meme sans aucune passkey.
   */
  function renderPasskeyUnlockSettings(): string {
    const intro =
      '<p class="muted">Ouvre ce coffre avec la biometrie ou le code de votre appareil (Touch ID, Windows Hello, ' +
      'cle de securite) au lieu de retaper votre mot de passe maitre. La passkey scelle une copie de la cle de ce ' +
      'coffre <strong>sur cet appareil uniquement</strong> : rien n’est envoye au serveur, rien n’entre dans vos ' +
      'exports. Votre mot de passe maitre reste la racine — il ouvre toujours le coffre, et reste indispensable sur ' +
      'un nouvel appareil.</p>';
    if (!isWebAuthnSupported()) {
      return `<div class="settings-block">
        <h3>Deverrouillage par passkey</h3>
        ${intro}
        <p class="muted">Ce navigateur ne prend pas en charge les passkeys (WebAuthn).</p>
      </div>`;
    }
    const rows = state.passkeyUnlocks
      .map((record) => {
        const created = Date.parse(record.createdAt);
        return `<li class="attachment-row">
          <span class="attachment-icon">${ICON_SHIELD}</span>
          <div class="attachment-meta">
            <span class="attachment-name" title="${escapeHtml(record.label)}">${escapeHtml(record.label)}</span>
            <span class="muted">Deverrouille cet appareil${Number.isNaN(created) ? '' : ` · enregistree le ${formatTimestamp(created)}`}</span>
          </div>
          <button class="icon-btn" data-action="revoke-passkey-unlock" data-name="${escapeHtml(record.label)}" data-credential="${escapeHtml(record.credentialId)}"
            title="Revoquer" aria-label="Revoquer ${escapeHtml(record.label)}" ${occupe(state.passkeyBusy)}>${ICON_TRASH}</button>
        </li>`;
      })
      .join('');
    const complet = state.passkeyUnlocks.length >= MAX_PASSKEY_UNLOCKS;
    return `<div class="settings-block">
      <h3>Deverrouillage par passkey</h3>
      ${intro}
      <p class="muted" data-testid="passkey-unlock-status">${
        state.passkeyUnlocks.length === 0
          ? 'Aucune passkey : ce coffre s’ouvre uniquement avec le mot de passe maitre.'
          : `${state.passkeyUnlocks.length} passkey(s) enregistree(s) sur cet appareil. Revoquer une passkey ne touche pas a votre coffre.`
      }</p>
      ${rows.length > 0 ? `<ul class="attachment-list">${rows}</ul>` : ''}
      ${state.passkeyMessage !== null ? `<p class="muted" role="status" data-testid="passkey-unlock-message">${escapeHtml(state.passkeyMessage)}</p>` : ''}
      ${
        complet
          ? `<p class="muted">Maximum atteint (${MAX_PASSKEY_UNLOCKS}) : revoquez-en une pour en ajouter une autre.</p>`
          : `<form data-form="passkey-unlock" class="stack">
              <label>Nom de la passkey (facultatif)<input name="label" maxlength="${MAX_PASSKEY_LABEL_LENGTH}" placeholder="Ce portable" autocomplete="off" /></label>
              <button type="submit" class="secondary" ${occupe(state.passkeyBusy)}>${ICON_SHIELD} ${state.passkeyBusy ? 'En attente de la passkey…' : 'Activer le deverrouillage par passkey'}</button>
            </form>`
      }
    </div>`;
  }

  /** Second facteur du compte serveur : protege la connexion, jamais le chiffrement (le mot de passe maitre reste la seule racine). */
  function renderTwoFactorSettings(): string {
    const intro = `<p class="muted">Exige une cle de securite (YubiKey, Titan…) ou une passkey en plus du mot de passe maitre pour connecter un appareil au serveur. Elle protege l’acces au compte ; le coffre reste chiffre par votre mot de passe maitre, qui n’est jamais transmis.</p>`;
    if (state.remoteSession === null) {
      return `<div class="settings-block">
        <h3>Double authentification</h3>
        ${intro}
        <p class="muted">Connectez-vous au serveur souverain pour la gerer.</p>
      </div>`;
    }
    const credentials = state.webauthnCredentials;
    const rows = (credentials ?? [])
      .map((credential) => {
        const name = credential.label ?? 'Cle de securite';
        const created = Date.parse(credential.createdAt);
        return `<li class="attachment-row">
          <span class="attachment-icon">${ICON_KEY}</span>
          <div class="attachment-meta">
            <span class="attachment-name" title="${escapeHtml(name)}">${escapeHtml(name)}</span>
            <span class="muted">${credential.deviceType === 'multiDevice' ? 'Passkey synchronisee' : 'Liee a cet authentificateur'}${Number.isNaN(created) ? '' : ` · ajoutee le ${formatTimestamp(created)}`}</span>
          </div>
          <button class="icon-btn" data-action="revoke-webauthn" data-name="${escapeHtml(name)}" data-credential="${escapeHtml(credential.credentialId)}" title="Revoquer" aria-label="Revoquer ${escapeHtml(name)}" ${occupe(state.webauthnBusy)}>${ICON_TRASH}</button>
        </li>`;
      })
      .join('');
    const status =
      credentials === null
        ? 'Chargement…'
        : credentials.length === 0
          ? state.accountTotpEnabled === true
            ? 'Aucune cle de securite : l’application d’authentification protege seule la connexion.'
            : 'Inactive : le mot de passe maitre suffit pour connecter un appareil.'
          : `Active : ${credentials.length} authentificateur(s). Gardez-en au moins deux (une cle de secours) pour ne pas perdre l’acces au serveur.`;
    return `<div class="settings-block">
      <h3>Double authentification</h3>
      ${intro}
      <p class="muted" data-testid="webauthn-status">${status}</p>
      ${rows.length > 0 ? `<ul class="attachment-list">${rows}</ul>` : ''}
      ${state.webauthnMessage !== null ? `<p class="muted" role="status">${escapeHtml(state.webauthnMessage)}</p>` : ''}
      ${
        isWebAuthnSupported()
          ? `<form data-form="webauthn-register" class="stack">
              <label>Nom de la cle (facultatif)<input name="label" maxlength="64" placeholder="YubiKey bureau" autocomplete="off" /></label>
              <button type="submit" class="secondary" ${occupe(state.webauthnBusy)}>${ICON_SHIELD} ${state.webauthnBusy ? 'En attente de la cle…' : 'Ajouter une cle de securite'}</button>
            </form>`
          : '<p class="muted">Ce navigateur ne prend pas en charge les cles de securite (WebAuthn).</p>'
      }
      ${renderAccountTotpSettings()}
    </div>`;
  }

  /**
   * Application d'authentification (TOTP) du compte serveur. La graine proposee n'apparait qu'entre « Activer » et la
   * confirmation, dans le code QR et en clair pour une saisie a la main ; elle n'est jamais relue ensuite.
   */
  function renderAccountTotpSettings(): string {
    const enabled = state.accountTotpEnabled;
    const busy = occupe(state.accountTotpBusy);
    const message = state.accountTotpMessage !== null ? `<p class="muted" role="status">${escapeHtml(state.accountTotpMessage)}</p>` : '';
    if (enabled === null) {
      return `<h4>Application d’authentification</h4><p class="muted" data-testid="account-totp-status">Etat inconnu : ce serveur ne la propose pas, ou n’a pas repondu.</p>${message}`;
    }
    if (enabled) {
      return `<h4>Application d’authentification</h4>
        <p class="muted" data-testid="account-totp-status">Active : chaque connexion d’un appareil demande le code a 6 chiffres de votre application.</p>
        ${message}
        <button type="button" class="secondary" data-action="account-totp-disable" ${busy}>${ICON_TRASH} Retirer l’application d’authentification</button>`;
    }
    const enrollment = state.accountTotpEnrollment;
    if (enrollment === null) {
      return `<h4>Application d’authentification</h4>
        <p class="muted" data-testid="account-totp-status">Inactive. Aegis, 2FAS, Google ou Microsoft Authenticator : un code a 6 chiffres en plus du mot de passe maitre, sans materiel a acheter.</p>
        ${message}
        <button type="button" class="secondary" data-action="account-totp-begin" ${busy}>${ICON_QR} Activer l’application d’authentification</button>`;
    }
    const code = encodeQr(enrollment.otpauthUri);
    const extent = code === null ? 0 : code.size + 8;
    const grouped = enrollment.secret.replace(/(.{4})(?=.)/gu, '$1 ');
    return `<h4>Application d’authentification</h4>
      <p class="muted" data-testid="account-totp-status">Etape 1 : scannez ce code avec votre application. Etape 2 : tapez le code qu’elle affiche.</p>
      ${
        code === null
          ? ''
          : `<figure class="wifi-qr-figure">
              <svg class="wifi-qr-code" data-account-totp="qr" viewBox="0 0 ${extent} ${extent}" shape-rendering="crispEdges" role="img" aria-label="Code QR d’activation de l’application d’authentification">
                <rect width="${extent}" height="${extent}" fill="#fff"/><path fill="#000" d="${qrSvgPath(code)}"/>
              </svg>
            </figure>`
      }
      <p class="muted">Pas d’appareil photo ? Saisissez cette cle dans l’application (type « basee sur le temps ») : <span class="mono" data-account-totp="secret">${escapeHtml(grouped)}</span></p>
      <p class="wifi-qr-warning">Ce code contient la graine : qui le photographie peut produire vos codes. Ne l’affichez qu’a l’abri des regards.</p>
      ${message}
      <form data-form="account-totp-confirm" class="stack">
        <label>Code affiche par l’application<input name="code" inputmode="numeric" autocomplete="one-time-code" maxlength="9" required /></label>
        <div class="credentials-transfer-actions">
          <button type="button" class="secondary" data-action="account-totp-cancel">Annuler</button>
          <button type="submit" class="primary" ${busy}>${ICON_CHECK} Confirmer et activer</button>
        </div>
      </form>`;
  }

  /**
   * Compte Idenva lie (docs/IDENVA-INTEGRATION.md §3.2) : couche d'identite
   * OPTIONNELLE. Lier n'ouvre aucun chemin vers le coffre -- le mot de passe
   * maitre reste la seule racine et ne part jamais chez Idenva.
   */
  function renderIdenvaSettings(): string {
    const intro = `<p class="muted">Reliez votre compte Idenva (identite SANTVIA) a ce compte Klavox : une deconnexion Idenva fermera aussi vos sessions Klavox. Votre coffre reste chiffre par votre mot de passe maitre, qui n’est jamais transmis a Idenva ; seul votre identifiant Idenva est conserve par le serveur, jamais votre adresse e-mail.</p>`;
    if (state.remoteSession === null) {
      return `<div class="settings-block">
        <h3>Compte Idenva</h3>
        ${intro}
        <p class="muted">Connectez-vous au serveur souverain pour le lier.</p>
      </div>`;
    }
    const config = state.idenvaConfig;
    if (config === 'absent') {
      return `<div class="settings-block">
        <h3>Compte Idenva</h3>
        <p class="muted" data-testid="idenva-status">Ce serveur fonctionne sans Idenva : aucune liaison possible, rien n’est requis.</p>
      </div>`;
    }
    let emetteur = '';
    if (config !== null) {
      try {
        emetteur = new URL(config.issuer).host;
      } catch {
        emetteur = config.issuer;
      }
    }
    return `<div class="settings-block">
      <h3>Compte Idenva</h3>
      ${intro}
      <p class="muted" data-testid="idenva-status">${config === null ? 'Chargement…' : `Fournisseur d’identite : ${escapeHtml(emetteur)}`}${
        config === null || state.idenvaLinked === null
          ? ''
          : state.idenvaLinked
            ? ` <span class="health-badge info" data-testid="idenva-linked">Compte lie</span>`
            : ` <span class="health-badge" data-testid="idenva-linked">Non lie</span>`
      }</p>
      ${state.idenvaMessage !== null ? `<p class="muted" role="status" data-testid="idenva-message">${escapeHtml(state.idenvaMessage)}</p>` : ''}
      ${
        config === null
          ? ''
          : `<div class="idenva-actions">
              ${
                state.idenvaBusy && idenvaRun !== null
                  ? `<button class="secondary" data-action="idenva-cancel">Annuler la liaison</button>`
                  : state.idenvaLinked === true
                    ? ''
                    : `<button class="secondary" data-action="idenva-link" ${occupe(state.idenvaBusy)}>${ICON_LINK} Lier mon compte Idenva</button>`
              }
              ${state.idenvaLinked === false ? '' : `<button class="secondary" data-action="idenva-unlink" ${occupe(state.idenvaBusy)}>Delier</button>`}
            </div>`
      }
    </div>`;
  }

  /**
   * Deconnecter tous mes appareils (« Deauthorize sessions » de Bitwarden, « Sessions » de Proton) :
   * le geste d'un appareil perdu ou d'une session qu'on soupconne volee. Dit AVANT le geste ce qui
   * se passe (cet appareil reste connecte, les autres redemanderont le mot de passe maitre, le coffre
   * ne bouge pas), puis exige le mot de passe maitre.
   */
  function renderSessionsSettings(): string {
    if (state.remoteSession === null) return '';
    const message =
      state.sessionsRevokeMessage === null
        ? ''
        : `<p class="muted" role="status" data-testid="sessions-revoke-message">${escapeHtml(state.sessionsRevokeMessage)}</p>`;
    return `<div class="settings-block">
      <h3>Appareils connectes</h3>
      <p class="muted">Un appareil perdu, un ordinateur partage, une connexion que vous ne reconnaissez pas : deconnectez d’un coup tous les appareils relies a ce compte serveur. Cet appareil reste connecte avec une session neuve ; les autres devront redonner votre mot de passe maitre avant de synchroniser. Le coffre, lui, ne bouge pas.</p>
      ${renderConnectedDevices()}
      ${message}
      <form data-form="sessions-revoke-all" class="stack">
        <button type="submit" class="secondary" ${occupe(state.sessionsRevokeBusy)} data-testid="sessions-revoke-submit">${ICON_LOCK} ${state.sessionsRevokeBusy ? 'Deconnexion…' : 'Deconnecter tous mes autres appareils'}</button>
      </form>
    </div>`;
  }

  /**
   * Liste des appareils connectes (« Devices » de Bitwarden, « Sessions » de Proton Pass), chargee A LA
   * DEMANDE : une ligne par connexion, date de connexion et derniere activite, navigateur resume par la
   * liste fermee de `describeUserAgent` (l'en-tete brut n'est qu'une infobulle echappee). Chaque autre
   * appareil a son bouton « Deconnecter » ; celui-ci n'en a pas -- on le quitte par « Se deconnecter ».
   */
  function renderConnectedDevices(): string {
    const message =
      state.connectedDevicesMessage === null
        ? ''
        : `<p class="muted" role="status" data-testid="connected-devices-message">${escapeHtml(state.connectedDevicesMessage)}</p>`;
    const button = `<button type="button" class="secondary" data-action="load-connected-devices" data-testid="connected-devices-load" ${occupe(state.connectedDevicesBusy)}>${ICON_CLOCK} ${
      state.connectedDevicesBusy ? 'Chargement…' : state.connectedDevices === null ? 'Afficher les appareils connectes' : 'Actualiser'
    }</button>`;
    const devices = state.connectedDevices;
    if (devices === null) return `${message}${button}`;
    const date = (value: number | null): string => (value === null ? 'date inconnue' : formatTimestamp(value));
    const rows = devices
      .map(
        (device) => `<li class="attachment-row" data-connected-device="${escapeHtml(device.id)}">
          <span class="attachment-icon">${ICON_SHIELD}</span>
          <div class="attachment-meta">
            <span class="attachment-name" title="${escapeHtml(device.userAgent ?? '')}">${escapeHtml(describeUserAgent(device.userAgent))}${
              device.current ? ` <span class="health-badge info" data-testid="connected-device-current">Cet appareil</span>` : ''
            }</span>
            <span class="muted">Connecte le ${escapeHtml(date(device.createdAt))} · derniere activite le ${escapeHtml(date(device.lastSeenAt))}</span>
          </div>
          ${
            device.current
              ? ''
              : `<button type="button" class="secondary" data-action="revoke-connected-device" data-name="${escapeHtml(describeUserAgent(device.userAgent))}" data-device="${escapeHtml(device.id)}" aria-label="Deconnecter ${escapeHtml(describeUserAgent(device.userAgent))}" ${occupe(state.connectedDevicesBusy)}>Deconnecter</button>`
          }
        </li>`,
      )
      .join('');
    return `${message}<ul class="attachment-list" data-testid="connected-devices">${rows}</ul>${button}`;
  }

  /**
   * Activite du compte (« Security logs » de Proton, « Activite recente » de Google) : connexions
   * reussies ET ratees, avec date, navigateur et adresse IP, 90 jours. IP et navigateur viennent de
   * QUI a frappe a la porte -- un tiers compris : tout passe par `escapeHtml`, et le navigateur n'est
   * montre qu'a travers la liste fermee de `describeUserAgent`.
   */
  function renderSecurityLogSettings(): string {
    if (state.remoteSession === null) return '';
    const intro = `<p class="muted">Ce que le serveur souverain a vu de votre compte ces 90 derniers jours : connexions reussies et ratees, cles de securite, recuperation, changements sensibles. Une connexion que vous ne reconnaissez pas merite un changement de mot de passe maitre.</p>`;
    const message =
      state.securityLogMessage === null ? '' : `<p class="muted" role="status" data-testid="security-log-message">${escapeHtml(state.securityLogMessage)}</p>`;
    const button = `<button type="button" class="secondary" data-action="load-security-log" data-testid="security-log-load" ${occupe(state.securityLogBusy)}>${ICON_CLOCK} ${
      state.securityLogBusy ? 'Chargement…' : state.securityLog === null ? 'Afficher l’activite recente' : 'Actualiser'
    }</button>`;
    const events = state.securityLog;
    if (events === null) {
      return `<div class="settings-block">
        <h3>Activite du compte</h3>
        ${intro}
        ${message}
        ${button}
      </div>`;
    }
    const summary = summarizeSecurityLog(events);
    const alert =
      summary.failures === 0
        ? ''
        : `<p class="muted" role="alert" data-testid="security-log-alert">${ICON_ALERT} ${summary.failures} tentative(s) ratee(s) en ${Math.round(SECURITY_LOG_ALERT_WINDOW_MS / 86_400_000)} jours, depuis ${summary.failureSources} adresse(s). Si ce n’etait pas vous : changez votre mot de passe maitre, puis deconnectez tous vos autres appareils.</p>`;
    const shown = events.slice(0, SECURITY_LOG_SHOWN);
    const rows = shown
      .map((event) => {
        const suspicious = isSuspiciousEvent(event);
        const badge = suspicious
          ? `<span class="health-badge danger">${securityResultLabel(event.resultat)}</span>`
          : event.resultat === 'succes'
            ? ''
            : `<span class="health-badge info">${securityResultLabel(event.resultat)}</span>`;
        const device = describeUserAgent(event.userAgent);
        return `<li class="attachment-row" data-security-event="${escapeHtml(event.evenement)}">
          <span class="attachment-icon">${suspicious ? ICON_ALERT : ICON_SHIELD}</span>
          <div class="attachment-meta">
            <span class="attachment-name">${escapeHtml(securityEventLabel(event.evenement))} ${badge}</span>
            <span class="muted" title="${escapeHtml(event.userAgent ?? '')}">${escapeHtml(formatTimestamp(event.horodatage))} · ${escapeHtml(device)}${event.ip === undefined ? '' : ` · ${escapeHtml(event.ip)}`}</span>
          </div>
        </li>`;
      })
      .join('');
    const more = events.length > shown.length ? `<p class="muted">Les ${shown.length} plus recents sur ${events.length}.</p>` : '';
    return `<div class="settings-block">
      <h3>Activite du compte</h3>
      ${intro}
      ${alert}
      ${message}
      ${events.length === 0 ? '<p class="muted">Aucun evenement enregistre ces 90 derniers jours.</p>' : `<ul class="attachment-list" data-testid="security-log">${rows}</ul>`}
      ${more}
      ${button}
    </div>`;
  }

  /**
   * Supprimer le compte serveur (droit a l'effacement, docs/DATA_PROTECTION.md §3 ;
   * « Delete account » de Bitwarden / 1Password / Proton Pass). Dit AVANT le geste
   * ce qui part, ce qui reste (coffre de cet appareil, sauvegardes, journal), et
   * exige l'adresse recopiee puis le mot de passe maitre.
   */
  function renderAccountDeletionSettings(): string {
    const session = state.remoteSession;
    const message =
      state.accountDeletionMessage === null
        ? ''
        : `<p class="muted" role="status" data-testid="account-deletion-message">${escapeHtml(state.accountDeletionMessage)}</p>`;
    if (session === null) {
      return message === ''
        ? ''
        : `<div class="settings-block account-deletion">
            <h3>Supprimer mon compte serveur</h3>
            ${message}
          </div>`;
    }
    let serveur = session.baseUrl;
    try {
      serveur = new URL(session.baseUrl).host;
    } catch {
      // Adresse deja acceptee par la synchro : on l'affiche telle quelle.
    }
    return `<div class="settings-block account-deletion">
      <h3>Supprimer mon compte serveur</h3>
      <p class="muted">Efface definitivement de <strong>${escapeHtml(serveur)}</strong> : votre adresse e-mail, le coffre chiffre (entrees, dossiers, pieces jointes, corbeille), vos envois (les liens cessent de repondre), vos delegations d’acces d’urgence, vos cles de securite, la liaison Idenva et les sessions de tous vos appareils. Personne, pas meme l’hebergeur, ne pourra le restaurer pour vous.</p>
      <p class="muted">Ce qui reste : le coffre de cet appareil, sauf si vous cochez la case ci-dessous, et la copie locale de vos autres appareils (les reconnecter a ce serveur recreerait un compte). Les sauvegardes de l’hebergeur gardent leur copie, toujours chiffree et illisible sans votre mot de passe maitre, jusqu’a la fin de leur cycle de conservation. Le journal de securite garde 90 jours un identifiant opaque, l’heure et l’adresse IP de ce geste, jamais votre adresse e-mail.</p>
      ${message}
      <form data-form="account-delete" class="stack">
        <label>Recopiez votre adresse e-mail pour confirmer<input name="confirmEmail" type="email" autocomplete="off" spellcheck="false" data-testid="account-delete-email" /></label>
        <label class="checkbox"><input type="checkbox" name="eraseDevice" data-testid="account-delete-device" /> Effacer aussi le coffre de cet appareil</label>
        <button type="submit" class="danger" ${occupe(state.accountDeletionBusy)} data-testid="account-delete-submit">${ICON_TRASH} ${state.accountDeletionBusy ? 'Suppression…' : 'Supprimer definitivement mon compte'}</button>
      </form>
    </div>`;
  }

  /**
   * Acces d'urgence / heritage (R32). Le kit affiche ici est le SEUL
   * exemplaire : il n'est ni stocke localement, ni envoye au serveur, et ne
   * se reaffiche jamais -- meme discipline que la cle de recuperation.
   */
  function renderEmergencyAccessSettings(): string {
    const intro = `<p class="muted">Designez un contact de confiance qui pourra ouvrir votre coffre s’il vous arrive quelque chose. Vous lui remettez un <strong>kit imprime, en main propre</strong> : le serveur ne l’a jamais. Quand il demande l’acces, <strong>vous etes prevenu</strong> et vous pouvez refuser pendant tout le delai d’attente. L’acces obtenu est en <strong>lecture seule</strong>.</p>`;
    if (state.remoteSession === null) {
      return `<div class="settings-block">
        <h3>Acces d’urgence</h3>
        ${intro}
        <p class="muted">Connectez-vous au serveur souverain pour le configurer.</p>
      </div>`;
    }
    const grants = state.emergencyGrants;
    const rows = (grants ?? [])
      .map((grant) => {
        const nom = grant.contactName ?? '(libelle illisible)';
        const etat =
          grant.status === 'revoked'
            ? 'Revoque'
            : grant.status === 'granted'
              ? 'ACCES OUVERT — refusez pour le fermer'
              : grant.status === 'waiting'
                ? `Demande en cours — acces le ${grant.availableAt === null ? '?' : formatTimestamp(grant.availableAt)}`
                : `En veille — delai de ${Math.round(grant.waitMs / (24 * 60 * 60 * 1000))} jour(s)`;
        const enAttente = grant.status === 'waiting' || grant.status === 'granted';
        return `<li class="attachment-row">
          <span class="attachment-icon">${ICON_KEY}</span>
          <div class="attachment-meta">
            <span class="attachment-name" title="${escapeHtml(nom)}">${escapeHtml(nom)}</span>
            <span class="muted">${escapeHtml(etat)}</span>
          </div>
          ${
            enAttente
              ? `<button class="secondary" data-action="refuse-emergency" data-id="${escapeHtml(grant.id)}" ${occupe(state.emergencyBusy)}>Refuser</button>`
              : ''
          }
          ${
            grant.status === 'revoked'
              ? ''
              : `<button class="icon-btn" data-action="revoke-emergency" data-id="${escapeHtml(grant.id)}" title="Revoquer" aria-label="Revoquer l’acces de ${escapeHtml(nom)}" ${occupe(state.emergencyBusy)}>${ICON_TRASH}</button>`
          }
        </li>`;
      })
      .join('');
    const options = EMERGENCY_WAIT_PRESETS.map(
      (preset) =>
        `<option value="${preset.waitMs}" ${preset.waitMs === DEFAULT_EMERGENCY_WAIT_MS ? 'selected' : ''}>${escapeHtml(preset.label)}</option>`,
    ).join('');
    return `<div class="settings-block">
      <h3>Acces d’urgence</h3>
      ${intro}
      <p class="muted" data-testid="emergency-status">${grants === null ? 'Chargement…' : grants.length === 0 ? 'Aucun contact de confiance.' : `${grants.filter((grant) => grant.status !== 'revoked').length} contact(s) de confiance actif(s).`}</p>
      ${rows.length > 0 ? `<ul class="attachment-list">${rows}</ul>` : ''}
      ${state.emergencyMessage !== null ? `<p class="muted" role="status">${escapeHtml(state.emergencyMessage)}</p>` : ''}
      ${
        state.emergencyKitReveal === null
          ? `<form data-form="emergency-grant" class="stack">
              <label>Nom du contact<input name="contactName" required maxlength="${MAX_CONTACT_NAME_LENGTH}" placeholder="Ma soeur" autocomplete="off" /></label>
              <label>Delai d’attente avant l’acces<select name="waitMs">${options}</select></label>
              <button type="submit" class="secondary" ${occupe(state.emergencyBusy)}>${ICON_KEY} ${state.emergencyBusy ? 'Generation…' : 'Designer un contact de confiance'}</button>
            </form>`
          : `<div class="recovery-reveal glass">
              <p data-sk-notice="warning"><strong>Remettez ce kit a ${escapeHtml(state.emergencyKitReveal.contactName)} en main propre.</strong> Il ne sera plus jamais affiche, et le serveur ne l’a pas. Ne l’envoyez ni par e-mail ni par messagerie.</p>
              <div class="generated-display mono" data-testid="emergency-kit">${escapeHtml(state.emergencyKitReveal.kitDisplay)}</div>
              <div class="detail-actions">
                <button class="secondary" data-action="print-emergency-kit">${ICON_PRINT} Imprimer</button>
                <button class="primary" data-action="confirm-emergency-kit">Le kit est remis</button>
              </div>
            </div>`
      }
    </div>`;
  }

  /**
   * Badge « Adresse trompeuse » : il nomme le site imite et la forme reelle (punycode) de l'adresse, la seule qui ne
   * ment pas -- l'utilisateur voit pourquoi `apple.com` n'est pas apple.com.
   */
  function renderDeceptiveBadge(entry: VaultItem): string {
    const found = findDeceptiveUri(entry.secrets.uris ?? [], vaultSites(state.entries));
    const why = found === null ? 'Une adresse de cette entree imite un autre site' : deceptiveHostReason(found);
    return `<span class="health-badge danger" data-testid="deceptive-uri" title="${escapeHtml(why)}">Adresse trompeuse</span>`;
  }

  /**
   * Badge « Site compromis » : il nomme la fuite et sa date, et dit ce qui fait de cette entree une cible -- son mot de
   * passe n'a pas change depuis. Ni l'adresse ni le mot de passe n'entrent dans le titre.
   */
  function renderSiteBreachBadge(breach: SiteBreach): string {
    const [year, month, day] = breach.breachDate.split('-');
    const why = `Fuite « ${breach.title} » du ${day}/${month}/${year} (source : Have I Been Pwned) : ce mot de passe n’a pas change depuis. Changez-le sur le site.`;
    return `<span class="health-badge danger" data-testid="compromised-site" title="${escapeHtml(why)}">Site compromis</span>`;
  }

  /** Lien « Changer sur le site » : la page de changement du site (`/.well-known/change-password`), hors du coffre. */
  function renderChangePasswordLink(href: string | null): string {
    if (href === null) return '';
    return `<a class="secondary health-link" data-testid="change-password" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" title="Ouvre la page de changement du mot de passe du site ; enregistrez ensuite le nouveau ici">${ICON_EXTERNAL} Changer sur le site</a>`;
  }

  /**
   * Badge d'une alerte que l'utilisateur peut juger mieux que l'outil, avec son bouton « Ignorer » : cette alerte, pour
   * cette entree seulement (`health-exclusion.ts`). Fuite, site compromis et sosie n'en ont jamais.
   */
  function ignorableBadge(entry: VaultItem, alert: IgnorableHealthAlert, tone: 'danger' | 'warn' | 'info', label: string): string {
    const name = escapeHtml(entry.secrets.name ?? 'Sans nom');
    return `<span class="health-badge ${tone} ignorable">${label}<button class="badge-ignore" data-action="ignore-health-alert" data-id="${entry.id}" data-alert="${alert}" data-name="${name}" title="Ignorer « ${label} » pour cette entree : l’alerte se tait, listee a part" aria-label="Ignorer « ${label} » pour ${name}">${ICON_CLOSE}</button></span>`;
  }

  /**
   * Badges d'un mot de passe secondaire (console, SMTP), nommes par leur compte : « Faible » seul laisserait croire que
   * c'est le mot de passe de l'entree. Ignorer « Faible » ou « Reutilise » vaut pour tous les mots de passe de l'entree.
   */
  function renderSecondaryPasswordBadges(entry: VaultItem, secondary: VaultSecondaryPasswordIssue): string {
    const account = SECONDARY_PASSWORD_LABELS[secondary.field];
    return [
      secondary.breachCount > 0
        ? `<span class="health-badge danger" data-testid="secondary-breach">${account} : fuite (${secondary.breachCount.toLocaleString('fr-FR')})</span>`
        : '',
      secondary.weak ? ignorableBadge(entry, 'weak', 'danger', `${account} : faible`) : '',
      secondary.reused ? ignorableBadge(entry, 'reused', 'danger', `${account} : reutilise`) : '',
    ].join('');
  }

  function renderHealthIssue(issue: VaultHealthIssue): string {
    const entry = state.entries.find((item) => item.id === issue.itemId);
    if (entry === undefined) return '';
    const badges = [
      issue.breachCount > 0 ? `<span class="health-badge danger">Fuite (${issue.breachCount.toLocaleString('fr-FR')})</span>` : '',
      issue.weak ? ignorableBadge(entry, 'weak', 'danger', 'Faible') : '',
      issue.reused ? ignorableBadge(entry, 'reused', 'danger', 'Reutilise') : '',
      issue.old ? ignorableBadge(entry, 'old', 'warn', 'Ancien') : '',
      issue.insecureUri ? ignorableBadge(entry, 'insecureUri', 'warn', entry.type === 'email' ? 'Connexion non chiffree' : 'Site non chiffre') : '',
      issue.deceptiveUri ? renderDeceptiveBadge(entry) : '',
      issue.siteBreach !== null ? renderSiteBreachBadge(issue.siteBreach) : '',
      issue.missingTotp ? ignorableBadge(entry, 'missingTotp', 'info', '2FA disponible') : '',
      ...issue.secondaryPasswords.map(renderSecondaryPasswordBadges.bind(null, entry)),
    ].join('');
    const domain = entry.secrets.uris?.[0] ? extractDomain(entry.secrets.uris[0]) : '';
    return `<li class="health-issue">
      <span class="entry-avatar">${TYPE_ICONS[entry.type] ?? entryInitials(entry.secrets.name ?? '')}</span>
      <span class="entry-meta">
        <span class="entry-name">${escapeHtml(entry.secrets.name ?? 'Sans nom')}</span>
        <span class="entry-sub muted">${escapeHtml(domain || entry.secrets.username || '')}</span>
      </span>
      <span class="health-badges">${badges}</span>
      ${
        issue.totpDocumentation === null
          ? ''
          : `<a class="secondary health-link" data-testid="totp-howto" href="${escapeHtml(issue.totpDocumentation)}" target="_blank" rel="noopener noreferrer" title="Marche a suivre sur le site (source : 2fa.directory)">Activer la 2FA</a>`
      }
      ${issue.mustChange ? renderChangePasswordLink(changePasswordUrl(entry, undefined, vaultSites(state.entries))) : ''}
      <button class="secondary" data-action="select-entry" data-id="${entry.id}" data-name="${escapeHtml(entry.secrets.name ?? 'Sans nom')}">Ouvrir</button>
      ${issue.breachCount > 0 || issue.weak || issue.reused || issue.old ? `<button class="primary" data-action="replace-password" data-id="${entry.id}" data-name="${escapeHtml(entry.secrets.name ?? 'Sans nom')}">${ICON_REFRESH} Remplacer</button>` : ''}
    </li>`;
  }

  function renderPasswordExpiryIssue(issue: VaultPasswordExpiryIssue): string {
    const entry = state.entries.find((item) => item.id === issue.itemId);
    if (entry === undefined) return '';
    const expiry = readPasswordExpiry(entry.secrets) ?? '';
    const badge = issue.expired
      ? '<span class="health-badge danger" data-testid="password-expiry" title="L’echeance que vous avez fixee est passee">Echeance passee</span>'
      : '<span class="health-badge warn" data-testid="password-expiry" title="L’echeance que vous avez fixee tombe sous 14 jours">Bientot</span>';
    // Avec une regle, le changement reporte l'echeance au lieu de l'acquitter : la ligne dit ou elle tombera.
    const renewDays = readPasswordRenewDays(entry.secrets);
    const renewal =
      renewDays === null
        ? ''
        : `<span class="entry-sub muted" data-health-renewal>${escapeHtml(`Change aujourd’hui : prochaine echeance le ${passwordExpiryAfterDays(renewDays, Date.now())} (tous les ${renewDays} jours)`)}</span>`;
    return `<li class="health-issue">
      <span class="entry-avatar">${TYPE_ICONS[entry.type] ?? entryInitials(entry.secrets.name ?? '')}</span>
      <span class="entry-meta">
        <span class="entry-name">${escapeHtml(entry.secrets.name ?? 'Sans nom')}</span>
        <span class="entry-sub muted">${escapeHtml(`Changer avant le ${expiry}`)}</span>${renewal}
      </span>
      <span class="health-badges">${badge}</span>
      ${renderChangePasswordLink(changePasswordUrl(entry, undefined, vaultSites(state.entries)))}
      <button class="secondary" data-action="select-entry" data-id="${entry.id}" data-name="${escapeHtml(entry.secrets.name ?? 'Sans nom')}">Ouvrir</button>
    </li>`;
  }

  /** Mots de passe a changer selon l'echeance fixee sur l'entree : liste a part, hors du score. */
  function renderPasswordExpiryIssues(expiries: readonly VaultPasswordExpiryIssue[]): string {
    if (expiries.length === 0) return '';
    const renewing = expiries.some((issue) => {
      const entry = state.entries.find((item) => item.id === issue.itemId);
      return entry !== undefined && readPasswordRenewDays(entry.secrets) !== null;
    });
    const settles = renewing ? 'le changement acquitte l’echeance, ou la reporte quand l’entree a une regle de renouvellement' : 'le changement acquitte l’echeance';
    return `<h3>Mots de passe a changer (${expiries.length})</h3>
      <p class="muted">L’echeance fixee sur l’entree est passee ou proche. Changez le mot de passe sur le site, puis dans l’entree : ${settles}.</p>
      <ul class="health-issues" data-health="password-expiry">${expiries.map(renderPasswordExpiryIssue).join('')}</ul>`;
  }

  function renderCardIssue(issue: VaultCardIssue): string {
    const entry = state.entries.find((item) => item.id === issue.itemId);
    if (entry === undefined) return '';
    const fields = readTypedFields(entry.secrets, 'card');
    const expiry = normalizeExpiry(fields['expiry'] ?? '') ?? '';
    const badge = issue.expired
      ? '<span class="health-badge danger">Expiree</span>'
      : '<span class="health-badge warn">Expire bientot</span>';
    return `<li class="health-issue">
      <span class="entry-avatar">${TYPE_ICONS[entry.type] ?? entryInitials(entry.secrets.name ?? '')}</span>
      <span class="entry-meta">
        <span class="entry-name">${escapeHtml(entry.secrets.name ?? 'Sans nom')}</span>
        <span class="entry-sub muted">${escapeHtml(expiry ? `Expiration ${expiry}` : '')}</span>
      </span>
      <span class="health-badges">${badge}</span>
      <button class="secondary" data-action="select-entry" data-id="${entry.id}" data-name="${escapeHtml(entry.secrets.name ?? 'Sans nom')}">Ouvrir</button>
    </li>`;
  }

  function renderDocumentIssue(issue: VaultDocumentIssue): string {
    const entry = state.entries.find((item) => item.id === issue.itemId);
    const document = IDENTITY_DOCUMENTS.find((doc) => doc.document === issue.document);
    if (entry === undefined || document === undefined) return '';
    const date = readTypedFields(entry.secrets, 'identity')[document.expiryKey] ?? '';
    const badge = issue.expired
      ? '<span class="health-badge danger">Expiree</span>'
      : '<span class="health-badge warn">Expire bientot</span>';
    return `<li class="health-issue">
      <span class="entry-avatar">${TYPE_ICONS[entry.type] ?? entryInitials(entry.secrets.name ?? '')}</span>
      <span class="entry-meta">
        <span class="entry-name">${escapeHtml(entry.secrets.name ?? 'Sans nom')}</span>
        <span class="entry-sub muted">${escapeHtml(`${document.label} valable jusqu’au ${normalizeDocumentDate(date) ?? date}`)}</span>
      </span>
      <span class="health-badges">${badge}</span>
      <button class="secondary" data-action="select-entry" data-id="${entry.id}" data-name="${escapeHtml(entry.secrets.name ?? 'Sans nom')}">Ouvrir</button>
    </li>`;
  }

  function renderApiKeyIssue(issue: VaultApiKeyIssue): string {
    const entry = state.entries.find((item) => item.id === issue.itemId);
    if (entry === undefined) return '';
    const date = readTypedFields(entry.secrets, 'api-key')['apiExpiry'] ?? '';
    const badge = issue.expired
      ? '<span class="health-badge danger">Expiree</span>'
      : '<span class="health-badge warn">Expire bientot</span>';
    return `<li class="health-issue">
      <span class="entry-avatar">${TYPE_ICONS[entry.type] ?? entryInitials(entry.secrets.name ?? '')}</span>
      <span class="entry-meta">
        <span class="entry-name">${escapeHtml(entry.secrets.name ?? 'Sans nom')}</span>
        <span class="entry-sub muted">${escapeHtml(`Expire le ${normalizeDocumentDate(date) ?? date}`)}</span>
      </span>
      <span class="health-badges">${badge}</span>
      <button class="secondary" data-action="select-entry" data-id="${entry.id}" data-name="${escapeHtml(entry.secrets.name ?? 'Sans nom')}">Ouvrir</button>
    </li>`;
  }

  function renderLicenseIssue(issue: VaultLicenseIssue): string {
    const entry = state.entries.find((item) => item.id === issue.itemId);
    if (entry === undefined) return '';
    const date = readTypedFields(entry.secrets, 'software-license')['softwareExpiry'] ?? '';
    const badge = issue.expired
      ? '<span class="health-badge danger">Expiree</span>'
      : '<span class="health-badge warn">Expire bientot</span>';
    return `<li class="health-issue">
      <span class="entry-avatar">${TYPE_ICONS[entry.type] ?? entryInitials(entry.secrets.name ?? '')}</span>
      <span class="entry-meta">
        <span class="entry-name">${escapeHtml(entry.secrets.name ?? 'Sans nom')}</span>
        <span class="entry-sub muted">${escapeHtml(`Expire le ${normalizeDocumentDate(date) ?? date}`)}</span>
      </span>
      <span class="health-badges">${badge}</span>
      <button class="secondary" data-action="select-entry" data-id="${entry.id}" data-name="${escapeHtml(entry.secrets.name ?? 'Sans nom')}">Ouvrir</button>
    </li>`;
  }

  function renderMembershipIssue(issue: VaultMembershipIssue): string {
    const entry = state.entries.find((item) => item.id === issue.itemId);
    if (entry === undefined) return '';
    const membership = readTypedFields(entry.secrets, 'membership');
    const date = membership['membershipExpiry'] ?? '';
    const badge = issue.expired
      ? '<span class="health-badge danger">Expiree</span>'
      : '<span class="health-badge warn">Expire bientot</span>';
    return `<li class="health-issue">
      <span class="entry-avatar">${TYPE_ICONS[entry.type] ?? entryInitials(entry.secrets.name ?? '')}</span>
      <span class="entry-meta">
        <span class="entry-name">${escapeHtml(entry.secrets.name ?? 'Sans nom')}</span>
        <span class="entry-sub muted">${escapeHtml([membership['organization'], `Expire le ${date}`].filter(Boolean).join(' · '))}</span>
      </span>
      <span class="health-badges">${badge}</span>
      <button class="secondary" data-action="select-entry" data-id="${entry.id}" data-name="${escapeHtml(entry.secrets.name ?? 'Sans nom')}">Ouvrir</button>
    </li>`;
  }

  function renderPasskeyIssue(issue: VaultPasskeyIssue): string {
    const entry = state.entries.find((item) => item.id === issue.itemId);
    if (entry === undefined) return '';
    const domain = entry.secrets.uris?.[0] ? extractDomain(entry.secrets.uris[0]) : '';
    const badge = issue.passwordless
      ? '<span class="health-badge info" title="Le site accepte la passkey a la place du mot de passe">Sans mot de passe</span>'
      : '<span class="health-badge info" title="Le site accepte la passkey comme second facteur">Second facteur</span>';
    return `<li class="health-issue">
      <span class="entry-avatar">${TYPE_ICONS[entry.type] ?? entryInitials(entry.secrets.name ?? '')}</span>
      <span class="entry-meta">
        <span class="entry-name">${escapeHtml(entry.secrets.name ?? 'Sans nom')}</span>
        <span class="entry-sub muted">${escapeHtml(domain || entry.secrets.username || '')}</span>
      </span>
      <span class="health-badges">${badge}</span>
      ${
        issue.documentation === null
          ? ''
          : `<a class="secondary health-link" data-testid="passkey-howto" href="${escapeHtml(issue.documentation)}" target="_blank" rel="noopener noreferrer" title="Marche a suivre sur le site (source : passkeys.directory)">Creer une passkey</a>`
      }
      <button class="secondary" data-action="select-entry" data-id="${entry.id}" data-name="${escapeHtml(entry.secrets.name ?? 'Sans nom')}">Ouvrir</button>
      <button class="secondary" data-action="passkey-elsewhere" data-id="${entry.id}" data-name="${escapeHtml(entry.secrets.name ?? 'Sans nom')}" title="La passkey de ce compte est rangee dans une cle de securite ou le trousseau du systeme">Deja fait</button>
    </li>`;
  }

  /** Passkeys disponibles : une recommandation, pas un defaut du mot de passe -- liste a part, hors du score. */
  function renderPasskeyIssues(issues: readonly VaultPasskeyIssue[]): string {
    if (issues.length === 0) return '';
    return `<h3>Passkeys disponibles (${issues.length})</h3>
      <p class="muted">Ces sites acceptent les passkeys : une connexion qu’un faux site ne peut pas detourner. Creez-la sur le site, puis indiquez « Deja fait » si elle est rangee hors de Klavox.</p>
      <ul class="health-issues">${issues.map(renderPasskeyIssue).join('')}</ul>`;
  }

  function renderRenamedSiteIssue(issue: VaultRenamedSiteIssue): string {
    const entry = state.entries.find((item) => item.id === issue.itemId);
    if (entry === undefined) return '';
    const name = escapeHtml(entry.secrets.name ?? 'Sans nom');
    return `<li class="health-issue" data-renamed-site="${entry.id}">
      <span class="entry-avatar">${TYPE_ICONS[entry.type] ?? entryInitials(entry.secrets.name ?? '')}</span>
      <span class="entry-meta">
        <span class="entry-name">${name}</span>
        <span class="entry-sub muted">${escapeHtml(issue.from)} est devenu ${escapeHtml(issue.to)}</span>
      </span>
      <span class="health-badges"><span class="health-badge info" title="Domaine quitte par le service (source : table d’Apple, celle de Safari)">Ancien domaine</span></span>
      <button class="secondary" data-action="select-entry" data-id="${entry.id}" data-name="${name}">Ouvrir</button>
      <button class="secondary" data-action="rename-site-address" data-id="${entry.id}" data-name="${name}" title="Remplace l’adresse ${escapeHtml(issue.from)} par https://${escapeHtml(issue.to)} -- l’ancienne reste dans les versions de l’entree">Mettre a jour l’adresse</button>
    </li>`;
  }

  /** Adresses sur un domaine abandonne : une mise a jour, pas un defaut du mot de passe -- liste a part, hors du score. */
  function renderRenamedSiteIssues(issues: readonly VaultRenamedSiteIssue[]): string {
    if (issues.length === 0) return '';
    return `<h3>Adresses a mettre a jour (${issues.length})</h3>
      <p class="muted">Ces services ont change de domaine. L’identifiant reste propose sur le nouveau ; mettre l’adresse a jour rend a l’entree son lien et les verifications du site actuel.</p>
      <ul class="health-issues" data-health="renamed-sites">${issues.map(renderRenamedSiteIssue).join('')}</ul>`;
  }

  /** Pieces d'identite a renouveler : liste a part, hors du score des mots de passe. */
  function renderDocumentIssues(documents: readonly VaultDocumentIssue[]): string {
    if (documents.length === 0) return '';
    return `<h3>Pieces d’identite a renouveler (${documents.length})</h3><ul class="health-issues">${documents.map(renderDocumentIssue).join('')}</ul>`;
  }

  /** Cles d'API a renouveler : liste a part, hors du score des mots de passe. */
  function renderApiKeyIssues(keys: readonly VaultApiKeyIssue[]): string {
    if (keys.length === 0) return '';
    return `<h3>Cles d’API a renouveler (${keys.length})</h3>
      <p class="muted">Emettez la nouvelle cle chez le service, deployez-la, puis revoquez l’ancienne : collee dans l’entree, elle rejoint l’historique.</p>
      <ul class="health-issues" data-health="api-keys">${keys.map(renderApiKeyIssue).join('')}</ul>`;
  }

  /** Licences logicielles a renouveler : liste a part, hors du score des mots de passe. */
  function renderLicenseIssues(licenses: readonly VaultLicenseIssue[]): string {
    if (licenses.length === 0) return '';
    return `<h3>Licences a renouveler (${licenses.length})</h3>
      <p class="muted">Renouvelez l’abonnement ou la maintenance avant l’echeance -- ou resiliez-le : collee dans l’entree, la nouvelle cle remplace l’ancienne.</p>
      <ul class="health-issues" data-health="licenses">${licenses.map(renderLicenseIssue).join('')}</ul>`;
  }

  /** Cartes de membre a renouveler : liste a part, hors du score des mots de passe. */
  function renderMembershipIssues(memberships: readonly VaultMembershipIssue[]): string {
    if (memberships.length === 0) return '';
    return `<h3>Adhesions et fidelite a renouveler (${memberships.length})</h3>
      <p class="muted">Renouvelez la cotisation avant l’echeance, ou utilisez vos points avant qu’ils n’expirent ; la nouvelle date se saisit dans l’entree.</p>
      <ul class="health-issues" data-health="memberships">${memberships.map(renderMembershipIssue).join('')}</ul>`;
  }

  /** Cartes a renouveler : liste a part, hors du score des mots de passe. */
  function renderCardIssues(cards: readonly VaultCardIssue[]): string {
    if (cards.length === 0) return '';
    return `<h3>Cartes a renouveler (${cards.length})</h3><ul class="health-issues">${cards.map(renderCardIssue).join('')}</ul>`;
  }

  /**
   * Entrees en double : chaque copie dit ce qu'elle porte en propre (passkey, code 2FA, notes, champs), pour que l'on
   * garde la plus complete. « Mettre a la corbeille » est reversible ; une entree sensible n'en a pas, sa fiche
   * redemande le mot de passe maitre. Quand il ne reste qu'une copie, le groupe disparait : la Sante ne propose
   * jamais de jeter la derniere.
   */
  function renderDuplicateGroups(groups: readonly VaultDuplicateGroup[]): string {
    const rendered = groups
      .map((group) => group.itemIds.map((id) => state.entries.find((item) => item.id === id)).filter((entry): entry is VaultItem => entry !== undefined))
      .filter((entries) => entries.length > 1)
      .map((entries) => {
        // Une copie sensible ne se fusionne pas plus qu'elle ne se jette sans le mot de passe maitre.
        const mergeable = !entries.some((entry) => isRepromptProtected(entry.secrets));
        const rows = entries.map((entry, index) => {
          const domain = entry.secrets.uris?.[0] ? extractDomain(entry.secrets.uris[0]) : '';
          const sub = [domain, entry.secrets.username ?? '', `modifiee le ${formatTimestamp(entry.updatedAt)}`].filter((part) => part.length > 0).join(' · ');
          const fields = readCustomFields(entry.secrets).length;
          const badges = [
            index === 0 ? '<span class="health-badge info">La plus recente</span>' : '',
            readPasskeys(entry.secrets).length > 0 ? '<span class="health-badge info">Passkey</span>' : '',
            entry.secrets.totpSecret ? '<span class="health-badge info">Code 2FA</span>' : '',
            (entry.secrets.notes ?? '').trim().length > 0 ? '<span class="health-badge info">Notes</span>' : '',
            fields > 0 ? `<span class="health-badge info">${fields} champ(s)</span>` : '',
          ].join('');
          return `<li class="health-issue" data-duplicate="${entry.id}">
      <span class="entry-avatar">${TYPE_ICONS[entry.type] ?? entryInitials(entry.secrets.name ?? '')}</span>
      <span class="entry-meta">
        <span class="entry-name">${escapeHtml(entry.secrets.name ?? 'Sans nom')}</span>
        <span class="entry-sub muted">${escapeHtml(sub)}</span>
      </span>
      <span class="health-badges">${badges}</span>
      <button class="secondary" data-action="select-entry" data-id="${entry.id}" data-name="${escapeHtml(entry.secrets.name ?? 'Sans nom')}">Ouvrir</button>
      ${mergeable ? `<button class="secondary" data-action="merge-duplicates" data-id="${entry.id}" data-name="${escapeHtml(entry.secrets.name ?? 'Sans nom')}" title="Garder cette copie et y reunir ce que portent les autres">Fusionner ici</button>` : ''}
      ${isRepromptProtected(entry.secrets) ? '' : `<button class="secondary" data-action="trash-duplicate" data-id="${entry.id}" data-name="${escapeHtml(entry.secrets.name ?? 'Sans nom')}">${ICON_TRASH} Mettre a la corbeille</button>`}
    </li>`;
        });
        return `<ul class="health-issues health-duplicates" data-health="duplicates">${rows.join('')}</ul>`;
      });
    if (rendered.length === 0) return '';
    return `<h3>Entrees en double (${rendered.length})</h3>
      <p class="muted">Meme site, meme identifiant, meme mot de passe : souvent un import fait deux fois. « Fusionner ici » garde cette copie et y reunit ce que portent les autres (adresses, notes, champs, passkeys, historique, pieces jointes), puis met les autres a la corbeille, restaurables ${TRASH_RETENTION_DAYS} jours ; une valeur qui differe n’est jamais ecrasee, elle devient un champ masque. Tant qu’elles restent, leur mot de passe compte aussi comme reutilise.</p>
      ${rendered.join('')}`;
  }

  /** Entrees exclues par l'utilisateur : listees a part, jamais escamotees, reinclusion en un clic. */
  function renderExcludedEntries(ids: readonly string[]): string {
    const rows = ids
      .map((id) => state.entries.find((item) => item.id === id))
      .filter((entry): entry is VaultItem => entry !== undefined)
      .map((entry) => {
        const domain = entry.secrets.uris?.[0] ? extractDomain(entry.secrets.uris[0]) : '';
        return `<li class="health-issue health-excluded">
      <span class="entry-avatar">${TYPE_ICONS[entry.type] ?? entryInitials(entry.secrets.name ?? '')}</span>
      <span class="entry-meta">
        <span class="entry-name">${escapeHtml(entry.secrets.name ?? 'Sans nom')}</span>
        <span class="entry-sub muted">${escapeHtml(domain || entry.secrets.username || '')}</span>
      </span>
      <span class="health-badges"><span class="health-badge info">Exclue</span></span>
      <button class="secondary" data-action="select-entry" data-id="${entry.id}" data-name="${escapeHtml(entry.secrets.name ?? 'Sans nom')}">Ouvrir</button>
      <button class="secondary" data-action="include-health" data-id="${entry.id}">${ICON_SHIELD} Surveiller a nouveau</button>
    </li>`;
      });
    if (rows.length === 0) return '';
    return `<h3>Exclues de l’analyse (${rows.length})</h3>
      <p class="muted">Ni alerte ni score pour ces entrees, a votre demande. Leur mot de passe compte toujours : une autre entree qui le reutilise reste signalee.</p>
      <ul class="health-issues">${rows.join('')}</ul>`;
  }

  /**
   * Alertes ignorees une a une : listees a part, jamais escamotees, chacune se retablit en un clic. L'entree reste
   * surveillee pour tout le reste.
   */
  function renderIgnoredAlerts(ignored: readonly VaultIgnoredAlerts[]): string {
    const rows = ignored
      .map(({ itemId, alerts }) => ({ entry: state.entries.find((item) => item.id === itemId), alerts }))
      .filter((row): row is { entry: VaultItem; alerts: readonly IgnorableHealthAlert[] } => row.entry !== undefined)
      .map(({ entry, alerts }) => {
        const domain = entry.secrets.uris?.[0] ? extractDomain(entry.secrets.uris[0]) : '';
        const name = escapeHtml(entry.secrets.name ?? 'Sans nom');
        return `<li class="health-issue health-ignored" data-ignored="${entry.id}">
      <span class="entry-avatar">${TYPE_ICONS[entry.type] ?? entryInitials(entry.secrets.name ?? '')}</span>
      <span class="entry-meta">
        <span class="entry-name">${name}</span>
        <span class="entry-sub muted">${escapeHtml(domain || entry.secrets.username || '')}</span>
      </span>
      <span class="health-badges">${alerts.map((alert) => `<span class="health-badge info">${HEALTH_ALERT_LABELS[alert]} ignore</span>`).join('')}</span>
      <button class="secondary" data-action="select-entry" data-id="${entry.id}" data-name="${name}">Ouvrir</button>
      ${alerts.map((alert) => `<button class="secondary" data-action="unignore-health-alert" data-id="${entry.id}" data-alert="${alert}" data-name="${name}">Signaler « ${HEALTH_ALERT_LABELS[alert]} »</button>`).join('')}
    </li>`;
      });
    if (rows.length === 0) return '';
    return `<h3>Alertes ignorees (${rows.length})</h3>
      <p class="muted">Ces alertes se taisent pour ces entrees, a votre demande, et ne comptent plus au score. Tout le reste est toujours surveille : une fuite, un site compromis ou une adresse sosie les signalent encore.</p>
      <ul class="health-issues" data-health="ignored">${rows.join('')}</ul>`;
  }

  function renderBreachBar(breached: number): string {
    if (state.breachEndpoint === null) {
      return `<div class="health-breach glass"><p class="muted">Verification des mots de passe fuites desactivee.</p>
        <button class="secondary" data-action="open-settings">Configurer</button></div>`;
    }
    const host = new URL(state.breachEndpoint).host;
    const status =
      state.breachError !== null
        ? echecOperation(state.breachError)
        : state.breachCounts === null
          ? `<p class="muted">Verification en k-anonymat via ${escapeHtml(host)} : seuls 5 caracteres d’empreinte sont envoyes.</p>`
          : `<p class="muted">${breached} mot(s) de passe expose(s) dans des fuites connues (verifie via ${escapeHtml(host)}).</p>`;
    return `<div class="health-breach glass">${status}
      <button class="secondary" data-action="check-breaches" ${occupe(state.breachBusy)}>${ICON_SHIELD} ${state.breachBusy ? 'Verification…' : 'Verifier les fuites'}</button></div>`;
  }

  /** E-mail du compte du coffre ouvert : la Sante du coffre et la jauge de l'editeur le lisent comme un mot connu. */
  function vaultAccountEmail(): string {
    return state.envelope?.email ?? state.localAccountEnvelope?.email ?? '';
  }

  function vaultHealthReport(): ReturnType<typeof computeVaultHealth> {
    return computeVaultHealth(state.entries, Date.now(), state.breachCounts ?? undefined, undefined, undefined, undefined, vaultAccountEmail());
  }

  function renderHealth(): string {
    const report = vaultHealthReport();
    if (report.score === null) {
      return report.passwordExpiryIssues.length === 0 &&
        report.cardIssues.length === 0 &&
        report.documentIssues.length === 0 &&
        report.apiKeyIssues.length === 0 &&
        report.licenseIssues.length === 0 &&
        report.membershipIssues.length === 0 &&
        report.renamedSiteIssues.length === 0 &&
        report.excludedIds.length === 0 &&
        report.ignoredAlerts.length === 0
        ? '<section class="detail-pane glass health-pane empty-state"><p class="muted">Aucun mot de passe a analyser pour le moment.</p></section>'
        : `<section class="detail-pane glass health-pane"><p class="muted">Aucun mot de passe a analyser pour le moment.</p>${renderPasswordExpiryIssues(report.passwordExpiryIssues)}${renderCardIssues(report.cardIssues)}${renderDocumentIssues(report.documentIssues)}${renderApiKeyIssues(report.apiKeyIssues)}${renderLicenseIssues(report.licenseIssues)}${renderMembershipIssues(report.membershipIssues)}${renderRenamedSiteIssues(report.renamedSiteIssues)}${renderIgnoredAlerts(report.ignoredAlerts)}${renderExcludedEntries(report.excludedIds)}</section>`;
    }
    const ignoredCount = report.ignoredAlerts.reduce((sum, ignored) => sum + ignored.alerts.length, 0);
    const level = report.score >= 70 ? 'good' : report.score >= 40 ? 'fair' : 'poor';
    const label = report.score >= 90 ? 'Excellent' : report.score >= 70 ? 'Bon' : report.score >= 40 ? 'A ameliorer' : 'Critique';
    const circumference = 2 * Math.PI * 52;
    const tile = (count: number, name: string, hint: string): string =>
      `<div class="health-tile ${count > 0 ? 'flagged' : ''}"><span class="health-count">${count}</span><span class="health-name">${name}</span><span class="muted">${hint}</span></div>`;
    return `<section class="detail-pane glass health-pane">
      <div class="health-summary">
        <svg class="health-ring ${level}" viewBox="0 0 120 120" role="img" aria-label="Score de sante ${report.score} sur 100">
          <circle cx="60" cy="60" r="52" class="track" />
          <circle cx="60" cy="60" r="52" class="value" stroke-dasharray="${circumference}" stroke-dashoffset="${circumference * (1 - report.score / 100)}" transform="rotate(-90 60 60)" />
          <text x="60" y="60" dominant-baseline="central" text-anchor="middle">${report.score}</text>
        </svg>
        <div>
          <h2>Sante du coffre : ${label}</h2>
          <p class="muted">${report.analyzed} mot(s) de passe analyse(s) sur cet appareil${report.excludedIds.length > 0 ? `, ${report.excludedIds.length} entree(s) exclue(s) a votre demande` : ''}${ignoredCount > 0 ? `, ${ignoredCount} alerte(s) ignoree(s)` : ''}. Rien ne quitte votre coffre.</p>
        </div>
      </div>
      <div class="health-tiles">
        ${tile(report.breached, 'Fuites', state.breachCounts !== null ? 'Presents dans des fuites connues' : state.breachEndpoint === null ? 'Verification desactivee' : 'Pas encore verifie')}
        ${tile(report.weak, 'Faibles', 'Moins de 40 bits estimes')}
        ${tile(report.reused, 'Reutilises', 'Partages entre plusieurs entrees')}
        ${tile(report.old, 'Anciens', 'Non modifies depuis plus d’un an')}
        ${tile(report.passwordExpiryIssues.length, 'A changer', 'Echeance fixee sur l’entree passee ou sous 14 jours')}
        ${tile(report.insecureUri, 'Non chiffres', 'Adresse en http:// : le mot de passe circule en clair')}
        ${tile(report.deceptiveUri, 'Sosies', 'Adresse qui imite un autre site : lettres d’un autre alphabet, une lettre changee ou deux interverties, nom d’un autre site enchasse devant le sien ou accole a un mot comme « login »')}
        ${tile(report.compromisedSite, 'Sites compromis', 'Le site a fuite depuis le dernier changement du mot de passe')}
        ${tile(report.missingTotp, 'Sans 2FA', 'Le site propose un code TOTP')}
        ${tile(report.passkeyAvailable, 'Passkeys', 'Le site accepte les passkeys')}
        ${tile(report.renamedSites, 'Anciens domaines', 'Le service a change de domaine : adresse a mettre a jour')}
        ${tile(report.cardIssues.length, 'Cartes', 'Expirees ou expirant sous 60 jours')}
        ${tile(report.documentIssues.length, 'Pieces d’identite', 'Passeport, carte, permis expires ou expirant sous 6 mois')}
        ${tile(report.apiKeyIssues.length, 'Cles d’API', 'Expirees ou expirant sous 30 jours')}
        ${tile(report.licenseIssues.length, 'Licences', 'Abonnements expires ou expirant sous 30 jours')}
        ${tile(report.membershipIssues.length, 'Adhesions', 'Cartes de membre et de fidelite expirees ou expirant sous 30 jours')}
        ${tile(report.duplicates, 'Doublons', 'Meme site, meme identifiant, meme mot de passe')}
      </div>
      ${renderBreachBar(report.breached)}
      ${
        report.passwordExpiryIssues.length === 0 &&
        report.apiKeyIssues.length === 0 &&
        report.licenseIssues.length === 0 &&
        report.membershipIssues.length === 0 &&
        report.duplicateGroups.length === 0 &&
        report.issues.length === 0 && report.cardIssues.length === 0 && report.documentIssues.length === 0
          ? `<p class="health-ok">${ICON_CHECK} Aucun probleme detecte.</p>`
          : report.issues.length === 0
            ? ''
            : `<h3>A traiter (${report.issues.length})</h3><ul class="health-issues">${report.issues.map(renderHealthIssue).join('')}</ul>`
      }
      ${renderPasswordExpiryIssues(report.passwordExpiryIssues)}
      ${renderCardIssues(report.cardIssues)}
      ${renderDocumentIssues(report.documentIssues)}
      ${renderPasskeyIssues(report.passkeyIssues)}
      ${renderRenamedSiteIssues(report.renamedSiteIssues)}
      ${renderApiKeyIssues(report.apiKeyIssues)}
      ${renderLicenseIssues(report.licenseIssues)}
      ${renderMembershipIssues(report.membershipIssues)}
      ${renderDuplicateGroups(report.duplicateGroups)}
      ${renderIgnoredAlerts(report.ignoredAlerts)}
      ${renderExcludedEntries(report.excludedIds)}
    </section>`;
  }

  // --- Envoi ponctuel, Klavox Send (R15) --------------------------------

  /** Duree restante lisible ; « expire » des que l'echeance est passee. */
  function remainingDelay(expiresAt: number, now = Date.now()): string {
    const ms = expiresAt - now;
    if (ms <= 0) return 'expire';
    const hours = Math.floor(ms / 3_600_000);
    if (hours < 1) return `${Math.max(1, Math.round(ms / 60_000))} min`;
    if (hours < 48) return `${hours} h`;
    return `${Math.round(hours / 24)} j`;
  }

  function renderSendRow(send: OwnedSend): string {
    const views = send.maxViews === null ? `${send.views} telechargement(s)` : `${send.views}/${send.maxViews} telechargement(s)`;
    const etat = send.revokedAt !== null ? 'Revoque' : send.active ? `Actif — expire dans ${remainingDelay(send.expiresAt)}` : 'Termine';
    return `<li class="send-row">
      <div class="send-row-main">
        <span class="send-row-label">${escapeHtml(send.label.length > 0 ? send.label : 'Envoi sans intitule')}</span>
        <span class="muted">${escapeHtml(etat)} · ${escapeHtml(views)}${send.passwordProtected ? ' · mot de passe requis' : ''}</span>
        ${send.note.length > 0 ? `<span class="send-row-note" data-send-note><span class="muted">Note privee :</span> ${escapeHtml(send.note)}</span>` : ''}
      </div>
      <div class="detail-actions">
        ${
          send.active
            ? `<button class="secondary" data-action="copy" data-name="${escapeHtml(send.label.length > 0 ? send.label : 'Envoi sans intitule')}" data-value="${escapeHtml(send.shareUrl)}">${ICON_COPY} Copier le lien</button>`
            : ''
        }
        ${
          // Un envoi epuise peut encore etre prolonge ou rouvert a d'autres telechargements ; un envoi revoque, jamais.
          send.revokedAt === null && state.sendEditingId !== send.id
            ? `<button class="secondary" data-action="edit-send" data-id="${escapeHtml(send.id)}" data-name="${escapeHtml(send.label.length > 0 ? send.label : 'Envoi sans intitule')}">${ICON_CLOCK} Modifier</button>`
            : ''
        }
        ${
          send.active
            ? `<button class="secondary" data-action="revoke-send" data-id="${escapeHtml(send.id)}" data-name="${escapeHtml(send.label.length > 0 ? send.label : 'Envoi sans intitule')}">${ICON_TRASH} Revoquer</button>`
            : ''
        }
      </div>
      ${send.revokedAt === null && state.sendEditingId === send.id ? renderSendEditor(send) : ''}
    </li>`;
  }

  /**
   * Modifier un envoi depose (« Edit Send » de Bitwarden) : echeance, telechargements, note privee. Le lien ne change
   * pas -- le destinataire garde celui qu'il a recu ; le contenu et le mot de passe ne se modifient pas.
   */
  function renderSendEditor(send: OwnedSend): string {
    const now = Date.now();
    const echeance = send.expiresAt > now ? send.expiresAt : now + 7 * 24 * 60 * 60 * 1000;
    return `<form data-form="send-edit" data-id="${escapeHtml(send.id)}" class="stack send-edit">
      <label>Expire le
        <input name="expiresAt" type="datetime-local" data-input="send-edit-expires-at" required min="${toLocalDateTimeInput(now)}" max="${toLocalDateTimeInput(now + MAX_SEND_TTL_MS)}" value="${toLocalDateTimeInput(echeance)}" />
      </label>
      <label>Telechargements autorises
        <input name="maxViews" type="number" inputmode="numeric" min="1" max="${MAX_SEND_VIEWS}" step="1" placeholder="Sans limite" value="${send.maxViews === null ? '' : String(send.maxViews)}" />
      </label>
      <p class="muted">Deja ${send.views} telechargement(s). Au plus 30 jours a compter d’aujourd’hui.</p>
      <label>Note privee
        <textarea name="note" rows="2" maxlength="${MAX_SEND_NOTE_LENGTH}" placeholder="Pour vous seul">${escapeHtml(send.note)}</textarea>
      </label>
      <p class="muted">Le lien reste le meme : rien a renvoyer au destinataire.</p>
      <div class="detail-actions">
        <button type="submit" class="primary" ${occupe(state.sendBusy)}>${state.sendBusy ? 'Enregistrement…' : 'Enregistrer'}</button>
        <button type="button" class="secondary" data-action="cancel-edit-send">Annuler</button>
      </div>
    </form>`;
  }

  function renderSendComposer(): string {
    const draft = state.sendDraft;
    const custom = draft.expiresAtLocal !== null;
    const expiries =
      SEND_EXPIRY_CHOICES.map(
        (choice) => `<option value="${choice.ms}" ${!custom && draft.expiresInMs === choice.ms ? 'selected' : ''}>${escapeHtml(choice.label)}</option>`,
      ).join('') + `<option value="custom" ${custom ? 'selected' : ''}>Date personnalisee…</option>`;
    // Bornes indicatives pour le selecteur du navigateur ; la vraie garde est `resolveCustomSendExpiry`, a l'envoi.
    const now = Date.now();
    const echeance = custom
      ? `<label>Expire le
          <input name="expiresAt" type="datetime-local" data-input="send-expires-at" required min="${toLocalDateTimeInput(now)}" max="${toLocalDateTimeInput(now + MAX_SEND_TTL_MS)}" value="${escapeHtml(draft.expiresAtLocal ?? '')}" />
        </label>
        <p class="muted">Au plus 30 jours. Passee cette date, le lien ne s’ouvre plus et l’envoi est efface du serveur.</p>`
      : '';
    const fichier =
      state.sendFile === null
        ? `<label class="secondary file-btn">${ICON_UPLOAD} Choisir un fichier
            <input type="file" data-input="send-file" hidden />
          </label>
          <p class="muted">${escapeHtml(formatFileSize(MAX_SEND_FILE_BYTES))} maximum. Le fichier est chiffre ici, avant tout envoi.</p>`
        : `<p class="muted">${ICON_FILE} ${escapeHtml(state.sendFile.name)} — ${escapeHtml(formatFileSize(state.sendFile.bytes.length))}
            <button type="button" class="link-btn" data-action="clear-send-file">Changer</button></p>`;
    const share = state.sendShare;
    // Fiche partagee : seuls les libelles et les references sont dans la page, jamais une valeur.
    const contenu =
      share !== null
        ? `<fieldset class="send-share" data-section="send-share">
            <legend>Fiche partagee : <strong>${escapeHtml(share.name)}</strong></legend>
            <p class="muted">Cochez ce que le destinataire verra. Les secrets lui arrivent masques : il les devoile ou les copie.</p>
            ${share.fields
              .map(
                (field) =>
                  `<label class="checkbox"><input type="checkbox" name="share-field" value="${escapeHtml(field.ref)}" ${share.excluded.includes(field.ref) ? '' : 'checked'} /> ${escapeHtml(field.label)}${field.secret ? ' <span class="muted">· secret</span>' : ''}</label>`,
              )
              .join('')}
            <p class="muted">Jamais partages : la graine de double authentification (un second facteur permanent survivrait au lien), l’historique des mots de passe, les passkeys, la cle SSH privee et les pieces jointes.</p>
            <button type="button" class="link-btn" data-action="cancel-share-entry">Envoyer autre chose</button>
          </fieldset>`
        : `<div class="send-kind" role="group" aria-label="Nature de l’envoi">
        <label class="checkbox"><input type="radio" name="send-kind" value="text" ${draft.kind === 'text' ? 'checked' : ''} /> Texte</label>
        <label class="checkbox"><input type="radio" name="send-kind" value="file" ${draft.kind === 'file' ? 'checked' : ''} /> Fichier</label>
      </div>
      ${
        draft.kind === 'text'
          ? `<label>Contenu
              <textarea name="text" data-input="send-text" rows="4" required placeholder="Le secret a transmettre">${escapeHtml(draft.text)}</textarea>
            </label>
            <label class="checkbox"><input type="checkbox" name="hidden" ${draft.hidden ? 'checked' : ''} /> Masquer le texte a l’ouverture</label>
            <p class="muted">Le destinataire devra cliquer « Afficher » : rien ne s’affiche par-dessus son epaule.</p>`
          : fichier
      }`;
    return `<form data-form="send" class="stack">
      <label>Intitule
        <input name="label" data-input="send-label" maxlength="120" placeholder="Ce que le destinataire verra" value="${escapeHtml(draft.label)}" />
      </label>
      ${contenu}
      <label>Expiration
        <select name="expiresIn" data-input="send-expiry">${expiries}</select>
      </label>
      ${echeance}
      <label>Telechargements autorises
        <input name="maxViews" type="number" inputmode="numeric" min="1" max="${MAX_SEND_VIEWS}" step="1" placeholder="Sans limite" value="${escapeHtml(draft.maxViews)}" />
      </label>
      <label>Mot de passe (facultatif)
        <input name="password" type="password" autocomplete="new-password" placeholder="S’AJOUTE au lien, ne le remplace pas" />
      </label>
      <p class="muted">Avec un mot de passe, le lien seul ne suffit plus : transmettez-les par deux canaux differents.</p>
      <label>Note privee (facultatif)
        <textarea name="note" data-input="send-note" rows="2" maxlength="${MAX_SEND_NOTE_LENGTH}" placeholder="A qui, pourquoi : pour vous seul">${escapeHtml(draft.note)}</textarea>
      </label>
      <p class="muted">Scellee dans votre coffre : ni le destinataire ni le serveur ne la voient.</p>
      <button type="submit" class="primary" ${occupe(state.sendBusy)}>
        ${ICON_SEND} ${state.sendBusy ? 'Chiffrement…' : 'Creer le lien'}
      </button>
    </form>`;
  }

  function renderSend(): string {
    if (state.remoteSession === null) {
      return `<section class="detail-pane glass send-pane">
        <h2>Envoi ponctuel</h2>
        <p class="muted">Partager un secret avec quelqu’un qui n’a pas Klavox suppose un serveur pour deposer l’envoi chiffre : votre coffre est pour l’instant local a cet appareil.</p>
        <div class="detail-actions">
          <button class="secondary" data-action="open-settings">Configurer la synchro</button>
        </div>
      </section>`;
    }
    const cree =
      state.sendCreated === null
        ? ''
        : `<div class="send-created glass">
            <p data-sk-notice="warning"><strong>Lien pret.</strong> Il ne sera plus affiche ainsi : copiez-le maintenant. La cle est apres le « # » — elle n’est jamais envoyee au serveur.</p>
            <div class="generated-display mono">${escapeHtml(state.sendCreated.shareUrl)}</div>
            <div class="detail-actions">
              <button class="secondary" data-action="copy" data-value="${escapeHtml(state.sendCreated.shareUrl)}">${ICON_COPY} Copier le lien</button>
              <button class="primary" data-action="dismiss-send-created">J’ai copie le lien</button>
            </div>
          </div>`;
    const liste =
      state.sends === null
        ? '<p class="muted">Chargement…</p>'
        : state.sends.length === 0
          ? '<p class="muted">Aucun envoi pour le moment.</p>'
          : `<ul class="send-list">${state.sends.map(renderSendRow).join('')}</ul>`;
    return `<section class="detail-pane glass send-pane">
      <h2>Envoi ponctuel</h2>
      <p class="muted">Transmettez un secret a quelqu’un qui n’a pas de compte Klavox. Le contenu est chiffre sur cet appareil ; la cle voyage dans le lien, apres le « # », que les navigateurs n’envoient jamais au serveur. Le serveur ne garde que du chiffre, une echeance et un compteur.</p>
      ${state.sendMessage !== null ? `<p class="muted" role="status" data-testid="send-status">${escapeHtml(state.sendMessage)}</p>` : ''}
      ${cree}
      ${renderSendComposer()}
      <div class="settings-block">
        <h3>Vos envois</h3>
        ${liste}
      </div>
    </section>`;
  }

  /** Vue du DESTINATAIRE : aucun coffre, aucun compte, juste un lien. */
  function renderSendReceive(): string {
    const receive = state.sendReceive!;
    const body = ((): string => {
      switch (receive.phase) {
        case 'loading':
          return '<p class="muted">Verification du lien…</p>';
        case 'error':
          return `<p data-sk-notice="alert" role="alert">${escapeHtml(receive.error ?? 'lien invalide')}</p>
            <p class="muted">Un envoi disparait des qu’il expire, qu’il est revoque ou que son nombre de telechargements est atteint. Demandez-en un nouveau a son expediteur.</p>`;
        case 'ready':
          return `<p class="muted">Cet envoi vous attend. L’ouvrir consomme un telechargement.</p>
            <button class="primary" data-action="receive-send" ${occupe(receive.busy)}>
              ${receive.busy ? 'Dechiffrement…' : 'Ouvrir l’envoi'}
            </button>`;
        case 'locked':
          return `<p class="muted">Cet envoi est protege par un mot de passe, demande a son expediteur. Il s’ajoute au lien : les deux sont necessaires.</p>
            ${receive.error !== null ? `<p class="error">${escapeHtml(receive.error)}</p>` : ''}
            <form data-form="send-unlock" class="stack">
              <label>Mot de passe de l’envoi
                <input name="password" type="password" required autocomplete="off" />
              </label>
              <button type="submit" class="primary" ${occupe(receive.busy)}>
                ${receive.busy ? 'Dechiffrement…' : 'Ouvrir l’envoi'}
              </button>
            </form>`;
        case 'opened':
          return renderSendPayload(receive.payload!);
      }
    })();
    return `<div class="auth-screen">
      <main class="auth-card glass send-receive">
        <div class="auth-logo">${ICON_SEND}</div>
        <h2>Recevoir un envoi</h2>
        ${body}
        <p class="muted">Le dechiffrement a lieu dans ce navigateur. Klavox n’a jamais vu ce contenu en clair.</p>
      </main>
    </div>`;
  }

  function renderSendPayload(payload: SendPayload): string {
    const titre = typeof payload.label === 'string' && payload.label.length > 0 ? `<h3>${escapeHtml(payload.label)}</h3>` : '';
    if (payload.kind === 'file') {
      return `${titre}
        <p class="muted">${ICON_FILE} ${escapeHtml(payload.fileName ?? 'fichier')}</p>
        <button class="primary" data-action="download-send-file">${ICON_DOWNLOAD} Telecharger le fichier</button>`;
    }
    if (payload.kind === 'item') {
      // Fiche partagee : un champ secret n'est ni affiche ni dans le DOM tant qu'il n'est pas devoile ; chaque
      // « Copier » ne porte qu'un rang, la valeur est relue dans le contenu dechiffre.
      const fields = payload.fields ?? [];
      const revealed = state.sendReceive?.revealed === true;
      const rows = fields
        .map((field, index) => {
          const shown = field.secret === true && !revealed ? '•'.repeat(10) : escapeHtml(field.value);
          return `<div class="field" data-send-field="${index}">
            <label>${escapeHtml(field.name)}</label>
            <div class="field-row">${field.value.includes('\n') ? `<p class="notes">${shown}</p>` : `<span class="mono">${shown}</span>`}
              <button class="icon-btn" data-action="copy-send-field" data-index="${index}" title="Copier" aria-label="Copier ${escapeHtml(field.name)}">${ICON_COPY}</button>
            </div>
          </div>`;
        })
        .join('');
      const toggle = fields.some((field) => field.secret === true)
        ? `<button class="secondary" data-action="toggle-send-text" aria-pressed="${revealed}">${revealed ? `${ICON_EYE_OFF} Masquer les secrets` : `${ICON_EYE} Afficher les secrets`}</button>`
        : '';
      return `${titre}<div class="send-item">${rows}</div>${toggle}`;
    }
    const texte = typeof payload.text === 'string' ? payload.text : '';
    if (payload.hidden === true) {
      // Masque : ni le texte, ni sa longueur dans le DOM tant qu'il n'est pas devoile ; la copie le relit dans l'etat.
      const revealed = state.sendReceive?.revealed === true;
      return `${titre}
        <div class="generated-display mono">${revealed ? escapeHtml(texte) : '•'.repeat(10)}</div>
        <div class="row">
          <button class="secondary" data-action="toggle-send-text" aria-pressed="${revealed}">${revealed ? `${ICON_EYE_OFF} Masquer` : `${ICON_EYE} Afficher`}</button>
          <button class="secondary" data-action="copy-send-text">${ICON_COPY} Copier</button>
        </div>`;
    }
    return `${titre}
      <div class="generated-display mono">${escapeHtml(texte)}</div>
      <button class="secondary" data-action="copy" data-value="${escapeHtml(texte)}">${ICON_COPY} Copier</button>`;
  }

  function renderShell(): string {
    const titles: Readonly<Record<Panel, string>> = {
      vault: 'Coffre',
      generator: 'Generateur',
      settings: 'Parametres',
      health: 'Sante du coffre',
      send: 'Envoi ponctuel',
    };
    const title = titles[state.panel];
    const main =
      state.panel === 'generator'
        ? renderGenerator()
        : state.panel === 'settings'
          ? renderSettings()
          : state.panel === 'health'
            ? state.entriesForHealth
              ? renderHealth()
              : '<section class="detail-pane glass health-loading" aria-busy="true"><p class="muted">Analyse du coffre…</p></section>'
            : state.panel === 'send'
              ? renderSend()
              : `${renderList()}${renderDetail()}`;
    return `<div class="app-shell">
      <h1 class="view-title">${escapeHtml(title)}</h1>
      ${renderRecoveryKeyStaleBanner()}
      <div class="app-body">
        ${renderSidebar()}
        ${main}
      </div>
      ${state.flash !== null ? `<div data-role="flash" role="status">${escapeHtml(state.flash)}</div>` : ''}
    </div>`;
  }

  /**
   * Cle de recuperation activee avant la preuve de possession : le serveur refuse sa reinitialisation, mais
   * l'enveloppe locale existe toujours et l'ecran disait « Activee ». Visible sur tous les panneaux tant que la
   * cle n'est pas regeneree -- c'est un filet de securite troue, pas un detail de reglage.
   */
  function renderRecoveryKeyStaleBanner(): string {
    if (!state.recoveryKeyStale || state.remoteSession === null) return '';
    return `<div class="account-banner" data-sk-notice="warning" role="alert" data-testid="recovery-key-stale">
      <p><strong>Votre cle de recuperation doit etre regeneree.</strong> Elle a ete creee avant que le serveur exige la preuve de possession : elle ne permet plus de retrouver votre coffre. Generez-en une nouvelle et conservez-la, l’ancienne ne sert plus.</p>
      <button class="primary" data-action="enable-recovery-key">${ICON_KEY} Regenerer la cle</button>
    </div>`;
  }

  // --- Actions ----------------------------------------------------------

  async function handleAuthSubmit(form: HTMLFormElement): Promise<void> {
    const data = new FormData(form);
    const email = String(data.get('email') ?? '');
    const masterPassword = String(data.get('masterPassword') ?? '');
    state.authError = null;
    state.authBusy = true;
    render();
    try {
      if (state.authMode === 'create') {
        const confirm = String(data.get('confirm') ?? '');
        if (masterPassword !== confirm) throw new Error('les mots de passe ne correspondent pas');
        assertNewMasterPasswordAccepted(masterPassword, email, data.get('acceptWeakMaster') === 'on');
        await assertNewMasterPasswordNotExposed(masterPassword, masterBreachCheck(data), data.get('acceptExposedMaster') === 'on');
        const existing = await state.repository.loadAccountEnvelope();
        if (existing !== null) throw new Error('un coffre existe deja sur cet appareil -- deverrouillez-le plutot');
        const home = state.idenvaHome;
        const signIn = state.idenvaSignIn;
        const viaIdenva = home !== null && home !== 'absent' && signIn !== null;
        // E-mail laisse vide, ou creation DEPUIS Idenva : compte sans e-mail, sel Argon2id aleatoire (`docs/ARCHITECTURE.md` §1).
        const account = await createAccountAsync({ masterPassword, email: viaIdenva || email.trim() === '' ? undefined : email });
        const envelope: AccountEnvelope = {
          email: account.email,
          kdfParams: account.kdfParams,
          protectedSymmetricKey: account.protectedSymmetricKey,
        };
        // Depuis Idenva, le compte serveur (rattache au `sub`) existe AVANT le coffre local : un refus du serveur
        // ne laisse pas sur cet appareil un coffre que le compte Idenva ne retrouverait pas.
        const created = viaIdenva
          ? await registerWithIdenva(home.baseUrl, {
              ...signIn.token,
              kdfParams: account.kdfParams,
              masterPasswordHashBase64: account.masterPasswordHashBase64,
              protectedSymmetricKey: account.protectedSymmetricKey,
            })
          : null;
        await state.repository.saveAccountEnvelope(envelope);
        state.localAccountEnvelope = envelope;
        if (viaIdenva && created !== null) {
          state.remoteSession = {
            baseUrl: home.baseUrl,
            sessionToken: created.sessionToken,
            refreshToken: created.refreshToken,
            expiresAt: created.expiresAt,
            lastSyncedAt: 0,
          };
          state.idenvaSignIn = null;
        }
        await unlockWith(new VaultStore(state.repository, account.vaultKey), envelope);
      } else {
        const envelope = await state.repository.loadAccountEnvelope();
        if (envelope === null) throw new Error('aucun coffre local -- creez-en un d’abord');
        const unlocked = await unlockVaultAsync({
          masterPassword,
          email: envelope.kdfParams.saltBase64 === undefined ? email : undefined,
          kdfParams: envelope.kdfParams,
          protectedSymmetricKey: envelope.protectedSymmetricKey,
        });
        state.localAccountEnvelope = envelope;
        await unlockWith(new VaultStore(state.repository, unlocked.vaultKey), envelope);
      }
      state.masterPasswordExposed = false;
    } catch (cause) {
      state.authError = cause instanceof Error ? cause.message : 'echec de la derivation';
      state.masterPasswordExposed = cause instanceof ExposedMasterPasswordError;
    } finally {
      state.authBusy = false;
      render();
    }
  }

  /**
   * « Continuer avec Idenva » (accueil) : ID token, puis le serveur dit si un coffre existe pour ce compte. La
   * fenetre s'ouvre AVANT tout `await`, sinon le navigateur la bloque. Rien du chemin crypto n'est en jeu ici.
   */
  async function handleIdenvaSignIn(): Promise<void> {
    const home = state.idenvaHome;
    if (home === null || home === 'absent' || state.authBusy) return;
    const popup = window.open('', 'klavox-idenva', 'popup,width=520,height=720');
    if (popup === null) {
      state.authError = 'Fenetre bloquee : autorisez les fenetres surgissantes pour Klavox, puis recommencez.';
      render();
      return;
    }
    popup.opener = null;
    const channel = new BroadcastChannel(IDENVA_CALLBACK_CHANNEL);
    state.authError = null;
    state.authBusy = true;
    render();
    try {
      const token = await obtainIdenvaIdToken({
        config: { issuer: home.issuer, clientId: home.clientId, redirectUri: idenvaRedirectUri(window.location.origin) },
        channel,
        navigate: (url) => popup.location.replace(url),
      });
      if (token === null) return;
      state.idenvaSignIn = { token, lookup: await lookupOidcAccount(home.baseUrl, token) };
    } catch (cause) {
      state.authError = describeIdenvaSignInError(cause);
    } finally {
      channel.close();
      if (!popup.closed) popup.close();
      state.authBusy = false;
      render();
    }
  }

  function describeIdenvaSignInError(cause: unknown): string {
    if (cause instanceof IdenvaLinkError) {
      if (cause.reason === 'refused') return 'Connexion Idenva refusee ou annulee.';
      if (cause.reason === 'network' || cause.reason === 'discovery') return 'Idenva est injoignable : reessayez plus tard.';
      return 'Retour d’Idenva refuse par controle de securite : reessayez.';
    }
    if (cause instanceof KlavoxApiError) {
      if (cause.code === 'authentication_failed') return 'Votre connexion Idenva a ete refusee par Klavox : reconnectez-vous.';
      if (cause.code === 'oidc_identity_taken') return 'Ce compte Idenva a deja un coffre : reconnectez-vous pour l’ouvrir.';
    }
    return 'Connexion Idenva impossible : reessayez plus tard.';
  }

  /** « Ouvrir votre coffre » depuis Idenva : le coffre du serveur revient sur cet appareil, mot de passe maitre seul. */
  async function handleIdenvaOpenSubmit(form: HTMLFormElement): Promise<void> {
    const home = state.idenvaHome;
    const signIn = state.idenvaSignIn;
    if (home === null || home === 'absent' || signIn === null || !signIn.lookup.exists) return;
    const { lookup, token } = signIn;
    state.authError = null;
    state.authBusy = true;
    render();
    try {
      const signedIn = await signInOnNewDevice({
        repository: state.repository,
        baseUrl: home.baseUrl,
        // Compte cree depuis Idenva : aucun e-mail, le sel est dans `kdfParams`. Compte ancien format lie : son adresse.
        email: lookup.email ?? '',
        kdfParams: lookup.kdfParams,
        masterPassword: String(new FormData(form).get('masterPassword') ?? ''),
        login: (baseUrl, _email, masterPasswordHashBase64) =>
          completeSecondFactor(baseUrl, loginWithIdenva(baseUrl, { ...token, masterPasswordHashBase64 })),
      });
      state.idenvaSignIn = null;
      state.localAccountEnvelope = signedIn.envelope;
      state.remoteSession = signedIn.remoteSession;
      state.authMode = 'unlock';
      await unlockWith(new VaultStore(state.repository, signedIn.vaultKey), signedIn.envelope);
      await loadWebAuthnCredentials();
      await loadIdenvaConfig();
      await loadEmergencyGrants();
      scheduleFlash(`Coffre ouvert sur cet appareil (${signedIn.itemCount} entrees, ${signedIn.folderCount} dossiers)`);
    } catch (cause) {
      state.authError =
        cause instanceof KlavoxApiError && cause.code === 'authentication_failed'
          ? 'mot de passe maitre incorrect'
          : cause instanceof LocalVaultAlreadyExistsError
            ? cause.message
            : 'ouverture impossible -- verifiez votre connexion';
      const envelope = await state.repository.loadAccountEnvelope();
      if (envelope !== null) {
        state.idenvaSignIn = null;
        state.localAccountEnvelope = envelope;
        state.authMode = 'unlock';
      }
    } finally {
      state.authBusy = false;
      render();
    }
  }

  async function handleRecoverSubmit(form: HTMLFormElement): Promise<void> {
    const data = new FormData(form);
    const recoveryKeyDisplay = String(data.get('recoveryKey') ?? '').trim();
    const newMasterPassword = String(data.get('newMasterPassword') ?? '');
    const confirm = String(data.get('confirm') ?? '');
    state.authError = null;
    if (newMasterPassword !== confirm) {
      state.authError = 'les mots de passe ne correspondent pas';
      render();
      return;
    }
    const envelope = state.localAccountEnvelope;
    if (envelope?.recoveryProtectedSymmetricKey === undefined || envelope.recoveryProtectedSymmetricKey === null) {
      state.authError = 'aucune cle de recuperation activee sur cet appareil';
      render();
      return;
    }
    try {
      assertNewMasterPasswordAccepted(newMasterPassword, envelope.email, data.get('acceptWeakMaster') === 'on');
      await assertNewMasterPasswordNotExposed(newMasterPassword, masterBreachCheck(data), data.get('acceptExposedMaster') === 'on');
    } catch (cause) {
      state.authError = (cause as Error).message;
      state.masterPasswordExposed = cause instanceof ExposedMasterPasswordError;
      render();
      return;
    }
    state.masterPasswordExposed = false;
    state.authBusy = true;
    render();
    try {
      const result = resetMasterPasswordWithRecoveryKey({
        recoveryKeyDisplay,
        recoveryProtectedSymmetricKey: envelope.recoveryProtectedSymmetricKey,
        email: envelope.email,
        newMasterPassword,
      });
      const nextEnvelope: AccountEnvelope = {
        email: result.email,
        kdfParams: result.kdfParams,
        protectedSymmetricKey: result.protectedSymmetricKey,
        recoveryProtectedSymmetricKey: envelope.recoveryProtectedSymmetricKey,
      };
      await state.repository.saveAccountEnvelope(nextEnvelope);
      state.localAccountEnvelope = nextEnvelope;
      state.authMode = 'unlock';
      scheduleFlash('Mot de passe maitre reinitialise -- deverrouillez avec le nouveau mot de passe');
    } catch (cause) {
      state.authError = cause instanceof Error ? cause.message : 'cle de recuperation invalide';
    } finally {
      state.authBusy = false;
      render();
    }
  }

  async function handleRemoteSignInSubmit(form: HTMLFormElement): Promise<void> {
    const data = new FormData(form);
    state.authError = null;
    state.authBusy = true;
    render();
    try {
      const signedIn = await signInOnNewDevice({
        repository: state.repository,
        baseUrl: String(data.get('baseUrl') ?? ''),
        email: String(data.get('email') ?? ''),
        masterPassword: String(data.get('masterPassword') ?? ''),
        login: loginWithSecondFactor,
      });
      state.localAccountEnvelope = signedIn.envelope;
      state.remoteSession = signedIn.remoteSession;
      state.authMode = 'unlock';
      await unlockWith(new VaultStore(state.repository, signedIn.vaultKey), signedIn.envelope);
      await loadWebAuthnCredentials();
      await loadIdenvaConfig();
      await loadEmergencyGrants();
      scheduleFlash(`Coffre ouvert sur cet appareil (${signedIn.itemCount} entrees, ${signedIn.folderCount} dossiers)`);
    } catch (cause) {
      state.authError =
        cause instanceof KlavoxApiError && cause.code === 'authentication_failed'
          ? 'e-mail ou mot de passe maitre incorrect'
          : cause instanceof LocalVaultAlreadyExistsError
            ? cause.message
            : 'ouverture impossible -- verifiez l’URL du serveur et votre connexion';
      // Enveloppe deja persistee si seul le rapatriement a echoue : l'appareil se deverrouille, la synchro se rejoue.
      const envelope = await state.repository.loadAccountEnvelope();
      if (envelope !== null) {
        state.localAccountEnvelope = envelope;
        state.authMode = 'unlock';
      }
    } finally {
      state.authBusy = false;
      render();
    }
  }

  async function handleRemoteRecoverSubmit(form: HTMLFormElement): Promise<void> {
    const data = new FormData(form);
    const newMasterPassword = String(data.get('newMasterPassword') ?? '');
    state.authError = null;
    if (newMasterPassword !== String(data.get('confirm') ?? '')) {
      state.authError = 'les mots de passe ne correspondent pas';
      render();
      return;
    }
    state.authBusy = true;
    render();
    try {
      const recovered = await recoverAccountOnNewDevice({
        repository: state.repository,
        baseUrl: String(data.get('baseUrl') ?? ''),
        email: String(data.get('email') ?? ''),
        recoveryKeyDisplay: String(data.get('recoveryKey') ?? '').trim(),
        newMasterPassword,
        acceptWeak: data.get('acceptWeakMaster') === 'on',
        breachCheck: masterBreachCheck(data),
        acceptExposed: data.get('acceptExposedMaster') === 'on',
      });
      state.masterPasswordExposed = false;
      state.localAccountEnvelope = recovered.envelope;
      state.remoteSession = recovered.remoteSession;
      state.authMode = 'unlock';
      await unlockWith(new VaultStore(state.repository, recovered.vaultKey), recovered.envelope);
      await loadWebAuthnCredentials();
      await loadIdenvaConfig();
      await loadEmergencyGrants();
      scheduleFlash(`Compte recupere (${recovered.itemCount} entrees, ${recovered.folderCount} dossiers)`);
    } catch (cause) {
      state.authError = describeRecoveryError(cause);
      state.masterPasswordExposed = cause instanceof ExposedMasterPasswordError;
      // Enveloppe deja persistee si seule la synchro a echoue : l'appareil se deverrouille avec le nouveau mot de passe.
      const envelope = await state.repository.loadAccountEnvelope();
      if (envelope !== null) {
        state.localAccountEnvelope = envelope;
        state.authMode = 'unlock';
      }
      if (envelope !== null && !(cause instanceof LocalVaultAlreadyExistsError)) {
        state.authError = 'Mot de passe maitre reinitialise, mais la synchro a echoue : deverrouillez puis resynchronisez.';
      }
    } finally {
      state.authBusy = false;
      render();
    }
  }

  /**
   * Recopie la saisie de l'editeur dans `state.draft` avant tout re-rendu
   * (changement de type, affichage des champs masques, generation) : un champ
   * absent du formulaire (masque pour ce type) garde sa valeur du brouillon.
   */
  function syncDraftFromForm(): EditorDraft | null {
    const form = root.querySelector<HTMLFormElement>('form[data-form="entry"]');
    if (state.draft === null || form === null) return state.draft;
    const data = new FormData(form);
    const previous = state.draft;
    const read = (name: string, fallback: string): string => {
      const value = data.get(name);
      return value === null ? fallback : String(value);
    };
    const fields = { ...previous.fields };
    for (const group of TYPED_FIELD_GROUPS) {
      for (const { key } of fieldSpecs(group)) fields[key] = read(`field:${key}`, fields[key] ?? '');
    }
    state.draft = {
      ...previous,
      type: read('type', previous.type) as VaultItemType,
      name: read('name', previous.name),
      username: read('username', previous.username),
      password: read('password', previous.password),
      uri: read('uri', previous.uri),
      extraUris: previous.extraUris.map((uri, index) => read(`uri-extra:${index}`, uri)),
      uriRules: [previous.uri, ...previous.extraUris].map((_uri, index) => read(`uri-match:${index}`, previous.uriRules[index] ?? '')),
      notes: read('notes', previous.notes),
      totpSecret: read('totpSecret', previous.totpSecret),
      fields,
      folderId: read('folderId', previous.folderId ?? '') || null,
      favorite: data.get('favorite') !== null,
      // Case absente (type sans site) : le brouillon garde sa valeur.
      equivalentDomains: form.elements.namedItem('equivalentDomains') === null ? previous.equivalentDomains : data.get('equivalentDomains') !== null,
      customFields: previous.customFields.map((field, index) =>
        form.elements.namedItem(`custom-name:${index}`) === null
          ? field
          : isCheckboxField(field)
            ? { name: read(`custom-name:${index}`, field.name), value: data.get(`custom-checked:${index}`) !== null ? 'true' : 'false', kind: 'boolean' }
            : { name: read(`custom-name:${index}`, field.name), value: read(`custom-value:${index}`, field.value), hidden: data.get(`custom-hidden:${index}`) !== null },
      ),
      reprompt: data.get('reprompt') !== null,
      tags: read('tags', previous.tags),
      passwordExpiry: read('passwordExpiry', previous.passwordExpiry),
      passwordRenewDays: read('passwordRenewDays', previous.passwordRenewDays),
      // Zone absente (cle privee masquee, autre type) : le brouillon garde sa valeur.
      privateKey: read('privateKey', previous.privateKey),
      publicKey: read('publicKey', previous.publicKey),
    };
    if (previous === savingDraft) savingDraft = state.draft;
    readDraft = state.draft;
    readForm = formState(form);
    return state.draft;
  }

  /** Etat des controles du formulaire, dans l'ordre : valeur, ou coche pour une case. Garde en memoire, jamais ecrit. */
  function formState(form: HTMLFormElement): string[] {
    return Array.from(form.elements, (element) =>
      element instanceof HTMLInputElement && (element.type === 'checkbox' || element.type === 'radio')
        ? `${element.name}:${element.checked}`
        : element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement
          ? `${element.name}=${element.value}`
          : '',
    );
  }

  /**
   * Le brouillon courant est la derniere lecture du formulaire (geste asynchrone en attente) et le formulaire a change
   * depuis : une saisie a eu lieu, avec ou sans evenement. Un geste qui ecrit dans le brouillon lu ne change pas le
   * formulaire : il n'est pas relu par-dessus, le geste prime.
   */
  function formChangedSinceRead(): boolean {
    const before = readForm;
    if (state.draft === null || state.draft !== readDraft || before === null) return false;
    const form = root.querySelector<HTMLFormElement>('form[data-form="entry"]');
    if (form === null) return false;
    const now = formState(form);
    return now.length !== before.length || now.some((value, index) => value !== before[index]);
  }

  /**
   * Insere `text` (retour a la ligne, texte colle multiligne) a la place de la selection d'une valeur de champ
   * personnalise d'une ligne : le brouillon prend la saisie de tout le formulaire, puis la valeur nouvelle ; au rendu,
   * elle passe dans une zone de texte (masquee : hors du formulaire, gardee par le brouillon) et le curseur la suit.
   */
  function breakCustomValue(input: HTMLInputElement, text: string): void {
    const index = Number(input.dataset.index);
    const draft = syncDraftFromForm();
    if (draft === null || draft.customFields[index] === undefined) return;
    const { value, caret } = spliceCustomValue(input.value, input.selectionStart ?? input.value.length, input.selectionEnd ?? input.value.length, text);
    draft.customFields = draft.customFields.map((field, position) => (position === index ? { ...field, value } : field));
    render();
    const area = root.querySelector<HTMLTextAreaElement>(`textarea[name="custom-value:${index}"]`);
    if (area !== null) {
      area.focus();
      area.setSelectionRange(caret, caret);
    } else {
      root.querySelector<HTMLElement>(`[name="custom-hidden:${index}"]`)?.focus();
    }
  }

  /**
   * Remplit le brouillon d'un reseau Wi-Fi depuis le texte d'un code QR (`parseWifiQr`) : nom du reseau, cle, securite,
   * reseau masque -- et le nom de l'entree s'il est vide. Le reste de la saisie est garde ; rien n'est enregistre avant
   * « Enregistrer ». Un texte illisible ou un reseau Entreprise ne touche a rien et le dit.
   */
  function fillDraftFromWifiQr(text: string): void {
    const draft = syncDraftFromForm();
    if (draft === null || draft.type !== 'wifi') return;
    const network = parseWifiQr(text);
    if (!network.ok) {
      scheduleFlash(network.problem);
      render();
      return;
    }
    state.draft = {
      ...draft,
      name: draft.name.trim().length === 0 ? network.ssid : draft.name,
      password: network.password,
      fields: { ...draft.fields, ssid: network.ssid, security: network.security, hidden: network.hidden ? 'true' : '' },
    };
    scheduleFlash('Reseau rempli depuis le code Wi-Fi : verifiez puis enregistrez.');
    render();
    root.querySelector<HTMLInputElement>('form[data-form="entry"] input[name="field:ssid"]')?.focus();
  }

  /**
   * Remplit la graine 2FA du brouillon depuis le texte d'un code QR (`totpFromQrText`) : l'URI `otpauth://totp/` entiere
   * (periode, chiffres, algorithme gardes), et le nom et l'identifiant de l'entree s'ils sont vides (emetteur et
   * compte du code). Rien n'est enregistre avant « Enregistrer » ; un autre code ne touche a rien et le dit.
   */
  function fillDraftFromTotpQr(text: string): void {
    const draft = syncDraftFromForm();
    if (draft === null || root.querySelector('form[data-form="entry"] input[name="totpSecret"]') === null) return;
    const reading = totpFromQrText(text);
    if (!reading.ok) {
      scheduleFlash(reading.problem);
      render();
      return;
    }
    state.draft = {
      ...draft,
      totpSecret: reading.seed,
      name: draft.name.trim().length === 0 && reading.issuer.length > 0 ? reading.issuer : draft.name,
      username: draft.username.trim().length === 0 && reading.account.length > 0 ? reading.account : draft.username,
    };
    scheduleFlash(`Graine 2FA lue sur le code QR${reading.issuer.length > 0 ? ` de ${reading.issuer}` : ''} : verifiez puis enregistrez.`);
    render();
    root.querySelector<HTMLInputElement>('form[data-form="entry"] input[name="totpSecret"]')?.focus();
  }

  /**
   * Lit le code QR d'une image choisie, collee ou deposee dans l'editeur -- SUR CET APPAREIL (`readQrFromImageBytes`) :
   * l'image n'est ni envoyee ni gardee, ses octets et ses pixels sont effaces une fois lus. Le texte lu remplit la
   * graine 2FA ou le reseau Wi-Fi ; un echec ne touche a rien et le dit, sans jamais citer le texte lu.
   */
  async function readDraftQrImage(file: File, into: 'totp' | 'wifi'): Promise<void> {
    if (state.draft === null) return;
    applyDraftQrReading(await readQrFromImageBytes(new Uint8Array(await file.arrayBuffer()), file.type), into);
  }

  function applyDraftQrReading(result: QrReadResult, into: 'totp' | 'wifi'): void {
    // Editeur ferme ou coffre verrouille pendant la lecture : rien a remplir.
    if (state.draft === null || state.vaultStore === null) return;
    if (!result.ok) {
      // La saisie faite pendant la lecture est relue avant le re-rendu : un refus ne l'efface pas.
      syncDraftFromForm();
      scheduleFlash(result.problem);
      render();
      return;
    }
    if (into === 'wifi') fillDraftFromWifiQr(result.text);
    else fillDraftFromTotpQr(result.text);
  }

  /**
   * Lit le code QR d'activation a la camera (`scanQrWithCamera`), sur cet appareil : l'apercu vit HORS de `root`
   * (un rendu ne le detruit pas), « Annuler » ou Echap l'arrete, et la camera est rendue des la lecture finie.
   */
  async function scanDraftQrWithCamera(): Promise<void> {
    if (state.draft === null || cameraScan !== null) return;
    syncDraftFromForm();
    const run = new AbortController();
    cameraScan = run;
    const doc = root.ownerDocument;
    const overlay = doc.createElement('div');
    overlay.className = 'modal-backdrop camera-scan';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Lire le code QR a la camera');
    overlay.innerHTML = `<div class="modal camera-scan-panel"><video class="camera-scan-video" muted playsinline></video><p>Visez le code QR d’activation, bien en face. L’image est lue sur cet appareil, jamais envoyee ni gardee.</p><button type="button" class="secondary">Annuler</button></div>`;
    const cancel = (): void => run.abort();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') cancel();
    };
    overlay.querySelector('button')!.addEventListener('click', cancel);
    doc.addEventListener('keydown', onKey);
    doc.body.append(overlay);
    overlay.querySelector('button')!.focus();
    try {
      const result = await scanQrWithCamera({ signal: run.signal, preview: overlay.querySelector('video')! });
      if (!result.ok && result.problem === CAMERA_CANCELLED) return;
      applyDraftQrReading(result, 'totp');
    } finally {
      doc.removeEventListener('keydown', onKey);
      overlay.remove();
      cameraScan = null;
    }
  }

  async function handleEntrySubmit(): Promise<void> {
    // Deja en cours d'enregistrement (double Entree, double clic) : une seule entree.
    if (state.draft !== null && state.draft === savingDraft) return;
    const draft = syncDraftFromForm();
    if (draft === null || state.vaultStore === null) return;
    savingDraft = draft;
    try {
      await submitEntryDraft(state.vaultStore, draft);
    } finally {
      savingDraft = null;
    }
  }

  async function submitEntryDraft(store: VaultStore, draft: EditorDraft): Promise<void> {
    const expiry = draft.type === 'card' ? (draft.fields['expiry'] ?? '').trim() : '';
    if (expiry.length > 0 && normalizeExpiry(expiry) === null) {
      scheduleFlash('Date d’expiration invalide (format MM/AA)');
      render();
      return;
    }
    // Une echeance illisible ne previendrait jamais : refusee ici, comme une expiration de carte.
    if (PASSWORD_EXPIRY_TYPES.has(draft.type) && draft.passwordExpiry.trim().length > 0 && normalizeDocumentDate(draft.passwordExpiry) === null) {
      scheduleFlash('Echeance du mot de passe illisible (format AAAA-MM-JJ)');
      render();
      return;
    }
    const cardNumber = draft.type === 'card' ? (draft.fields['number'] ?? '').trim() : '';
    // Une cle qu'aucun appareil n'accepterait ne sert a rien : refusee ici, comme une expiration illisible.
    const wifiProblem = draft.type === 'wifi' ? wifiPasswordProblem(wifiSecurity(draft.fields['security'] ?? '') ?? '', draft.password) : null;
    if (wifiProblem !== null) {
      scheduleFlash(wifiProblem);
      render();
      return;
    }
    // Un IBAN dont la cle ne tient pas ferait echouer (ou devier) un virement : refuse, comme une cle Wi-Fi inutilisable.
    const bankProblem = draft.type === 'bank' ? bankAccountProblem(draft.fields) : null;
    if (bankProblem !== null) {
      scheduleFlash(bankProblem);
      render();
      return;
    }
    // Une phrase de recuperation mal recopiee ne se decouvre qu'a la restauration, fonds perdus : refusee ici.
    const walletProblem = draft.type === 'wallet' ? recoveryPhraseProblem(draft.fields['recoveryPhrase'] ?? '') : null;
    if (walletProblem !== null) {
      scheduleFlash(walletProblem);
      render();
      return;
    }
    // Un port hors de 1-65535, une adresse entiere (mot de passe compris) dans « Serveur » : refuses avant le chiffrement.
    const databaseIssue = draft.type === 'database' ? databaseProblem(draft.fields) : null;
    if (databaseIssue !== null) {
      scheduleFlash(databaseIssue);
      render();
      return;
    }
    // Date illisible, validite inversee, cle collee dans l'adresse de l'API : refuses avant le chiffrement.
    const apiKeyIssue = draft.type === 'api-key' ? apiKeyProblem(draft.fields) : null;
    if (apiKeyIssue !== null) {
      scheduleFlash(apiKeyIssue);
      render();
      return;
    }
    // Date illisible, achat posterieur a l'expiration, e-mail injoignable : refuses avant le chiffrement.
    const licenseIssue = draft.type === 'software-license' ? softwareLicenseProblem(draft.fields) : null;
    if (licenseIssue !== null) {
      scheduleFlash(licenseIssue);
      render();
      return;
    }
    // Date illisible, carte expiree avant d'etre ouverte, site a secret, telephone illisible : refuses avant le chiffrement.
    const membershipIssue = draft.type === 'membership' ? membershipProblem(draft.fields) : null;
    if (membershipIssue !== null) {
      scheduleFlash(membershipIssue);
      render();
      return;
    }
    // Date illisible, telephone illisible : refuses avant le chiffrement.
    const medicalIssue = draft.type === 'medical' ? medicalProblem(draft.fields) : null;
    if (medicalIssue !== null) {
      scheduleFlash(medicalIssue);
      render();
      return;
    }
    // Une adresse qui porte un mot de passe ou une cle, un telephone du support illisible : refuses avant le chiffrement.
    const serverIssue = draft.type === 'server' ? serverProblem(draft.fields) : null;
    if (serverIssue !== null) {
      scheduleFlash(serverIssue);
      render();
      return;
    }
    // Un hote qui porte une adresse ou un compte, un port hors de 1-65535 : refuses avant le chiffrement.
    const emailIssue = draft.type === 'email' ? emailAccountProblem(draft.fields) : null;
    if (emailIssue !== null) {
      scheduleFlash(emailIssue);
      render();
      return;
    }
    const sshProblem = draft.type === 'ssh-key' ? checkSshKey(draft) : null;
    if (sshProblem !== null) {
      scheduleFlash(SSH_KEY_PROBLEM_MESSAGES[sshProblem]);
      render();
      return;
    }
    // Relue au coffre, pas dans `state.entries` (filtree par la recherche ou le dossier).
    const existing = draft.id === null ? null : await store.getEntry(draft.id);
    const secrets = draftToSecrets(draft, existing?.secrets);
    if (draft.totpSecret.trim().length > 0) {
      // Une graine inexploitable enregistree casserait l'affichage de l'entree : on la refuse ici.
      try {
        totp({ secret: secrets.totpSecret ?? '' });
      } catch {
        scheduleFlash('Graine TOTP invalide (base32 ou URI otpauth://totp attendue)');
        render();
        return;
      }
    }
    if (draft.id === null) {
      const created = await store.createEntry({ type: draft.type, secrets, folderId: draft.folderId, favorite: draft.favorite });
      state.selectedEntryId = created.id;
    } else {
      await store.updateEntrySecrets(draft.id, secrets);
      if (draft.favorite !== state.entries.find((e) => e.id === draft.id)?.favorite) {
        await store.setFavorite(draft.id, draft.favorite);
      }
      if (draft.folderId !== state.entries.find((e) => e.id === draft.id)?.folderId) {
        await store.moveEntry(draft.id, draft.folderId);
      }
      state.selectedEntryId = draft.id;
    }
    // Qui vient d'enregistrer l'entree en connait le contenu : pas de re-demande dans la foulee.
    state.repromptConfirmedId = state.selectedEntryId;
    // L'editeur reste ouvert tant que la liste n'est pas relue : ferme avant, un rendu d'arriere-plan tombe entre les
    // deux et montre « Entree introuvable » a la place de l'entree neuve. Une relecture depassee n'a rien ecrit : relue.
    while (!(await refreshVaultData()));
    // Un geste fait pendant l'enregistrement (nouvelle entree, autre fiche, Echap) a remplace le brouillon : il prime.
    if (state.draft === savingDraft) {
      state.draft = null;
      state.revealDraftPassword = false;
    }
    // Avertir sans bloquer : quelques cartes privatives ne respectent pas Luhn.
    scheduleFlash(cardNumber.length > 0 && !isLuhnValid(cardNumber) ? 'Entree enregistree -- numero de carte non conforme (controle de Luhn)' : 'Entree enregistree');
    render();
  }

  /** Etat du mode voyage ; un serveur anterieur a R18 (route absente) = inactif. */
  async function readTravelMode(baseUrl: string, sessionToken: string): Promise<boolean> {
    try {
      return (await fetchTravelMode(baseUrl, sessionToken)).enabled;
    } catch (cause) {
      if (cause instanceof KlavoxApiError && cause.code === 'not_found') return false;
      throw cause;
    }
  }

  /** Message d'une purge de mode voyage -- des comptes, jamais un nom d'entree. */
  function describeTravelPurge(plan: TravelPurgePlan): string {
    return (
      `Mode voyage actif : ${plan.itemIds.length} entree(s) et ${plan.folderIds.length} dossier(s) retire(s) de cet appareil.` +
      (plan.unsynced > 0
        ? ` ${plan.unsynced} enregistrement(s) CONSERVE(S) faute d'etre sauvegarde(s) sur le serveur : synchronisez avant de partir.`
        : '')
    );
  }

  /**
   * Arme ou desarme le mode voyage. L'armement pousse d'abord le coffre puis
   * ne retire que ce que le serveur detient : le mode voyage protege, il ne
   * detruit pas. Le desarmement est REFUSE par le serveur s'il vient de
   * l'appareil qui a arme -- c'est tout l'interet de la fonction.
   */
  async function handleTravelMode(enabled: boolean): Promise<void> {
    const session = await liveRemoteSession();
    const vaultStore = state.vaultStore;
    if (session === null || vaultStore === null) {
      state.travelMessage =
        'Connectez-vous d’abord au serveur souverain : le mode voyage est porte par le compte, c’est ce qui permet de le desactiver depuis un autre appareil.';
      render();
      return;
    }
    state.travelBusy = true;
    state.travelMessage = null;
    render();
    try {
      const deviceId = await ensureDeviceId(state.repository);
      const result = await apiSetTravelMode(session.baseUrl, session.sessionToken, { enabled, deviceId });
      state.travelMode = result.enabled;
      if (result.enabled) {
        await pushSync(session.baseUrl, session.sessionToken, {
          items: await state.repository.listItems(),
          folders: await state.repository.listFolders(),
        });
        const pulled = await pullSync(session.baseUrl, session.sessionToken, 0);
        state.travelMessage = describeTravelPurge(
          await vaultStore.purgeForTravelMode({
            items: pulled.items,
            folders: pulled.folders,
            attachmentMetadata: pulled.attachments ?? [],
          }),
        );
        forgetAttachments();
        await refreshVaultData();
      } else {
        state.travelMessage = 'Mode voyage desactive. Synchronisez cet appareil pour rapatrier le coffre.';
      }
    } catch (cause) {
      state.travelMessage = cause instanceof Error ? `Echec : ${cause.message}` : 'Echec du mode voyage';
    } finally {
      state.travelBusy = false;
      render();
    }
  }

  async function handleSyncSubmit(form: HTMLFormElement): Promise<void> {
    if (state.vaultStore === null || state.envelope === null) return;
    const data = new FormData(form);
    const baseUrl = String(data.get('baseUrl') ?? '').trim();
    if (baseUrl.length === 0) return;
    // Coffre cree sans e-mail (sel aleatoire) : il n'a pas d'adresse pour s'inscrire, son compte serveur est
    // celui de son compte Idenva. La fenetre Idenva s'ouvre AVANT tout `await`, sinon le navigateur la bloque.
    const viaIdenva = state.envelope.kdfParams.saltBase64 !== undefined;
    const popup = viaIdenva ? window.open('', 'klavox-idenva', 'popup,width=520,height=720') : null;
    if (viaIdenva && popup === null) {
      state.syncMessage = 'Fenetre bloquee : ce coffre se synchronise par votre compte Idenva, autorisez les fenetres surgissantes pour Klavox.';
      render();
      return;
    }
    state.syncBusy = true;
    state.syncMessage = null;
    render();
    try {
      let session;
      if (popup !== null) {
        session = await idenvaSyncSession(baseUrl, popup);
        if (session === null) return;
      } else {
        const masterPasswordHashBase64 = await promptMasterPasswordHash();
        if (masterPasswordHashBase64 === null) return;
        try {
          session = await loginWithSecondFactor(baseUrl, state.envelope.email, masterPasswordHashBase64);
        } catch (cause) {
          if (cause instanceof KlavoxApiError && cause.code === 'authentication_failed') {
            session = await registerThenLogin(baseUrl, masterPasswordHashBase64);
          } else {
            throw cause;
          }
        }
      }
      state.remoteSession = {
        baseUrl,
        sessionToken: session.sessionToken,
        refreshToken: session.refreshToken,
        expiresAt: session.expiresAt,
        lastSyncedAt: 0,
      };
      // Les envois appartiennent au compte serveur : changer de serveur (ou
      // s'y connecter) invalide la liste chargee pour le precedent.
      state.sends = null;
      state.connectedDevices = null;
      const localItems = await state.repository.listItems();
      const localFolders = await state.repository.listFolders();
      await pushSync(baseUrl, session.sessionToken, { items: localItems, folders: localFolders });
      const pulled = await pullSync(baseUrl, session.sessionToken, 0);
      // Serveur non digne de confiance : enregistrements forges ecartes, puis fusion avec l'etat
      // LOCAL (une ancienne revision rejouee ne remplace jamais une version locale plus recente).
      const remote = state.vaultStore.authenticateExternal({ items: pulled.items, folders: pulled.folders });
      const mergedItems = mergeVaultRecords(localItems, remote.items);
      const mergedFolders = mergeVaultRecords(localFolders, remote.folders);
      await state.repository.putItems(mergedItems.merged);
      await state.repository.putFolders(mergedFolders.merged);
      const vaultStore = state.vaultStore;
      const attachments = await syncAttachments({
        baseUrl,
        sessionToken: session.sessionToken,
        repository: state.repository,
        remote: pulled.attachments ?? [],
        authenticate: (batch) => vaultStore.authenticateExternal({ attachments: batch }).attachments,
      });
      forgetAttachments();
      // Mode voyage (R18) : l'etat vient du COMPTE, pas de cet appareil. Une
      // synchro depuis un appareil parti en voyage purge donc a nouveau -- et
      // c'est la synchro qui suit la desactivation depuis un AUTRE appareil
      // qui rapatrie tout (la fusion ci-dessus a deja remis les enregistrements).
      const travelEnabled = await readTravelMode(baseUrl, session.sessionToken);
      state.travelMode = travelEnabled;
      const travelNote = travelEnabled
        ? ` ${describeTravelPurge(
            await vaultStore.purgeForTravelMode({
              items: pulled.items,
              folders: pulled.folders,
              attachmentMetadata: pulled.attachments ?? [],
            }),
          )}`
        : '';
      await refreshVaultData();
      await loadWebAuthnCredentials();
      await loadEmergencyGrants();
      await loadIdenvaConfig();
      const rejected = remote.rejected + attachments.rejected;
      state.syncMessage =
        `Synchronise (${mergedItems.merged.length} entrees, ${mergedFolders.merged.length} dossiers` +
        `, pieces jointes : ${attachments.pushed} envoyee(s), ${attachments.fetched} recue(s)).` +
        (rejected > 0 ? ` ${rejected} enregistrement(s) altere(s) cote serveur refuse(s).` : '') +
        travelNote;
    } catch (cause) {
      state.syncMessage = cause instanceof Error ? `Echec : ${cause.message}` : 'Echec de la synchro';
    } finally {
      state.syncBusy = false;
      render();
    }
  }

  async function handleMasterPasswordSubmit(form: HTMLFormElement): Promise<void> {
    if (state.envelope === null || state.masterPasswordBusy) return;
    const data = new FormData(form);
    const currentMasterPassword = String(data.get('currentMasterPassword') ?? '');
    const newMasterPassword = String(data.get('newMasterPassword') ?? '');
    const confirm = String(data.get('confirm') ?? '');
    if (newMasterPassword !== confirm) {
      state.masterPasswordMessage = 'Les nouveaux mots de passe ne correspondent pas : rien n’a ete modifie.';
      render();
      return;
    }
    if (newMasterPassword === currentMasterPassword) {
      state.masterPasswordMessage = 'Le nouveau mot de passe maitre est identique a l’actuel.';
      render();
      return;
    }
    state.masterPasswordBusy = true;
    state.masterPasswordMessage = null;
    render();
    try {
      const changed = await changeAccountMasterPassword({
        repository: state.repository,
        envelope: state.envelope,
        currentMasterPassword,
        newMasterPassword,
        acceptWeak: data.get('acceptWeakMaster') === 'on',
        breachCheck: masterBreachCheck(data),
        acceptExposed: data.get('acceptExposedMaster') === 'on',
        remoteSession: state.remoteSession,
      });
      state.masterPasswordExposed = false;
      state.envelope = changed.envelope;
      state.localAccountEnvelope = changed.envelope;
      state.remoteSession = changed.remoteSession;
      state.masterPasswordMessage = changed.serverUpdated
        ? 'Mot de passe maitre change ici et sur le serveur. Vos autres appareils ont ete deconnectes : ils demanderont le nouveau mot de passe.'
        : 'Mot de passe maitre change sur cet appareil. Le contenu du coffre n’a pas bouge.';
      scheduleFlash('Mot de passe maitre change');
    } catch (cause) {
      state.masterPasswordMessage = describeMasterPasswordChangeError(cause);
      state.masterPasswordExposed = cause instanceof ExposedMasterPasswordError;
    } finally {
      state.masterPasswordBusy = false;
      // Le re-rendu complet reconstruit le formulaire : les mots de passe tapes
      // ne restent pas dans le DOM.
      render();
    }
  }

  /**
   * Changement d'adresse e-mail. L'enveloppe locale n'est remplacee qu'apres l'accord du serveur
   * (`account-email-change.ts`) ; la session neuve qu'il rend remplace celle qu'il vient de revoquer.
   */
  async function handleEmailChangeSubmit(form: HTMLFormElement): Promise<void> {
    const envelope = state.envelope;
    if (envelope === null || state.emailChangeBusy) return;
    const data = new FormData(form);
    const session = await liveRemoteSession();
    state.emailChangeBusy = true;
    state.emailChangeMessage = null;
    render();
    try {
      const changed = await changeAccountEmail({
        repository: state.repository,
        envelope,
        masterPassword: String(data.get('masterPassword') ?? ''),
        newEmail: String(data.get('newEmail') ?? ''),
        remoteSession: session,
      });
      // L'enveloppe enregistree est deja la nouvelle : l'ecran de deverrouillage doit la proposer, meme si le
      // coffre a ete verrouille pendant la derivation (l'ancienne ouvrirait encore ici, plus sur le serveur).
      state.localAccountEnvelope = changed.envelope;
      if (state.envelope !== envelope) return;
      state.envelope = changed.envelope;
      state.remoteSession = changed.remoteSession;
      // Le serveur a deconnecte tous les autres appareils : la liste lue avant ne dit plus vrai.
      if (changed.serverUpdated) state.connectedDevices = null;
      state.emailChangeMessage = changed.serverUpdated
        ? `Adresse changee ici et sur le serveur : connectez-vous desormais avec ${changed.envelope.email}. Vos autres appareils ont ete deconnectes.`
        : `Adresse changee sur cet appareil : deverrouillez desormais avec ${changed.envelope.email}.`;
      scheduleFlash('Adresse e-mail changee');
    } catch (cause) {
      state.emailChangeMessage = describeEmailChangeError(cause);
    } finally {
      state.emailChangeBusy = false;
      // Le re-rendu complet reconstruit le formulaire : le mot de passe tape ne reste pas dans le DOM.
      render();
    }
  }

  function handleBreachSettingsSubmit(form: HTMLFormElement): void {
    const data = new FormData(form);
    try {
      state.breachEndpoint = data.get('enabled') !== null ? normalizeBreachEndpoint(String(data.get('endpoint') ?? '')) : null;
    } catch (cause) {
      scheduleFlash(cause instanceof Error ? cause.message : 'URL invalide');
      render();
      return;
    }
    if (state.breachEndpoint === null) localStorage.removeItem(BREACH_ENDPOINT_KEY);
    else localStorage.setItem(BREACH_ENDPOINT_KEY, state.breachEndpoint);
    state.breachCounts = null;
    state.breachError = null;
    state.breachCheckedAt = null;
    scheduleFlash(state.breachEndpoint === null ? 'Verification des fuites desactivee' : 'Verification des fuites activee');
    render();
  }

  /**
   * Session serveur d'un coffre SANS e-mail, par Idenva : ID token, preuve du mot de passe maitre, puis connexion
   * si ce compte Idenva a deja un coffre, inscription de CE coffre sinon. `null` = l'utilisateur renonce. Un coffre
   * different deja rattache a ce compte Idenva n'est jamais ecrase : la preuve de CE coffre y est refusee (401).
   */
  async function idenvaSyncSession(baseUrl: string, popup: Window): Promise<AuthenticatedSession | null> {
    popup.opener = null;
    const channel = new BroadcastChannel(IDENVA_CALLBACK_CHANNEL);
    try {
      const envelope = state.envelope;
      if (envelope === null) throw new Error('coffre non deverrouille');
      const config = await fetchIdenvaConfig(baseUrl);
      if (config === null) throw new Error('ce serveur ne propose pas Idenva : un coffre cree sans e-mail ne s’y synchronise pas');
      state.syncMessage = 'Connectez-vous a Idenva dans la fenetre ouverte…';
      render();
      const token = await obtainIdenvaIdToken({
        config: { issuer: config.issuer, clientId: config.clientId, redirectUri: idenvaRedirectUri(window.location.origin) },
        channel,
        navigate: (url) => popup.location.replace(url),
      });
      if (!popup.closed) popup.close();
      if (token === null) return null;
      const masterPasswordHashBase64 = await promptMasterPasswordHash();
      if (masterPasswordHashBase64 === null) return null;
      const lookup = await lookupOidcAccount(baseUrl, token);
      if (lookup.exists) return completeSecondFactor(baseUrl, loginWithIdenva(baseUrl, { ...token, masterPasswordHashBase64 }));
      return await registerWithIdenva(baseUrl, {
        ...token,
        kdfParams: envelope.kdfParams,
        masterPasswordHashBase64,
        protectedSymmetricKey: envelope.protectedSymmetricKey,
      });
    } finally {
      channel.close();
      if (!popup.closed) popup.close();
    }
  }

  async function registerThenLogin(baseUrl: string, masterPasswordHashBase64: string) {
    if (state.envelope === null) throw new Error('coffre non deverrouille');
    await registerAccount(baseUrl, {
      email: state.envelope.email,
      kdfParams: state.envelope.kdfParams,
      masterPasswordHashBase64,
      protectedSymmetricKey: state.envelope.protectedSymmetricKey,
    });
    return loginWithSecondFactor(baseUrl, state.envelope.email, masterPasswordHashBase64);
  }

  /**
   * Connexion serveur ; si le compte exige un second facteur, code de l'application d'authentification ou
   * ceremonie WebAuthn avant toute session. Le code est propose d'abord quand il est actif : il marche partout.
   */
  async function loginWithSecondFactor(
    baseUrl: string,
    email: string,
    masterPasswordHashBase64: string,
  ): Promise<AuthenticatedSession> {
    return completeSecondFactor(baseUrl, apiLogin(baseUrl, { email, masterPasswordHashBase64 }));
  }

  /**
   * Fin commune des connexions (par e-mail ou par Idenva) : session rendue telle quelle, ou second facteur -- code
   * de l'application d'authentification ou ceremonie WebAuthn -- avant toute session.
   */
  async function completeSecondFactor(
    baseUrl: string,
    pending: Promise<AuthenticatedSession | MfaRequired>,
  ): Promise<AuthenticatedSession> {
    const result = await pending;
    if (!isMfaRequired(result)) return result;
    const methods = mfaMethods(result);
    const webauthnUsable = methods.includes('webauthn') && isWebAuthnSupported();
    if (methods.includes('totp')) {
      let error: string | null = null;
      for (;;) {
        const answer = await promptTotpCode(error, webauthnUsable);
        if (answer === null) throw new Error('connexion annulee : code de l’application d’authentification non saisi');
        if (answer === 'webauthn') break;
        try {
          return await finishTotpLogin(baseUrl, result.mfaToken, answer);
        } catch (cause) {
          if (!(cause instanceof KlavoxApiError) || cause.code !== 'totp_invalid') {
            if (cause instanceof KlavoxApiError && cause.code === 'mfa_challenge_expired') {
              throw new Error('trop de codes refuses ou connexion expiree : redonnez votre mot de passe maitre');
            }
            throw cause;
          }
          error = 'Code refuse : faux, expire ou deja utilise. Attendez le suivant.';
        }
      }
    }
    if (!isWebAuthnSupported()) {
      throw new Error('ce compte exige une cle de securite, non prise en charge par ce navigateur');
    }
    state.syncMessage = 'Double authentification : utilisez votre cle de securite ou votre passkey.';
    render();
    const options = await beginWebAuthnLogin(baseUrl, result.mfaToken);
    const assertion = await getWebAuthnAssertion(options);
    return finishWebAuthnLogin(baseUrl, result.mfaToken, assertion);
  }

  // --- Acces d'urgence / heritage (R32) ---------------------------------

  /** Non bloquant : un echec de liste n'annule jamais une synchro reussie. */
  async function loadEmergencyGrants(): Promise<void> {
    const session = await liveRemoteSession();
    const vault = state.vaultStore;
    if (session === null || vault === null) return;
    try {
      const grants = await readEmergencyGrants({ baseUrl: session.baseUrl, sessionToken: session.sessionToken, vault });
      if (state.remoteSession === session) state.emergencyGrants = grants;
    } catch (cause) {
      if (state.remoteSession === session) state.emergencyMessage = describeEmergencyError(cause);
    }
  }

  async function handleEmergencyGrantSubmit(form: HTMLFormElement): Promise<void> {
    const session = await liveRemoteSession();
    const vault = state.vaultStore;
    if (session === null || vault === null || state.emergencyBusy) return;
    const data = new FormData(form);
    state.emergencyBusy = true;
    state.emergencyMessage = null;
    render();
    try {
      const created = await createEmergencyGrantWithKit({
        baseUrl: session.baseUrl,
        sessionToken: session.sessionToken,
        vault,
        contactName: String(data.get('contactName') ?? ''),
        waitMs: Number(data.get('waitMs') ?? DEFAULT_EMERGENCY_WAIT_MS),
      });
      // Affiche UNE fois, jamais persiste : c'est le seul exemplaire du kit.
      state.emergencyKitReveal = { contactName: created.grant.contactName ?? '', kitDisplay: created.kitDisplay };
      await loadEmergencyGrants();
    } catch (cause) {
      state.emergencyMessage = describeEmergencyError(cause);
    } finally {
      state.emergencyBusy = false;
      render();
    }
  }

  async function handleEmergencyOwnerAction(id: string, action: 'refuse' | 'revoke'): Promise<void> {
    const session = await liveRemoteSession();
    if (session === null || state.emergencyBusy) return;
    if (
      action === 'revoke' &&
      !window.confirm('Revoquer definitivement cet acces d’urgence ? Le kit remis a ce contact ne servira plus jamais.')
    ) {
      return;
    }
    state.emergencyBusy = true;
    state.emergencyMessage = null;
    render();
    try {
      if (action === 'refuse') await refuseEmergencyGrantById(session.baseUrl, session.sessionToken, id);
      else await revokeEmergencyGrantById(session.baseUrl, session.sessionToken, id);
      await loadEmergencyGrants();
      scheduleFlash(action === 'refuse' ? 'Demande d’acces refusee' : 'Acces d’urgence revoque');
    } catch (cause) {
      state.emergencyMessage = describeEmergencyError(cause);
    } finally {
      state.emergencyBusy = false;
      render();
    }
  }

  /** Voie du CONTACT : `request` declenche l'attente, `open` retire le coffre une fois le delai ecoule. */
  async function handleEmergencyContactSubmit(form: HTMLFormElement, intent: 'request' | 'open'): Promise<void> {
    if (state.emergencyBusy) return;
    const data = new FormData(form);
    const baseUrl = String(data.get('baseUrl') ?? '').trim();
    const kitDisplay = String(data.get('kit') ?? '');
    state.emergencyContactDraft = { baseUrl, kit: kitDisplay };
    state.emergencyBusy = true;
    state.authError = null;
    render();
    try {
      if (intent === 'request') {
        const etat = await requestEmergencyAccessWithKit({ baseUrl, kitDisplay });
        state.emergencyContactState = { status: etat.status, availableAt: etat.availableAt };
      } else {
        state.emergencyContactVault = await openEmergencyVault({ baseUrl, kitDisplay });
      }
    } catch (cause) {
      state.authError = describeEmergencyError(cause);
    } finally {
      state.emergencyBusy = false;
      render();
    }
  }

  /** Non bloquant, comme la liste des authentificateurs : sans reponse, le bloc Idenva reste en chargement. */
  async function loadIdenvaConfig(): Promise<void> {
    const session = await liveRemoteSession();
    if (session === null) return;
    try {
      const config = await fetchIdenvaConfig(session.baseUrl);
      if (state.remoteSession === session) state.idenvaConfig = config ?? 'absent';
      if (config !== null) await loadIdenvaLinkStatus(session);
    } catch (cause) {
      if (state.remoteSession === session) state.idenvaMessage = describeIdenvaError(cause);
    }
  }

  /**
   * Etat lie / non lie. Non lu (serveur anterieur a la route, panne, reponse
   * illisible) = `null`, jamais `false` : on ne cache pas « Delier » sur une
   * absence de donnee.
   */
  async function loadIdenvaLinkStatus(session: RemoteSession): Promise<void> {
    let linked: boolean | null = null;
    try {
      linked = await fetchOidcLinkStatus(session.baseUrl, session.sessionToken);
    } catch {
      linked = null;
    }
    if (state.remoteSession === session) state.idenvaLinked = linked;
  }

  function describeIdenvaError(cause: unknown): string {
    if (cause instanceof IdenvaLinkError) {
      if (cause.reason === 'refused') return 'Liaison refusee ou annulee sur Idenva : rien n’a ete modifie.';
      if (cause.reason === 'expired') return 'Liaison expiree (plus de dix minutes) : recommencez.';
      if (cause.reason === 'network' || cause.reason === 'discovery') return 'Idenva est injoignable ou mal configure : reessayez plus tard.';
      return 'Retour d’Idenva refuse par controle de securite : rien n’a ete lie.';
    }
    if (cause instanceof KlavoxApiError) {
      if (cause.code === 'oidc_identity_taken') return 'Ce compte Idenva est deja lie a un autre compte Klavox.';
      if (cause.code === 'authentication_failed') return 'Mot de passe maitre incorrect ou jeton Idenva refuse : rien n’a ete modifie.';
      if (cause.code === 'session_expired') return 'Session serveur expiree : reconnectez-vous puis recommencez.';
      if (cause.code === 'not_found') return 'Aucun compte Idenva n’est lie a ce compte.';
    }
    return cause instanceof Error ? `Echec : ${cause.message}` : 'Echec de l’operation';
  }

  /**
   * « Lier mon compte Idenva ». La fenetre s'ouvre AVANT tout `await` (sinon le
   * navigateur la bloque) ; elle n'a pas acces a cet onglet (`opener` coupe) et
   * rend son adresse de retour par `BroadcastChannel`. Le verifier PKCE ne vit
   * que dans `runIdenvaLink`, en memoire : ni stockage, ni URL.
   */
  async function handleIdenvaLink(): Promise<void> {
    const session = await liveRemoteSession();
    const config = state.idenvaConfig;
    if (session === null || config === null || config === 'absent' || state.idenvaBusy) return;
    const popup = window.open('', 'klavox-idenva', 'popup,width=520,height=720');
    if (popup === null) {
      state.idenvaMessage = 'Fenetre bloquee : autorisez les fenetres surgissantes pour Klavox, puis recommencez.';
      render();
      return;
    }
    popup.opener = null;
    const controller = new AbortController();
    idenvaRun = controller;
    const channel = new BroadcastChannel(IDENVA_CALLBACK_CHANNEL);
    state.idenvaBusy = true;
    state.idenvaMessage = 'Connectez-vous a Idenva dans la fenetre ouverte…';
    render();
    try {
      const issue = await runIdenvaLink({
        config: { issuer: config.issuer, clientId: config.clientId, redirectUri: idenvaRedirectUri(window.location.origin) },
        channel,
        signal: controller.signal,
        navigate: (url) => popup.location.replace(url),
        proveMasterPassword: () => promptMasterPasswordHash('Confirmez votre mot de passe maitre pour lier votre compte Idenva :'),
        link: (token, proof) => linkOidcIdentity(session.baseUrl, session.sessionToken, { ...token, masterPasswordHashBase64: proof }),
      });
      if (issue === 'linked') {
        state.idenvaLinked = true;
        state.idenvaMessage = 'Compte Idenva lie : une deconnexion Idenva fermera aussi vos sessions Klavox.';
        scheduleFlash('Compte Idenva lie');
      } else {
        state.idenvaMessage = 'Liaison annulee : rien n’a ete modifie.';
      }
    } catch (cause) {
      state.idenvaMessage = describeIdenvaError(cause);
    } finally {
      channel.close();
      if (!popup.closed) popup.close();
      if (idenvaRun === controller) idenvaRun = null;
      state.idenvaBusy = false;
      render();
    }
  }

  async function handleIdenvaUnlink(): Promise<void> {
    const session = await liveRemoteSession();
    if (session === null || state.idenvaBusy) return;
    if (!window.confirm('Delier votre compte Idenva ? Une deconnexion Idenva ne fermera plus vos sessions Klavox.')) return;
    state.idenvaBusy = true;
    render();
    try {
      const masterPasswordHashBase64 = await promptMasterPasswordHash('Confirmez votre mot de passe maitre pour delier votre compte Idenva :');
      if (masterPasswordHashBase64 === null) return;
      await unlinkOidcIdentity(session.baseUrl, session.sessionToken, masterPasswordHashBase64);
      state.idenvaLinked = false;
      state.idenvaMessage = 'Compte Idenva delie.';
      scheduleFlash('Compte Idenva delie');
    } catch (cause) {
      // Delie entre-temps (autre appareil) : le serveur le dit, l'ecran le reflete.
      if (cause instanceof KlavoxApiError && cause.code === 'not_found') state.idenvaLinked = false;
      state.idenvaMessage = describeIdenvaError(cause);
    } finally {
      state.idenvaBusy = false;
      render();
    }
  }

  /**
   * Deconnexion de tous les appareils. Le serveur revoque AUSSI la session de cet appareil : la session
   * neuve qu'il rend remplace l'ancienne, sans quoi la synchro suivante echouerait en « session expiree ».
   */
  async function handleSessionsRevokeSubmit(): Promise<void> {
    const session = await liveRemoteSession();
    if (session === null || state.sessionsRevokeBusy) return;
    state.sessionsRevokeBusy = true;
    state.sessionsRevokeMessage = null;
    render();
    try {
      const masterPasswordHashBase64 = await promptMasterPasswordHash(
        'Confirmez votre mot de passe maitre pour deconnecter tous vos autres appareils :',
      );
      if (masterPasswordHashBase64 === null) return;
      const renewed = await revokeAllServerSessions(session.baseUrl, session.sessionToken, masterPasswordHashBase64);
      // Verrouille ou deconnecte pendant l'attente : la session neuve n'a plus de coffre a qui revenir.
      if (state.remoteSession !== session) return;
      state.remoteSession = {
        ...session,
        sessionToken: renewed.sessionToken,
        refreshToken: renewed.refreshToken,
        expiresAt: renewed.expiresAt,
      };
      state.connectedDevices = null;
      state.sessionsRevokeMessage = 'Tous vos autres appareils ont ete deconnectes : ils redemanderont votre mot de passe maitre. Cet appareil reste connecte.';
      scheduleFlash('Autres appareils deconnectes');
    } catch (cause) {
      state.sessionsRevokeMessage = describeSessionsRevokeError(cause);
    } finally {
      state.sessionsRevokeBusy = false;
      render();
    }
  }

  function describeSessionsRevokeError(cause: unknown): string {
    if (cause instanceof KlavoxApiError) {
      if (cause.code === 'authentication_failed') return 'Mot de passe maitre incorrect : aucun appareil n’a ete deconnecte.';
      if (cause.status === 429) return 'Trop d’essais : patientez quelques minutes. Aucun appareil n’a ete deconnecte.';
      if (cause.code === 'session_expired') return 'Session serveur expiree : reconnectez-vous puis recommencez.';
    }
    return cause instanceof Error ? `Echec, aucun appareil deconnecte : ${cause.message}` : 'Echec : aucun appareil n’a ete deconnecte.';
  }

  /**
   * Suppression du compte serveur. Le coffre local n'est efface qu'APRES la reponse
   * du serveur, et seulement si la case est cochee : un echec ne coute jamais la copie locale.
   */
  async function handleAccountDeletionSubmit(form: HTMLFormElement): Promise<void> {
    const session = await liveRemoteSession();
    const email = state.envelope?.email;
    if (session === null || email === undefined || state.accountDeletionBusy) return;
    const data = new FormData(form);
    const eraseDevice = data.get('eraseDevice') !== null;
    if (String(data.get('confirmEmail') ?? '').trim().toLowerCase() !== email.trim().toLowerCase()) {
      state.accountDeletionMessage = 'Recopiez exactement l’adresse e-mail du compte : rien n’a ete supprime.';
      render();
      return;
    }
    state.accountDeletionBusy = true;
    state.accountDeletionMessage = null;
    render();
    let serverDeleted = false;
    try {
      const masterPasswordHashBase64 = await promptMasterPasswordHash(
        'Confirmez votre mot de passe maitre pour supprimer definitivement votre compte serveur :',
      );
      if (masterPasswordHashBase64 === null) return;
      await deleteServerAccount(session.baseUrl, session.sessionToken, masterPasswordHashBase64);
      serverDeleted = true;
      // Le compte n'existe plus : rien de ce qui lui appartenait ne reste affiche.
      state.remoteSession = null;
      state.sends = null;
      state.webauthnCredentials = null;
      state.accountTotpEnabled = null;
      state.accountTotpEnrollment = null;
      state.securityLog = null;
      state.connectedDevices = null;
      state.idenvaConfig = null;
      state.idenvaLinked = null;
      state.emergencyGrants = null;
      state.syncMessage = null;
      if (eraseDevice) {
        await state.repository.clearAll();
        state.localAccountEnvelope = null;
        state.passkeyUnlocks = [];
        await lock();
        scheduleFlash('Compte serveur supprime et coffre efface de cet appareil');
        return;
      }
      state.accountDeletionMessage = `Compte supprime de ${session.baseUrl}. Votre coffre reste sur cet appareil.`;
      scheduleFlash('Compte serveur supprime');
    } catch (cause) {
      state.accountDeletionMessage = serverDeleted
        ? `Compte serveur supprime, mais le coffre de cet appareil n’a pas pu etre efface : ${cause instanceof Error ? cause.message : 'erreur inconnue'}`
        : describeAccountDeletionError(cause);
    } finally {
      state.accountDeletionBusy = false;
      render();
    }
  }

  function describeAccountDeletionError(cause: unknown): string {
    if (cause instanceof KlavoxApiError) {
      if (cause.code === 'authentication_failed') return 'Mot de passe maitre incorrect : rien n’a ete supprime.';
      if (cause.status === 429) return 'Trop d’essais : patientez quelques minutes. Rien n’a ete supprime.';
      if (cause.code === 'session_expired') return 'Session serveur expiree : reconnectez-vous puis recommencez. Rien n’a ete supprime.';
    }
    return cause instanceof Error ? `Echec, rien n’a ete supprime : ${cause.message}` : 'Echec de la suppression : rien n’a ete supprime.';
  }

  /** Lecture seule : la session suffit. Une session changee pendant l'attente n'affiche pas le journal d'un autre compte. */
  async function loadSecurityLog(): Promise<void> {
    const session = await liveRemoteSession();
    if (session === null || state.securityLogBusy) return;
    state.securityLogBusy = true;
    state.securityLogMessage = null;
    render();
    try {
      const events = await fetchSecurityLog(session.baseUrl, session.sessionToken);
      if (state.remoteSession === session) state.securityLog = events;
    } catch (cause) {
      if (state.remoteSession === session) state.securityLogMessage = describeSecurityLogError(cause);
    } finally {
      state.securityLogBusy = false;
      render();
    }
  }

  /** Lecture seule : la session suffit. Une session changee pendant l'attente n'affiche pas les appareils d'un autre compte. */
  async function loadConnectedDevices(): Promise<void> {
    const session = await liveRemoteSession();
    if (session === null || state.connectedDevicesBusy) return;
    state.connectedDevicesBusy = true;
    state.connectedDevicesMessage = null;
    render();
    try {
      const devices = await fetchConnectedDevices(session.baseUrl, session.sessionToken);
      if (state.remoteSession === session) state.connectedDevices = devices;
    } catch (cause) {
      if (state.remoteSession === session) state.connectedDevicesMessage = describeConnectedDevicesError(cause, 'Liste indisponible');
    } finally {
      state.connectedDevicesBusy = false;
      render();
    }
  }

  /**
   * Deconnecte UN autre appareil (« Remove device » de Bitwarden) : une confirmation, pas de mot de passe
   * maitre -- le geste protege, il n'ouvre rien. Un appareil hors de la liste lue, ou celui-ci, ne part pas.
   */
  async function handleRevokeConnectedDevice(deviceId: string): Promise<void> {
    const session = await liveRemoteSession();
    if (session === null || state.connectedDevicesBusy) return;
    const device = state.connectedDevices?.find((item) => item.id === deviceId);
    if (device === undefined || device.current) return;
    if (!window.confirm(`Deconnecter « ${describeUserAgent(device.userAgent)} » ? Cet appareil devra redonner votre mot de passe maitre avant de synchroniser. Son coffre local ne bouge pas.`)) return;
    state.connectedDevicesBusy = true;
    state.connectedDevicesMessage = null;
    render();
    try {
      await revokeConnectedDevice(session.baseUrl, session.sessionToken, deviceId);
      if (state.remoteSession !== session) return;
      state.connectedDevices = (state.connectedDevices ?? []).filter((item) => item.id !== deviceId);
      state.connectedDevicesMessage = 'Appareil deconnecte : il redemandera votre mot de passe maitre.';
      scheduleFlash('Appareil deconnecte');
    } catch (cause) {
      if (state.remoteSession !== session) return;
      // Deja parti (deconnecte ailleurs, expire) : la liste le reflete.
      if (cause instanceof KlavoxApiError && cause.code === 'not_found') {
        state.connectedDevices = (state.connectedDevices ?? []).filter((item) => item.id !== deviceId);
        state.connectedDevicesMessage = 'Cet appareil n’etait deja plus connecte.';
      } else {
        state.connectedDevicesMessage = describeConnectedDevicesError(cause, 'Echec, rien n’a ete deconnecte');
      }
    } finally {
      state.connectedDevicesBusy = false;
      render();
    }
  }

  function describeConnectedDevicesError(cause: unknown, prefix: string): string {
    if (cause instanceof KlavoxApiError && cause.code === 'session_expired') return 'Session serveur expiree : reconnectez-vous puis recommencez.';
    return cause instanceof Error ? `${prefix} : ${cause.message}` : `${prefix}.`;
  }

  function describeSecurityLogError(cause: unknown): string {
    if (cause instanceof KlavoxApiError && cause.code === 'session_expired') return 'Session serveur expiree : reconnectez-vous puis recommencez.';
    return cause instanceof Error ? `Activite indisponible : ${cause.message}` : 'Activite indisponible.';
  }

  /**
   * Non bloquant : un echec de liste n'annule jamais une synchro reussie. Charge aussi l'etat du facteur TOTP et
   * celui de la cle de recuperation.
   */
  async function loadWebAuthnCredentials(): Promise<void> {
    const session = await liveRemoteSession();
    if (session === null) return;
    try {
      const credentials = await listWebAuthnCredentials(session.baseUrl, session.sessionToken);
      if (state.remoteSession === session) state.webauthnCredentials = credentials;
    } catch (cause) {
      if (state.remoteSession === session) state.webauthnMessage = describeWebAuthnError(cause);
    }
    try {
      const enabled = await getTotpStatus(session.baseUrl, session.sessionToken);
      if (state.remoteSession === session) state.accountTotpEnabled = enabled;
    } catch {
      // Serveur anterieur au TOTP de compte (404) ou injoignable : l'etat reste inconnu, le bloc le dit.
      if (state.remoteSession === session) state.accountTotpEnabled = null;
    }
    try {
      const recovery = await getRecoveryKeyStatus(session.baseUrl, session.sessionToken);
      if (state.remoteSession === session) state.recoveryKeyStale = recovery.enabled && !recovery.proof;
    } catch {
      // Serveur anterieur a l'etat de recuperation (404) ou injoignable : rien d'affirme.
      if (state.remoteSession === session) state.recoveryKeyStale = false;
    }
  }

  function describeAccountTotpError(cause: unknown): string {
    if (cause instanceof KlavoxApiError) {
      if (cause.code === 'session_expired') return 'Session serveur expiree : reconnectez-vous puis recommencez.';
      if (cause.code === 'authentication_failed') return 'Mot de passe maitre incorrect : rien n’a ete modifie.';
      if (cause.code === 'totp_invalid') return 'Code refuse : verifiez l’heure du telephone et tapez le code affiche maintenant.';
      if (cause.code === 'not_found') return 'Activation expiree : recommencez.';
    }
    return cause instanceof Error ? `Echec : ${cause.message}` : 'Echec de l’operation';
  }

  async function handleAccountTotpBegin(): Promise<void> {
    const session = await liveRemoteSession();
    if (session === null || state.accountTotpBusy) return;
    state.accountTotpBusy = true;
    state.accountTotpMessage = null;
    render();
    try {
      const enrollment = await apiBeginTotpEnrollment(session.baseUrl, session.sessionToken);
      if (state.remoteSession === session) state.accountTotpEnrollment = enrollment;
    } catch (cause) {
      state.accountTotpMessage = describeAccountTotpError(cause);
    } finally {
      state.accountTotpBusy = false;
      render();
    }
  }

  /** Confirmation : le code prouve que l'application lit la graine ; la preuve maitre, que c'est bien le titulaire. */
  async function handleAccountTotpConfirmSubmit(form: HTMLFormElement): Promise<void> {
    const session = await liveRemoteSession();
    if (session === null || state.accountTotpBusy || state.accountTotpEnrollment === null) return;
    const code = normalizeTotpCode(String(new FormData(form).get('code') ?? ''));
    if (code === null) {
      state.accountTotpMessage = 'Six chiffres attendus : tapez le code affiche par l’application.';
      render();
      return;
    }
    state.accountTotpBusy = true;
    render();
    try {
      const masterPasswordHashBase64 = await promptMasterPasswordHash(
        'Confirmez votre mot de passe maitre pour activer l’application d’authentification :',
      );
      if (masterPasswordHashBase64 === null) return;
      await apiFinishTotpEnrollment(session.baseUrl, session.sessionToken, { code, masterPasswordHashBase64 });
      state.accountTotpEnrollment = null;
      state.accountTotpEnabled = true;
      state.accountTotpMessage = null;
      scheduleFlash('Application d’authentification activee : son code sera demande a la prochaine connexion');
    } catch (cause) {
      state.accountTotpMessage = describeAccountTotpError(cause);
    } finally {
      state.accountTotpBusy = false;
      render();
    }
  }

  async function handleAccountTotpDisable(): Promise<void> {
    const session = await liveRemoteSession();
    if (session === null || state.accountTotpBusy) return;
    const lastFactor = (state.webauthnCredentials ?? []).length === 0;
    const warning = lastFactor
      ? 'Retirer l’application d’authentification desactive la double authentification : le mot de passe maitre suffira pour connecter un appareil. Continuer ?'
      : 'Retirer l’application d’authentification ? Vos cles de securite resteront demandees a la connexion.';
    if (!window.confirm(warning)) return;
    state.accountTotpBusy = true;
    render();
    try {
      const masterPasswordHashBase64 = await promptMasterPasswordHash(
        'Confirmez votre mot de passe maitre pour retirer l’application d’authentification :',
      );
      if (masterPasswordHashBase64 === null) return;
      await apiDisableTotp(session.baseUrl, session.sessionToken, masterPasswordHashBase64);
      state.accountTotpEnabled = false;
      state.accountTotpMessage = null;
      scheduleFlash('Application d’authentification retiree');
    } catch (cause) {
      state.accountTotpMessage = describeAccountTotpError(cause);
    } finally {
      state.accountTotpBusy = false;
      render();
    }
  }

  function describeWebAuthnError(cause: unknown): string {
    if (cause instanceof KlavoxApiError) {
      if (cause.code === 'session_expired') return 'Session serveur expiree : reconnectez-vous puis recommencez.';
      if (cause.code === 'authentication_failed') return 'Mot de passe maitre incorrect : rien n’a ete modifie.';
      if (cause.code === 'webauthn_failed') return 'Cle de securite non verifiee par le serveur.';
      if (cause.code === 'not_found') return 'Authentificateur deja revoque.';
    }
    return cause instanceof Error ? `Echec : ${cause.message}` : 'Echec de l’operation';
  }

  async function handleWebAuthnRegisterSubmit(form: HTMLFormElement): Promise<void> {
    const session = await liveRemoteSession();
    if (session === null || state.webauthnBusy) return;
    const label = String(new FormData(form).get('label') ?? '').trim().slice(0, 64);
    state.webauthnBusy = true;
    state.webauthnMessage = 'Touchez ou deverrouillez votre cle de securite…';
    render();
    try {
      const options = await beginWebAuthnRegistration(session.baseUrl, session.sessionToken);
      const response = await createWebAuthnCredential(options);
      await finishWebAuthnRegistration(session.baseUrl, session.sessionToken, { response, ...(label.length > 0 ? { label } : {}) });
      state.webauthnMessage = null;
      await loadWebAuthnCredentials();
      scheduleFlash('Cle de securite ajoutee : elle sera demandee a la prochaine connexion');
    } catch (cause) {
      state.webauthnMessage = describeWebAuthnError(cause);
    } finally {
      state.webauthnBusy = false;
      render();
    }
  }

  /**
   * Enrole une passkey de deverrouillage. Le coffre est deja ouvert, donc rien
   * de plus n'est demande -- meme regle que l'activation de la cle de
   * recuperation : ce qu'on scelle, on l'a deja sous la main.
   */
  async function handlePasskeyEnrollSubmit(form: HTMLFormElement): Promise<void> {
    const vaultStore = state.vaultStore;
    const envelope = state.envelope;
    if (vaultStore === null || envelope === null || state.passkeyBusy) return;
    const label = String(new FormData(form).get('label') ?? '');
    state.passkeyBusy = true;
    state.passkeyMessage = 'Confirmez avec votre passkey (biometrie ou code de l’appareil)…';
    render();
    try {
      await enrollPasskeyUnlock({ repository: state.repository, vaultStore, email: envelope.email, label });
      state.passkeyUnlocks = await state.repository.listPasskeyUnlocks();
      state.passkeyMessage = null;
      scheduleFlash('Passkey enregistree : elle ouvrira ce coffre sur cet appareil');
    } catch (cause) {
      state.passkeyMessage = describePasskeyUnlockError(cause);
    } finally {
      state.passkeyBusy = false;
      render();
    }
  }

  /** Deverrouillage du coffre par passkey : aucune derivation Argon2id, aucun reseau. */
  async function handlePasskeyUnlock(): Promise<void> {
    if (state.vaultStore !== null || state.authBusy) return;
    state.authError = null;
    state.authBusy = true;
    render();
    try {
      const unlocked = await unlockVaultWithPasskey({ repository: state.repository });
      state.localAccountEnvelope = unlocked.envelope;
      await unlockWith(new VaultStore(state.repository, unlocked.vaultKey), unlocked.envelope);
      scheduleFlash(`Coffre ouvert avec « ${unlocked.record.label} »`);
    } catch (cause) {
      state.authError = describePasskeyUnlockError(cause);
    } finally {
      state.authBusy = false;
      render();
    }
  }

  /** Revocation LOCALE : la fiche disparait, le coffre n'est pas touche. */
  async function handleRevokePasskeyUnlock(credentialId: string): Promise<void> {
    if (state.passkeyBusy) return;
    const record = state.passkeyUnlocks.find((candidate) => candidate.credentialId === credentialId);
    if (record === undefined) return;
    if (
      !window.confirm(
        `Revoquer « ${record.label} » ? Ce coffre ne s’ouvrira plus avec cette passkey sur cet appareil. ` +
          'Votre mot de passe maitre, lui, l’ouvre toujours et aucune entree n’est modifiee.',
      )
    ) {
      return;
    }
    state.passkeyBusy = true;
    render();
    try {
      state.passkeyUnlocks = await revokePasskeyUnlock({ repository: state.repository, credentialId });
      state.passkeyMessage = null;
      scheduleFlash('Passkey revoquee sur cet appareil');
    } catch (cause) {
      state.passkeyMessage = describePasskeyUnlockError(cause);
    } finally {
      state.passkeyBusy = false;
      render();
    }
  }

  async function revokeWebAuthnCredential(credentialId: string): Promise<void> {
    const session = await liveRemoteSession();
    if (session === null || state.webauthnBusy) return;
    const last = (state.webauthnCredentials ?? []).length <= 1;
    const warning = last
      ? 'Revoquer cette derniere cle desactive la double authentification : le mot de passe maitre suffira pour connecter un appareil. Continuer ?'
      : 'Revoquer cette cle de securite ? Elle ne permettra plus de se connecter.';
    if (!window.confirm(warning)) return;
    state.webauthnBusy = true;
    render();
    try {
      const masterPasswordHashBase64 = await promptMasterPasswordHash('Confirmez votre mot de passe maitre pour revoquer la cle :');
      if (masterPasswordHashBase64 === null) return;
      await deleteWebAuthnCredential(session.baseUrl, session.sessionToken, credentialId, masterPasswordHashBase64);
      state.webauthnMessage = null;
      await loadWebAuthnCredentials();
      scheduleFlash(last ? 'Double authentification desactivee' : 'Cle de securite revoquee');
    } catch (cause) {
      state.webauthnMessage = describeWebAuthnError(cause);
    } finally {
      state.webauthnBusy = false;
      render();
    }
  }

  /** Redemande le mot de passe maitre pour prouver l'identite au serveur (jamais mis en cache). */
  function promptMasterPasswordHash(message = 'Confirmez votre mot de passe maitre pour la synchro serveur :'): Promise<string | null> {
    if (state.envelope === null) return Promise.resolve(null);
    // Une seule demande a la fois : une precedente restee ouverte est annulee.
    answerMasterPasswordPrompt(null);
    return new Promise<string | null>((resolve) => {
      state.masterPasswordPrompt = { message, busy: false, error: null, resolve };
      render();
    });
  }

  /** Demande le code TOTP du compte ; une demande precedente restee ouverte est annulee. */
  function promptTotpCode(error: string | null, allowWebAuthn: boolean): Promise<string | null> {
    answerTotpCodePrompt(null);
    return new Promise<string | null>((resolve) => {
      state.totpCodePrompt = { error, allowWebAuthn, resolve };
      render();
    });
  }

  function answerTotpCodePrompt(answer: string | null): void {
    const pending = state.totpCodePrompt;
    if (pending === null) return;
    state.totpCodePrompt = null;
    pending.resolve(answer);
    render();
  }

  /** Forme verifiee avant le reseau : un code mal tape ne coute pas un des cinq essais du serveur. */
  function handleTotpCodePromptSubmit(form: HTMLFormElement): void {
    const pending = state.totpCodePrompt;
    if (pending === null) return;
    const code = normalizeTotpCode(String(new FormData(form).get('code') ?? ''));
    if (code === null) {
      pending.error = 'Six chiffres attendus.';
      render();
      return;
    }
    answerTotpCodePrompt(code);
  }

  /** Ferme la fenetre du mot de passe maitre et rend la preuve (ou `null`) a l'appelant. */
  function answerMasterPasswordPrompt(hash: string | null): void {
    const pending = state.masterPasswordPrompt;
    if (pending === null) return;
    state.masterPasswordPrompt = null;
    pending.resolve(hash);
    render();
  }

  async function handleMasterPasswordPromptSubmit(form: HTMLFormElement): Promise<void> {
    const pending = state.masterPasswordPrompt;
    const envelope = state.envelope;
    if (pending === null || envelope === null || pending.busy) return;
    const masterPassword = String(new FormData(form).get('masterPassword') ?? '');
    pending.busy = true;
    pending.error = null;
    render();
    try {
      const hash = await masterPasswordHashIfValid(envelope, masterPassword);
      // Annulee ou verrouillee pendant la derivation : la preuve n'est rendue a personne.
      if (state.masterPasswordPrompt !== pending) return;
      if (hash !== null) answerMasterPasswordPrompt(hash);
      else pending.error = 'Mot de passe maitre incorrect.';
    } catch (cause) {
      pending.error = cause instanceof Error ? cause.message : 'Verification impossible';
    } finally {
      pending.busy = false;
      // Re-rendu complet : le mot de passe tape ne reste pas dans le DOM.
      if (state.masterPasswordPrompt === pending) render();
    }
  }

  async function copyToClipboard(value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      scheduleFlash(`Copie dans le presse-papiers (efface dans ${clipboardClearLabel(state.clipboardClearSeconds)})`);
      // Le delai court depuis la DERNIERE copie : un minuteur d'une copie precedente n'efface pas celle-ci en avance.
      if (clipboardTimer !== null) clearTimeout(clipboardTimer);
      clipboardTimer = setTimeout(() => {
        clipboardTimer = null;
        navigator.clipboard.writeText('').catch(() => undefined);
      }, state.clipboardClearSeconds * 1000);
    } catch {
      scheduleFlash('Impossible de copier');
    }
    render();
  }

  /** Ouvre la fiche d'une entree : clic sur sa ligne ou raccourci clavier, un seul chemin. */
  async function selectEntry(id: string): Promise<void> {
    const entry = state.entries.find((item) => item.id === id);
    if (entry !== undefined) await leaveHealthFor(entry);
    if (state.selectedEntryId !== id) state.repromptConfirmedId = null;
    state.selectedEntryId = id;
    state.panel = 'vault';
    state.draft = null;
    state.revealedEntryId = null;
    state.largeType = null;
    state.wifiQr = null;
    state.totpQr = null;
    state.repromptError = null;
    state.linkPickerFor = null;
    closeAttachmentPreview();
    await refreshRelated();
    render();
  }

  /**
   * Copie un champ d'une entree (bouton Copier ou raccourci) : lu dans l'entree au moment du geste, un secret masque
   * n'est jamais pose dans un attribut du DOM ; une entree sensible pas encore confirmee ne livre rien.
   */
  async function copyEntryField(id: string, ref: string): Promise<boolean> {
    const entry = state.entries.find((item) => item.id === id);
    if (entry === undefined || repromptPending(entry)) return false;
    const value = entryFieldValue(entry.secrets, ref);
    if (value === null) return false;
    await copyToClipboard(value);
    return true;
  }

  /** Code 2FA courant de l'entree, calcule au moment du raccourci ; meme garde que les autres copies. */
  async function copyEntryTotp(id: string): Promise<boolean> {
    const entry = state.entries.find((item) => item.id === id);
    if (entry === undefined || repromptPending(entry) || !entry.secrets.totpSecret) return false;
    let code: string;
    try {
      code = computeTotpDisplay({ secret: entry.secrets.totpSecret }).formattedCode.replace(/\s+/gu, '');
    } catch {
      return false;
    }
    await copyToClipboard(code);
    return true;
  }

  function closeHelp(): void {
    state.helpOpen = false;
    state.helpQuestion = '';
    state.helpAnswer = null;
    state.helpCommand = null;
    state.helpTarget = { kind: 'aucun' };
    render();
  }

  /** Une question a l'aide : la reponse documentee ET, si elle demande un geste du produit, la commande (§ 8.5). */
  function askAssistant(question: string): void {
    state.helpQuestion = question;
    state.helpAnswer = askHelp(question, currentHelpView(), helpSnapshot());
    // Les noms viennent de l'ecran (`data-name`) et se comparent sur le poste : rien ne part, rien n'est devine.
    state.helpCommand = matchCommand(question, (command) => findNamed(question, namesFor(command)).kind !== 'aucun');
    state.helpTarget = state.helpCommand.kind === 'commande' ? findNamed(question, namesFor(state.helpCommand.command)) : { kind: 'aucun' };
  }

  /**
   * Le SEUL point d'execution des commandes (§ 8.3 g, § 8.6 b bis) : `checkRunnable` refuse l'inconnue,
   * l'indisponible et toute `preparer`, d'ou que vienne la demande. L'execution clique le bouton affiche -- le chemin
   * du clic humain. `args.target` = le NOM affiche de l'element vise : il filtre les controles d'une commande nommee,
   * ou ouvre d'abord l'element (« concernant X ») par sa propre commande, donc sous le meme refus.
   */
  async function runCommand(id: string, args: CommandArgs = {}): Promise<CommandResult> {
    const command = KLAVOX_COMMANDS.find((candidate) => candidate.id === id);
    const via = command === undefined || args.target === undefined ? undefined : designator(command);
    if (via !== undefined && via !== command) {
      const ouvert = await runCommand(via.id, args);
      if (!ouvert.ok) return ouvert;
    }
    const target = via === command ? args.target : undefined;
    const verdict = checkRunnable(id, commandCatalog((candidate) => controlsFor(candidate, candidate.id === id ? target : undefined).length));
    if (!verdict.ok) return verdict;
    controlsFor(command!, target)[0]!.click();
    await pendingClick;
    return verdict;
  }

  /**
   * `preparer` : amener l'utilisateur devant le bouton -- ou le premier champ du formulaire --, focus pose, et lui
   * rendre la main. Rien n'est clique, rien n'est saisi.
   */
  async function prepareCommand(command: KlavoxCommand, target?: string): Promise<void> {
    const via = target === undefined ? undefined : designator(command);
    if (via !== undefined && via !== command) await runCommand(via.id, { target });
    const own = via === command ? target : undefined;
    if (controlsFor(command, own).length !== 1 && command.opens !== undefined && command.opens !== via?.id) await runCommand(command.opens);
    closeHelp();
    const pret = controlsFor(command, own).length === 1;
    scheduleFlash(pret ? `Pret : cliquez vous-meme sur « ${command.title} » pour confirmer` : `« ${command.title} » est indisponible : ${command.unavailable}`);
    render();
    const control = controlsFor(command, own);
    if (control.length === 1) {
      const champ = control[0]!.tagName === 'FORM' ? (control[0]!.querySelector<HTMLElement>('input:not([type="hidden"]), select, textarea') ?? control[0]!) : control[0]!;
      champ.focus();
      champ.scrollIntoView?.({ block: 'nearest' });
    }
  }

  async function handleClick(event: MouseEvent): Promise<void> {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.dataset.input === 'import-csv' && hasImportFilePicker(window)) {
      // Selecteur natif plutot que le champ fichier : lui seul rend une poignee, donc une suppression en un clic.
      event.preventDefault();
      await handleImportPick(input.accept);
      return;
    }
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (target === null) return;
    const action = target.dataset.action;
    const id = target.dataset.id;
    switch (action) {
      case 'open-help':
        state.helpOpen = true;
        state.helpAnswer = null;
        render();
        return;
      case 'close-help':
        // Le fond porte l'action pour fermer au clic exterieur : un clic DANS
        // le panneau remonte jusqu'a lui et ne doit pas le refermer.
        if (target.classList.contains('help-backdrop') && (event.target as HTMLElement).closest('.help-panel') !== null) return;
        closeHelp();
        return;
      case 'ask-help-suggestion':
        askAssistant(target.dataset.question ?? '');
        render();
        return;
      case 'help-command-cancel':
        state.helpCommand = null;
        render();
        return;
      case 'help-command-run': {
        const command = KLAVOX_COMMANDS.find((candidate) => candidate.id === id);
        if (command === undefined || state.helpCommandBusy) return;
        const commandTarget = target.dataset.target;
        if (command.nature === 'preparer') {
          await prepareCommand(command, commandTarget);
          return;
        }
        state.helpCommandBusy = true;
        render();
        let result: CommandResult;
        try {
          result = await runCommand(command.id, commandTarget === undefined ? {} : { target: commandTarget });
        } finally {
          state.helpCommandBusy = false;
        }
        if (!result.ok) {
          state.helpCommand = null;
          state.helpAnswer = { kind: 'inconnu', title: 'Commande refusee', text: result.error, sources: [], related: [] };
          render();
          return;
        }
        closeHelp();
        scheduleFlash(`Fait : ${command.title}`);
        render();
        return;
      }
      case 'reset-shortcuts':
        saveShortcutSettings(DEFAULT_SHORTCUT_SETTINGS);
        scheduleFlash('Touches par defaut retablies');
        render();
        return;
      case 'toggle-theme':
        state.theme = state.theme === 'dark' ? 'light' : 'dark';
        localStorage.setItem('klavox:theme', state.theme);
        render();
        return;
      case 'toggle-auth-mode':
        state.authMode = state.authMode === 'create' ? 'unlock' : 'create';
        state.authError = null;
        render();
        return;
      case 'toggle-recover-mode':
        state.authMode = 'recover';
        state.authError = null;
        render();
        return;
      case 'open-remote-signin':
        state.authMode = 'signin-remote';
        state.authError = null;
        render();
        return;
      case 'open-remote-recover':
        state.authMode = 'recover-remote';
        state.authError = null;
        render();
        return;
      case 'cancel-remote-recover':
        state.authMode = state.localAccountEnvelope === null ? 'create' : 'unlock';
        state.authError = null;
        render();
        return;
      case 'cancel-recover':
        state.authMode = 'unlock';
        state.authError = null;
        render();
        return;
      case 'select-all':
        exitBulk();
        state.selectedFolderId = null;
        state.selectedTag = null;
        state.panel = 'vault';
        state.favoritesOnly = false;
        state.archivedOnly = false;
        state.trashOnly = false;
        state.searchQuery = '';
        await refreshVaultAndRender();
        return;
      case 'select-favorites':
        exitBulk();
        state.favoritesOnly = true;
        state.archivedOnly = false;
        state.trashOnly = false;
        state.panel = 'vault';
        state.selectedFolderId = null;
        state.selectedTag = null;
        state.searchQuery = '';
        await refreshVaultAndRender();
        return;
      case 'select-archives':
        exitBulk();
        state.archivedOnly = true;
        state.trashOnly = false;
        state.favoritesOnly = false;
        state.panel = 'vault';
        state.selectedFolderId = null;
        state.selectedTag = null;
        state.selectedEntryId = null;
        state.draft = null;
        state.searchQuery = '';
        await refreshVaultAndRender();
        return;
      case 'select-trash':
        exitBulk();
        state.trashOnly = true;
        state.archivedOnly = false;
        state.favoritesOnly = false;
        state.panel = 'vault';
        state.selectedFolderId = null;
        state.selectedTag = null;
        state.selectedEntryId = null;
        state.draft = null;
        state.searchQuery = '';
        await refreshVaultAndRender();
        return;
      case 'select-folder':
        if (id !== undefined) {
          exitBulk();
          state.selectedFolderId = id;
          state.selectedTag = null;
          state.favoritesOnly = false;
          state.archivedOnly = false;
          state.trashOnly = false;
          state.panel = 'vault';
          state.searchQuery = '';
          await refreshVaultAndRender();
        }
        return;
      case 'toggle-folder-collapse':
        // Affichage seul : ni le store ni la vue ne changent. Le chevron est hors de l'arbre accessible (souris
        // seulement) : le focus passe sur la ligne du dossier, qui porte `aria-expanded`.
        if (id !== undefined && state.folders.some((folder) => folder.id === id)) {
          if (!state.collapsedFolderIds.delete(id)) state.collapsedFolderIds.add(id);
          render();
          Array.from(root.querySelectorAll<HTMLElement>('.sidebar [data-action="select-folder"]'))
            .find((row) => row.dataset.id === id)
            ?.focus();
        }
        return;
      case 'toggle-tag-collapse':
        // Affichage seul, comme les dossiers : ni le store ni la vue ne changent. Le chevron est hors de l'arbre
        // accessible (souris seulement) : le focus passe sur la ligne de l'etiquette, qui porte `aria-expanded`.
        if (id !== undefined && state.tags.some((row) => tagKey(row.tag) === tagKey(id))) {
          if (!state.collapsedTagKeys.delete(tagKey(id))) state.collapsedTagKeys.add(tagKey(id));
          render();
          Array.from(root.querySelectorAll<HTMLElement>('.sidebar [data-action="select-tag"]'))
            .find((row) => row.dataset.id === id)
            ?.focus();
        }
        return;
      case 'select-tag':
        if (id !== undefined) {
          exitBulk();
          state.selectedTag = id;
          state.selectedFolderId = null;
          state.favoritesOnly = false;
          state.archivedOnly = false;
          state.trashOnly = false;
          state.panel = 'vault';
          state.searchQuery = '';
          await refreshVaultAndRender();
        }
        return;
      case 'toggle-travel-safe': {
        if (id === undefined || state.vaultStore === null) return;
        const folder = state.folders.find((candidate) => candidate.id === id);
        if (folder === undefined) return;
        await state.vaultStore.setFolderTravelSafe(id, folder.secrets.travelSafe !== true);
        await refreshVaultData();
        render();
        return;
      }
      case 'enable-travel-mode':
        await handleTravelMode(true);
        return;
      case 'disable-travel-mode':
        await handleTravelMode(false);
        return;
      case 'new-folder': {
        const name = window.prompt('Nom du dossier :');
        if (name !== null && name.trim().length > 0 && state.vaultStore !== null) {
          await state.vaultStore.createFolder(name.trim());
          await refreshVaultData();
          render();
        }
        return;
      }
      case 'open-generator':
        state.panel = 'generator';
        // Le type garde d'une session precedente s'ouvre tel quel : un identifiant y est tire des l'ouverture
        // (la liste de mots d'une phrase de passe, elle, est chargee au deverrouillage).
        if (state.generatorMode === 'username') {
          await regenerateUsername();
          return;
        }
        state.generatedPassword = generateForGenerator();
        render();
        return;
      case 'open-health':
        // Analyse sur TOUT le coffre (la reutilisation se juge globalement), pas sur la vue filtree.
        state.panel = 'health';
        state.selectedFolderId = null;
        state.selectedTag = null;
        state.favoritesOnly = false;
        state.archivedOnly = false;
        state.trashOnly = false;
        state.searchQuery = '';
        state.draft = null;
        await refreshVaultAndRender();
        return;
      case 'check-breaches': {
        if (state.breachEndpoint === null || state.breachBusy || state.vaultStore === null) return;
        const vaultStore = state.vaultStore;
        state.breachBusy = true;
        state.breachError = null;
        render();
        try {
          const counts = await checkPwnedPasswords(
            healthPasswords(state.entries),
            { endpoint: state.breachEndpoint },
          );
          // Verrouille pendant la verification : le resultat (indexe par mot de passe) est jete.
          if (state.vaultStore === vaultStore) {
            state.breachCounts = counts;
            state.breachCheckedAt = Date.now();
          }
        } catch (cause) {
          state.breachError = cause instanceof BreachCheckError ? cause.message : 'Verification des fuites impossible';
        } finally {
          state.breachBusy = false;
          render();
        }
        return;
      }
      case 'replace-password': {
        const entry = state.entries.find((item) => item.id === id);
        if (entry !== undefined) {
          await leaveHealthFor(entry);
          if (state.selectedEntryId !== entry.id) state.repromptConfirmedId = null;
          state.panel = 'vault';
          state.selectedEntryId = entry.id;
          // Entree sensible : la fiche s'ouvre sur la re-demande, l'editeur attendra la confirmation.
          if (repromptPending(entry)) {
            state.draft = null;
            render();
            return;
          }
          const draft = draftFromEntry(entry);
          state.draft = { ...draft, password: generateSecretForDraft(draft) };
          state.revealDraftPassword = true;
          render();
        }
        return;
      }
      case 'open-send':
        state.panel = 'send';
        state.selectedEntryId = null;
        state.sendMessage = null;
        state.sendShare = null;
        if (state.sends === null) void refreshSends();
        render();
        return;
      case 'share-entry': {
        const entry = id === undefined ? undefined : state.entries.find((item) => item.id === id);
        // Meme garde que la fiche : une entree sensible non confirmee, ou a la corbeille, ne s'ouvre pas au partage.
        if (entry === undefined || repromptPending(entry) || isTrashed(entry.secrets)) return;
        const fields = shareableFields(entry.secrets);
        if (fields.length === 0) return;
        const name = entry.secrets.name?.trim() || 'Sans nom';
        state.sendShare = { entryId: entry.id, name, fields, excluded: [] };
        state.sendDraft = { ...EMPTY_SEND_DRAFT, label: name };
        state.sendFile = null;
        state.sendCreated = null;
        state.sendMessage = null;
        state.panel = 'send';
        state.selectedEntryId = null;
        if (state.sends === null) void refreshSends();
        render();
        return;
      }
      case 'cancel-share-entry':
        syncSendDraftFromForm();
        state.sendShare = null;
        render();
        return;
      case 'copy-send-field': {
        // La valeur est relue dans le contenu dechiffre, jamais dans la page : un champ masque se copie sans etre devoile.
        const field = state.sendReceive?.payload?.fields?.[Number(target.dataset.index)];
        if (field !== undefined && field.value.length > 0) await copyToClipboard(field.value);
        return;
      }
      case 'clear-send-file':
        syncSendDraftFromForm();
        state.sendFile = null;
        render();
        return;
      case 'dismiss-send-created':
        state.sendCreated = null;
        render();
        return;
      case 'revoke-send':
        if (id !== undefined) await handleRevokeSend(id);
        return;
      case 'edit-send':
        if (id !== undefined && state.sends?.some((send) => send.id === id && send.revokedAt === null)) {
          state.sendEditingId = id;
          state.sendMessage = null;
          render();
        }
        return;
      case 'cancel-edit-send':
        state.sendEditingId = null;
        render();
        return;
      case 'receive-send':
        await openReceivedSend();
        return;
      case 'download-send-file':
        downloadReceivedFile();
        return;
      case 'toggle-send-text':
        if (state.sendReceive !== null) {
          state.sendReceive.revealed = !state.sendReceive.revealed;
          render();
        }
        return;
      case 'copy-send-text': {
        const texte = state.sendReceive?.payload?.text;
        if (typeof texte === 'string' && texte.length > 0) await copyToClipboard(texte);
        return;
      }
      case 'open-settings':
        state.panel = 'settings';
        render();
        return;
      case 'copy-error-journal': {
        const journal = options.journalErreurs?.text() ?? '';
        if (journal.length > 0) await copyToClipboard(journal);
        else {
          scheduleFlash('Journal d’erreurs vide : aucune erreur notee');
          render();
        }
        return;
      }
      case 'idenva-link':
        await handleIdenvaLink();
        return;
      case 'idenva-signin':
        await handleIdenvaSignIn();
        return;
      case 'idenva-signout':
        state.idenvaSignIn = null;
        state.authError = null;
        render();
        return;
      case 'idenva-cancel':
        idenvaRun?.abort();
        return;
      case 'idenva-unlink':
        await handleIdenvaUnlink();
        return;
      case 'load-security-log':
        await loadSecurityLog();
        return;
      case 'load-connected-devices':
        await loadConnectedDevices();
        return;
      case 'revoke-connected-device': {
        const deviceId = target.dataset.device;
        if (deviceId !== undefined) await handleRevokeConnectedDevice(deviceId);
        return;
      }
      case 'revoke-webauthn': {
        const credentialId = target.dataset.credential;
        if (credentialId !== undefined) await revokeWebAuthnCredential(credentialId);
        return;
      }
      case 'unlock-with-passkey':
        await handlePasskeyUnlock();
        return;
      case 'revoke-passkey-unlock': {
        const credentialId = target.dataset.credential;
        if (credentialId !== undefined) await handleRevokePasskeyUnlock(credentialId);
        return;
      }
      case 'preview-attachment': {
        const attachmentId = target.dataset.attachment;
        if (id === undefined || attachmentId === undefined || state.vaultStore === null) return;
        // Meme garde que la fiche : une entree sensible non confirmee ne montre rien, meme par un bouton forge.
        const owner = state.entries.find((item) => item.id === id);
        if (owner === undefined || repromptPending(owner)) return;
        const vaultStore = state.vaultStore;
        try {
          const file = await vaultStore.readAttachment(id, attachmentId);
          const preview = attachmentPreview(file.bytes);
          // Le coffre a pu se verrouiller, ou la fiche changer, pendant le dechiffrement : rien ne s'ouvre alors.
          if (state.vaultStore !== vaultStore || state.selectedEntryId !== id) return;
          closeAttachmentPreview();
          // Type de la SIGNATURE, jamais celui du nom : une URL `blob:` ouverte dans un onglet ne rend qu'une image.
          const url = preview.kind === 'image' ? URL.createObjectURL(new Blob([new Uint8Array(file.bytes)], { type: preview.mimeType })) : null;
          state.attachmentPreview = { entryId: id, attachmentId, fileName: file.fileName, size: file.bytes.length, preview, url };
        } catch {
          scheduleFlash('Piece jointe illisible (alteree ?)');
        }
        render();
        return;
      }
      case 'close-attachment-preview':
        // Le fond porte l'action pour fermer au clic exterieur : un clic DANS le panneau ne le referme pas.
        if (target.classList.contains('attachment-preview-backdrop') && (event.target as HTMLElement).closest('.attachment-preview-panel') !== null) return;
        closeAttachmentPreview();
        render();
        return;
      case 'download-attachment': {
        const attachmentId = target.dataset.attachment;
        if (id === undefined || attachmentId === undefined || state.vaultStore === null) return;
        // Une entree sensible non confirmee ne livre pas ses pieces jointes, meme par un bouton forge.
        const owner = state.entries.find((item) => item.id === id);
        if (owner === undefined || repromptPending(owner)) return;
        try {
          const file = await state.vaultStore.readAttachment(id, attachmentId);
          // octet-stream : toujours un telechargement, jamais un rendu (HTML/SVG) dans l'origine du coffre.
          downloadBlob(file.fileName, new Blob([new Uint8Array(file.bytes)], { type: 'application/octet-stream' }));
        } catch {
          scheduleFlash('Piece jointe illisible (alteree ?)');
          render();
        }
        return;
      }
      case 'delete-attachment': {
        const attachmentId = target.dataset.attachment;
        if (id === undefined || attachmentId === undefined || state.vaultStore === null) return;
        if (!window.confirm('Supprimer cette piece jointe ?')) return;
        await state.vaultStore.deleteAttachment(id, attachmentId);
        if (state.attachmentPreview?.attachmentId === attachmentId) closeAttachmentPreview();
        forgetAttachments();
        scheduleFlash('Piece jointe supprimee');
        render();
        return;
      }
      case 'lock':
        await lock();
        return;
      case 'bulk-start':
        exitBulk();
        state.bulkMode = true;
        render();
        return;
      case 'bulk-exit':
        exitBulk();
        render();
        return;
      case 'bulk-toggle':
        // Maj+clic coche (ou decoche) toute la plage affichee depuis la derniere ligne cliquee.
        if (id !== undefined && state.bulkMode) toggleBulkRow(id, event.shiftKey);
        return;
      case 'bulk-select-all': {
        const visible = state.entries.map((item) => item.id);
        const all = bulkTargets(state.entries, state.bulkSelected).length === visible.length;
        if (!all) {
          selectAllBulk();
          return;
        }
        state.bulkSelected = state.bulkSelected.filter((selected) => !visible.includes(selected));
        state.bulkReprompt = null;
        state.bulkError = null;
        render();
        return;
      }
      case 'bulk-cancel-reprompt':
        state.bulkReprompt = null;
        state.bulkError = null;
        render();
        return;
      case 'bulk-apply':
        await requestBulk(target.dataset.bulk);
        return;
      case 'rename-tag': {
        const from = state.selectedTag;
        if (from === null || state.vaultStore === null || state.draft !== null) return;
        const typed = window.prompt(`Nouveau nom de l’etiquette « ${from} »${hasSubTags(from) ? ' (ses sous-etiquettes suivent)' : ''} :`, from);
        const to = typed === null ? '' : normalizeTag(typed);
        if (to.length === 0 || to === from) return;
        const count = await state.vaultStore.renameTag(from, to);
        state.selectedTag = to;
        await refreshVaultData();
        scheduleFlash(`Etiquette renommee en « ${to} » sur ${count} entree(s)`);
        render();
        return;
      }
      case 'delete-tag': {
        const tag = state.selectedTag;
        if (tag === null || state.vaultStore === null || state.draft !== null) return;
        if (!window.confirm(`Retirer l’etiquette « ${tag} »${hasSubTags(tag) ? ' et ses sous-etiquettes' : ''} de toutes les entrees ? Les entrees restent dans le coffre.`)) return;
        const count = await state.vaultStore.deleteTag(tag);
        state.selectedTag = null;
        await refreshVaultData();
        scheduleFlash(`Etiquette « ${tag} » retiree de ${count} entree(s)`);
        render();
        return;
      }
      case 'new-subfolder': {
        // Cree sous le dossier de la vue ; la vue passe au sous-dossier, vide, pret a recevoir ses entrees.
        const parent = state.folders.find((candidate) => candidate.id === state.selectedFolderId);
        if (parent === undefined || state.vaultStore === null || state.draft !== null) return;
        const name = window.prompt(`Nom du sous-dossier de « ${parent.secrets.name} » :`)?.trim() ?? '';
        if (name.length === 0) return;
        const created = await state.vaultStore.createFolder(name, parent.id);
        state.selectedFolderId = created.id;
        // Le nouveau sous-dossier, ouvert, se voit : son parent se deplie.
        state.collapsedFolderIds.delete(parent.id);
        await refreshVaultData();
        scheduleFlash(`Sous-dossier « ${name} » cree dans « ${parent.secrets.name} »`);
        render();
        return;
      }
      case 'rename-folder': {
        const folder = state.folders.find((candidate) => candidate.id === state.selectedFolderId);
        if (folder === undefined || state.vaultStore === null || state.draft !== null) return;
        const typed = window.prompt(`Nouveau nom du dossier « ${folder.secrets.name} » :`, folder.secrets.name);
        const to = typed === null ? '' : typed.trim();
        if (to.length === 0 || to === folder.secrets.name) return;
        await state.vaultStore.renameFolder(folder.id, to);
        await refreshVaultData();
        scheduleFlash(`Dossier renomme en « ${to} »`);
        render();
        return;
      }
      case 'delete-folder': {
        const folder = state.folders.find((candidate) => candidate.id === state.selectedFolderId);
        if (folder === undefined || state.vaultStore === null || state.draft !== null) return;
        const name = folder.secrets.name;
        const hasChildren = state.folders.some((candidate) => candidate.parentId === folder.id);
        const question = `Supprimer le dossier « ${name} » ? Ses entrees restent dans le coffre, hors dossier.${
          hasChildren ? ' Ses sous-dossiers remontent d’un niveau.' : ''
        }`;
        if (!window.confirm(question)) return;
        const { entries } = await state.vaultStore.deleteFolder(folder.id);
        state.selectedFolderId = null;
        state.selectedEntryId = null;
        await refreshVaultData();
        scheduleFlash(`Dossier « ${name} » supprime : ${entries} entree(s) remise(s) hors dossier`);
        render();
        return;
      }
      case 'select-entry':
        if (id !== undefined) await selectEntry(id);
        return;
      case 'new-entry':
        // Depuis la vue d'une etiquette, la nouvelle entree la porte deja (comme le dossier de la vue).
        state.draft = { ...EMPTY_DRAFT, folderId: state.selectedFolderId, tags: state.selectedTag ?? '' };
        state.revealDraftPassword = false;
        render();
        return;
      case 'edit-entry': {
        const entry = state.entries.find((item) => item.id === id);
        if (entry !== undefined && !repromptPending(entry)) {
          state.draft = draftFromEntry(entry);
          state.revealDraftPassword = false;
          render();
        }
        return;
      }
      case 'duplicate-entry': {
        const entry = state.entries.find((item) => item.id === id);
        // Meme garde que « Modifier » : dupliquer une entree sensible devoile tout son contenu dans l'editeur.
        if (entry !== undefined && state.vaultStore !== null && !repromptPending(entry)) {
          const copy = await state.vaultStore.duplicateEntry(entry.id);
          // La copie nait active, meme depuis les Archives : on la montre la ou elle est, ouverte en edition.
          state.archivedOnly = false;
          state.favoritesOnly = false;
          state.selectedEntryId = copy.id;
          forgetAttachments();
          state.revealedEntryId = null;
          // L'original vient d'etre devoile : sa copie, identique, ne redemande pas dans la foulee.
          state.repromptConfirmedId = copy.id;
          await refreshVaultData();
          state.draft = draftFromEntry(copy);
          state.revealDraftPassword = false;
          scheduleFlash('Copie creee -- passkeys, historique et pieces jointes restent a l’original');
          render();
        }
        return;
      }
      case 'cancel-edit':
        state.draft = null;
        render();
        return;
      case 'add-uri': {
        const draft = syncDraftFromForm();
        if (draft === null) return;
        draft.uriRules = [draft.uri, ...draft.extraUris, ''].map((_uri, position) => draft.uriRules[position] ?? '');
        draft.extraUris = [...draft.extraUris, ''];
        render();
        root.querySelector<HTMLInputElement>(`[name="uri-extra:${draft.extraUris.length - 1}"]`)?.focus();
        return;
      }
      case 'remove-uri': {
        const draft = syncDraftFromForm();
        if (draft === null) return;
        const index = Number(target.dataset.index);
        draft.extraUris = draft.extraUris.filter((_uri, position) => position !== index);
        // La regle part avec son adresse (rang `index + 1`, la premiere etant « Site (URL) ») : rien ne glisse.
        draft.uriRules = draft.uriRules.filter((_rule, position) => position !== index + 1);
        render();
        return;
      }
      case 'move-uri': {
        const draft = syncDraftFromForm();
        if (draft === null) return;
        const index = Number(target.dataset.index);
        const direction = target.dataset.direction === '-1' ? -1 : 1;
        const moved = moveEntryUri([draft.uri, ...draft.extraUris], draft.uriRules, index, direction);
        if (moved === null) return;
        // L'adresse et sa regle changent de ligne ensemble ; la premiere ligne est « Site (URL) ».
        draft.uri = moved.uris[0]!;
        draft.extraUris = moved.uris.slice(1);
        draft.uriRules = moved.rules;
        render();
        // Le clavier suit l'adresse deplacee : son bouton dans le meme sens s'il existe encore, sinon l'autre.
        const to = index + direction;
        (root.querySelector<HTMLElement>(`[data-action="move-uri"][data-index="${to}"][data-direction="${direction}"]`) ??
          root.querySelector<HTMLElement>(`[data-action="move-uri"][data-index="${to}"]`))?.focus();
        return;
      }
      case 'add-custom-field': {
        const draft = syncDraftFromForm();
        if (draft === null) return;
        draft.customFields = [...draft.customFields, { name: '', value: '', hidden: false }];
        render();
        root.querySelector<HTMLInputElement>(`[name="custom-name:${draft.customFields.length - 1}"]`)?.focus();
        return;
      }
      case 'add-custom-checkbox': {
        // Case a cocher de Bitwarden (« Compte professionnel », « Abonnement actif ») : decochee, le nom a saisir.
        const draft = syncDraftFromForm();
        if (draft === null) return;
        draft.customFields = [...draft.customFields, { name: '', value: 'false', kind: 'boolean' }];
        render();
        root.querySelector<HTMLInputElement>(`[name="custom-name:${draft.customFields.length - 1}"]`)?.focus();
        return;
      }
      case 'remove-custom-field': {
        const draft = syncDraftFromForm();
        if (draft === null) return;
        const index = Number(target.dataset.index);
        draft.customFields = draft.customFields.filter((_field, position) => position !== index);
        render();
        return;
      }
      case 'toggle-draft-reveal':
        syncDraftFromForm();
        state.revealDraftPassword = !state.revealDraftPassword;
        render();
        return;
      case 'generate-username-into-draft':
        {
          const draft = syncDraftFromForm();
          if (draft === null) return;
          const username = await drawUsername(siteSuffixFromUris([draft.uri, ...draft.extraUris]));
          // La saisie a pu changer pendant le chargement de la liste : on relit le brouillon courant.
          const current = syncDraftFromForm();
          if (current === null) return;
          if (username === null) {
            scheduleFlash(`${state.usernameError ?? 'Generation impossible'} Reglages : Generateur, onglet Identifiant.`);
            // Sans rendu, le refus restait invisible : le bouton semblait ne rien faire.
            render();
            return;
          }
          current.username = username;
          render();
        }
        return;
      case 'generate-ssh-key-into-draft':
        {
          const draft = syncDraftFromForm();
          if (draft === null || draft.type !== 'ssh-key' || state.sshKeygenBusy) return;
          // Une cle deja la (collee, ou celle de l'entree) n'est remplacee que sur accord explicite.
          if (
            draft.privateKey.trim().length > 0 &&
            !window.confirm('Remplacer la cle privee par une nouvelle cle Ed25519 ? A l’enregistrement, l’ancienne ne sera plus dans cette entree.')
          ) {
            return;
          }
          state.sshKeygenBusy = true;
          let generated: SshKeyMaterial;
          try {
            generated = await generateSshKey(draft.name.trim().length > 0 ? draft.name : 'klavox');
          } catch (error) {
            scheduleFlash(error instanceof SshKeygenError ? error.message : 'Generation de la cle impossible');
            return;
          } finally {
            state.sshKeygenBusy = false;
          }
          // Le brouillon a pu etre ferme (verrouillage, annulation) pendant la generation : rien n'y est ecrit. Une
          // saisie faite entre-temps est relue avant d'ecrire la paire.
          const current = syncDraftFromForm();
          if (current === null || current.id !== draft.id || current.type !== 'ssh-key') return;
          current.privateKey = generated.privateKey;
          current.publicKey = generated.publicKey;
          // La cle privee reste masquee : seule l'empreinte s'affiche, la cle publique est a copier depuis la fiche.
          state.revealDraftPassword = false;
          scheduleFlash('Cle Ed25519 generee : enregistrez, puis copiez la cle publique dans authorized_keys');
          render();
        }
        return;
      case 'password-expiry-preset':
        {
          const draft = syncDraftFromForm();
          if (draft === null) return;
          const months = Number(target.dataset.months);
          draft.passwordExpiry = months > 0 ? passwordExpiryIn(months, Date.now()) : '';
          // « Aucune echeance » retire aussi la regle : sinon elle reposerait une date a l'enregistrement.
          if (months === 0) draft.passwordRenewDays = '';
          render();
        }
        return;
      case 'generate-into-draft':
        {
          const draft = syncDraftFromForm();
          if (draft === null) return;
          draft.password = generateSecretForDraft(draft);
          state.revealDraftPassword = true;
          render();
        }
        return;
      case 'check-draft-breach': {
        // Le geste explicite : rien ne part sans ce clic. Lu dans le formulaire, pas dans le brouillon d'avant la frappe.
        const draft = syncDraftFromForm();
        if (draft === null || draft.password === '' || state.draftBreach?.verdict === null) return;
        const vaultStore = state.vaultStore;
        const password = draft.password;
        state.draftBreach = { password, verdict: null };
        render();
        let verdict: DraftBreachVerdict | null = null;
        try {
          verdict = await checkDraftPasswordExposure(password, { endpoint: draftBreachEndpoint() });
        } finally {
          // Verrouille ou fiche fermee pendant la verification : le verdict (et le mot de passe qu'il garde) est jete.
          state.draftBreach = state.vaultStore === vaultStore && state.draft !== null && verdict !== null ? { password, verdict } : null;
          render();
        }
        return;
      }
      case 'open-large-type':
        state.largeType = id === undefined ? null : { entryId: id, field: target.dataset.field ?? '' };
        // Meme lecture que le rendu : une entree sensible non confirmee, un champ vide ou forge n'ouvrent rien.
        if (largeTypeValue() === null) state.largeType = null;
        render();
        return;
      case 'scan-totp-qr-camera':
        void scanDraftQrWithCamera();
        return;
      case 'open-wifi-qr':
        state.wifiQr = id === undefined ? null : { entryId: id };
        // Meme lecture que le rendu : une entree sensible non confirmee, ou qui n'est pas un reseau, n'ouvre rien.
        if (wifiQrValue() === null) state.wifiQr = null;
        render();
        return;
      case 'print-wifi-qr':
        // Meme garde que le rendu : seul un code effectivement montre s'imprime (jamais sur un bouton forge).
        if (wifiQrValue()?.content.ok !== true) return;
        window.print();
        return;
      case 'open-totp-qr':
        state.totpQr = id === undefined ? null : { entryId: id };
        // Meme lecture que le rendu : une entree sensible non confirmee, ou sans graine, n'ouvre rien.
        if (totpQrValue() === null) state.totpQr = null;
        render();
        return;
      case 'close-totp-qr':
        // Le fond porte l'action pour fermer au clic exterieur : un clic DANS le panneau ne le referme pas.
        if (target.classList.contains('totp-qr-backdrop') && (event.target as HTMLElement).closest('.totp-qr-panel') !== null) return;
        state.totpQr = null;
        render();
        return;
      case 'close-wifi-qr':
        // Le fond porte l'action pour fermer au clic exterieur : un clic DANS le panneau ne le referme pas.
        if (target.classList.contains('wifi-qr-backdrop') && (event.target as HTMLElement).closest('.wifi-qr-panel') !== null) return;
        state.wifiQr = null;
        render();
        return;
      case 'credentials-transfer-accept':
        answerCredentialsTransfer(true);
        return;
      case 'credentials-transfer-refuse':
        answerCredentialsTransfer(false);
        return;
      case 'master-password-prompt-cancel':
        answerMasterPasswordPrompt(null);
        return;
      case 'totp-code-prompt-cancel':
        answerTotpCodePrompt(null);
        return;
      case 'totp-code-prompt-webauthn':
        answerTotpCodePrompt('webauthn');
        return;
      case 'account-totp-begin':
        await handleAccountTotpBegin();
        return;
      case 'account-totp-cancel':
        state.accountTotpEnrollment = null;
        state.accountTotpMessage = null;
        render();
        return;
      case 'account-totp-disable':
        await handleAccountTotpDisable();
        return;
      case 'dismiss-import-cleanup':
        state.importCleanupFile = null;
        state.importCleanupHandle = null;
        state.importCleanupResult = null;
        render();
        return;
      case 'remove-import-file': {
        // Au clic seulement : le navigateur demande le droit d'ecriture, rien n'est tente sans lui.
        const handle = state.importCleanupHandle;
        if (handle === null) return;
        state.importCleanupResult = await removeImportFile(handle);
        // Supprime ou deja parti : plus rien a supprimer ici. Refuse : le bouton reste, l'utilisateur peut reessayer.
        if (state.importCleanupResult !== 'refuse') state.importCleanupHandle = null;
        render();
        return;
      }
      case 'close-large-type':
        // Le fond porte l'action pour fermer au clic exterieur : un clic DANS le panneau ne le referme pas.
        if (target.classList.contains('large-type-backdrop') && (event.target as HTMLElement).closest('.large-type-panel') !== null) return;
        state.largeType = null;
        render();
        return;
      case 'toggle-reveal':
        if (state.entries.some((item) => item.id === id && repromptPending(item))) return;
        state.revealedEntryId = state.revealedEntryId === id ? null : (id ?? null);
        render();
        return;
      case 'toggle-notes-format':
        state.notesFormat = state.notesFormat === 'plain' ? 'markdown' : 'plain';
        // Cle retiree pour le defaut : seul un choix explicite du texte brut est garde.
        if (state.notesFormat === 'plain') localStorage.setItem(NOTES_FORMAT_KEY, 'plain');
        else localStorage.removeItem(NOTES_FORMAT_KEY);
        render();
        return;
      case 'toggle-notes-preview':
        // Le brouillon prend d'abord la saisie en cours : le re-rendu ne doit rien en perdre.
        syncDraftFromForm();
        state.notesPreview = !state.notesPreview;
        render();
        return;
      case 'toggle-history':
        state.historyOpenEntryId = state.historyOpenEntryId === id ? null : (id ?? null);
        render();
        return;
      case 'toggle-versions':
        state.versionsOpenEntryId = state.versionsOpenEntryId === id ? null : (id ?? null);
        state.versionShownIndex = null;
        state.versionCompare = false;
        render();
        return;
      case 'compare-version':
        // Reglage d'affichage seulement : le contenu n'entre dans la page que par une version depliee (`show-version`).
        state.versionCompare = !state.versionCompare;
        render();
        return;
      case 'show-version': {
        const index = Number(target.dataset.index);
        state.versionShownIndex = state.versionShownIndex === index || !Number.isSafeInteger(index) ? null : index;
        render();
        return;
      }
      case 'restore-version': {
        // Relue au coffre : la liste affichee peut dater d'avant une synchro. Une entree sensible non confirmee ne restaure rien.
        const shown = state.entries.find((item) => item.id === id);
        const entry = shown === undefined || repromptPending(shown) || state.vaultStore === null ? null : await state.vaultStore.getEntry(shown.id);
        const version = entry === null ? undefined : readItemHistory(entry.secrets)[Number(target.dataset.index)];
        if (entry !== null && version !== undefined && state.vaultStore !== null &&
          window.confirm(`Restaurer la version du ${formatTimestamp(version.replacedAt)} ? Le contenu actuel rejoindra les versions precedentes.`)) {
          await state.vaultStore.updateEntrySecrets(entry.id, restoreItemVersion(entry.secrets, version));
          // Les versions se decalent d'un rang : le contenu deplie designerait une autre version.
          state.versionShownIndex = null;
          await refreshVaultData();
          scheduleFlash('Version restauree');
          render();
        }
        return;
      }
      case 'clear-versions': {
        const entry = state.entries.find((item) => item.id === id);
        if (entry !== undefined && state.vaultStore !== null && window.confirm('Effacer definitivement les versions precedentes de cette entree ?')) {
          await state.vaultStore.updateEntrySecrets(entry.id, { ...entry.secrets, itemHistory: [] });
          state.versionShownIndex = null;
          await refreshVaultData();
          scheduleFlash('Versions effacees');
          render();
        }
        return;
      }
      case 'restore-password': {
        // Passe par l'editeur : rien n'est ecrit tant que l'utilisateur n'enregistre pas (le mot de passe courant rejoint alors l'historique).
        const entry = state.entries.find((item) => item.id === id);
        const old = entry === undefined || repromptPending(entry) ? undefined : readPasswordHistory(entry.secrets)[Number(target.dataset.index)];
        if (entry !== undefined && old !== undefined) {
          state.draft = { ...draftFromEntry(entry), password: old.password };
          state.revealDraftPassword = true;
          scheduleFlash('Ancien mot de passe repris : enregistrez pour le restaurer');
          render();
        }
        return;
      }
      case 'remove-passkey': {
        const entry = state.entries.find((item) => item.id === id);
        const passkeys = entry === undefined ? [] : readPasskeys(entry.secrets);
        const removed = passkeys[Number(target.dataset.index)];
        if (entry !== undefined && removed !== undefined && state.vaultStore !== null &&
          window.confirm(`Retirer la passkey « ${passkeyLabel(removed)} » de cette entree ? Sa cle privee sera definitivement effacee du coffre.`)) {
          await state.vaultStore.updateEntrySecrets(entry.id, withPasskeys(entry.secrets, passkeys.filter((item) => item !== removed)));
          await refreshVaultData();
          scheduleFlash('Passkey retiree');
          render();
        }
        return;
      }
      case 'clear-history': {
        const entry = state.entries.find((item) => item.id === id);
        // Les versions precedentes gardent les anciens mots de passe : effacer l'un sans l'autre laisserait ce qu'on croit efface.
        const versions = entry === undefined ? 0 : readItemHistory(entry.secrets).length;
        if (entry !== undefined && state.vaultStore !== null && window.confirm(versions > 0
          ? `Effacer definitivement les anciens mots de passe de cette entree, et ses ${versions} version(s) precedente(s) qui les contiennent ?`
          : 'Effacer definitivement les anciens mots de passe de cette entree ?')) {
          await state.vaultStore.updateEntrySecrets(entry.id, { ...entry.secrets, passwordHistory: [], itemHistory: [] });
          await refreshVaultData();
          scheduleFlash('Historique efface');
          render();
        }
        return;
      }
      case 'toggle-favorite':
        if (id !== undefined && state.vaultStore !== null) {
          const entry = state.entries.find((item) => item.id === id);
          if (entry !== undefined) {
            await state.vaultStore.setFavorite(id, !entry.favorite);
            await refreshVaultData();
            render();
          }
        }
        return;
      case 'start-link':
        state.linkPickerFor = id ?? null;
        render();
        root.querySelector<HTMLSelectElement>('[data-input="link-target"]')?.focus();
        return;
      case 'cancel-link':
        state.linkPickerFor = null;
        render();
        return;
      case 'link-entry': {
        const otherId = root.querySelector<HTMLSelectElement>('[data-input="link-target"]')?.value;
        if (id === undefined || otherId === undefined || otherId === '' || state.vaultStore === null) return;
        await state.vaultStore.linkEntries(id, otherId);
        state.linkPickerFor = null;
        await refreshVaultData();
        scheduleFlash('Elements lies -- chacun s’ouvre depuis la fiche de l’autre');
        render();
        return;
      }
      case 'unlink-entry': {
        const otherId = target.dataset.other;
        if (id === undefined || otherId === undefined || state.vaultStore === null) return;
        await state.vaultStore.unlinkEntries(id, otherId);
        await refreshVaultData();
        scheduleFlash('Lien retire -- les deux entrees restent dans le coffre');
        render();
        return;
      }
      case 'open-related': {
        if (id === undefined || state.vaultStore === null) return;
        const other = await state.vaultStore.getEntry(id);
        if (other === null || isTrashed(other.secrets)) return;
        // L'element lie peut etre range ailleurs (autre dossier, etiquette, archives) : la liste reprend la vue qui le montre.
        if (!state.entries.some((item) => item.id === id)) {
          exitBulk();
          state.panel = 'vault';
          state.selectedFolderId = null;
          state.selectedTag = null;
          state.favoritesOnly = false;
          state.trashOnly = false;
          state.archivedOnly = isArchived(other.secrets);
          state.searchQuery = '';
          await refreshVaultData();
        }
        await selectEntry(id);
        return;
      }
      case 'archive-entry':
      case 'unarchive-entry':
        if (id !== undefined && state.vaultStore !== null) {
          const archive = action === 'archive-entry';
          await state.vaultStore.setArchived(id, archive);
          // L'entree quitte la vue courante (Archives ou le reste du coffre) : la fiche se ferme avec elle.
          state.selectedEntryId = null;
          forgetAttachments();
          await refreshVaultData();
          scheduleFlash(archive ? 'Entree archivee -- retrouvez-la dans Archives' : 'Entree desarchivee -- de retour dans le coffre');
          render();
        }
        return;
      case 'exclude-health':
      case 'include-health':
        // Reversible, et l'entree reste listee a part dans la Sante du coffre : pas de confirmation.
        if (id !== undefined && state.vaultStore !== null) {
          const exclude = action === 'exclude-health';
          await state.vaultStore.setHealthExcluded(id, exclude);
          await refreshVaultData();
          scheduleFlash(exclude ? 'Entree exclue de la Sante du coffre -- listee a part, reinclusion en un clic' : 'Entree de nouveau surveillee par la Sante du coffre');
          render();
        }
        return;
      case 'ignore-health-alert':
      case 'unignore-health-alert': {
        // Reversible, et l'alerte reste listee a part dans la Sante du coffre : pas de confirmation.
        const alert = target.dataset.alert;
        if (id !== undefined && isIgnorableHealthAlert(alert) && state.vaultStore !== null) {
          const ignore = action === 'ignore-health-alert';
          await state.vaultStore.setHealthAlertIgnored(id, alert, ignore);
          await refreshVaultData();
          scheduleFlash(
            ignore
              ? `Alerte « ${HEALTH_ALERT_LABELS[alert]} » ignoree pour cette entree -- listee a part, retablie en un clic`
              : `Alerte « ${HEALTH_ALERT_LABELS[alert]} » de nouveau signalee pour cette entree`,
          );
          render();
        }
        return;
      }
      case 'passkey-elsewhere':
      case 'passkey-suggest':
        // Reversible depuis la fiche de l'entree : pas de confirmation.
        if (id !== undefined && state.vaultStore !== null) {
          const elsewhere = action === 'passkey-elsewhere';
          await state.vaultStore.setPasskeyElsewhere(id, elsewhere);
          await refreshVaultData();
          scheduleFlash(elsewhere ? 'Passkey notee comme rangee ailleurs -- annulable depuis la fiche de l’entree' : 'La Sante du coffre propose de nouveau une passkey pour cette entree');
          render();
        }
        return;
      case 'rename-site-address': {
        // Depuis la Sante du coffre : les adresses sont relues au coffre et reecrites par la table, jamais d'apres le DOM --
        // un bouton forge ne change pas l'adresse d'une entree que la Sante ne signale pas.
        if (id === undefined || state.vaultStore === null) return;
        const entry = await state.vaultStore.getEntry(id);
        if (entry === null) return;
        const uris = entry.secrets.uris ?? [];
        const renamed = withRenamedSiteAddresses(uris);
        if (renamed.length === uris.length && renamed.every((uri, index) => uri === uris[index])) return;
        // Entree sensible : jamais modifiee sans le mot de passe maitre -- sa fiche s'ouvre sur la re-demande.
        if (repromptPending(entry)) {
          await leaveHealthFor(entry);
          if (state.selectedEntryId !== entry.id) state.repromptConfirmedId = null;
          state.panel = 'vault';
          state.selectedEntryId = entry.id;
          state.draft = null;
          render();
          return;
        }
        // Une version de l'entree garde l'ancienne adresse (`withItemHistory`) : pas de confirmation.
        await state.vaultStore.updateEntrySecrets(id, { ...entry.secrets, uris: renamed });
        await refreshVaultData();
        scheduleFlash(`Adresse de « ${entry.secrets.name ?? 'Sans nom'} » mise a jour -- l’ancienne reste dans les versions de l’entree`);
        render();
        return;
      }
      case 'trash-duplicate': {
        // Depuis la Sante du coffre : la copie part a la corbeille (restaurable) et la vue reste sur les doublons restants.
        const entry = state.entries.find((item) => item.id === id);
        if (entry === undefined || state.vaultStore === null) return;
        // Entree sensible : jamais jetee sans le mot de passe maitre -- sa fiche s'ouvre sur la re-demande.
        if (repromptPending(entry)) {
          await leaveHealthFor(entry);
          if (state.selectedEntryId !== entry.id) state.repromptConfirmedId = null;
          state.panel = 'vault';
          state.selectedEntryId = entry.id;
          state.draft = null;
          render();
          return;
        }
        await state.vaultStore.trashEntry(entry.id);
        await refreshVaultData();
        scheduleFlash(`Copie placee dans la corbeille -- restaurable pendant ${TRASH_RETENTION_DAYS} jours`);
        render();
        return;
      }
      case 'merge-duplicates': {
        // Depuis la Sante du coffre : le groupe est relu dans le rapport courant, jamais dans le DOM -- un bouton forge ne
        // fusionne pas deux entrees que la Sante ne rapproche pas.
        if (id === undefined || state.vaultStore === null) return;
        const group = vaultHealthReport().duplicateGroups.find((candidate) =>
          candidate.itemIds.includes(id),
        );
        if (group === undefined) return;
        const copies = group.itemIds.map((itemId) => state.entries.find((item) => item.id === itemId)).filter((entry): entry is VaultItem => entry !== undefined);
        // Une copie sensible : jamais fusionnee (donc jetee) sans le mot de passe maitre -- sa fiche s'ouvre sur la re-demande.
        const guarded = copies.find((entry) => repromptPending(entry));
        if (guarded !== undefined) {
          await leaveHealthFor(guarded);
          if (state.selectedEntryId !== guarded.id) state.repromptConfirmedId = null;
          state.panel = 'vault';
          state.selectedEntryId = guarded.id;
          state.draft = null;
          render();
          return;
        }
        const others = copies.filter((entry) => entry.id !== id).map((entry) => entry.id);
        try {
          await state.vaultStore.mergeEntries(id, others);
        } catch (error) {
          await refreshVaultData();
          scheduleFlash(error instanceof Error && error.message.includes('champs') ? 'Fusion impossible : trop de champs personnalises reunis' : 'Fusion impossible');
          render();
          return;
        }
        await refreshVaultData();
        scheduleFlash(`${others.length} copie(s) fusionnee(s) puis placee(s) dans la corbeille -- restaurables pendant ${TRASH_RETENTION_DAYS} jours`);
        render();
        return;
      }
      case 'delete-entry':
        // Reversible : l'entree part a la corbeille, d'ou elle se restaure -- pas de confirmation a valider a l'aveugle.
        if (id !== undefined && state.vaultStore !== null) {
          await state.vaultStore.trashEntry(id);
          state.selectedEntryId = null;
          forgetAttachments();
          await refreshVaultData();
          scheduleFlash(`Entree placee dans la corbeille -- restaurable pendant ${TRASH_RETENTION_DAYS} jours`);
          render();
        }
        return;
      case 'restore-entry':
        if (id !== undefined && state.vaultStore !== null) {
          await state.vaultStore.restoreEntry(id);
          state.selectedEntryId = null;
          forgetAttachments();
          await refreshVaultData();
          scheduleFlash('Entree restauree -- de retour a sa place dans le coffre');
          render();
        }
        return;
      case 'purge-entry':
        // Irreversible, lui : la confirmation est la derniere chance.
        if (id !== undefined && state.vaultStore !== null && window.confirm('Supprimer definitivement cette entree ? Ce geste est irreversible.')) {
          await state.vaultStore.deleteEntry(id);
          state.selectedEntryId = null;
          forgetAttachments();
          await refreshVaultData();
          scheduleFlash('Entree supprimee definitivement');
          render();
        }
        return;
      case 'empty-trash':
        if (state.vaultStore !== null && window.confirm('Vider la corbeille ? Les entrees seront supprimees definitivement.')) {
          const count = await state.vaultStore.emptyTrash();
          state.selectedEntryId = null;
          forgetAttachments();
          await refreshVaultData();
          scheduleFlash(`${count} entree(s) supprimee(s) definitivement`);
          render();
        }
        return;
      case 'copy': {
        const value = target.dataset.value ?? '';
        await copyToClipboard(value);
        return;
      }
      case 'copy-generated': {
        // Lu dans l'etat au moment du clic : le secret copie est exactement celui qui entre dans l'historique.
        const username = state.generatorMode === 'username';
        const value = username ? state.generatedUsername : state.generatedPassword;
        if (value.length === 0) return;
        rememberGenerated(value, username ? 'username' : state.generatorMode === 'passphrase' ? 'passphrase' : 'password');
        await copyToClipboard(value);
        return;
      }
      case 'copy-generator-history': {
        const item = state.generatorHistory[Number(target.dataset.index)];
        if (item !== undefined) await copyToClipboard(item.value);
        return;
      }
      case 'remove-generator-history':
        state.generatorHistory = removeGenerated(state.generatorHistory, Number(target.dataset.index));
        persistGeneratorHistory();
        render();
        return;
      case 'clear-generator-history':
        if (!window.confirm('Effacer tout l’historique du generateur sur cet appareil ? Les entrees du coffre ne changent pas.')) return;
        state.generatorHistory = [];
        state.generatorHistoryRevealed = false;
        persistGeneratorHistory();
        render();
        return;
      case 'toggle-generator-history':
        state.generatorHistoryOpen = !state.generatorHistoryOpen;
        // Replier remasque : rouvrir ne retrouve pas les secrets affiches.
        if (!state.generatorHistoryOpen) state.generatorHistoryRevealed = false;
        render();
        return;
      case 'toggle-generator-history-reveal':
        state.generatorHistoryRevealed = !state.generatorHistoryRevealed;
        render();
        return;
      case 'copy-entry-field':
        if (id !== undefined) await copyEntryField(id, target.dataset.field ?? '');
        return;
      case 'autofill-entry': {
        // Lue dans l'entree au moment du clic, et la correspondance reverifiee la (fillFromEntry) : le bouton n'est
        // qu'une proposition, c'est ce chemin qui decide de remettre l'identifiant a l'OS.
        const entry = state.entries.find((item) => item.id === id);
        const bridge = (globalThis as { KlavoxAndroid?: AutofillBridge }).KlavoxAndroid;
        if (entry === undefined || repromptPending(entry) || bridge === undefined) return;
        if (fillFromEntry(bridge, entry.secrets, state.defaultUriMatch)) state.autofillTarget = null;
        return;
      }
      case 'copy-emergency-password': {
        // Coffre du titulaire en lecture seule : ses mots de passe ne sont jamais affiches, ni poses dans la page.
        const item = state.emergencyContactVault?.items[Number(target.dataset.index)];
        const value = item === undefined ? null : entryFieldValue(item.secrets, 'password');
        if (value !== null) await copyToClipboard(value);
        return;
      }
      case 'copy-ssh-private': {
        // Lue dans l'entree au moment du clic : la cle privee n'est jamais posee dans un attribut du DOM.
        const entry = state.entries.find((item) => item.id === id);
        if (entry === undefined || repromptPending(entry)) return;
        const { privateKey } = readSshKey(entry.secrets);
        if (privateKey.length > 0) await copyToClipboard(privateKey);
        return;
      }
      case 'copy-wallet-secret': {
        // Lue dans l'entree au moment du clic : ni la phrase de recuperation ni sa phrase de passe ne vont dans le DOM.
        const entry = state.entries.find((item) => item.id === id);
        const key = target.dataset.key;
        if (entry === undefined || repromptPending(entry) || (key !== 'recoveryPhrase' && key !== 'seedPassphrase')) return;
        const value = readTypedFields(entry.secrets, 'wallet')[key];
        if (value !== undefined) await copyToClipboard(value);
        return;
      }
      case 'regenerate':
        if (state.generatorMode === 'username') {
          await regenerateUsername();
          return;
        }
        state.generatedPassword = generateForGenerator();
        render();
        return;
      case 'generator-mode': {
        const mode = target.dataset.mode;
        state.generatorMode = readGeneratorMode(mode);
        localStorage.setItem(GENERATOR_MODE_KEY, state.generatorMode);
        if (state.generatorMode === 'username') {
          await regenerateUsername();
          return;
        }
        if (state.generatorMode === 'alias') {
          state.aliasError = null;
          render();
          return;
        }
        if (state.generatorMode === 'passphrase') await ensureWordlist();
        state.generatedPassword = generateForGenerator();
        render();
        return;
      }
      case 'delete-alias':
        await handleAliasDelete();
        return;
      case 'export-vault': {
        if (state.envelope === null) return;
        const items = await state.repository.listItems();
        const folders = await state.repository.listFolders();
        const attachments = await state.repository.listAttachments();
        const file = buildVaultExport({ account: state.envelope, items, folders, attachments });
        downloadJson(`klavox-export-${new Date().toISOString().slice(0, 10)}.json`, serializeVaultExport(file));
        scheduleFlash('Export telecharge');
        return;
      }
      case 'cancel-import': {
        // Les octets de l'export quittent la memoire des que l'utilisateur renonce ; une derivation en cours s'arrete.
        state.importPrompt = null;
        if (importRun !== null) {
          importRun.abort();
          importRun = null;
          state.importBusy = false;
          state.importProgress = null;
          scheduleFlash("import annule : rien n'a ete verse au coffre");
        }
        render();
        return;
      }
      case 'enable-recovery-key': {
        if (state.vaultStore === null || state.envelope === null) return;
        const enabled = state.vaultStore.createRecoveryKey();
        const nextEnvelope: AccountEnvelope = { ...state.envelope, recoveryProtectedSymmetricKey: enabled.recoveryProtectedSymmetricKey };
        await state.repository.saveAccountEnvelope(nextEnvelope);
        state.envelope = nextEnvelope;
        state.localAccountEnvelope = nextEnvelope;
        state.recoveryKeyReveal = enabled.recoveryKeyDisplay;
        // La cle ne s'affiche qu'une fois, dans les Parametres : depuis le bandeau du coffre, on y va.
        state.panel = 'settings';
        enabled.recoveryKey.destroy();
        if (state.remoteSession !== null) {
          try {
            const live = await liveRemoteSession();
            if (live === null) throw new Error('session serveur fermee pendant le renouvellement');
            await enableServerRecoveryKey(
              live.baseUrl,
              live.sessionToken,
              enabled.recoveryProtectedSymmetricKey,
              enabled.recoveryAuthProofBase64,
            );
            // Le verificateur est desormais depose : la cle neuve ouvre la reinitialisation, le bandeau n'a plus lieu.
            state.recoveryKeyStale = false;
          } catch {
            scheduleFlash('Cle de recuperation activee localement (echec de synchro serveur)');
          }
        }
        render();
        return;
      }
      case 'confirm-recovery-key':
        state.recoveryKeyReveal = null;
        scheduleFlash('Cle de recuperation enregistree');
        render();
        return;
      case 'print-recovery-key':
      case 'print-emergency-kit':
        window.print();
        return;
      case 'open-emergency-contact':
        state.authMode = 'emergency-contact';
        state.authError = null;
        state.emergencyContactState = null;
        state.emergencyContactVault = null;
        state.emergencyContactDraft = { baseUrl: '', kit: '' };
        render();
        return;
      case 'cancel-emergency-contact':
        state.authMode = state.localAccountEnvelope === null ? 'create' : 'unlock';
        state.authError = null;
        state.emergencyContactState = null;
        state.emergencyContactVault = null;
        state.emergencyContactDraft = { baseUrl: '', kit: '' };
        render();
        return;
      case 'open-emergency-vault': {
        // Bouton hors soumission : « ouvrir » n'est pas « demander », et une
        // touche Entree dans le champ ne doit jamais declencher le retrait.
        const form = root.querySelector<HTMLFormElement>('form[data-form="emergency-contact"]');
        if (form !== null) void handleEmergencyContactSubmit(form, 'open');
        return;
      }
      case 'confirm-emergency-kit':
        state.emergencyKitReveal = null;
        render();
        return;
      case 'refuse-emergency':
        void handleEmergencyOwnerAction(target.dataset.id ?? '', 'refuse');
        return;
      case 'revoke-emergency':
        void handleEmergencyOwnerAction(target.dataset.id ?? '', 'revoke');
        return;
      default:
        return;
    }
  }

  // --- Envoi ponctuel : actions (R15) -----------------------------------

  /** Recopie le brouillon depuis le DOM avant un re-rendu, pour ne rien perdre de la saisie. */
  function syncSendDraftFromForm(): void {
    const form = root.querySelector<HTMLFormElement>('form[data-form="send"]');
    if (form === null) return;
    const data = new FormData(form);
    state.sendDraft = {
      ...state.sendDraft,
      label: String(data.get('label') ?? state.sendDraft.label),
      text: String(data.get('text') ?? state.sendDraft.text),
      // Case absente (envoi de fichier) : le choix fait en mode texte est garde.
      hidden: form.querySelector('input[name="hidden"]') !== null ? data.get('hidden') !== null : state.sendDraft.hidden,
      note: String(data.get('note') ?? state.sendDraft.note),
      ...readSendLimits(data),
    };
    if (state.sendShare !== null) state.sendShare = { ...state.sendShare, excluded: excludedShareRefs(data) };
  }

  /** Echeance et telechargements tels qu'affiches : basculer texte / fichier ou la liste ne les remet pas a zero. */
  function readSendLimits(data: FormData): Pick<SendDraft, 'expiresInMs' | 'expiresAtLocal' | 'maxViews'> {
    const expiresIn = String(data.get('expiresIn') ?? '');
    const custom = expiresIn === 'custom';
    return {
      expiresInMs: custom || expiresIn === '' ? state.sendDraft.expiresInMs : Number(expiresIn),
      // Premier passage en « Date personnalisee » : la date proposee est l'echeance de la duree choisie jusque-la.
      expiresAtLocal: custom ? String(data.get('expiresAt') ?? state.sendDraft.expiresAtLocal ?? toLocalDateTimeInput(Date.now() + state.sendDraft.expiresInMs)) : null,
      maxViews: String(data.get('maxViews') ?? state.sendDraft.maxViews),
    };
  }

  /** References decochees de la fiche partagee, relues du formulaire (seules celles de la fiche comptent). */
  function excludedShareRefs(data: FormData): string[] {
    const chosen = new Set(data.getAll('share-field').map(String));
    return (state.sendShare?.fields ?? []).filter((field) => !chosen.has(field.ref)).map((field) => field.ref);
  }

  async function refreshSends(): Promise<void> {
    if (state.remoteSession === null || state.vaultStore === null) return;
    try {
      const live = await liveRemoteSession();
      if (live === null) throw new Error('session serveur fermee pendant le renouvellement');
      state.sends = [...(await listVaultSends(live.baseUrl, live.sessionToken, state.vaultStore))];
    } catch (cause) {
      state.sends = [];
      state.sendMessage = cause instanceof KlavoxApiError ? cause.message : 'envois indisponibles';
    }
    render();
  }

  async function handleSendSubmit(form: HTMLFormElement): Promise<void> {
    if (state.remoteSession === null || state.vaultStore === null || state.sendBusy) return;
    const data = new FormData(form);
    const label = String(data.get('label') ?? '').trim();
    const kind = state.sendDraft.kind;
    const text = String(data.get('text') ?? '');
    const hidden = data.get('hidden') !== null;
    const note = String(data.get('note') ?? '');
    const password = String(data.get('password') ?? '');
    state.sendDraft = { label, kind, text, hidden, note, ...readSendLimits(data) };
    // Echeance et plafond relus AVANT tout chiffrement : une saisie refusee le dit, jamais remplacee par un defaut.
    let expiresInMs: number;
    let maxViews: number | null;
    try {
      expiresInMs =
        state.sendDraft.expiresAtLocal === null ? state.sendDraft.expiresInMs : resolveCustomSendExpiry(state.sendDraft.expiresAtLocal, Date.now());
      maxViews = parseSendMaxViews(state.sendDraft.maxViews);
    } catch (cause) {
      state.sendMessage = cause instanceof Error ? cause.message : 'envoi refuse';
      render();
      return;
    }

    let payload: SendPayload;
    if (state.sendShare !== null) {
      const share = state.sendShare;
      const chosen = new Set(data.getAll('share-field').map(String));
      state.sendShare = { ...share, excluded: excludedShareRefs(data) };
      // L'entree est relue au coffre, pas dans la liste affichee : meme garde que la fiche, rien pour une entree
      // sensible non confirmee ou partie a la corbeille entre-temps.
      const entry = await state.vaultStore.getEntry(share.entryId);
      if (entry === null || repromptPending(entry) || isTrashed(entry.secrets)) {
        state.sendMessage = 'fiche indisponible : rouvrez-la pour la partager';
        render();
        return;
      }
      const built = buildEntrySharePayload(entry.secrets, label, chosen);
      if (built === null) {
        state.sendMessage = 'cochez au moins un champ a partager';
        render();
        return;
      }
      payload = built;
    } else if (kind === 'file') {
      if (state.sendFile === null) {
        state.sendMessage = 'choisissez un fichier a envoyer';
        render();
        return;
      }
      payload = {
        kind: 'file',
        label,
        fileName: state.sendFile.name,
        mimeType: state.sendFile.mimeType,
        dataBase64: bytesToBase64(state.sendFile.bytes),
      };
    } else {
      if (text.length === 0) {
        state.sendMessage = 'le contenu est vide';
        render();
        return;
      }
      payload = { kind: 'text', label, text, ...(hidden ? { hidden: true } : {}) };
    }

    state.sendBusy = true;
    state.sendMessage = null;
    render();
    try {
      const live = await liveRemoteSession();
      if (live === null) throw new Error('session serveur fermee pendant le renouvellement');
      const created = await createVaultSend({
        baseUrl: live.baseUrl,
        sessionToken: live.sessionToken,
        vault: state.vaultStore,
        label,
        payload,
        note,
        expiresInMs,
        maxViews,
        ...(password.length > 0 ? { password } : {}),
      });
      state.sendCreated = created;
      // Le brouillon repart a zero : le contenu est parti, le garder a l'ecran
      // en laisserait une copie en clair dans la page.
      state.sendDraft = { ...EMPTY_SEND_DRAFT };
      state.sendFile = null;
      state.sendShare = null;
      state.sendMessage = null;
      await refreshSends();
    } catch (cause) {
      state.sendMessage = cause instanceof KlavoxApiError ? cause.message : 'envoi refuse';
    } finally {
      state.sendBusy = false;
      render();
    }
  }

  function handleSendFile(file: File): void {
    if (file.size === 0 || file.size > MAX_SEND_FILE_BYTES) {
      state.sendMessage = `fichier refuse (${formatFileSize(MAX_SEND_FILE_BYTES)} maximum)`;
      render();
      return;
    }
    void file.arrayBuffer().then((buffer) => {
      state.sendFile = { name: file.name, mimeType: file.type, bytes: new Uint8Array(buffer) };
      state.sendMessage = null;
      render();
    });
  }

  async function handleSendEditSubmit(form: HTMLFormElement): Promise<void> {
    if (state.remoteSession === null || state.vaultStore === null || state.sendBusy) return;
    const send = state.sends?.find((item) => item.id === form.dataset.id && item.revokedAt === null);
    if (send === undefined) return;
    const data = new FormData(form);
    let expiresInMs: number;
    let maxViews: number | null;
    try {
      // Memes gardes qu'a la creation : une date passee ou un « 0 » est refuse, jamais rabote ni lu « sans limite ».
      expiresInMs = resolveCustomSendExpiry(String(data.get('expiresAt') ?? ''), Date.now());
      maxViews = parseSendMaxViews(String(data.get('maxViews') ?? ''));
    } catch (cause) {
      state.sendMessage = cause instanceof Error ? cause.message : 'modification refusee';
      render();
      return;
    }
    state.sendBusy = true;
    render();
    try {
      const live = await liveRemoteSession();
      if (live === null) throw new Error('session serveur fermee pendant le renouvellement');
      await updateVaultSend({
        baseUrl: live.baseUrl,
        sessionToken: live.sessionToken,
        vault: state.vaultStore,
        send,
        expiresInMs,
        maxViews,
        note: String(data.get('note') ?? ''),
      });
      state.sendEditingId = null;
      state.sendMessage = 'Envoi modifie. Le lien reste le meme.';
    } catch (cause) {
      state.sendMessage = cause instanceof Error ? cause.message : 'modification impossible';
    } finally {
      state.sendBusy = false;
    }
    await refreshSends();
  }

  async function handleRevokeSend(id: string): Promise<void> {
    if (state.remoteSession === null) return;
    if (!window.confirm('Revoquer cet envoi ? Le lien cessera immediatement de servir.')) return;
    try {
      const live = await liveRemoteSession();
      if (live === null) throw new Error('session serveur fermee pendant le renouvellement');
      await revokeVaultSend(live.baseUrl, live.sessionToken, id);
      state.sendMessage = 'Envoi revoque.';
    } catch (cause) {
      state.sendMessage = cause instanceof KlavoxApiError ? cause.message : 'revocation impossible';
    }
    await refreshSends();
  }

  // --- Envoi ponctuel : cote destinataire -------------------------------

  /** L'envoi est servi par l'origine qui sert cette page : meme deploiement Klavox. */
  function receiveBaseUrl(): string {
    return window.location.origin;
  }

  async function startSendReceive(id: string, secret: string): Promise<void> {
    state.sendReceive = {
      id,
      secret,
      phase: 'loading',
      description: null,
      content: null,
      payload: null,
      revealed: false,
      error: null,
      busy: false,
    };
    render();
    try {
      const description = await describeVaultSend(receiveBaseUrl(), id);
      state.sendReceive = {
        ...state.sendReceive,
        description,
        // Demander le mot de passe AVANT de telecharger : une saisie ratee ne
        // doit pas couter l'unique telechargement disponible.
        phase: description.passwordProtected ? 'locked' : 'ready',
      };
    } catch (cause) {
      state.sendReceive = {
        ...state.sendReceive,
        phase: 'error',
        error: cause instanceof KlavoxApiError ? cause.message : 'lien invalide ou envoi expire',
      };
    }
    render();
  }

  async function openReceivedSend(password?: string): Promise<void> {
    const receive = state.sendReceive;
    if (receive === null || receive.busy) return;
    receive.busy = true;
    receive.error = null;
    render();
    try {
      // Telecharge UNE fois : reessayer un mot de passe se fait hors ligne.
      const content = receive.content ?? (await fetchVaultSend(receiveBaseUrl(), receive.id));
      receive.content = content;
      receive.payload = await openVaultSend(content, receive.secret, password);
      receive.phase = 'opened';
    } catch (cause) {
      if (cause instanceof KlavoxApiError) {
        receive.phase = 'error';
        receive.error = cause.message;
      } else if (cause instanceof SendPasswordRequiredError) {
        receive.phase = 'locked';
        receive.error = cause.message;
      } else {
        // Mauvais mot de passe, lien tronque ou contenu altere : indistinguables.
        receive.phase = receive.content?.kdfParams != null ? 'locked' : 'error';
        receive.error = 'impossible d’ouvrir cet envoi : lien ou mot de passe incorrect';
      }
    } finally {
      receive.busy = false;
      render();
    }
  }

  function downloadReceivedFile(): void {
    const payload = state.sendReceive?.payload;
    if (payload === undefined || payload === null || typeof payload.dataBase64 !== 'string') return;
    // octet-stream : toujours un telechargement, jamais un rendu dans l'origine du coffre.
    downloadBlob(
      payload.fileName ?? 'envoi',
      new Blob([new Uint8Array(base64ToBytes(payload.dataBase64))], { type: 'application/octet-stream' }),
    );
  }

  function downloadJson(filename: string, content: string): void {
    downloadBlob(filename, new Blob([content], { type: 'application/json' }));
  }

  function downloadBlob(filename: string, blob: Blob): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    // Laisse le navigateur prendre le fichier en charge, puis libere la copie en memoire.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function handleAttachFiles(entryId: string, files: readonly File[]): Promise<void> {
    if (state.vaultStore === null || files.length === 0 || state.attachmentBusyEntryId !== null) return;
    const vaultStore = state.vaultStore;
    state.attachmentBusyEntryId = entryId;
    render();
    let added = 0;
    const refused: string[] = [];
    for (const file of files) {
      try {
        assertAttachmentSize(file.size);
        await vaultStore.addAttachment(entryId, { fileName: file.name, mimeType: file.type, bytes: new Uint8Array(await file.arrayBuffer()) });
        added += 1;
      } catch (cause) {
        refused.push(`${file.name} : ${cause instanceof AttachmentLimitError ? cause.message : 'ajout impossible'}`);
      }
    }
    if (state.vaultStore !== vaultStore) return;
    state.attachmentBusyEntryId = null;
    forgetAttachments();
    scheduleFlash(
      refused.length > 0
        ? `${added} piece(s) jointe(s) ajoutee(s) -- refuse(s) : ${refused.join(' ; ')}`
        : `${added} piece(s) jointe(s) chiffree(s) et ajoutee(s)`,
    );
    render();
  }

  /**
   * Export FIDO CXF (R8) : la charge CXF est construite a partir du coffre
   * DECHIFFRE puis immediatement scellee sous le mot de passe d'export -- elle
   * n'existe en clair qu'en memoire, le temps du chiffrement. Le mot de passe
   * d'export n'est ni conserve ni journalise, et n'a rien a voir avec le mot
   * de passe maitre.
   */
  async function handleCxfExportSubmit(form: HTMLFormElement): Promise<void> {
    if (state.vaultStore === null || state.envelope === null || state.cxfBusy) return;
    const data = new FormData(form);
    const password = String(data.get('password') ?? '');
    if (password !== String(data.get('confirmPassword') ?? '')) {
      state.cxfMessage = 'Les deux mots de passe saisis different : l’export n’a pas ete produit.';
      render();
      return;
    }
    try {
      // Avant de dechiffrer le coffre : un mot de passe faible n'est accepte qu'avec la case cochee.
      assertExportPasswordAccepted(password, state.envelope.email, data.get('acceptWeakExport') === 'on');
    } catch (cause) {
      state.cxfMessage = `${cause instanceof Error ? cause.message : 'mot de passe d’export refuse'} -- l’export n’a pas ete produit.`;
      render();
      return;
    }
    state.cxfBusy = true;
    state.cxfMessage = null;
    render();
    try {
      // Le coffre ENTIER : une entree archivee est exportee comme les autres.
      const items = await state.vaultStore.listEntries({ archived: 'include' });
      const folders = await state.vaultStore.listFolders();
      const header = buildCxfHeader({ email: state.envelope.email, items, folders });
      const file = await encryptCxfExport(header, password);
      downloadJson(`klavox-cxf-${new Date().toISOString().slice(0, 10)}.json`, serializeCxfExport(file));
      state.cxfMessage = `${items.length} entree(s) exportees au format FIDO CXF, sous enveloppe chiffree.`;
    } catch (cause) {
      state.cxfMessage = cause instanceof Error ? cause.message : 'export CXF impossible';
    }
    state.cxfBusy = false;
    render();
  }

  /**
   * Un tour de synchro WebDAV. Le mot de passe est lu du formulaire et laisse
   * la : il ne passe ni par `state`, ni par `localStorage`, ni par un message.
   */
  async function handleWebdavSubmit(form: HTMLFormElement): Promise<void> {
    const vaultStore = state.vaultStore;
    if (vaultStore === null || state.envelope === null) return;
    const data = new FormData(form);
    const password = String(data.get('password') ?? '');
    const username = String(data.get('username') ?? '').trim();
    let folderUrl: string;
    try {
      folderUrl = normalizeWebdavFolderUrl(String(data.get('folderUrl') ?? ''));
    } catch (cause) {
      state.webdavMessage = cause instanceof Error ? cause.message : 'URL WebDAV invalide';
      render();
      return;
    }
    if (username === '' || password === '') {
      state.webdavMessage = 'Identifiant et mot de passe d’application requis.';
      render();
      return;
    }
    state.webdavFolderUrl = folderUrl;
    state.webdavUsername = username;
    localStorage.setItem(WEBDAV_FOLDER_KEY, folderUrl);
    localStorage.setItem(WEBDAV_USERNAME_KEY, username);
    state.webdavBusy = true;
    state.webdavMessage = null;
    render();
    try {
      const result = await syncVaultWithWebdav({
        config: { folderUrl, username, password },
        account: state.envelope,
        local: {
          items: await state.repository.listItems(),
          folders: await state.repository.listFolders(),
          attachments: await state.repository.listAttachments(),
        },
        // Le WebDAV n'est pas plus fiable que le serveur souverain.
        authenticate: (remote) => vaultStore.authenticateExternal(remote).rejected,
      });
      await state.repository.putItems(result.merged.items);
      await state.repository.putFolders(result.merged.folders);
      await state.repository.putAttachments(result.merged.attachments);
      forgetAttachments();
      await refreshVaultData();
      state.webdavMessage = result.hadRemote
        ? `Synchro terminee (${result.conflicts} conflit(s) resolu(s)).`
        : 'Coffre chiffre depose sur votre stockage.';
    } catch (cause) {
      state.webdavMessage =
        cause instanceof WebdavSyncError ? cause.message : 'Synchro WebDAV impossible';
    }
    state.webdavBusy = false;
    render();
  }

  async function handleImportVaultFile(file: File): Promise<void> {
    if (state.vaultStore === null) return;
    const text = await file.text();
    try {
      const parsed = parseVaultExport(text);
      if (state.vaultStore.authenticateExternal(parsed).rejected > 0) {
        throw new Error('fichier d’export altere ou issu d’un autre coffre : import refuse');
      }
      const localItems = await state.repository.listItems();
      const localFolders = await state.repository.listFolders();
      const localAttachments = await state.repository.listAttachments();
      const merged = mergeVaultExport({ items: localItems, folders: localFolders, attachments: localAttachments }, parsed);
      await state.repository.putItems(merged.items);
      await state.repository.putFolders(merged.folders);
      await state.repository.putAttachments(merged.attachments);
      forgetAttachments();
      await refreshVaultData();
      scheduleFlash(`Import termine (${merged.itemConflicts + merged.folderConflicts} conflits resolus)`);
    } catch (cause) {
      scheduleFlash(cause instanceof Error ? cause.message : 'import invalide');
    }
    render();
  }

  /**
   * Recree l'arborescence de dossiers d'un export (groupes KeePass, dossiers
   * Bitwarden, coffres Proton Pass). Les dossiers deja presents sont reutilises
   * -- reimporter ne duplique pas l'arborescence.
   */
  async function resolveImportFolder(path: readonly string[], known: Map<string, string>): Promise<string | null> {
    if (state.vaultStore === null) return null;
    let parentId: string | null = null;
    const walked: string[] = [];
    for (const name of path) {
      walked.push(name.toLowerCase());
      const key = JSON.stringify(walked);
      const existing = known.get(key);
      if (existing !== undefined) {
        parentId = existing;
        continue;
      }
      const folder = await state.vaultStore.createFolder(name, parentId);
      known.set(key, folder.id);
      parentId = folder.id;
    }
    return parentId;
  }

  /** Index `chemin -> id` des dossiers existants, pour ne pas recreer ce qui est deja la. */
  function existingFolderIndex(): Map<string, string> {
    const byId = new Map(state.folders.map((folder) => [folder.id, folder]));
    const index = new Map<string, string>();
    for (const folder of state.folders) {
      const parts: string[] = [];
      let current: VaultFolder | undefined = folder;
      // Garde-fou : un parent manquant ou circulaire ne doit pas boucler.
      for (let depth = 0; current !== undefined && depth < 16; depth++) {
        parts.unshift(current.secrets.name.toLowerCase());
        current = current.parentId === null ? undefined : byId.get(current.parentId);
      }
      index.set(JSON.stringify(parts), folder.id);
    }
    return index;
  }

  async function importParsedEntries(
    entries: readonly ImportedEntry[],
    declaredFolders: readonly (readonly string[])[] = [],
  ): Promise<{ imported: number; linked: number; attachments: number; refused: number; related: number; missingRelated: number; unresolved: number }> {
    if (state.vaultStore === null) return { imported: 0, linked: 0, attachments: 0, refused: 0, related: 0, missingRelated: 0, unresolved: 0 };
    const folders = existingFolderIndex();
    // Dossiers declares par l'export (vides compris), dans son ordre ; un dossier deja au coffre est repris, jamais double.
    for (const path of declaredFolders) await resolveImportFolder(path, folders);
    let attachments = 0;
    let refused = 0;
    // Graine 2FA seule d'un compte deja au coffre : posee sur sa fiche (`seed-attach.ts`), une fois par fiche.
    const linkedIds = new Set<string>();
    // Entree du coffre qui a recu chaque element de l'export : les elements lies (`reference` 1Password) s'y resolvent.
    const receivedIds: string[] = [];
    for (const entry of entries) {
      const target = seedAttachTarget(entry, state.entries.filter((item) => !linkedIds.has(item.id)));
      const merged = target === null ? null : mergeDuplicateSecrets(target.secrets, [importedEntryToSecrets(entry)]);
      if (target !== null && merged !== null) {
        await state.vaultStore.updateEntrySecrets(target.id, merged);
        // Favori de l'authentificateur (Aegis, epingle d'Ente) repris comme a la fusion : jamais retire a la fiche.
        if (entry.favorite && !target.favorite) await state.vaultStore.setFavorite(target.id, true);
        linkedIds.add(target.id);
        receivedIds.push(target.id);
        continue;
      }
      const folderId = entry.folderPath === undefined ? null : await resolveImportFolder(entry.folderPath, folders);
      const created = await state.vaultStore.createEntry({
        type: entry.type,
        favorite: entry.favorite,
        folderId,
        secrets: importedEntryToSecrets(entry),
      });
      receivedIds.push(created.id);
      for (const attachment of entry.attachments ?? []) {
        try {
          await state.vaultStore.addAttachment(created.id, { fileName: attachment.fileName, bytes: attachment.bytes });
          attachments += 1;
        } catch {
          // Quota du coffre ou fichier trop volumineux : compte et signale, jamais silencieux.
          refused += 1;
        }
      }
    }
    // Liens poses une fois toutes les entrees creees : l'element designe peut venir apres celui qui le designe.
    const { links, missing } = resolveImportedLinks(entries, receivedIds);
    for (const link of links) await state.vaultStore.linkEntries(link.from, link.to);
    // References KeePass sans cible unique : gardees en texte, comptees -- jamais un mot de passe choisi au hasard.
    const unresolved = entries.reduce((sum, entry) => sum + (entry.unresolvedReferences ?? 0), 0);
    return { imported: entries.length, linked: linkedIds.size, attachments, refused, related: links.length, missingRelated: missing, unresolved };
  }

  /**
   * Import d'un export concurrent. Si le fichier est chiffre (KDBX, export
   * Bitwarden protege), le mot de passe est demande puis l'import est rejoue
   * avec les MEMES octets -- ils restent en memoire, jamais sur disque.
   */
  async function handleImportCsvFile(
    fileName: string,
    bytes: Uint8Array,
    password?: string,
    keyFile: Uint8Array | null = null,
    source: ImportFileHandle | null = null,
  ): Promise<void> {
    if (state.vaultStore === null || state.importBusy) return;
    state.importBusy = true;
    const run = new AbortController();
    importRun = run;
    render();
    try {
      // Une base KeePass a des millions de tours AES-KDF derive plusieurs dizaines de secondes, un export Bitwarden Argon2id
      // plusieurs secondes : le bouton en dit l'avancement.
      // Rendu au plus toutes les 200 ms : un rendu complet par pourcent coutait plus que la derivation elle-meme
      // (mesure : 2,3 s contre 0,09 s pour une base Argon2 legere).
      let renderedAt = 0;
      const onProgress = (fraction: number): void => {
        // Annule (bouton, verrouillage) : la derivation s'interrompt a son prochain signal d'avancement.
        run.signal.throwIfAborted();
        const percent = Math.floor(fraction * 100);
        if (percent === state.importProgress) return;
        state.importProgress = percent;
        if (Date.now() - renderedAt < 200) return;
        renderedAt = Date.now();
        render();
      };
      const { entries, skippedFiles, folders } = await parseImportFile(fileName, bytes, { password, keyFile, format: state.importFormat, onProgress });
      // PBKDF2 WebCrypto ne signale que sa fin : annule pendant, rien n'est verse.
      run.signal.throwIfAborted();
      // L'ecriture au coffre ne s'interrompt pas a moitie : plus d'« Annuler » a partir d'ici.
      importRun = null;
      state.importPrompt = null;
      const { imported, linked, attachments, refused, related, missingRelated, unresolved } = await importParsedEntries(entries, folders);
      state.importCleanupFile = imported > 0 && isPlaintextExport(fileName, password) ? fileName : null;
      state.importCleanupHandle = state.importCleanupFile !== null && canRemoveImportFile(source) ? source : null;
      state.importCleanupResult = null;
      await refreshVaultData();
      const skipped = skippedFiles.length > 0 ? ` -- fichier(s) non reconnu(s) : ${skippedFiles.join(', ')}` : '';
      const joined = attachments > 0 ? ` et ${attachments} piece(s) jointe(s) chiffree(s)` : '';
      const lost = refused > 0 ? ` -- ${refused} piece(s) jointe(s) refusee(s) (taille ou quota)` : '';
      const attached = linked > 0 ? ` -- ${linked} graine(s) 2FA rattachee(s) a un identifiant deja au coffre` : '';
      const tied = related > 0 ? ` -- ${related} lien(s) entre elements repris` : '';
      const untied = missingRelated > 0 ? ` -- ${missingRelated} lien(s) vers un element absent de l'export, non repris` : '';
      const literal = unresolved > 0 ? ` -- ${unresolved} reference(s) KeePass sans cible unique, laissee(s) en texte : a verifier` : '';
      scheduleFlash(`${imported - linked} entree(s) importee(s)${attached}${joined}${tied}${skipped}${lost}${untied}${literal}`);
    } catch (cause) {
      // Annule : « Annuler » ou le verrouillage a deja remis l'etat, un autre import a pu commencer depuis.
      if (run.signal.aborted) return;
      if (importRun === run) importRun = null;
      if (cause instanceof ImportPasswordRequiredError) {
        state.importPrompt = { fileName, bytes, format: cause.format, error: null };
      } else if (cause instanceof ImportPasswordInvalidError && state.importPrompt !== null) {
        const message = acceptsKeyFile(cause.format) ? `mot de passe ou fichier-cle incorrect pour cette base ${cause.format}` : cause.message;
        state.importPrompt = { ...state.importPrompt, error: message };
      } else {
        state.importPrompt = null;
        scheduleFlash(cause instanceof Error ? cause.message : "fichier d'import invalide");
      }
    }
    state.importBusy = false;
    state.importProgress = null;
    render();
  }

  /**
   * Captures des codes QR d'un transfert Google Authenticator (ou de graines otpauth://) : lues SUR CET APPAREIL,
   * toutes ou rien -- un code du transfert oublie ou illisible n'importe rien et le dit. Les captures portent les
   * graines en clair : leur suppression est proposee, comme pour un export en clair.
   */
  async function handleImportQrImages(files: readonly File[], cleanupName?: string): Promise<void> {
    if (state.vaultStore === null || state.importBusy) return;
    state.importBusy = true;
    render();
    try {
      const read = await authenticatorUrisFromImages(files);
      if (!read.ok) throw new Error(read.problem);
      const { imported, linked } = await importParsedEntries(parseAuthenticatorUriList(read.text));
      state.importCleanupFile = imported > 0 ? (cleanupName ?? files.map((file) => file.name).join(' », « ')) : null;
      state.importCleanupHandle = null;
      state.importCleanupResult = null;
      await refreshVaultData();
      const attached = linked > 0 ? ` -- ${linked} graine(s) 2FA rattachee(s) a un identifiant deja au coffre` : '';
      scheduleFlash(`${imported - linked} entree(s) importee(s)${attached}`);
    } catch (cause) {
      scheduleFlash(cause instanceof Error ? cause.message : 'captures de codes QR illisibles');
    }
    state.importBusy = false;
    render();
  }

  /** Cree l'alias chez le fournisseur de l'utilisateur. Un echec s'affiche : un
   * alias faux serait enregistre comme identifiant d'un compte irrecuperable. */
  async function handleAliasSubmit(form: HTMLFormElement): Promise<void> {
    if (state.aliasBusy) return;
    const data = new FormData(form);
    state.aliasProviderId = String(data.get('provider') ?? '');
    state.aliasEndpoint = String(data.get('endpoint') ?? '');
    state.aliasApiKey = String(data.get('apiKey') ?? '');
    state.aliasNote = String(data.get('note') ?? '');
    state.aliasDomain = String(data.get('domain') ?? state.aliasDomain);
    state.aliasDestination = String(data.get('destination') ?? state.aliasDestination);
    state.aliasBusy = true;
    state.aliasError = null;
    render();
    try {
      state.aliasResult = await createEmailAlias({
        providerId: state.aliasProviderId,
        apiKey: state.aliasApiKey,
        endpoint: state.aliasEndpoint,
        note: state.aliasNote,
        domain: state.aliasDomain,
        destination: state.aliasDestination,
      });
      scheduleFlash('Alias cree');
    } catch (cause) {
      state.aliasError = cause instanceof Error ? cause.message : "creation d'alias impossible";
    }
    state.aliasBusy = false;
    render();
  }

  /** Supprime l'alias affiche (MailKrypt) : un alias expire garde sa place dans
   * le plafond du domaine, seul le supprimer la rend. */
  async function handleAliasDelete(): Promise<void> {
    if (state.aliasBusy || state.aliasResult === '') return;
    state.aliasBusy = true;
    state.aliasError = null;
    render();
    try {
      await deleteEmailAlias({
        providerId: state.aliasProviderId,
        apiKey: state.aliasApiKey,
        endpoint: state.aliasEndpoint,
        alias: state.aliasResult,
      });
      state.aliasResult = '';
      scheduleFlash('Alias supprime');
    } catch (cause) {
      state.aliasError = cause instanceof Error ? cause.message : "suppression d'alias impossible";
    }
    state.aliasBusy = false;
    render();
  }

  root.addEventListener('click', (event) => {
    resetIdleTimer();
    pendingClick = handleClick(event as MouseEvent);
  });

  // L'aide s'atteint et se quitte au clavier : une aide qu'on ne peut pas
  // fermer sans souris n'est pas accessible (Regle IHM galaxie).
  root.addEventListener('keydown', (event) => {
    if ((event as KeyboardEvent).key !== 'Escape') return;
    // Echap sur la fenetre de consentement = Refuser : rien n'entre dans le coffre sans le bouton explicite.
    if (state.credentialsTransferOffer !== null) answerCredentialsTransfer(false);
    else if (state.masterPasswordPrompt !== null) answerMasterPasswordPrompt(null);
    else if (state.totpCodePrompt !== null) answerTotpCodePrompt(null);
    else if (state.helpOpen) closeHelp();
    else if (state.largeType !== null) {
      state.largeType = null;
      render();
    } else if (state.wifiQr !== null) {
      state.wifiQr = null;
      render();
    } else if (state.attachmentPreview !== null) {
      closeAttachmentPreview();
      render();
    } else if (state.totpQr !== null) {
      state.totpQr = null;
      render();
    }
  });

  // Selection multiple au clavier : fleches d'une ligne a l'autre, Origine / Fin aux extremites, Maj+fleche /
  // Maj+Origine / Maj+Fin etendent la plage, Maj+Espace / Maj+Entree = Maj+clic, Ctrl+A / Cmd+A = Tout cocher.
  // Lu a l'appui : le clic deduit d'une touche ne porte pas Maj de facon fiable.
  root.addEventListener('keydown', (event) => {
    const keyEvent = event as KeyboardEvent;
    const row = keyEvent.target instanceof HTMLElement ? keyEvent.target.closest<HTMLElement>('[data-action="bulk-toggle"]') : null;
    const id = row?.dataset.id;
    if (!state.bulkMode || id === undefined || !view.contains(row)) return;
    const key = bulkRowKey(keyEvent);
    if (key === null) return;
    if (key === 'range') toggleBulkRow(id, true);
    else if (key === 'all') selectAllBulk();
    else if (!moveBulkFocus(id, key.replace('extend-', '') as BulkFocusMove, key.startsWith('extend-'))) return;
    // Pas de clic deduit de la touche par-dessus (il re-basculerait la ligne), pas de defilement de la page.
    keyEvent.preventDefault();
    resetIdleTimer();
  });
  // Arbre des dossiers au clavier (motif « tree » de l'ARIA APG) : fleches d'un dossier visible a l'autre, Droite /
  // Gauche deplient / replient ou passent au sous-dossier / au parent, Origine / Fin aux extremites. Depuis le nom
  // du dossier comme depuis son chevron ; le focus arrive toujours sur le nom ; Entree / Espace l'ouvrent. Avec un
  // modificateur, la touche reste au navigateur (Alt+Gauche = page precedente). La ligne qui a le focus devient
  // l'arret de Tabulation de l'arbre (tabulation itinerante) : Maj+Tab puis Tab y ramene. `*` deplie les freres.
  // Recherche par frappe : une lettre mene au dossier suivant dont le nom commence par elle, plusieurs lettres
  // rapprochees au nom qui commence par elles (Maj compris : une capitale est une lettre). L'arbre garde ses lettres
  // meme sans dossier correspondant : un raccourci d'une seule lettre (`l` = verrouiller) n'en part jamais.
  root.addEventListener('keydown', (event) => {
    const keyEvent = event as KeyboardEvent;
    if (keyEvent.ctrlKey || keyEvent.metaKey || keyEvent.altKey || keyEvent.isComposing) return;
    const row =
      keyEvent.target instanceof HTMLElement
        ? keyEvent.target.closest<HTMLElement>('.sidebar [data-action="select-folder"], .sidebar [data-action="toggle-folder-collapse"]')
        : null;
    const id = row?.dataset.id;
    if (id === undefined) return;
    const rows = visibleFolderTree(folderTree(state.folders), state.collapsedFolderIds, state.selectedFolderId);
    const typed = folderTypeAheadStep(state.folderTypeAhead, keyEvent.key, Date.now());
    if (typed === null && keyEvent.shiftKey && keyEvent.key !== '*') return;
    state.folderTypeAhead = typed;
    const move = typed === null ? folderTreeKey(rows, id, keyEvent.key) : folderTreeTypeAhead(rows, id, typed.text, (folder) => folder.secrets.name);
    if (typed === null && move === null) return;
    keyEvent.preventDefault();
    resetIdleTimer();
    if (move === null) return;
    const rowOf = (folderId: string): HTMLElement | undefined =>
      Array.from(root.querySelectorAll<HTMLElement>('.sidebar [data-action="select-folder"]')).find((button) => button.dataset.id === folderId);
    // Entree / Espace : le meme chemin que le clic (la ligne est un `treeitem`, pas un bouton qui le ferait seul).
    if (move.kind === 'select') {
      rowOf(move.id)?.click();
      return;
    }
    if (move.kind === 'collapse') state.collapsedFolderIds.add(move.id);
    if (move.kind === 'expand') state.collapsedFolderIds.delete(move.id);
    for (const sibling of move.ids ?? []) state.collapsedFolderIds.delete(sibling);
    if (move.kind !== 'focus') render();
    rowOf(move.id)?.focus();
  });
  // Arbre des etiquettes au clavier : le meme motif que les dossiers, par les memes fonctions (`folderTreeKey`,
  // recherche par frappe) sur `tagTreeNavRows`. Une etiquette repliee l'est par sa cle sans casse (`tagKey`).
  root.addEventListener('keydown', (event) => {
    const keyEvent = event as KeyboardEvent;
    if (keyEvent.ctrlKey || keyEvent.metaKey || keyEvent.altKey || keyEvent.isComposing) return;
    const tag =
      keyEvent.target instanceof HTMLElement
        ? keyEvent.target.closest<HTMLElement>('.sidebar [data-action="select-tag"], .sidebar [data-action="toggle-tag-collapse"]')?.dataset.id
        : undefined;
    if (tag === undefined) return;
    const tagRows = tagTreeNavRows(visibleTagTree(state.tags, state.collapsedTagKeys, state.selectedTag));
    const typed = folderTypeAheadStep(state.tagTypeAhead, keyEvent.key, Date.now());
    if (typed === null && keyEvent.shiftKey && keyEvent.key !== '*') return;
    state.tagTypeAhead = typed;
    const move = typed === null ? folderTreeKey(tagRows, tag, keyEvent.key) : folderTreeTypeAhead(tagRows, tag, typed.text, (node) => node.label);
    if (typed === null && move === null) return;
    keyEvent.preventDefault();
    resetIdleTimer();
    if (move === null) return;
    const tagRowOf = (target: string): HTMLElement | undefined =>
      Array.from(root.querySelectorAll<HTMLElement>('.sidebar [data-action="select-tag"]')).find((row) => row.dataset.id === target);
    if (move.kind === 'select') {
      tagRowOf(move.id)?.click();
      return;
    }
    if (move.kind === 'collapse') state.collapsedTagKeys.add(tagKey(move.id));
    if (move.kind === 'expand') state.collapsedTagKeys.delete(tagKey(move.id));
    for (const sibling of move.ids ?? []) state.collapsedTagKeys.delete(tagKey(sibling));
    if (move.kind !== 'focus') render();
    tagRowOf(move.id)?.focus();
  });
  // Tabulation itinerante, arbre par arbre : la ligne qui prend le focus devient l'arret de Tabulation de SON arbre,
  // l'autre arbre de la barre garde le sien.
  root.addEventListener('focusin', (event) => {
    const item = event.target instanceof HTMLElement ? event.target.closest<HTMLElement>('.sidebar [role="treeitem"]') : null;
    const tree = item?.closest<HTMLElement>('[role="tree"]') ?? null;
    if (item === null || tree === null) return;
    for (const other of tree.querySelectorAll<HTMLElement>('[role="treeitem"]')) other.tabIndex = other === item ? 0 : -1;
  });
  // Valeur d'un champ personnalise : Maj+Entree passe a la ligne, et un texte colle a plusieurs lignes les garde --
  // le champ d'une ligne les aplatirait (spec HTML). La valeur passe alors dans une zone de texte, curseur suivi.
  root.addEventListener('keydown', (event) => {
    const keyEvent = event as KeyboardEvent;
    if (keyEvent.key !== 'Enter' || !keyEvent.shiftKey || keyEvent.ctrlKey || keyEvent.metaKey || keyEvent.altKey || keyEvent.isComposing) return;
    if (!(keyEvent.target instanceof HTMLInputElement) || keyEvent.target.dataset.input !== 'custom-value') return;
    // Pas d'envoi implicite du formulaire par la touche Entree.
    keyEvent.preventDefault();
    breakCustomValue(keyEvent.target, '\n');
  });
  root.addEventListener('paste', (event) => {
    const pasteEvent = event as ClipboardEvent;
    if (!(pasteEvent.target instanceof HTMLInputElement) || pasteEvent.target.dataset.input !== 'custom-value') return;
    const text = pasteEvent.clipboardData?.getData('text/plain') ?? '';
    if (!isMultilineCustomValue(text)) return;
    pasteEvent.preventDefault();
    breakCustomValue(pasteEvent.target, text);
  });

  // Texte d'un code QR Wi-Fi colle dans le nom du reseau : il remplit l'entree (`fillDraftFromWifiQr`), jamais le SSID
  // brut -- la cle qu'il porte n'a rien a faire dans le nom du reseau.
  root.addEventListener('paste', (event) => {
    const pasteEvent = event as ClipboardEvent;
    const target = pasteEvent.target;
    if (!(target instanceof HTMLInputElement) || target.name !== 'field:ssid' || target.closest('form[data-form="entry"]') === null || !view.contains(target)) return;
    const text = pasteEvent.clipboardData?.getData('text/plain') ?? '';
    if (!looksLikeWifiQr(text)) return;
    pasteEvent.preventDefault();
    fillDraftFromWifiQr(text);
  });

  // Capture d'ecran d'un code QR collee dans la graine 2FA ou le nom d'un reseau Wi-Fi : lue sur l'appareil
  // (`readDraftQrImage`), jamais collee telle quelle. Un texte colle avec elle a deja ete traite au-dessus.
  root.addEventListener('paste', (event) => {
    const pasteEvent = event as ClipboardEvent;
    const target = pasteEvent.target;
    if (pasteEvent.defaultPrevented || !(target instanceof HTMLInputElement) || target.closest('form[data-form="entry"]') === null || !view.contains(target)) return;
    const into = target.name === 'totpSecret' ? 'totp' : target.name === 'field:ssid' ? 'wifi' : null;
    if (into === null) return;
    const image = firstImageFile(pasteEvent.clipboardData?.files);
    if (image === null) return;
    pasteEvent.preventDefault();
    void readDraftQrImage(image, into);
  });

  // Capture d'un code QR de transfert Google Authenticator collee (Ctrl+V) pendant que l'ecran d'import est affiche.
  // Ecoute sur `document` : sans champ actif, le collage part de `body`, hors de `root`. Un champ de saisie garde
  // son collage ; le texte colle n'est jamais lu ici.
  document.addEventListener('paste', (event) => {
    const target = event.target;
    if (event.defaultPrevented || root.querySelector('[data-dropzone="import-qr"]') === null) return;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || (target instanceof HTMLElement && target.isContentEditable)) return;
    const images = Array.from(event.clipboardData?.files ?? []).filter((file) => file.type.startsWith('image/'));
    if (images.length === 0) return;
    event.preventDefault();
    // Le presse-papiers nomme toute image « image.png » : l'effacement propose vise la capture collee, pas ce nom.
    void handleImportQrImages(images, 'capture d’ecran collee');
  });

  root.addEventListener('keyup', (event) => {
    const keyEvent = event as KeyboardEvent;
    if (state.bulkMode && keyEvent.key === ' ' && keyEvent.shiftKey && keyEvent.target instanceof HTMLElement && keyEvent.target.closest('[data-action="bulk-toggle"]') !== null) keyEvent.preventDefault();
  });

  /** Le seul chemin qui change les raccourcis : etat, `localStorage` (cle retiree pour les defauts), erreur effacee. */
  function saveShortcutSettings(settings: ShortcutSettings): void {
    state.shortcuts = settings;
    state.shortcutError = null;
    const serialized = serializeShortcutSettings(settings);
    if (serialized === null) localStorage.removeItem(SHORTCUT_SETTINGS_KEY);
    else localStorage.setItem(SHORTCUT_SETTINGS_KEY, serialized);
  }

  /** Ou la touche a ete frappee, pour `shortcutCommand` : hors de la racine (autre composant), rien n'est pris. */
  function shortcutContext(event: KeyboardEvent): ShortcutContext {
    const target = event.target;
    const selected = (root.ownerDocument.getSelection()?.toString() ?? '').length > 0;
    if (target instanceof HTMLInputElement && target.dataset.input === 'search' && view.contains(target)) {
      const inField = target.selectionStart !== null && target.selectionStart !== target.selectionEnd;
      return { target: 'search', textSelected: inField || selected };
    }
    const page = target === root.ownerDocument.body || target === root.ownerDocument.documentElement || target === root.ownerDocument;
    if (!(target instanceof HTMLElement) || (!page && !view.contains(target))) return { target: page ? 'other' : 'editable', textSelected: selected };
    const editable = target.isContentEditable || target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
    return { target: editable ? 'editable' : 'other', textSelected: selected };
  }

  /**
   * Le geste d'un raccourci, ou `null` s'il n'a rien a faire ici (la touche reste alors au navigateur). Il passe
   * par les MEMES chemins que les clics : Nouvelle entree, Modifier et Verrouiller cliquent le bouton affiche (pas
   * de bouton, pas de geste -- corbeille, archives, entree sensible non confirmee) ; les copies passent par
   * `copyEntryField` (re-demande du mot de passe maitre comprise) ; les fleches par `selectEntry`.
   */
  function shortcutAction(command: ShortcutCommand): (() => unknown) | null {
    if (state.sendReceive !== null || state.vaultStore === null || state.helpOpen || state.largeType !== null || state.wifiQr !== null || state.totpQr !== null || state.draft !== null || state.bulkMode) return null;
    // L'apercu d'une piece jointe est modal : aucune touche du coffre n'agit dessous.
    if (state.attachmentPreview !== null) return null;
    // La fenetre de consentement d'un versement attend un bouton : aucune touche du coffre n'agit dessous.
    if (state.credentialsTransferOffer !== null || state.masterPasswordPrompt !== null || state.totpCodePrompt !== null) return null;
    const clickOn = (selector: string): (() => void) | null => {
      const button = view.querySelector<HTMLElement>(selector);
      return button === null ? null : () => button.click();
    };
    const search = view.querySelector<HTMLInputElement>('[data-input="search"]');
    const selected = state.panel === 'vault' ? state.entries.find((item) => item.id === state.selectedEntryId) : undefined;
    const copyable = selected !== undefined && !repromptPending(selected) && view.querySelector('.detail-pane') !== null ? selected : undefined;
    switch (command) {
      case 'lock':
        return clickOn('[data-action="lock"]');
      case 'show-shortcuts':
        return () => {
          state.helpOpen = true;
          state.helpQuestion = 'raccourcis clavier';
          state.helpAnswer = askHelp(state.helpQuestion, currentHelpView(), helpSnapshot());
          render();
        };
      case 'new-entry':
        return clickOn('.list-pane [data-action="new-entry"]');
      case 'edit-entry':
        return clickOn('.detail-pane [data-action="edit-entry"]');
      case 'large-type':
        return clickOn('.detail-pane [data-action="open-large-type"][data-field="password"]');
      case 'focus-search':
        return search === null
          ? null
          : () => {
              search.focus();
              search.select();
            };
      case 'clear-search':
        if (search === null || (search.value === '' && state.searchQuery === '')) return null;
        return () => {
          search.value = '';
          state.searchQuery = '';
          void refreshVaultAndRender();
        };
      case 'next-entry':
      case 'previous-entry': {
        if (search === null) return null;
        const ids = state.entries.map((item) => item.id);
        const next = neighbourEntryId(ids, state.selectedEntryId, command === 'next-entry' ? 1 : -1);
        if (next === null) return null;
        return async () => {
          await selectEntry(next);
          Array.from(view.querySelectorAll<HTMLElement>('.entry-row')).find((row) => row.dataset.id === next)?.scrollIntoView?.({ block: 'nearest' });
        };
      }
      case 'copy-username':
      case 'copy-password': {
        const ref = command === 'copy-username' ? 'username' : 'password';
        if (copyable === undefined || entryFieldValue(copyable.secrets, ref) === null) return null;
        return () => copyEntryField(copyable.id, ref);
      }
      case 'copy-totp':
        if (copyable === undefined || !copyable.secrets.totpSecret) return null;
        return () => copyEntryTotp(copyable.id);
    }
  }

  // Piege de focus de la fenetre modale du dessus (la derniere rendue) : Tab et Maj+Tab y bouclent, et un focus egare
  // (clic sur le voile, barre de titre) y rentre. Ecoute sur le document : apres un clic sur le voile, le focus est sur
  // la page elle-meme et la tabulation suivante n'atteindrait pas `root`.
  root.ownerDocument.addEventListener('keydown', (event) => {
    if (!root.isConnected || event.key !== 'Tab' || event.ctrlKey || event.metaKey || event.altKey) return;
    const dialog = topModal(view);
    if (dialog === null) return;
    const focusables = modalFocusables(dialog);
    const active = root.ownerDocument.activeElement;
    const target = modalTabTarget<Element>(focusables, active, event.shiftKey);
    if (target === null && focusables.length > 0) return;
    event.preventDefault();
    (target as HTMLElement | null)?.focus();
  });

  // Raccourcis clavier du coffre : ecoutes sur le document, car apres certains gestes le focus est sur la page elle-meme.
  // Une touche deja prise par un composant (arbre des dossiers, selection multiple) n'est plus un raccourci.
  root.ownerDocument.addEventListener('keydown', (event) => {
    if (!root.isConnected || event.defaultPrevented) return;
    const command = shortcutCommand(event, shortcutContext(event), state.shortcuts);
    const action = command === null ? null : shortcutAction(command);
    if (action === null) return;
    event.preventDefault();
    resetIdleTimer();
    void action();
  });

  root.addEventListener('submit', (event) => {
    event.preventDefault();
    resetIdleTimer();
    const form = event.target as HTMLFormElement;
    if (form.dataset.form === 'auth') void handleAuthSubmit(form);
    if (form.dataset.form === 'idenva-open') void handleIdenvaOpenSubmit(form);
    if (form.dataset.form === 'recover') void handleRecoverSubmit(form);
    if (form.dataset.form === 'recover-remote') void handleRemoteRecoverSubmit(form);
    if (form.dataset.form === 'signin-remote') void handleRemoteSignInSubmit(form);
    if (form.dataset.form === 'entry') void handleEntrySubmit();
    if (form.dataset.form === 'reprompt') void handleRepromptSubmit(form);
    if (form.dataset.form === 'master-password-prompt') void handleMasterPasswordPromptSubmit(form);
    if (form.dataset.form === 'bulk-reprompt') void handleBulkRepromptSubmit(form);
    if (form.dataset.form === 'sync') void handleSyncSubmit(form);
    if (form.dataset.form === 'webauthn-register') void handleWebAuthnRegisterSubmit(form);
    if (form.dataset.form === 'account-delete') void handleAccountDeletionSubmit(form);
    if (form.dataset.form === 'sessions-revoke-all') void handleSessionsRevokeSubmit();
    if (form.dataset.form === 'emergency-grant') void handleEmergencyGrantSubmit(form);
    if (form.dataset.form === 'emergency-contact') void handleEmergencyContactSubmit(form, 'request');
    if (form.dataset.form === 'passkey-unlock') void handlePasskeyEnrollSubmit(form);
    if (form.dataset.form === 'master-password') void handleMasterPasswordSubmit(form);
    if (form.dataset.form === 'totp-code-prompt') handleTotpCodePromptSubmit(form);
    if (form.dataset.form === 'account-totp-confirm') void handleAccountTotpConfirmSubmit(form);
    if (form.dataset.form === 'account-email') void handleEmailChangeSubmit(form);
    if (form.dataset.form === 'cxf-export') void handleCxfExportSubmit(form);
    if (form.dataset.form === 'breach') handleBreachSettingsSubmit(form);
    if (form.dataset.form === 'webdav') void handleWebdavSubmit(form);
    if (form.dataset.form === 'alias') void handleAliasSubmit(form);
    if (form.dataset.form === 'username') void regenerateUsername();
    if (form.dataset.form === 'send') void handleSendSubmit(form);
    if (form.dataset.form === 'send-edit') void handleSendEditSubmit(form);
    if (form.dataset.form === 'send-unlock') void openReceivedSend(String(new FormData(form).get('password') ?? ''));
    if (form.dataset.form === 'import-password' && state.importPrompt !== null) {
      const prompt = state.importPrompt;
      const password = String(new FormData(form).get('password') ?? '');
      // Fichier-cle lu en memoire au seul moment de l'envoi : jamais garde dans l'etat, jamais ecrit.
      const keyFile = (form.elements.namedItem('keyFile') as HTMLInputElement | null)?.files?.[0];
      void (async () => {
        const keyBytes = keyFile === undefined ? null : new Uint8Array(await keyFile.arrayBuffer());
        // Annule (ou verrouille) pendant la lecture du fichier-cle : rien ne part.
        if (state.importPrompt !== prompt) return;
        await handleImportCsvFile(prompt.fileName, prompt.bytes, password, keyBytes);
      })();
    }
    if (form.dataset.form === 'help') {
      askAssistant(String(new FormData(form).get('question') ?? ''));
      render();
    }
    // Formulaires 'generator' et 'passphrase' : geres par l'ecouteur input ci-dessous (regeneration en direct).
  });

  /** Toutes les adresses du formulaire de l'editeur, la premiere et les ajoutees. */
  const editorUris = (form: Element | null): string[] =>
    [...(form?.querySelectorAll<HTMLInputElement>('input[name="uri"], input[name^="uri-extra:"]') ?? [])].map((input) => input.value);

  root.addEventListener('input', (event) => {
    resetIdleTimer();
    const target = event.target as HTMLElement;
    if (target.dataset.input === 'search') {
      state.searchQuery = (target as HTMLInputElement).value;
      void refreshVaultAndRender();
      return;
    }
    if (target.dataset.input === 'bulk-tag') {
      // Pas de re-rendu a la frappe ; un geste en attente du mot de passe maitre visait l'ancienne saisie : il tombe.
      state.bulkTag = (target as HTMLInputElement).value;
      if (state.bulkReprompt !== null) {
        state.bulkReprompt = null;
        render();
      }
      return;
    }
    if (target.dataset.input === 'shortcut-letter') {
      // A la frappe, pas a la sortie du champ : la lettre tapee est la touche retenue (le focus revient par `focusSignature`).
      // Champ vide (lettre effacee pour en taper une autre) : rien a retenir encore.
      if ((target as HTMLInputElement).value === '') return;
      const command = target.dataset.field as LetterCommand;
      const rebind = rebindShortcut(state.shortcuts, command, (target as HTMLInputElement).value);
      if (rebind.ok) {
        saveShortcutSettings(rebind.settings);
        scheduleFlash(`${LETTER_COMMAND_LABELS[command]} : touche ${rebind.settings.keys[command]}`);
      } else {
        state.shortcutError = rebind.error;
      }
      render();
      return;
    }
    if (target.dataset.input === 'help-question') {
      // Pas de re-rendu a la frappe : la saisie est conservee pour survivre au
      // prochain rendu declenche par ailleurs (minuteur TOTP, par exemple).
      state.helpQuestion = (target as HTMLInputElement).value;
      return;
    }
    if (target.dataset.input === 'card-number') {
      const hint = target.closest('label')?.querySelector('[data-hint="card-number"]');
      if (hint) hint.textContent = cardNumberHint((target as HTMLInputElement).value);
      return;
    }
    if (target.dataset.input === 'entry-notes') {
      // Pas de re-rendu a la frappe (la zone perdrait le focus) : seul l'apercu, s'il est ouvert, suit la saisie.
      const preview = target.closest('form')?.querySelector('[data-preview="notes"]');
      if (preview) preview.innerHTML = renderNotesPreview((target as HTMLTextAreaElement).value);
      return;
    }
    const exportHint = target.closest('form')?.querySelector<HTMLElement>('[data-hint="export-password-strength"]');
    if (exportHint && (target as HTMLInputElement).name === 'password') {
      // Pas de re-rendu a la frappe : la jauge suit le mot de passe d'export CXF, avec l'e-mail du coffre ouvert.
      const password = (target as HTMLInputElement).value;
      const strength = exportPasswordStrength(password, state.envelope?.email ?? '');
      exportHint.textContent = strength?.message ?? '';
      exportHint.dataset.strength = strength?.label ?? '';
      exportHint.hidden = strength === null;
      const consent = target.closest('form')!.querySelector<HTMLElement>('[data-hint="export-weak-consent"]');
      if (consent !== null) consent.hidden = password.length < MIN_CXF_PASSWORD_LENGTH || strength?.label !== 'faible';
      return;
    }
    const masterHint = target.closest('form')?.querySelector<HTMLElement>('[data-hint="master-password-strength"]');
    if (masterHint && ['masterPassword', 'newMasterPassword', 'email'].includes((target as HTMLInputElement).name)) {
      // Pas de re-rendu a la frappe : la jauge suit le nouveau mot de passe maitre et l'e-mail du compte.
      const masterForm = target.closest('form')!;
      const value = (name: string): string | undefined => masterForm.querySelector<HTMLInputElement>(`input[name="${name}"]`)?.value;
      const email = value('email') ?? state.envelope?.email ?? state.localAccountEnvelope?.email ?? '';
      const password = value('newMasterPassword') ?? value('masterPassword') ?? '';
      const strength = masterPasswordStrength(password, email);
      masterHint.textContent = strength?.message ?? '';
      masterHint.dataset.strength = strength?.label ?? '';
      masterHint.hidden = strength === null;
      const consent = masterForm.querySelector<HTMLElement>('[data-hint="master-weak-consent"]');
      if (consent !== null) consent.hidden = password.length < MIN_MASTER_PASSWORD_LENGTH || strength?.label !== 'faible';
      return;
    }
    const entryForm = target.closest<HTMLFormElement>('form[data-form="entry"]');
    const entryField = (target as HTMLInputElement).name ?? '';
    if (entryForm !== null && (entryField === 'password' || entryField === 'username' || entryField === 'name' || entryField === 'uri' || entryField.startsWith('uri-extra:'))) {
      // Pas de re-rendu a la frappe : la jauge suit le mot de passe, le nom, l'identifiant et les sites (les memes mots que la Sante du coffre).
      const strengthHint = entryForm.querySelector<HTMLElement>('[data-hint="password-strength"]');
      const value = (name: string): string => entryForm.querySelector<HTMLInputElement>(`input[name="${name}"]`)?.value ?? '';
      const strength = draftPasswordStrength(value('password'), value('username'), editorUris(entryForm), value('name'), vaultAccountEmail());
      if (strengthHint) {
        strengthHint.textContent = strength?.message ?? '';
        strengthHint.dataset.strength = strength?.label ?? '';
        strengthHint.hidden = strength === null;
      }
      // Le verdict « fuites » ne vaut que pour le mot de passe verifie : modifie, il se masque (sans re-rendu) jusqu'au prochain clic.
      const breachHint = entryForm.querySelector<HTMLElement>('[data-hint="password-breach"]');
      if (breachHint && state.draftBreach !== null) breachHint.hidden = value('password') !== state.draftBreach.password || breachHint.textContent === '';
    }
    if (target.dataset.input === 'entry-uri' || (target as HTMLInputElement).name?.startsWith('uri-extra:')) {
      const form = target.closest('form');
      if (target.dataset.input === 'entry-uri') {
        const hint = form?.querySelector('[data-hint="equivalent-domains"]');
        if (hint) hint.textContent = equivalentDomainsHint((target as HTMLInputElement).value);
      }
      // Pas de re-rendu a la frappe : seules l'alerte et la regle du site suivent, sur TOUTES les adresses du formulaire.
      const uris = editorUris(form);
      const alert = form?.querySelector<HTMLElement>('[data-hint="deceptive-uri"]');
      if (alert) {
        alert.textContent = deceptiveUriHint(uris, vaultSites(state.entries));
        alert.hidden = alert.textContent === '';
      }
      const rulesHint = form?.querySelector<HTMLElement>('[data-hint="site-password-rules"]');
      if (rulesHint) {
        rulesHint.textContent = sitePasswordRulesHint(uris);
        rulesHint.hidden = rulesHint.textContent === '';
      }
      return;
    }
    const form = target.closest('form[data-form="generator"]');
    if (form !== null && (target as HTMLInputElement).name === 'site') {
      // Pas de re-rendu a la frappe du site : seule l'indication suit ; le tirage se fait a la validation (`change`).
      state.generatorSite = (target as HTMLInputElement).value;
      const hint = form.querySelector<HTMLElement>('[data-hint="generator-site-rules"]');
      if (hint) {
        hint.textContent = generatorSiteRulesHint(state.generatorSite);
        hint.hidden = hint.textContent === '';
      }
      return;
    }
    if (form !== null) {
      // Pas de re-rendu pendant la frappe d'un minimum ou d'un jeu de caracteres : le champ perdrait le focus.
      // Ils s'appliquent a la validation (Entree, sortie du champ, fleches du champ numerique : evenement `change`).
      if (GENERATOR_TYPED_FIELDS.has((target as HTMLInputElement).name)) return;
      applyGeneratorForm(form as HTMLFormElement);
    }
    const usernameForm = target.closest('form[data-form="username"]');
    if (usernameForm !== null) {
      const data = new FormData(usernameForm as HTMLFormElement);
      const kind = usernameKind(data.get('kind'));
      const previous = state.usernameSettings;
      const wordOptions = usernameForm.querySelector('input[name="includeNumber"]') !== null;
      state.usernameSettings = {
        kind,
        // Les champs absents du formulaire (autre forme) gardent leur valeur.
        capitalize: wordOptions ? data.get('capitalize') !== null : previous.capitalize,
        includeNumber: wordOptions ? data.get('includeNumber') !== null : previous.includeNumber,
        suffix: data.has('suffix') ? usernameSuffix(data.get('suffix')) : previous.suffix,
        email: data.has('email') ? String(data.get('email')) : previous.email,
        domain: data.has('domain') ? String(data.get('domain')) : previous.domain,
      };
      // Pas de re-rendu pendant la frappe d'une adresse : le champ perdrait le focus.
      // Le tirage se fait a la validation (Entree, sortie du champ) ou avec « Regenerer ».
      if ((target as HTMLInputElement).name === 'email' || (target as HTMLInputElement).name === 'domain') return;
      void regenerateUsername();
      return;
    }
    const passphraseForm = target.closest('form[data-form="passphrase"]');
    if (passphraseForm !== null) {
      state.passphraseSettings = passphraseSettingsFromForm(new FormData(passphraseForm as HTMLFormElement));
      localStorage.setItem(PASSPHRASE_SETTINGS_KEY, serializePassphraseSettings(state.passphraseSettings));
      void ensureWordlist().then(() => {
        state.generatedPassword = generateForGenerator();
        render();
      });
    }
  });

  // « Verrouiller des que je quitte l'onglet » : la page masquee (autre onglet, fenetre reduite, ecran verrouille)
  // referme le coffre tout de suite, sans attendre le delai d'inactivite.
  root.ownerDocument.addEventListener('visibilitychange', () => {
    if (state.lockOnHide && root.ownerDocument.visibilityState === 'hidden' && state.vaultStore !== null) void lock();
  });

  root.addEventListener('change', (event) => {
    resetIdleTimer();
    const target = event.target as HTMLInputElement;
    if (target.dataset.input === 'alias-provider') {
      // Le point d'acces suit le fournisseur : une cle ne part pas chez le precedent.
      const form = target.closest('form');
      if (form !== null) {
        const data = new FormData(form);
        state.aliasApiKey = String(data.get('apiKey') ?? '');
        state.aliasNote = String(data.get('note') ?? '');
      }
      state.aliasProviderId = target.value;
      state.aliasEndpoint = aliasProvider(target.value).defaultEndpoint;
      state.aliasResult = '';
      state.aliasError = null;
      render();
    }
    if (target.dataset.input === 'import-vault' && target.files?.[0]) void handleImportVaultFile(target.files[0]);
    if (target.dataset.input === 'import-format') {
      state.importFormat = isImportFormat(target.value) ? target.value : 'auto';
      render();
    }
    if (target.dataset.input === 'import-csv' && target.files?.[0]) {
      const file = target.files[0];
      void file.arrayBuffer().then((buffer) => handleImportCsvFile(file.name, new Uint8Array(buffer)));
    }
    if (target.dataset.input === 'import-qr-images' && target.files !== null && target.files.length > 0) {
      void handleImportQrImages(Array.from(target.files));
    }
    if (target.dataset.input === 'attach-files' && target.dataset.id !== undefined && target.files !== null) {
      void handleAttachFiles(target.dataset.id, Array.from(target.files));
    }
    if (target.dataset.input === 'send-file' && target.files?.[0]) handleSendFile(target.files[0]);
    if (target.dataset.input === 'totp-qr-image' && target.files?.[0]) void readDraftQrImage(target.files[0], 'totp');
    if (target.dataset.input === 'send-expiry') {
      // « Date personnalisee » fait paraitre le champ date (et le retire si l'on revient a une duree), saisie gardee.
      syncSendDraftFromForm();
      render();
    }
    if (target.name === 'send-kind') {
      // La saisie en cours est relue AVANT le re-rendu : basculer texte/fichier
      // ne doit pas effacer l'intitule deja tape.
      syncSendDraftFromForm();
      state.sendDraft = { ...state.sendDraft, kind: target.value === 'file' ? 'file' : 'text' };
      render();
    }
    const generatorForm = target.closest('form[data-form="generator"]');
    if (generatorForm !== null && GENERATOR_TYPED_FIELDS.has(target.name)) applyGeneratorForm(generatorForm as HTMLFormElement);
    if (generatorForm !== null && target.name === 'site') {
      // Site valide (Entree ou sortie du champ) : un nouveau tirage, selon sa regle s'il en a une.
      state.generatorSite = target.value;
      state.generatedPassword = generateForGenerator();
      render();
    }
    if ((target.name === 'email' || target.name === 'domain') && target.closest('form[data-form="username"]') !== null) {
      // Adresse validee (Entree ou sortie du champ) : l'evenement `input` l'a deja relue sans re-rendu.
      void regenerateUsername();
    }
    if (target.dataset.input === 'text-zoom') {
      state.textZoom = palierZoomTexte(target.value);
      localStorage.setItem(CLE_ZOOM_TEXTE, String(state.textZoom));
      render();
    }
    if (target.dataset.input === 'folder-parent' && state.selectedFolderId !== null) {
      moveFolderUnder(state.selectedFolderId, target.value === BULK_NO_FOLDER ? null : target.value);
    }
    if (target.dataset.input === 'bulk-folder') {
      // Un identifiant inconnu (option forgee) n'est pas retenu : `applyBulk` le refuserait de toute facon.
      state.bulkFolderId = target.value === BULK_NO_FOLDER ? null : state.folders.some((folder) => folder.id === target.value) ? target.value : undefined;
      state.bulkReprompt = null;
      render();
    }
    if (target.dataset.input === 'entry-sort') {
      state.entrySort = entrySortOrder(target.value);
      localStorage.setItem(ENTRY_SORT_KEY, state.entrySort);
      scheduleFlash(`Liste triee : ${entrySortLabel(state.entrySort)}`);
      void refreshVaultAndRender();
    }
    if (target.dataset.input === 'auto-lock-minutes') {
      state.autoLockMinutes = autoLockMinutes(target.value);
      localStorage.setItem(AUTO_LOCK_MINUTES_KEY, String(state.autoLockMinutes));
      // Le nouveau delai s'applique tout de suite, pas a la prochaine interaction.
      resetIdleTimer();
      scheduleFlash(`Verrouillage apres ${autoLockLabel(state.autoLockMinutes)} d'inactivite`);
      render();
    }
    if (target.dataset.input === 'clipboard-clear-seconds') {
      state.clipboardClearSeconds = clipboardClearSeconds(target.value);
      localStorage.setItem(CLIPBOARD_CLEAR_SECONDS_KEY, String(state.clipboardClearSeconds));
      scheduleFlash(`Presse-papiers efface apres ${clipboardClearLabel(state.clipboardClearSeconds)}`);
      render();
    }
    if (target.dataset.input === 'default-uri-match') {
      state.defaultUriMatch = defaultUriMatchRule(target.value);
      if (state.defaultUriMatch === 'domain') localStorage.removeItem(DEFAULT_URI_MATCH_KEY);
      else localStorage.setItem(DEFAULT_URI_MATCH_KEY, state.defaultUriMatch);
      scheduleFlash(`Detection par defaut : ${URI_MATCH_SHORT[state.defaultUriMatch]}`);
      render();
    }
    if (target.dataset.input === 'single-key-shortcuts') {
      saveShortcutSettings({ singleKey: target.checked, keys: state.shortcuts.keys });
      scheduleFlash(target.checked ? 'Raccourcis d\'une seule touche actives' : 'Raccourcis d\'une seule touche coupes');
      render();
    }
    if (target.dataset.input === 'lock-on-hide') {
      state.lockOnHide = target.checked;
      if (target.checked) localStorage.setItem(LOCK_ON_HIDE_KEY, 'oui');
      else localStorage.removeItem(LOCK_ON_HIDE_KEY);
      scheduleFlash(target.checked ? 'Verrouillage a la sortie de l\'onglet active' : 'Verrouillage a la sortie de l\'onglet desactive');
      render();
    }
    if (target.dataset.input === 'credentials-audit-consent') {
      state.credentialsAuditConsent = target.checked;
      if (target.checked) localStorage.setItem(CREDENTIALS_AUDIT_CONSENT_KEY, 'oui');
      else localStorage.removeItem(CREDENTIALS_AUDIT_CONSENT_KEY);
      scheduleFlash(target.checked ? 'Verdict par site partageable' : 'Partage du verdict revoque');
      render();
    }
    // Changer de type, de moteur ou cocher « Masque » change le rendu de la fiche : re-rendre depuis le brouillon a jour.
    if ((target.name === 'type' || target.name === 'field:engine' || target.name === 'field:incomingProtocol' || target.name === 'field:incomingSecurity' || target.name === 'field:smtpSecurity' || target.name.startsWith('custom-hidden:')) && target.closest('form[data-form="entry"]') !== null) {
      syncDraftFromForm();
      render();
    }
  });

  /**
   * Deplace un dossier sous `parentId` (`null` = racine) : « Deplacer sous » de la barre du dossier ET glisser-deposer
   * dans la barre laterale passent par ici.
   */
  function moveFolderUnder(folderId: string, parentId: string | null): void {
    const folder = state.folders.find((candidate) => candidate.id === folderId);
    const parent = parentId === null ? undefined : state.folders.find((candidate) => candidate.id === parentId);
    if (folder === undefined || state.vaultStore === null || (parentId !== null && parent === undefined)) return;
    void state.vaultStore
      .moveFolder(folder.id, parentId)
      .then(async () => {
        // Le dossier deplace reste visible : son nouveau parent se deplie.
        if (parentId !== null) state.collapsedFolderIds.delete(parentId);
        await refreshVaultData();
        scheduleFlash(`Dossier « ${folder.secrets.name} » deplace ${parent === undefined ? 'a la racine' : `sous « ${parent.secrets.name} »`}`);
      })
      // Option forgee (cycle, dossier disparu entre-temps) : le store refuse, la liste revient a l'etat reel.
      .catch((error: unknown) => scheduleFlash(`Deplacement refuse : ${error instanceof Error ? error.message : String(error)}`))
      .finally(render);
  }

  /** Range une entree dans `folderId` (`null` = hors dossier) : glisser une ligne de la liste sur la barre laterale. */
  function moveEntryToFolder(entryId: string, folderId: string | null): void {
    const entry = state.entries.find((candidate) => candidate.id === entryId);
    const folder = folderId === null ? undefined : state.folders.find((candidate) => candidate.id === folderId);
    if (entry === undefined || state.vaultStore === null || (folderId !== null && folder === undefined)) return;
    const name = entry.secrets.name ?? 'Sans nom';
    void state.vaultStore
      .moveEntry(entry.id, folderId)
      .then(async () => {
        await refreshVaultData();
        // La fiche ouverte se ferme si son entree a quitte la vue (comme apres « Deplacer » de la selection multiple).
        if (state.selectedEntryId !== null && !state.entries.some((item) => item.id === state.selectedEntryId)) {
          state.selectedEntryId = null;
          forgetAttachments();
        }
        scheduleFlash(`Entree « ${name} » ${folder === undefined ? 'sortie de son dossier' : `rangee dans « ${folder.secrets.name} »`}`);
      })
      .catch((error: unknown) => scheduleFlash(`Deplacement refuse : ${error instanceof Error ? error.message : String(error)}`))
      .finally(render);
  }

  /**
   * Glisser une etiquette sur une autre (ou sur le titre « Etiquettes ») : le renommage de « Renommer », sur tout le
   * coffre, sous-etiquettes comprises. La vue ouverte suit l'etiquette deplacee, et le nouveau parent se deplie.
   */
  function moveTagUnder(from: string, parent: string | null): void {
    const to = tagDropRename(from, parent);
    if (to === null || state.vaultStore === null || state.draft !== null) return;
    void state.vaultStore
      .renameTag(from, to)
      .then(async (count) => {
        const selected = state.selectedTag;
        if (selected !== null && isWithinTag(selected, from)) state.selectedTag = [to, ...tagPath(normalizeTag(selected)).slice(tagPath(normalizeTag(from)).length)].join('/');
        if (parent !== null) state.collapsedTagKeys.delete(tagKey(parent));
        await refreshVaultData();
        scheduleFlash(`Etiquette « ${normalizeTag(from)} » deplacee ${parent === null ? 'a la racine' : `sous « ${normalizeTag(parent)} »`} (« ${to} », ${count} entree(s))`);
      })
      .catch((error: unknown) => scheduleFlash(`Deplacement refuse : ${error instanceof Error ? error.message : String(error)}`))
      .finally(render);
  }

  /** Ajoute l'etiquette `tag` a une entree : glisser une ligne de la liste sur une etiquette de la barre laterale. */
  function tagEntryByDrop(entryId: string, tag: string): void {
    const entry = state.entries.find((candidate) => candidate.id === entryId);
    if (entry === undefined || state.vaultStore === null) return;
    const name = entry.secrets.name ?? 'Sans nom';
    void state.vaultStore
      // Meme ecriture que « Etiqueter » de la selection multiple : une entree qui la porte deja n'est pas reecrite.
      .applyBulk([entry.id], 'tag', { tag })
      .then(async (count) => {
        await refreshVaultData();
        scheduleFlash(count > 0 ? `Etiquette « ${normalizeTag(tag)} » ajoutee a « ${name} »` : `« ${name} » porte deja « ${normalizeTag(tag)} »`);
      })
      .catch((error: unknown) => scheduleFlash(`Etiquetage refuse : ${error instanceof Error ? error.message : String(error)}`))
      .finally(render);
  }

  // Glisser-deposer d'un dossier dans la barre laterale (le glisser de 1Password) : sur un autre dossier = dessous,
  // sur le titre « Dossiers » = a la racine. Le chemin clavier reste « Deplacer sous ». Le dossier glisse est tenu
  // ici, jamais dans la charge du glisser : rien du coffre (pas meme un identifiant) ne part vers le systeme.
  // Une ligne de la liste se glisse de meme : sur un dossier = rangee dedans, sur « Dossiers » = hors dossier.
  // En selection multiple, une ligne cochee emporte toute la selection, et le depot passe par « Deplacer » (requestBulk).
  // Sur une etiquette de la barre laterale, une ligne (ou la selection) recoit cette etiquette (le glisser de 1Password) :
  // c'est une copie, pas un deplacement -- l'entree garde son dossier et ses autres etiquettes ; la selection passe par
  // « Etiqueter » (requestBulk('tag')), donc par le mot de passe maitre si une entree sensible est cochee.
  // Une etiquette de la barre se glisse sur une autre (= dessous) ou sur le titre « Etiquettes » (= racine) : c'est
  // « Renommer » (« Clients » sous « Pro » devient « Pro/Clients »), jamais pendant une edition (le brouillon a ses etiquettes).
  const TAG_DRAG_TYPE = 'application/x-klavox-tag';
  const FOLDER_DRAG_TYPE = 'application/x-klavox-folder';
  const ENTRY_DRAG_TYPE = 'application/x-klavox-entry';
  const BULK_DRAG_TYPE = 'application/x-klavox-entries';
  let draggedFolderId: string | null = null;
  let draggedEntryId: string | null = null;
  let draggedBulk = false;
  let draggedTag: string | null = null;
  /** Une ligne de la liste se glisse si la vue permet « Deplacer » (pas la corbeille) et si l'entree n'attend pas le mot de passe maitre. */
  const entryDraggable = (entry: VaultItem): boolean => bulkActionsFor(bulkView()).includes('move') && !repromptPending(entry);
  /** En selection multiple, seule une ligne cochee se glisse, et seulement si la vue permet « Deplacer ». */
  const bulkRowDraggable = (id: string): boolean => state.bulkMode && state.bulkSelected.includes(id) && bulkActionsFor(bulkView()).includes('move');
  const folderDropOf = (event: DragEvent): { zone: HTMLElement; parentId: string | null } | null => {
    if (draggedFolderId === null && draggedEntryId === null && !draggedBulk) return null;
    const zone = (event.target as HTMLElement).closest<HTMLElement>('.sidebar .nav-folder[data-id], .sidebar [data-folder-drop="root"]');
    if (zone === null) return null;
    const parentId = zone.dataset.folderDrop === 'root' ? null : zone.dataset.id!;
    if (draggedBulk) {
      // Cible permise si au moins une entree cochee et visible en sortirait changee.
      const moved = bulkTargets(state.entries, state.bulkSelected).some((entry) => entryDropAllowed(state.folders, entry.folderId, parentId));
      return state.bulkMode && moved ? { zone, parentId } : null;
    }
    if (draggedEntryId !== null) {
      const entry = state.entries.find((candidate) => candidate.id === draggedEntryId);
      return entry !== undefined && entryDropAllowed(state.folders, entry.folderId, parentId) ? { zone, parentId } : null;
    }
    if (draggedFolderId !== null) return folderDropAllowed(state.folders, draggedFolderId, parentId) ? { zone, parentId } : null;
    return null;
  };
  /** Cible « etiquette » : seulement pour une entree ou une selection, et si au moins une entree en sortirait etiquetee. */
  const tagDropOf = (event: DragEvent): { zone: HTMLElement; tag: string } | null => {
    if (draggedEntryId === null && !draggedBulk) return null;
    const zone = (event.target as HTMLElement).closest<HTMLElement>('.sidebar .nav-tag[data-id]');
    if (zone === null) return null;
    const tag = normalizeTag(zone.dataset.id!);
    if (tag.length === 0) return null;
    const entries = draggedBulk
      ? state.bulkMode
        ? bulkTargets(state.entries, state.bulkSelected)
        : []
      : state.entries.filter((candidate) => candidate.id === draggedEntryId);
    return entries.some((entry) => bulkApplies(entry, 'tag', null, tag)) ? { zone, tag } : null;
  };
  /** Cible d'une etiquette glissee : une autre etiquette (dessous) ou le titre « Etiquettes » (racine), si son nom changerait. */
  const tagNestOf = (event: DragEvent): { zone: HTMLElement; parent: string | null } | null => {
    if (draggedTag === null) return null;
    const zone = (event.target as HTMLElement).closest<HTMLElement>('.sidebar .nav-tag[data-id], .sidebar [data-tag-drop="root"]');
    if (zone === null) return null;
    const parent = zone.dataset.tagDrop === 'root' ? null : zone.dataset.id!;
    return tagDropRename(draggedTag, parent) !== null ? { zone, parent } : null;
  };
  const clearFolderDrop = (): void => root.querySelectorAll('.sidebar .drag-over').forEach((el) => el.classList.remove('drag-over'));
  /**
   * Image du glisser d'une selection : une pastille « N entrees » (le Finder, 1Password), au lieu de la seule ligne saisie.
   * N = ce que le depot deplacera vraiment (`bulkTargets` : coche ET visible). Le nombre seul, jamais un nom d'entree.
   * Le navigateur photographie l'element apres le `dragstart` : il est pose hors ecran, puis retire au tour suivant.
   */
  const setBulkDragImage = (dataTransfer: DataTransfer | null, count: number): void => {
    if (count < 2 || dataTransfer === null || typeof dataTransfer.setDragImage !== 'function') return;
    const badge = document.createElement('div');
    badge.className = 'drag-badge';
    badge.setAttribute('aria-hidden', 'true');
    badge.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="3" width="13" height="13" rx="2.5"/><path d="M4 8v9.5A2.5 2.5 0 0 0 6.5 20H16"/></svg><span>${count} entrees</span>`;
    document.body.appendChild(badge);
    dataTransfer.setDragImage(badge, 18, 18);
    setTimeout(() => badge.remove(), 0);
  };
  root.addEventListener('dragstart', (event) => {
    const bulkRow = (event.target as HTMLElement).closest<HTMLElement>('.entry-row.bulk-row[data-id]');
    if (bulkRow !== null) {
      if (!bulkRowDraggable(bulkRow.dataset.id!)) {
        event.preventDefault();
        return;
      }
      draggedBulk = true;
      event.dataTransfer?.setData(BULK_DRAG_TYPE, '');
      // « move » vers un dossier, « copy » vers une etiquette.
      if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copyMove';
      setBulkDragImage(event.dataTransfer, bulkTargets(state.entries, state.bulkSelected).length);
      return;
    }
    const entryRow = (event.target as HTMLElement).closest<HTMLElement>('.entry-row[data-action="select-entry"][data-id]');
    if (entryRow !== null) {
      const entry = state.entries.find((candidate) => candidate.id === entryRow.dataset.id);
      // Une entree sensible ne change pas de dossier sans le mot de passe maitre, comme par sa fiche ou la selection multiple.
      if (entry === undefined || !entryDraggable(entry)) {
        event.preventDefault();
        return;
      }
      draggedEntryId = entry.id;
      event.dataTransfer?.setData(ENTRY_DRAG_TYPE, '');
      if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copyMove';
      return;
    }
    const tagRow = (event.target as HTMLElement).closest<HTMLElement>('.sidebar .nav-tag[data-id]');
    if (tagRow !== null) {
      // Pendant une edition, le brouillon porte ses etiquettes : « Renommer » est refuse, le glisser aussi.
      if (state.draft !== null) {
        event.preventDefault();
        return;
      }
      draggedTag = tagRow.dataset.id!;
      event.dataTransfer?.setData(TAG_DRAG_TYPE, '');
      if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
      return;
    }
    const row = (event.target as HTMLElement).closest<HTMLElement>('.sidebar .nav-folder[data-id]');
    if (row === null) return;
    draggedFolderId = row.dataset.id!;
    event.dataTransfer?.setData(FOLDER_DRAG_TYPE, '');
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  });
  root.addEventListener('dragover', (event) => {
    const tagNest = tagNestOf(event);
    if (tagNest !== null) {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
      tagNest.zone.classList.add('drag-over');
      return;
    }
    const tagDrop = tagDropOf(event);
    if (tagDrop !== null) {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      tagDrop.zone.classList.add('drag-over');
      return;
    }
    const drop = folderDropOf(event);
    if (drop === null) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    drop.zone.classList.add('drag-over');
  });
  root.addEventListener('dragleave', (event) => {
    const zone = (event.target as HTMLElement).closest<HTMLElement>('.sidebar .drag-over');
    if (zone !== null && !zone.contains(event.relatedTarget as Node | null)) zone.classList.remove('drag-over');
  });
  root.addEventListener('drop', (event) => {
    const tagNest = tagNestOf(event);
    const tagDrop = tagDropOf(event);
    const drop = folderDropOf(event);
    const folderId = draggedFolderId;
    const entryId = draggedEntryId;
    const bulk = draggedBulk;
    const tag = draggedTag;
    draggedFolderId = null;
    draggedEntryId = null;
    draggedBulk = false;
    draggedTag = null;
    clearFolderDrop();
    if (tagNest !== null && tag !== null) {
      event.preventDefault();
      resetIdleTimer();
      moveTagUnder(tag, tagNest.parent);
      return;
    }
    if (tagDrop !== null) {
      event.preventDefault();
      resetIdleTimer();
      if (bulk) {
        // Meme chemin que le bouton « Etiqueter » : mot de passe maitre si une entree sensible est cochee.
        state.bulkTag = tagDrop.tag;
        void requestBulk('tag');
        return;
      }
      if (entryId !== null) tagEntryByDrop(entryId, tagDrop.tag);
      return;
    }
    if (drop === null) return;
    event.preventDefault();
    resetIdleTimer();
    if (bulk) {
      // Meme chemin que le bouton « Deplacer » : gestes de la vue, mot de passe maitre si une entree sensible est cochee.
      state.bulkFolderId = drop.parentId;
      void requestBulk('move');
      return;
    }
    if (entryId !== null) {
      moveEntryToFolder(entryId, drop.parentId);
      return;
    }
    if (folderId === null) return;
    moveFolderUnder(folderId, drop.parentId);
  });
  root.addEventListener('dragend', () => {
    draggedFolderId = null;
    draggedTag = null;
    draggedEntryId = null;
    draggedBulk = false;
    clearFolderDrop();
  });

  // Glisser-deposer de fichiers sur la section « Pieces jointes » d'une entree, d'une capture de code QR sur la
  // graine 2FA de l'editeur, ou des captures d'un transfert Google Authenticator sur le bloc d'import.
  const dropzoneOf = (event: DragEvent): HTMLElement | null =>
    event.dataTransfer?.types.includes('Files') === true
      ? (event.target as HTMLElement).closest<HTMLElement>('[data-dropzone="attachments"], [data-dropzone="totp-qr"], [data-dropzone="import-qr"]')
      : null;
  root.addEventListener('dragover', (event) => {
    if (event.dataTransfer?.types.includes('Files') !== true) return;
    // Hors zone : on bloque aussi, sinon le navigateur ouvre le fichier a la place du coffre.
    event.preventDefault();
    const zone = dropzoneOf(event);
    if (zone === null) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }
    zone.classList.add('drag-over');
  });
  root.addEventListener('dragleave', (event) => {
    const zone = dropzoneOf(event);
    if (zone !== null && !zone.contains(event.relatedTarget as Node | null)) zone.classList.remove('drag-over');
  });
  root.addEventListener('drop', (event) => {
    if (event.dataTransfer?.types.includes('Files') !== true) return;
    event.preventDefault();
    const zone = dropzoneOf(event);
    if (zone === null) return;
    resetIdleTimer();
    zone.classList.remove('drag-over');
    if (zone.dataset.dropzone === 'totp-qr') {
      const image = firstImageFile(event.dataTransfer?.files);
      if (image !== null) void readDraftQrImage(image, 'totp');
      else {
        syncDraftFromForm();
        scheduleFlash('Deposez une image : la capture d’ecran du code QR.');
        render();
      }
      return;
    }
    if (zone.dataset.dropzone === 'import-qr') {
      // Un seul fichier qui n'est pas une image : un export, lu comme par « Choisir un fichier d'export ». Sinon tout
      // ce qui est depose part a la lecture des captures : un fichier qui n'en est pas une y est nomme, rien d'importe.
      const files = Array.from(event.dataTransfer?.files ?? []);
      const exportFile = files.length === 1 && !files[0]!.type.startsWith('image/') ? files[0]! : null;
      if (exportFile !== null) {
        const source = droppedImportFileHandle(event.dataTransfer);
        void Promise.all([exportFile.arrayBuffer(), source]).then(([buffer, handle]) =>
          handleImportCsvFile(exportFile.name, new Uint8Array(buffer), undefined, null, handle),
        );
        return;
      }
      if (files.length > 0) void handleImportQrImages(files);
      return;
    }
    if (zone.dataset.id !== undefined) void handleAttachFiles(zone.dataset.id, Array.from(event.dataTransfer?.files ?? []));
  });

  /**
   * Un lien d'envoi peut arriver au chargement OU par navigation dans la page
   * (l'utilisateur colle le lien dans la barre d'adresse d'un onglet deja
   * ouvert). Les deux passent par ici ; quitter la route rend la main au coffre.
   */
  function routeFromHash(): void {
    const link = parseSendLink(window.location.hash);
    if (link === null) {
      if (state.sendReceive === null) return;
      state.sendReceive = null;
      render();
      return;
    }
    if (state.sendReceive?.id === link.id) return;
    void startSendReceive(link.id, link.secretBase64Url);
  }

  /**
   * Capacite `credentials.audit` — KLAVOX est le PRODUCTEUR (contrat de socle
   * Master_1 du 2026-09-24). Le bus de capacites SANTVIA n'existe pas encore :
   * en attendant, le seul canal qu'une extension de navigateur (NAVKRYPT)
   * partage avec cette PWA est `postMessage` MEME ORIGINE. Le jour ou le bus
   * arrive, seul ce brin d'ecoute change -- `serveCredentialsAudit` reste le
   * chemin qui repond.
   *
   * Rien n'est jamais pousse : on ne repond qu'a une demande, et la reponse
   * dit « coffre-non-connecte » ou « consentement-requis » plutot que de
   * mentir. Le repondeur n'est pas un declencheur : il ne lance aucun audit
   * de fuites, il rend ce qui a deja ete verifie (sinon `verifie_le: null`).
   */
  window.addEventListener('message', (event) => {
    if (event.origin !== window.location.origin) return;
    const demande = event.data as { capacite?: unknown; demande?: unknown; origines?: unknown } | null;
    // `demande: true` distingue la question de la reponse : sans ce marqueur,
    // notre propre reponse (qui porte aussi `capacite`) nous reveillerait en boucle.
    if (demande === null || typeof demande !== 'object') return;
    if (demande.capacite !== CREDENTIALS_AUDIT_CAPABILITY || demande.demande !== true) return;
    const request: CredentialsAuditRequest = Array.isArray(demande.origines)
      ? { origines: demande.origines.filter((origine): origine is string => typeof origine === 'string') }
      : {};
    window.postMessage(
      serveCredentialsAudit(request, {
        deverrouille: state.vaultStore !== null,
        consenti: state.credentialsAuditConsent,
        entrees: state.entries,
        breachCounts: state.breachCounts,
        breachVerifieLe: state.breachCheckedAt,
        detectionParDefaut: state.defaultUriMatch,
        emailCompte: vaultAccountEmail(),
      }),
      window.location.origin,
    );
  });

  /**
   * Capacite `klavox.credentials` v1 — KLAVOX est le DESTINATAIRE (`bus/klavox-credentials.v1.md`). Meme canal que
   * `credentials.audit` : `postMessage` MEME ORIGINE en attendant le bus. Tout message passe par
   * `credentialsInbox.receive` (donc par `parseCredentialsTransfer`) ; nos propres `status`/`result` relus sur le
   * canal y sont ignores. Un echec ne se journalise pas : il pourrait citer la charge.
   */
  window.addEventListener('message', (event) => {
    if (event.origin !== window.location.origin) return;
    const data = event.data as { capability?: unknown } | null;
    if (data === null || typeof data !== 'object' || data.capability !== CREDENTIALS_TRANSFER_CAPABILITY) return;
    void credentialsInbox
      .receive(event.data)
      .then((reponse) => {
        if (reponse !== null) window.postMessage(reponse, window.location.origin);
      })
      .catch(() => undefined);
  });

  window.addEventListener('hashchange', routeFromHash);
  routeFromHash();

  void (async () => {
    const envelope = await state.repository.loadAccountEnvelope();
    state.localAccountEnvelope = envelope;
    // Seul l'ecran initial suit le stockage : un choix deja fait (recuperation, nouvel appareil) n'est pas ecrase.
    if (state.authMode === 'create') state.authMode = envelope === null ? 'create' : 'unlock';
    state.passkeyUnlocks = envelope === null ? [] : await state.repository.listPasskeyUnlocks();
    render();
    // Accueil Idenva : seulement sans coffre dans ce navigateur. Un serveur muet ou sans Idenva = accueil local.
    const homeServer = options.homeServer;
    if (homeServer !== undefined && envelope === null) {
      try {
        const config = await fetchIdenvaConfig(homeServer);
        state.idenvaHome = config === null ? 'absent' : { baseUrl: homeServer, ...config };
      } catch {
        state.idenvaHome = 'absent';
      }
      render();
    } else {
      state.idenvaHome = 'absent';
    }
  })();
  render();
}
